import { translate, formatDate as intlFormatDate, formatNumber as intlFormatNumber, formatUsd as intlFormatUsd } from './i18n/index.js';
import { getDocPage } from './i18n/docs.js';
import { STATUS_DESIGN, getStatusRank, getStatusCategory, formatTtftCell, formatSuccessRateCell, renderStatusTableCard } from './status.design.js';

// DESIGN LOCKED: matches the reference Status table. Do not change layout, spacing, columns, or sort order without an explicit design request.

    // ============================================================================
    // COMPLETE 28 MODELS CATALOG DATA (EXACT MATCH TO VIDEO)
    // ============================================================================
    let MODELS_DATA = [];

    function getExampleModelId() {
      if (Array.isArray(MODELS_DATA) && MODELS_DATA.length > 0) {
        const active = MODELS_DATA.filter(m => m.active !== false);
        if (active.length > 0) {
          const sorted = [...active].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
          return sorted[0].id;
        }
        return MODELS_DATA[0].id;
      }
      return 'MODEL_ID';
    }


    function generateRandomReferralCode() {
      const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
      let code = '';
      for (let i = 0; i < 10; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return code;
    }

    // ============================================================================
    // APPLICATION STATE
    // ============================================================================
    let storedUser = null;
    try {
      const rawUser = typeof localStorage !== 'undefined' ? localStorage.getItem('opencsn_user') : null;
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        if (parsed && (parsed.name === 'Osmora Store' || parsed.email === 'osmora@opencsn.dev')) {
          localStorage.removeItem('opencsn_user');
          storedUser = null;
        } else {
          storedUser = parsed;
          if (storedUser && !storedUser.referralCode) {
            storedUser.referralCode = generateRandomReferralCode();
            try { localStorage.setItem('opencsn_user', JSON.stringify(storedUser)); } catch (e) {}
          }
        }
      }
    } catch (e) {
      storedUser = null;
    }

    const state = {
      user: storedUser,
      turnstileToken: null,
      route: '/',
      docSubpage: 'introduction',
      docCodeLang: 'nodejs',
      lang: 'id', // 'id' or 'en'
      theme: 'dark',
      history: ['/'],
      historyIndex: 0,
      openFaqIndex: 0,
      agentDropdownOpen: false,
      // Pricing state
      pricingCompareTab: 'pro',
      pricingCalcMode: 'payg', // 'payg', 'go', 'pro', 'max', 'max5x', 'max10x'
      pricingTopupAmount: 10,
      // Public Models filter state
      modelsSearch: '',
      modelsProviders: [],
      modelsPriceRange: 'all', // 'all', 'free', 'under1', '1to5', '5to15', 'over15'
      modelsPricingType: 'all', // 'all', 'free', 'discounted'
      modelsSort: 'provider',
      modelsView: 'grid',
      // Console state
      bellOpen: false,
      userMenuOpen: false,
      dashTimeRange: '30d',
      catalogFreeOnly: false,
      catalogSearch: '',
      catalogLastUpdated: 0,
      catalogStale: false,
      catalogConnected: false,
      leaderboardRange: '7d',
      leaderboardLastUpdated: 0,
      statusRange: '24h',
      usageRange: '30d',
      apiBaseUrl: 'https://api.opencsn.com/v1',
      dashboardData: null,
      usageSummary: null,
      leaderboardData: [],
      statusData: null,
      lowBalanceDismissed: false,
      apiKeys: [],
      justCreatedKey: null,
      selectedQuickstartKey: '',
      selectedQuickstartModel: '',
      selectedQuickstartTab: 'chat',
      quickstartModelDropdownOpen: false,
      quickstartKeyDropdownOpen: false,
      hermesStep: 1,
      hermesInstallMode: 'fresh',
      hermesChatPlatform: 'telegram',
      hermesInstallTab: 'manual',
      hermesToken: '',
      planCarouselIndex: 0,
      billingAmount: 25,
      billingBalance: 0.00,
      referralCode: (storedUser && storedUser.referralCode) ? storedUser.referralCode : generateRandomReferralCode(),
      referralPeopleCount: 0,
      referralFundedCount: 0,
      referralTotalReward: 0.00,
      referralList: [],
      redeemCodeInput: '',
      invoices: [],
      announcementFilter: 'all',
      // Dev Management State
      managementTab: 'accounts', // 'accounts', 'payments', 'terms', 'privacy', 'announcements', 'models', 'system'
      selectedModelId: '',
      modelDetailApiTab: 'chat', // 'chat', 'responses', 'messages'
      modelSearchMgmt: '',
      modelStatusFilterMgmt: 'all', // 'all', 'active', 'inactive'
      modelHistory: [],
      
      // Accounts Management State
      accountsSearch: '',
      accountsRoleFilter: 'all',
      accountsStatusFilter: 'all',
      accountsList: [
        { id: 'usr_dev_master', name: 'Developer Admin', email: 'dev@opencsn.com', role: 'Admin', status: 'Active', balance: 0.00, keys: 0, joined: 'Nov 1, 2026', lastSeen: 'Just now', isDev: true, referralCode: 'DEVADMIN88' }
      ],
      activities: [],
      requestLogs: [],
      devLogs: [],
      devSystem: null,
      
      // Payments Management State
      paymentsSearch: '',
      paymentsGatewayFilter: 'all',
      paymentsStatusFilter: 'all',
      paymentsGateways: { stripe: true, midtrans: true, crypto: true, wire: true },
      paymentsList: [],
      
      // Terms of Service Management State
      termsVersion: 'v2.4',
      termsLastUpdated: 'September 28, 2026',
      termsTab: 'edit', // 'edit' or 'preview'
      termsData: {
        title: 'Terms of Service',
        version: 'v2.4',
        lastUpdated: 'September 28, 2026',
        sections: [
          { id: 1, title: '1. Acceptance of Terms', body: 'By accessing or using OpenCSN ("the Service"), you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access the Service.' },
          { id: 2, title: '2. Identity & Ownership', body: 'OpenCSN is operated independently by our team. This site and service are <b class="text-main">not affiliated with, endorsed by, or connected to openrouter.ai or any other party</b> with a similar name or brand.' },
          { id: 3, title: '3. The Service', body: 'OpenCSN is an enterprise AI gateway that provides standardized routing and access to a range of artificial intelligence (AI) models through APIs compatible with OpenAI and Anthropic formats.' },
          { id: 4, title: '4. Your Data & Content', body: '<b class="text-main">Zero Data Retention (ZDR).</b> OpenCSN does not store prompt, input, or model output content. We record metadata only for billing, rate limiting, and security compliance.' },
          { id: 5, title: '5. Accounts, API Keys & Acceptable Use', body: 'You may use the Service for lawful purposes only. You agree not to abuse the API, circumvent rate limits, send content that violates provider policies, or reverse engineer proxy endpoints.' },
          { id: 6, title: '6. Payment, Credits & Billing', body: 'The Service is billed per token (pay-per-use) or via prepaid balance. Topped-up balance is non-refundable unless a verified technical error on our gateway infrastructure caused the charge.' },
          { id: 7, title: '7. Service Level & Liability Limitations', body: 'OpenCSN provides routing on an "as is" and "as available" basis. Upstream model downtime, latency spikes, or third-party outages are outside our direct SLA guarantees.' },
          { id: 8, title: '8. Governing Law & Contact', body: 'These Terms are governed by the laws of the Republic of Indonesia. Inquiries and legal notices: <a href="mailto:support@opencsn.com" class="text-main underline">support@opencsn.com</a>.' }
        ]
      },
      
      // Privacy Policy Management State
      privacyVersion: 'v1.8',
      privacyLastUpdated: 'October 10, 2026',
      privacyZdrEnabled: true,
      privacyTab: 'edit', // 'edit' or 'preview'
      privacyData: {
        title: 'Privacy Policy',
        version: 'v1.8',
        lastUpdated: 'October 10, 2026',
        zeroDataRetention: true,
        summaryHtml: `
          <div class="surface-card rounded-2xl p-6 space-y-3 border border-strong">
            <div class="text-base font-semibold text-main">Summary: Zero Data Retention</div>
            <ul class="list-disc pl-5 space-y-2 text-xs sm:text-sm text-sec">
              <li><b class="text-main">We do not store request or response content.</b> Prompts, messages, system prompts, images, files, tool calls, and model outputs are never written to our database or logs.</li>
              <li>Content exists only in server memory while the request is in flight. It is forwarded to the model provider and discarded as soon as the response has been delivered to you.</li>
              <li><b class="text-main">One exception: prompts a provider refuses.</b> When a model provider refuses a request under its usage policy, we keep at most the first 500 characters of that prompt for 3 days to handle abuse, then delete it automatically.</li>
              <li>Your content is never used to train models and never sold.</li>
              <li>We keep <b class="text-main">metadata only</b> (model, token counts, charge, status, timing) so billing and your usage history in the dashboard work.</li>
            </ul>
          </div>
        `,
        sections: [
          { id: 1, title: '1. Introduction', body: 'OpenCSN ("we", "the Service") respects your privacy. This policy explains what data we collect, how we use it, how long we keep it, and who it is shared with.' },
          { id: 2, title: '2. Ownership & Affiliation', body: 'OpenCSN is an independent service. <b class="text-main">We are not affiliated with, part of, or officially connected to openrouter.ai or any other party</b> using a similar name or brand.' },
          { id: 3, title: '3. Data We Collect', body: '<b class="text-main">a. Account data:</b> Email address, display name, password hashed with Argon2id, Google account ID, referral codes.<br/><b class="text-main">b. API request metadata:</b> API key ID, model, provider, protocol, input/output/cache token counts, amount charged, status, error code, latency. <b class="text-main">This metadata contains no prompt or output content.</b>' },
          { id: 4, title: '4. How We Use Data', body: 'Provide the Service, calculate charges, show usage history in your dashboard, prevent abuse, and meet legal obligations.' },
          { id: 5, title: '5. AI Model Providers', body: 'OpenCSN is a gateway. To generate an answer, your request content is forwarded to the provider serving the model you chose (Anthropic, OpenAI, Google, DeepSeek, etc.) and the response is passed back to you.' },
          { id: 6, title: '6. Other Third Parties', body: 'Payment gateways (PayBridge / Pakasir for QRIS, OxaPay for crypto) and Cloudflare Turnstile. <b class="text-main">We do not sell, rent, or share your personal data for marketing.</b>' },
          { id: 7, title: '7. Security & 8. Data Retention', body: 'All traffic is encrypted with TLS/HTTPS. Prompt and output content: never stored. Request and transaction metadata: kept while your account is active.' },
          { id: 8, title: '9. Your Rights & 12. Contact', body: 'Under Indonesia\'s Personal Data Protection Law (UU No. 27/2022), you have the right to access, correct, or delete your personal data. Contact <a href="mailto:support@opencsn.com" class="text-main underline">support@opencsn.com</a>.' }
        ]
      },
      
      // Announcements Management State
      announcementBannerEnabled: true,
      announcementsList: [
        { id: 1, date: '1 Nov 2026', type: 'Promo', title: 'Kompensasi Down', body: 'Semalem sempet down, atas kompensasi tersebut Topup Bonus menjadi 50% sampai jam 12 Siang', banner: true, active: true },
        { id: 2, date: '29 Oct 2026', type: 'News', title: 'Gateway Integration Live', body: 'OpenCSN gateway integration with prepaid token balance and zero data retention is now active.', banner: false, active: true },
        { id: 3, date: '25 Oct 2026', type: 'News', title: 'Catalog Sync Active', body: 'Catalog synchronization from LiteLLM is active with live pricing and usage metering.', banner: false, active: true }
      ],
      turnstileVerified: true,
      forgotStep: 1,
      forgotEmail: ''
    };

    // ============================================================================
    // LANGUAGE: single dictionary lookup, persisted to localStorage and cookie
    // ============================================================================
    function readStoredLanguage() {
      try {
        const stored = localStorage.getItem('opencsn_lang');
        if (stored === 'id' || stored === 'en') return stored;
      } catch (e) {}
      try {
        const match = document.cookie.match(/(?:^|;\s*)opencsn_lang=([^;]+)/);
        if (match) {
          const value = decodeURIComponent(match[1]);
          if (value === 'id' || value === 'en') return value;
        }
      } catch (e) {}
      try {
        const nav = (navigator.language || '').toLowerCase();
        return nav.indexOf('id') === 0 ? 'id' : 'en';
      } catch (e) {}
      return 'en';
    }

    function persistLanguage(lang) {
      try { localStorage.setItem('opencsn_lang', lang); } catch (e) {}
      try {
        document.cookie = 'opencsn_lang=' + lang + '; Path=/; Max-Age=' + (365 * 24 * 60 * 60) + '; SameSite=Lax';
      } catch (e) {}
    }

    function t(key, vars) {
      return translate(state.lang, key, vars);
    }

    function fmtDate(value, options) {
      return intlFormatDate(value, state.lang, options);
    }

    function fmtNumber(value, options) {
      return intlFormatNumber(value, state.lang, options);
    }

    function fmtUsd(value) {
      return intlFormatUsd(value, state.lang);
    }

    function fmtTokens(n) {
      if (!n || n <= 0) return '0';
      if (n >= 1000000000) return (n / 1000000000).toFixed(1) + 'B';
      if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
      if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
      return n.toLocaleString();
    }

    function fmtSpent(s) {
      if (!s || s === 0) return '$0.00';
      if (s >= 1) return '$' + s.toFixed(2);
      return '$' + s.toFixed(4);
    }

    state.lang = readStoredLanguage();

    // ============================================================================
    // NAVIGATION & HELPERS
    // ============================================================================
    function navigate(route, docSubpage = null) {
      state.route = route;
      if (docSubpage) state.docSubpage = docSubpage;
      if (route === '/login' || route === '/register' || route === '/forgot-password') {
        state.turnstileToken = null;
        state.turnstileVerified = false;
        if (route === '/forgot-password') {
          state.forgotStep = 1;
        }
      }
      state.bellOpen = false;
      state.userMenuOpen = false;
      state.agentDropdownOpen = false;
      state.quickstartModelDropdownOpen = false;
      state.quickstartKeyDropdownOpen = false;

      const fullUrl = route === '/docs' && state.docSubpage !== 'introduction'
        ? `opencsn.com/docs/${state.docSubpage}`
        : `opencsn.com${route === '/' ? '' : route}`;

      const urlEl = document.getElementById('browser-url-display');
      if (urlEl) urlEl.textContent = fullUrl;

      if (state.history[state.historyIndex] !== route) {
        state.history = state.history.slice(0, state.historyIndex + 1);
        state.history.push(route);
        state.historyIndex = state.history.length - 1;
      }

      if (typeof window !== 'undefined' && window.location.pathname !== route) {
        try {
          window.history.pushState({ route, docSubpage }, '', route);
        } catch (e) {
          /* ignore */
        }
      }

      if (route === '/billing') {
        loadBillingData().then(() => render());
      }

      if ((route === '/api-keys' || route === '/dashboard') && state.user) {
        loadApiKeys();
      }

      if (route === '/dashboard' && state.user) {
        loadDashboardData();
      } else if (route === '/usage' && state.user) {
        loadUsageData();
      } else if (route === '/activity' && state.user) {
        loadActivityData();
      } else if (route === '/request-logs' && state.user) {
        loadRequestLogsData();
      } else if (route === '/leaderboard') {
        loadLeaderboardData();
      } else if (route === '/status') {
        loadStatusData();
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
      render();
    }

    async function apiFetch(path, options = {}) {
      const headers = {
        'Content-Type': 'application/json',
        ...(options.headers || {})
      };
      const res = await fetch(path, {
        ...options,
        credentials: 'same-origin',
        headers
      });
      if (res.status === 401) {
        state.user = null;
        try {
          localStorage.removeItem('opencsn_user');
        } catch (e) {}
        const protectedRoutes = [
          '/dashboard', '/usage', '/activity', '/request-logs',
          '/api-keys', '/plan', '/billing', '/referral',
          '/management', '/dev/management', '/structure', '/dev/structure', '/logs', '/dev-logs', '/dev/logs'
        ];
        if (protectedRoutes.includes(state.route)) {
          navigate('/login');
        }
      }
      return res;
    }

    function historyBack() {
      if (state.historyIndex > 0) {
        state.historyIndex--;
        state.route = state.history[state.historyIndex];
        render();
      }
    }

    function historyForward() {
      if (state.historyIndex < state.history.length - 1) {
        state.historyIndex++;
        state.route = state.history[state.historyIndex];
        render();
      }
    }

    function captureFormState() {
      const out = {};
      try {
        document.querySelectorAll('#app input[id], #app textarea[id], #app select[id]').forEach((el) => {
          out[el.id] = { value: el.value, checked: !!el.checked };
        });
      } catch (e) {}
      return out;
    }

    function restoreFormState(values) {
      Object.keys(values).forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        try {
          el.value = values[id].value;
          if ('checked' in el) el.checked = values[id].checked;
        } catch (e) {}
      });
    }

    function setLang(l) {
      if (l !== 'id' && l !== 'en') return;
      const formState = captureFormState();
      state.lang = l;
      persistLanguage(l);
      try { document.documentElement.lang = l; } catch (e) {}
      render();
      restoreFormState(formState);
      if (state.route === '/login' || state.route === '/register' || (state.route === '/forgot-password' && state.forgotStep !== 2)) {
        setTimeout(initTurnstile, 100);
      }
      updateDocumentTitle();
    }

    function updateDocumentTitle() {
      const map = {
        '/': 'meta.home',
        '/pricing': 'meta.pricing',
        '/models': 'meta.models',
        '/docs': 'meta.docs',
        '/changelog': 'meta.changelog',
        '/login': 'meta.login',
        '/register': 'meta.register'
      };
      let key = map[state.route];
      if (!key && state.route.indexOf('/models/') === 0) key = 'meta.models';
      if (!map[state.route] && !key) key = 'meta.home';
      try { document.title = t(key); } catch (e) {}
    }

    function toggleTheme(event) {
      const isDark = state.theme === 'dark';
      const newTheme = isDark ? 'light' : 'dark';

      const updateDOM = () => {
        state.theme = newTheme;
        const htmlEl = document.documentElement;
        if (newTheme === 'light') {
          htmlEl.classList.add('light');
          htmlEl.classList.remove('dark');
        } else {
          htmlEl.classList.add('dark');
          htmlEl.classList.remove('light');
        }
        render();
      };

      const e = event || (typeof window !== 'undefined' ? window.event : null);

      if (typeof document !== 'undefined' && document.startViewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        let x = window.innerWidth - 80;
        let y = 40;
        if (e && typeof e.clientX === 'number' && e.clientX > 0) {
          x = e.clientX;
          y = e.clientY;
        } else if (e && e.currentTarget && e.currentTarget.getBoundingClientRect) {
          const rect = e.currentTarget.getBoundingClientRect();
          x = rect.left + rect.width / 2;
          y = rect.top + rect.height / 2;
        } else {
          const btn = document.querySelector('button[onclick*="toggleTheme"]');
          if (btn) {
            const rect = btn.getBoundingClientRect();
            x = rect.left + rect.width / 2;
            y = rect.top + rect.height / 2;
          }
        }

        const endRadius = Math.hypot(
          Math.max(x, window.innerWidth - x),
          Math.max(y, window.innerHeight - y)
        );

        const transition = document.startViewTransition(() => {
          updateDOM();
        });

        transition.ready.then(() => {
          const clipPath = [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${endRadius}px at ${x}px ${y}px)`
          ];
          document.documentElement.animate(
            {
              clipPath: clipPath
            },
            {
              duration: 350,
              easing: 'ease-in-out',
              pseudoElement: '::view-transition-new(root)'
            }
          );
        });
      } else {
        const htmlEl = document.documentElement;
        htmlEl.classList.add('theme-transitioning');
        updateDOM();
        setTimeout(() => {
          htmlEl.classList.remove('theme-transitioning');
        }, 350);
      }
    }

    function showToast(msg) {
      const root = document.getElementById('toast-root');
      if (!root) return;
      const item = document.createElement('div');
      item.className = 'surface-card-solid border border-[#c87a51]/40 text-main px-4 py-2.5 rounded-xl shadow-2xl text-xs flex items-center gap-2 transition-all';
      item.innerHTML = `<span class="text-[#c87a51]">✓</span><span>${msg}</span>`;
      root.appendChild(item);
      setTimeout(() => {
        item.remove();
      }, 2200);
    }

    function copyText(text, btnEl) {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch (e) {}
      document.body.removeChild(ta);
      if (btnEl) {
        const orig = btnEl.innerHTML;
        btnEl.innerHTML = '✓ Copied';
        setTimeout(() => { btnEl.innerHTML = orig; }, 1400);
      }
      showToast('Disalin ke clipboard');
    }

    function copyFromAttr(btnEl) {
      const code = btnEl.getAttribute('data-copy') || '';
      copyText(code, btnEl);
    }


    // ============================================================================
    // ICONS: line icons (lucide-style) + logos from asset-image-opencsn sprite
    // ============================================================================
    const ICONS = {"percent":"<line x1=\"19\" x2=\"5\" y1=\"5\" y2=\"19\"/><circle cx=\"6.5\" cy=\"6.5\" r=\"2.5\"/><circle cx=\"17.5\" cy=\"17.5\" r=\"2.5\"/>","gauge":"<path d=\"m12 14 4-4\"/><path d=\"M3.34 19a10 10 0 1 1 17.32 0\"/>","shield":"<path d=\"M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z\"/>","cluster":"<circle cx=\"12\" cy=\"7\" r=\"2.5\"/><circle cx=\"7.5\" cy=\"15\" r=\"2.5\"/><circle cx=\"16.5\" cy=\"15\" r=\"2.5\"/><line x1=\"10.2\" y1=\"8.5\" x2=\"8.8\" y2=\"13\"/><line x1=\"13.8\" y1=\"8.5\" x2=\"15.2\" y2=\"13\"/><line x1=\"10\" y1=\"15\" x2=\"14\" y2=\"15\"/>","shield-alert":"<path d=\"M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z\"/><line x1=\"12\" y1=\"8\" x2=\"12\" y2=\"12\"/><line x1=\"12\" y1=\"16\" x2=\"12.01\" y2=\"16\"/>","check":"<polyline points=\"20 6 9 17 4 12\"/>","wallet":"<path d=\"M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1\"/><path d=\"M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4\"/>","user-plus":"<path d=\"M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2\"/><circle cx=\"9\" cy=\"7\" r=\"4\"/><line x1=\"19\" x2=\"19\" y1=\"8\" y2=\"14\"/><line x1=\"22\" x2=\"16\" y1=\"11\" y2=\"11\"/>","users":"<path d=\"M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2\"/><circle cx=\"9\" cy=\"7\" r=\"4\"/><path d=\"M22 21v-2a4 4 0 0 0-3-3.87\"/><path d=\"M16 3.13a4 4 0 0 1 0 7.75\"/>","pencil":"<path d=\"M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z\"/><path d=\"m15 5 4 4\"/>","grid":"<rect width=\"7\" height=\"7\" x=\"3\" y=\"3\" rx=\"1\"/><rect width=\"7\" height=\"7\" x=\"14\" y=\"3\" rx=\"1\"/><rect width=\"7\" height=\"7\" x=\"14\" y=\"14\" rx=\"1\"/><rect width=\"7\" height=\"7\" x=\"3\" y=\"14\" rx=\"1\"/>","list":"<line x1=\"8\" x2=\"21\" y1=\"6\" y2=\"6\"/><line x1=\"8\" x2=\"21\" y1=\"12\" y2=\"12\"/><line x1=\"8\" x2=\"21\" y1=\"18\" y2=\"18\"/><line x1=\"3\" x2=\"3.01\" y1=\"6\" y2=\"6\"/><line x1=\"3\" x2=\"3.01\" y1=\"12\" y2=\"12\"/><line x1=\"3\" x2=\"3.01\" y1=\"18\" y2=\"18\"/>","layout-dashboard":"<rect width=\"7\" height=\"9\" x=\"3\" y=\"3\" rx=\"1\"/><rect width=\"7\" height=\"5\" x=\"14\" y=\"3\" rx=\"1\"/><rect width=\"7\" height=\"9\" x=\"14\" y=\"12\" rx=\"1\"/><rect width=\"7\" height=\"5\" x=\"3\" y=\"16\" rx=\"1\"/>","boxes":"<path d=\"M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5.5l-5-3-4.03 2.42Z\"/><path d=\"m7 16.5-4.74-2.85\"/><path d=\"m7 16.5 5-3\"/><path d=\"M7 16.5v5.17\"/><path d=\"M12 13.5V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 10.5l-5 3Z\"/><path d=\"m17 16.5-5-3\"/><path d=\"m17 16.5 4.74-2.85\"/><path d=\"M17 16.5v5.17\"/><path d=\"M7.97 4.42A2 2 0 0 0 7 6.13v4.37l5 3 5-3V6.13a2 2 0 0 0-.97-1.71l-3-1.8a2 2 0 0 0-2.06 0l-3 1.8Z\"/><path d=\"M12 8 7.26 5.15\"/><path d=\"m12 8 4.74-2.85\"/><path d=\"M12 13.5V8\"/>","radio":"<path d=\"M4.9 19.1C1 15.2 1 8.8 4.9 4.9\"/><path d=\"M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5\"/><circle cx=\"12\" cy=\"12\" r=\"2\"/><path d=\"M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5\"/><path d=\"M19.1 4.9C23 8.8 23 15.1 19.1 19\"/>","trophy":"<path d=\"M6 9H4.5a2.5 2.5 0 0 1 0-5H6\"/><path d=\"M18 9h1.5a2.5 2.5 0 0 0 0-5H18\"/><path d=\"M4 22h16\"/><path d=\"M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22\"/><path d=\"M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22\"/><path d=\"M18 2H6v7a6 6 0 0 0 12 0V2Z\"/>","chart-column":"<path d=\"M3 3v16a2 2 0 0 0 2 2h16\"/><path d=\"M18 17V9\"/><path d=\"M13 17V5\"/><path d=\"M8 17v-3\"/>","activity":"<path d=\"M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2\"/>","scroll-text":"<path d=\"M15 12h-5\"/><path d=\"M15 8h-5\"/><path d=\"M19 17V5a2 2 0 0 0-2-2H4\"/><path d=\"M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3\"/>","key-round":"<path d=\"M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z\"/><circle cx=\"16.5\" cy=\"7.5\" r=\".5\" fill=\"currentColor\"/>","plug":"<path d=\"M12 22v-5\"/><path d=\"M9 8V2\"/><path d=\"M15 8V2\"/><path d=\"M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z\"/>","circle-dollar-sign":"<circle cx=\"12\" cy=\"12\" r=\"10\"/><path d=\"M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8\"/><path d=\"M12 18V6\"/>","credit-card":"<rect width=\"20\" height=\"14\" x=\"2\" y=\"5\" rx=\"2\"/><line x1=\"2\" x2=\"22\" y1=\"10\" y2=\"10\"/>","gift":"<rect x=\"3\" y=\"8\" width=\"18\" height=\"4\" rx=\"1\"/><path d=\"M12 8v13\"/><path d=\"M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7\"/><path d=\"M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5\"/>","search":"<circle cx=\"11\" cy=\"11\" r=\"8\"/><path d=\"m21 21-4.3-4.3\"/>","book-open":"<path d=\"M12 7v14\"/><path d=\"M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z\"/>","bell":"<path d=\"M10.268 21a2 2 0 0 0 3.464 0\"/><path d=\"M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326\"/>","sun":"<circle cx=\"12\" cy=\"12\" r=\"4\"/><path d=\"M12 2v2\"/><path d=\"M12 20v2\"/><path d=\"m4.93 4.93 1.41 1.41\"/><path d=\"m17.66 17.66 1.41 1.41\"/><path d=\"M2 12h2\"/><path d=\"M20 12h2\"/><path d=\"m6.34 17.66-1.41 1.41\"/><path d=\"m19.07 4.93-1.41 1.41\"/>","moon":"<path d=\"M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z\"/>","zap":"<path d=\"M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z\"/>","copy":"<rect width=\"14\" height=\"14\" x=\"8\" y=\"8\" rx=\"2\" ry=\"2\"/><path d=\"M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2\"/>","trending-up":"<polyline points=\"22 7 13.5 15.5 8.5 10.5 2 17\"/><polyline points=\"16 7 22 7 22 13\"/>","globe":"<circle cx=\"12\" cy=\"12\" r=\"10\"/><path d=\"M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20\"/><path d=\"M2 12h20\"/>","eye":"<path d=\"M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0\"/><circle cx=\"12\" cy=\"12\" r=\"3\"/>","eye-off":"<path d=\"M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49\"/><path d=\"M14.084 14.158a3 3 0 0 1-4.242-4.242\"/><path d=\"M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143\"/><path d=\"m2 2 20 20\"/>","plus":"<path d=\"M5 12h14\"/><path d=\"M12 5v14\"/>","x":"<path d=\"M18 6 6 18\"/><path d=\"m6 6 12 12\"/>","flame":"<path d=\"M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z\"/>","crown":"<path d=\"M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z\"/><path d=\"M5 21h14\"/>","rocket":"<path d=\"M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z\"/><path d=\"m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z\"/><path d=\"M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0\"/><path d=\"M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5\"/>","gem":"<path d=\"M6 3h12l4 6-10 13L2 9Z\"/><path d=\"M11 3 8 9l4 13 4-13-3-6\"/><path d=\"M2 9h20\"/>","user":"<path d=\"M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2\"/><circle cx=\"12\" cy=\"7\" r=\"4\"/>","message-square":"<path d=\"M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z\"/>","brain":"<path d=\"M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z\"/><path d=\"M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z\"/><path d=\"M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4\"/><path d=\"M17.599 6.5a3 3 0 0 0 .399-1.375\"/><path d=\"M6.003 5.125A3 3 0 0 0 6.401 6.5\"/><path d=\"M3.477 10.896a4 4 0 0 1 .585-.396\"/><path d=\"M19.938 10.5a4 4 0 0 1 .585.396\"/><path d=\"M6 18a4 4 0 0 1-1.967-.516\"/><path d=\"M19.967 17.484A4 4 0 0 1 18 18\"/>","clock":"<circle cx=\"12\" cy=\"12\" r=\"10\"/><polyline points=\"12 6 12 12 16 14\"/>","trash-2":"<path d=\"M3 6h18\"/><path d=\"M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6\"/><path d=\"M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2\"/><line x1=\"10\" x2=\"10\" y1=\"11\" y2=\"17\"/><line x1=\"14\" x2=\"14\" y1=\"11\" y2=\"17\"/>","link":"<path d=\"M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71\"/><path d=\"M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71\"/>","send":"<path d=\"M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z\"/><path d=\"m21.854 2.147-10.94 10.939\"/>","rotate-ccw":"<path d=\"M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8\"/><path d=\"M3 3v5h5\"/>","log-out":"<path d=\"M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4\"/><polyline points=\"16 17 21 12 16 7\"/><line x1=\"21\" x2=\"9\" y1=\"12\" y2=\"12\"/>","arrow-left-right":"<path d=\"M8 3 4 7l4 4\"/><path d=\"M4 7h16\"/><path d=\"m16 21 4-4-4-4\"/><path d=\"M20 17H4\"/>","chevron-down":"<path d=\"m6 9 6 6 6-6\"/>","qr-code":"<rect width=\"5\" height=\"5\" x=\"3\" y=\"3\" rx=\"1\"/><rect width=\"5\" height=\"5\" x=\"16\" y=\"3\" rx=\"1\"/><rect width=\"5\" height=\"5\" x=\"3\" y=\"16\" rx=\"1\"/><path d=\"M21 16h-3a2 2 0 0 0-2 2v3\"/><path d=\"M21 21v.01\"/><path d=\"M12 7v3a2 2 0 0 1-2 2H7\"/><path d=\"M3 12h.01\"/><path d=\"M12 3h.01\"/><path d=\"M12 16v.01\"/><path d=\"M16 12h1\"/><path d=\"M21 12v.01\"/><path d=\"M12 21v-1\"/>","circle-dot":"<circle cx=\"12\" cy=\"12\" r=\"10\"/><circle cx=\"12\" cy=\"12\" r=\"1\"/>","more-horizontal":"<circle cx=\"12\" cy=\"12\" r=\"1.5\"/><circle cx=\"19\" cy=\"12\" r=\"1.5\"/><circle cx=\"5\" cy=\"12\" r=\"1.5\"/>","sliders":"<line x1=\"4\" y1=\"21\" x2=\"4\" y2=\"14\"/><line x1=\"4\" y1=\"10\" x2=\"4\" y2=\"3\"/><line x1=\"12\" y1=\"21\" x2=\"12\" y2=\"12\"/><line x1=\"12\" y1=\"8\" x2=\"12\" y2=\"3\"/><line x1=\"20\" y1=\"21\" x2=\"20\" y2=\"16\"/><line x1=\"20\" y1=\"12\" x2=\"20\" y2=\"3\"/><line x1=\"1\" y1=\"14\" x2=\"7\" y2=\"14\"/><line x1=\"9\" y1=\"8\" x2=\"15\" y2=\"8\"/><line x1=\"17\" y1=\"16\" x2=\"23\" y2=\"16\"/>","layers":"<polygon points=\"12 2 2 7 12 12 22 7 12 2\"/><polyline points=\"2 17 12 22 22 17\"/><polyline points=\"2 12 12 17 22 12\"/>","terminal":"<polyline points=\"4 17 10 11 4 5\"/><line x1=\"12\" y1=\"19\" x2=\"20\" y2=\"19\"/>"};
    const ASSET_VB = {"anthropic":"0 0 24 24","openai":"0 0 24 24","google":"0 0 256 262","gemini":"0 0 256 262","deepseek":"0 0 24 24","moonshot":"0 0 24 24","zai":"0 0 24 24","xiaomi-mimo-wordmark":"0 0 24 14.8","minimax":"0 0 24 24","qwen":"0 0 24 24","clackyai":"0 0 187 60","cline":"0 0 152 60","codex":"0 0 24 24","gork":"0 0 139 60","mastra":"0 0 151 60","obsidian":"0 0 181 60","claude-code":"0 0 248 248","cursor":"0 0 466.73 533.32","opencode":"0 0 512 512","droid-factory":"0 0 508 508","hermes-agent":"0 0 512.000000 512.000000"};
    const WIDE_ASSETS = ['xiaomi-mimo-wordmark'];

    function ico(name, cls = '') {
      return `<svg class="ico ${cls}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[name] || ''}</svg>`;
    }

    function assetLogo(name, cls = 'w-5 h-5') {
      return `<svg class="${cls} shrink-0" viewBox="${ASSET_VB[name]}" fill="currentColor" aria-hidden="true"><use href="#asset-${name}"/></svg>`;
    }

    function getOpenCSNLogo() {
      return `<img src="data:image/png;base64,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" alt="OpenCSN" class="h-6 w-auto shrink-0" draggable="false" />`;
    }

    function getProviderIcon(provider, cls = 'w-4 h-4') {
      const assetMap = {
        'Anthropic': 'anthropic', 'OpenAI': 'openai', 'Google': 'google', 'Google Gemini': 'google', 'Gemini': 'google', 'DeepSeek': 'deepseek',
        'Moonshot': 'moonshot', 'Z.ai': 'zai', 'Xiaomi': 'xiaomi-mimo-wordmark',
        'MiniMax': 'minimax', 'Qwen': 'qwen'
      };
      if (assetMap[provider]) {
        const name = assetMap[provider];
        const c = WIDE_ASSETS.includes(name) ? cls.replace(/\bw-\S+/g, 'w-auto') : cls;
        return assetLogo(name, c);
      }
      // No asset provided for these providers yet (Meta, StepFun): fallback glyphs
      const fallback = {
        'Meta': `<svg class="${cls}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18.2 8C16.2 8 14.6 9.8 12 13.5C9.4 9.8 7.8 8 5.8 8C3.6 8 2 9.8 2 12.2C2 14.8 3.8 16.5 6 16.5C8 16.5 9.8 14.8 12 11.5C14.2 14.8 16 16.5 18 16.5C20.2 16.5 22 14.8 22 12.2C22 9.8 20.4 8 18.2 8Z"/></svg>`,
        'StepFun': `<svg class="${cls}" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H9.5A2.5 2.5 0 0 0 7 5.5V11h5v2H5v5.5A2.5 2.5 0 0 0 7.5 21H17a2.5 2.5 0 0 0 2.5-2.5V13h-5v-2h7V5.5A2.5 2.5 0 0 0 19 3z"/></svg>`
      };
      return fallback[provider] || assetLogo('openai', cls);
    }

    function getFlagIcon(code) {
      if (code === 'id') {
        return `<svg class="w-4 h-4 rounded-full" viewBox="0 0 24 24" aria-hidden="true">
          <defs><clipPath id="flag-clip-id"><circle cx="12" cy="12" r="12"/></clipPath></defs>
          <g clip-path="url(#flag-clip-id)">
            <rect width="24" height="12" fill="#E70011"/>
            <rect y="12" width="24" height="12" fill="#FFFFFF"/>
          </g>
          <circle cx="12" cy="12" r="11.6" fill="none" stroke="rgba(255,255,255,0.25)" stroke-width="0.8"/>
        </svg>`;
      }
      if (code === 'en' || code === 'gb') {
        return `<svg class="w-4 h-4 rounded-full" viewBox="0 0 24 24" aria-hidden="true">
          <defs><clipPath id="flag-clip-gb"><circle cx="12" cy="12" r="12"/></clipPath></defs>
          <g clip-path="url(#flag-clip-gb)">
            <rect width="24" height="24" fill="#012169"/>
            <path d="M0,0 L24,24 M24,0 L0,24" stroke="#FFFFFF" stroke-width="4.5"/>
            <path d="M0,0 L24,24 M24,0 L0,24" stroke="#C8102E" stroke-width="2"/>
            <path d="M12,0 V24 M0,12 H24" stroke="#FFFFFF" stroke-width="7"/>
            <path d="M12,0 V24 M0,12 H24" stroke="#C8102E" stroke-width="4"/>
          </g>
          <circle cx="12" cy="12" r="11.6" fill="none" stroke="rgba(255,255,255,0.25)" stroke-width="0.8"/>
        </svg>`;
      }
      const stripes = [0,2,4,6,8,10,12].map(i => `<rect y="${(i*24/13).toFixed(3)}" width="24" height="${(24/13).toFixed(3)}" fill="#B22234"/>`).join('');
      const stars = [];
      for (let r = 0; r < 4; r++) for (let c = 0; c < 4; c++) stars.push(`<circle cx="${(1.9 + c*2.6).toFixed(1)}" cy="${(1.9 + r*2.6).toFixed(1)}" r="0.55" fill="#FFFFFF"/>`);
      return `<svg class="w-5 h-5 rounded-full" viewBox="0 0 24 24" aria-hidden="true">
        <defs><clipPath id="flag-clip-us"><circle cx="12" cy="12" r="12"/></clipPath></defs>
        <g clip-path="url(#flag-clip-us)">
          <rect width="24" height="24" fill="#FFFFFF"/>
          ${stripes}
          <rect width="11" height="12.92" fill="#3C3B6E"/>
          ${stars.join('')}
        </g>
        <circle cx="12" cy="12" r="11.6" fill="none" stroke="rgba(255,255,255,0.25)" stroke-width="0.8"/>
      </svg>`;
    }

    // Auto-scrolling marquee row (pauses while pressed/touched, resumes on release)
    function renderMarqueeRow(label, items, opts = {}) {
      const duration = opts.duration || 32;
      const reverse = !!opts.reverse;
      const gap = opts.gap || 'gap-9';
      const pad = opts.pad || 'pr-9';
      const group = `<div class="marquee-group flex items-center shrink-0 ${gap} ${pad}">${items.map(i => `<span class="shrink-0 whitespace-nowrap flex items-center">${i}</span>`).join('')}</div>`;
      const half = group.repeat(3);
      return `
        <div class="flex items-center gap-6">
          <span class="font-mono text-xs uppercase tracking-widest text-mut w-20 shrink-0">${label}</span>
          <div class="marquee flex-1 min-w-0 overflow-hidden">
            <div class="marquee-track flex w-max items-center" style="animation-duration:${duration}s;${reverse ? 'animation-direction:reverse;' : ''}">
              ${half}${half}
            </div>
          </div>
        </div>`;
    }

    // ============================================================================
    // PUBLIC FLOATING PILL NAVBAR & FOOTER
    // ============================================================================
    function renderPublicNavbar() {
      const isId = state.lang === 'id';
      return `
        <div class="sticky top-0 z-40 flex justify-center px-4 pt-4 pointer-events-none">
          <nav class="pointer-events-auto surface-card-solid backdrop-blur-xl border border-strong rounded-full px-3 sm:px-5 py-2 flex items-center gap-2.5 sm:gap-6 shadow-2xl">
            <button onclick="navigate('/')" class="flex items-center gap-2 font-semibold text-sm text-main pr-1">
              ${getOpenCSNLogo()}
              <span>OpenCSN</span>
            </button>

            <div class="hidden sm:flex items-center gap-4 sm:gap-6 text-xs font-medium text-sec">
              <button onclick="navigate('/models')" class="${state.route === '/models' ? 'text-main font-semibold' : 'hover:text-main'} transition">${t('nav.models')}</button>
              <button onclick="navigate('/pricing')" class="${state.route === '/pricing' ? 'text-main font-semibold' : 'hover:text-main'} transition">${t('nav.pricing')}</button>
              <button onclick="navigate('/docs', 'introduction')" class="${state.route === '/docs' ? 'text-main font-semibold' : 'hover:text-main'} transition">${t('nav.docs')}</button>
            </div>

            <!-- Language & Theme Controls (Segmented Capsule matching reference) -->
            <div class="flex items-center gap-1.5 border-l border-subtle pl-2 sm:pl-3">
              <div class="flex items-center surface-card-solid border border-subtle rounded-full p-0.5 text-xs">
                <button onclick="setLang('id')" class="flex items-center gap-1 px-2.5 py-1 rounded-full transition ${isId ? 'bg-[var(--copper)] text-white font-semibold shadow-sm' : 'text-sec hover:text-main'}" title="${t('nav.langId')}">
                  ${getFlagIcon('id')}
                  <span class="text-xs font-medium">ID</span>
                </button>
                <button onclick="setLang('en')" class="flex items-center gap-1 px-2.5 py-1 rounded-full transition ${!isId ? 'bg-[var(--copper)] text-white font-semibold shadow-sm' : 'text-sec hover:text-main'}" title="${t('nav.langEn')}">
                  ${getFlagIcon('en')}
                  <span class="text-xs font-medium">EN</span>
                </button>
              </div>
              <button onclick="toggleTheme(event)" class="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-sec hover:text-main transition hover:bg-black/5 dark:hover:bg-white/5" title="${t('nav.toggleTheme')}">${state.theme === 'dark' ? ico('sun', 'w-4 h-4') : ico('moon', 'w-4 h-4')}</button>
            </div>

            <button onclick="navigate('/dashboard')" class="bg-[var(--text-main)] hover:opacity-90 text-[var(--bg-main)] font-semibold text-xs px-3.5 sm:px-4 py-2 rounded-full flex items-center gap-1.5 transition shadow">
              <span>${t('nav.console')}</span>
              <span class="text-xs">↗</span>
            </button>
          </nav>
        </div>
      `;
    }

    function renderPublicFooter() {
      const isId = state.lang === 'id';
      return `
        <footer class="footer-scrim border-t border-[rgba(255,255,255,0.14)] mt-24 py-16 px-6 relative z-10">
          <div class="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
            <div class="md:col-span-6 space-y-4">
              <button onclick="navigate('/')" class="flex items-center gap-2.5 font-semibold text-base text-[var(--text-primary)]">
                ${getOpenCSNLogo()}
                <span>OpenCSN</span>
              </button>
              <p class="text-xs text-[var(--text-secondary)] max-w-sm leading-relaxed">
                ${t('footer.tagline')}
              </p>
              <div class="flex items-center gap-2.5 pt-1">
                <a href="https://x.com/OpenCSN_id" target="_blank" rel="noopener noreferrer" class="w-8 h-8 rounded-full bg-[rgba(22,15,11,0.9)] border border-[rgba(255,255,255,0.14)] flex items-center justify-center text-[#EDE3DA] hover:text-white hover:border-[rgba(255,255,255,0.3)] transition shadow-sm" aria-label="X (Twitter)">
                  <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4l11.733 16h4.267l-11.733 -16z"/><path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772"/></svg>
                </a>
                <a href="https://t.me/OpenCSN_id" target="_blank" rel="noopener noreferrer" class="w-8 h-8 rounded-full bg-[rgba(22,15,11,0.9)] border border-[rgba(255,255,255,0.14)] flex items-center justify-center text-[#EDE3DA] hover:text-white hover:border-[rgba(255,255,255,0.3)] transition shadow-sm" aria-label="Telegram">
                  ${ico('send', 'w-3.5 h-3.5')}
                </a>
                <a href="mailto:support@opencsn.com" class="w-8 h-8 rounded-full bg-[rgba(22,15,11,0.9)] border border-[rgba(255,255,255,0.14)] flex items-center justify-center text-[#EDE3DA] hover:text-white hover:border-[rgba(255,255,255,0.3)] transition shadow-sm" aria-label="Email support">
                  <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                </a>
              </div>
              <div class="text-xs text-[var(--text-secondary)] pt-1">
                ${t('footer.emailIntro')} <a href="mailto:support@opencsn.com" class="text-white underline decoration-[#c87a51] hover:text-[#EDE3DA] transition">support@opencsn.com</a>
              </div>
            </div>

            <div class="md:col-span-3 space-y-2.5 text-xs">
              <div class="font-mono text-xs uppercase tracking-wider text-[#E6D8CB] font-semibold">${t('footer.product')}</div>
              <ul class="space-y-2">
                <li><button onclick="navigate('/models')" class="text-[#EDE3DA] hover:text-white hover:underline transition">${t('footer.models')}</button></li>
                <li><button onclick="navigate('/pricing')" class="text-[#EDE3DA] hover:text-white hover:underline transition">${t('footer.pricing')}</button></li>
                <li><button onclick="navigate('/docs', 'introduction')" class="text-[#EDE3DA] hover:text-white hover:underline transition">${t('footer.docs')}</button></li>
                <li><button onclick="navigate('/changelog')" class="text-[#EDE3DA] hover:text-white hover:underline transition">${t('footer.changelog')}</button></li>
              </ul>
            </div>

            <div class="md:col-span-3 space-y-2.5 text-xs">
              <div class="font-mono text-xs uppercase tracking-wider text-[#E6D8CB] font-semibold">${t('footer.legal')}</div>
              <ul class="space-y-2">
                <li><a href="/terms" class="text-[#EDE3DA] hover:text-white hover:underline transition">${t('footer.terms')}</a></li>
                <li><a href="/privacy" class="text-[#EDE3DA] hover:text-white hover:underline transition">${t('footer.privacy')}</a></li>
              </ul>
            </div>
          </div>

          <div class="max-w-6xl mx-auto mt-12 pt-6 border-t border-[rgba(255,255,255,0.14)] text-xs text-[var(--text-muted)] font-mono">
            © 2026 OpenCSN. ${t('footer.rights')}
          </div>
        </footer>
      `;
    }

    // ============================================================================
    // PUBLIC PAGE 1: LANDING PAGE (/)
    // ============================================================================
    
    window.activeHeroTab = 'openai';

    function getHeroCodeSnippetHtml(tab) {
      if (tab === 'anthropic') {
        return `<span class="text-[#c084fc]">import</span> <span class="text-[#93c5fd]">Anthropic</span> <span class="text-[#c084fc]">from</span> <span class="text-[#86efac]">'@anthropic-ai/sdk'</span>;

<span class="text-[#64748b]">// Replace with your public OpenCSN instance domain</span>
<span class="text-[#c084fc]">const</span> client = <span class="text-[#c084fc]">new</span> <span class="text-[#93c5fd]">Anthropic</span>({
  baseURL: <span class="text-[#86efac]">'https://opencsn.com'</span>,
  apiKey: <span class="text-[#fbbf24]">process.env.OPENCSN_API_KEY</span>,
});

<span class="text-[#c084fc]">const</span> response = <span class="text-[#c084fc]">await</span> client.messages.<span class="text-[#60a5fa]">create</span>({
  model: <span class="text-[#86efac]">'MODEL_ID_FROM_CONSOLE'</span>,
  max_tokens: <span class="text-[#f59e0b]">1024</span>,
  messages: [{ role: <span class="text-[#86efac]">'user'</span>, content: <span class="text-[#86efac]">'Optimalkan query SQL ini...'</span> }],
});`;
      }
      if (tab === 'curl') {
        return `<span class="text-[#38bdf8]">curl</span> <span class="text-[#86efac]">https://api.opencsn.com/v1/chat/completions</span> \
  <span class="text-[#cbd5e1]">-H</span> <span class="text-[#86efac]">"Authorization: Bearer $OPENCSN_API_KEY"</span> \
  <span class="text-[#cbd5e1]">-H</span> <span class="text-[#86efac]">"Content-Type: application/json"</span> \
  <span class="text-[#cbd5e1]">-d</span> <span class="text-[#fbbf24]">'{
    "model": "MODEL_ID_FROM_CONSOLE",
    "messages": [{"role": "user", "content": "Optimalkan query SQL ini..."}]
  }'</span>`;
      }
      return `<span class="text-[#c084fc]">import</span> <span class="text-[#93c5fd]">OpenAI</span> <span class="text-[#c084fc]">from</span> <span class="text-[#86efac]">'openai'</span>;

<span class="text-[#64748b]">// Replace with your public OpenCSN instance domain</span>
<span class="text-[#c084fc]">const</span> client = <span class="text-[#c084fc]">new</span> <span class="text-[#93c5fd]">OpenAI</span>({
  baseURL: <span class="text-[#86efac]">'https://api.opencsn.com/v1'</span>,
  apiKey: <span class="text-[#fbbf24]">process.env.OPENCSN_API_KEY</span>,
});

<span class="text-[#c084fc]">const</span> response = <span class="text-[#c084fc]">await</span> client.chat.completions.<span class="text-[#60a5fa]">create</span>({
  model: <span class="text-[#86efac]">'MODEL_ID_FROM_CONSOLE'</span>, <span class="text-[#64748b]">// gunakan model ID yang aktif pada konsol instance ini</span>
  messages: [{ role: <span class="text-[#86efac]">'user'</span>, content: <span class="text-[#86efac]">'Optimalkan query SQL ini...'</span> }],
});`;
    }

    function switchHeroCodeTab(tab) {
      window.activeHeroTab = tab;
      const pre = document.getElementById('hero-code-content');
      if (pre) {
        pre.innerHTML = getHeroCodeSnippetHtml(tab);
      }
      ['openai', 'anthropic', 'curl'].forEach(t => {
        const btn = document.getElementById('hero-tab-' + t);
        if (btn) {
          if (t === tab) {
            btn.className = 'px-3 py-1 rounded-lg text-white font-medium bg-white/10 transition shadow-sm';
          } else {
            btn.className = 'px-3 py-1 rounded-lg text-sec hover:text-white transition';
          }
        }
      });
    }

    function copyHeroCodeSnippet(btn) {
      const rawCodes = {
        openai: `import OpenAI from 'openai';\n\n// Replace with your public OpenCSN instance domain\nconst client = new OpenAI({\n  baseURL: 'https://api.opencsn.com/v1',\n  apiKey: process.env.OPENCSN_API_KEY,\n});\n\nconst response = await client.chat.completions.create({\n  model: 'MODEL_ID_FROM_CONSOLE', // gunakan model ID yang aktif pada konsol instance ini\n  messages: [{ role: 'user', content: 'Optimalkan query SQL ini...' }],\n});`,
        anthropic: `import Anthropic from '@anthropic-ai/sdk';\n\n// Replace with your public OpenCSN instance domain\nconst client = new Anthropic({\n  baseURL: 'https://opencsn.com',\n  apiKey: process.env.OPENCSN_API_KEY,\n});\n\nconst response = await client.messages.create({\n  model: 'MODEL_ID_FROM_CONSOLE',\n  max_tokens: 1024,\n  messages: [{ role: 'user', content: 'Optimalkan query SQL ini...' }],\n});`,
        curl: `curl https://api.opencsn.com/v1/chat/completions \\\n  -H "Authorization: Bearer $OPENCSN_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "model": "MODEL_ID_FROM_CONSOLE",\n    "messages": [{"role": "user", "content": "Optimalkan query SQL ini..."}]\n  }'`
      };
      const code = rawCodes[window.activeHeroTab || 'openai'];
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(() => {
          const label = document.getElementById('hero-copy-label');
          if (label) {
            const orig = label.textContent;
            label.textContent = 'Copied!';
            setTimeout(() => { label.textContent = orig; }, 2000);
          }
          showToast('Code copied to clipboard');
        }).catch(() => copyText(code));
      } else {
        copyText(code);
      }
    }

    function renderLandingPage() {
      const isId = state.lang === 'id';
      const faqs = [
        { q: t('landing.faq1Q'), a: t('landing.faq1A') },
        { q: t('landing.faq2Q'), a: t('landing.faq2A') },
        { q: t('landing.faq3Q'), a: t('landing.faq3A') },
        { q: t('landing.faq4Q'), a: t('landing.faq4A') },
        { q: t('landing.faq5Q'), a: t('landing.faq5A') }
      ];

      const agents = [
        { name: 'Claude Code', sub: 'Anthropic CLI', icon: 'claude-code' },
        { name: 'Codex', sub: 'OpenAI agent', icon: 'codex' },
        { name: 'Cursor', sub: 'AI code editor', icon: 'cursor' },
        { name: 'OpenCode', sub: 'Open source agent', icon: 'opencode' },
        { name: 'Droid Factory', sub: 'Coding agent', icon: 'droid-factory' },
        { name: 'Hermes', sub: 'Autonomous agent', icon: 'hermes-agent' }
      ];

      return `
        ${renderPublicNavbar()}
        <main class="flex-1">
          <!-- Hero Section -->
          <section class="max-w-5xl mx-auto px-6 pt-20 pb-20 text-center">
            <h1 class="hero-headline font-serif font-medium text-main mx-auto">
              ${t('landing.heroTitle1')}
              <span class="block text-[#c87a51] mt-1">${t('landing.heroTitle2')}</span>
            </h1>
            <p class="hero-subtitle font-serif text-sec mx-auto mt-6">
              ${t('landing.heroSubtitle')}
            </p>

            <div class="mt-9 flex flex-wrap items-center justify-center gap-3.5">
              <button onclick="navigate('/register')" class="bg-[#f5efe6] hover:bg-white text-[#14110f] font-semibold text-xs sm:text-sm px-6 py-3.5 rounded-full flex items-center gap-2 shadow-lg transition">
                <span>${t('landing.getApiKey')}</span>
                <span>→</span>
              </button>
              <button onclick="navigate('/models')" class="surface-card hover:border-white/25 text-main font-medium text-xs sm:text-sm px-6 py-3.5 rounded-full flex items-center gap-2.5 transition group">
                <span class="hero-model-icon-carousel text-[#c87a51] shrink-0" aria-hidden="true">
                  <span class="hero-model-icon-track">
                    <span class="hero-model-icon-item">${assetLogo('deepseek', 'w-4 h-4')}</span>
                    <span class="hero-model-icon-item">${assetLogo('openai', 'w-4 h-4')}</span>
                    <span class="hero-model-icon-item">${assetLogo('anthropic', 'w-4 h-4')}</span>
                    <span class="hero-model-icon-item">${assetLogo('gemini', 'w-4 h-4')}</span>
                    <span class="hero-model-icon-item">${assetLogo('moonshot', 'w-4 h-4')}</span>
                    <span class="hero-model-icon-item">${assetLogo('zai', 'w-4 h-4')}</span>
                    <span class="hero-model-icon-item">${assetLogo('minimax', 'w-4 h-4')}</span>
                    <span class="hero-model-icon-item">${assetLogo('qwen', 'w-4 h-4')}</span>
                    <span class="hero-model-icon-item">${assetLogo('deepseek', 'w-4 h-4')}</span>
                  </span>
                </span>
                <span>${t('landing.browseModels')}</span>
              </button>
            </div>

            <div class="mt-10 flex flex-wrap items-center justify-center gap-6 text-xs text-sec">
              <span class="flex items-center gap-1.5"><span class="text-[#c87a51]">✓</span> ${t('landing.featOpenAI')}</span>
              <span class="flex items-center gap-1.5"><span class="text-[#c87a51]">✓</span> ${t('landing.featGateway')}</span>
              <span class="flex items-center gap-1.5"><span class="text-[#c87a51]">✓</span> ${t('landing.featConfig')}</span>
            </div>

            <!-- Terminal Box matching video reference -->
            <div class="mt-12 sm:mt-16 max-w-3xl w-full mx-auto text-left">
              <div class="surface-card-solid rounded-2xl border border-white/10 shadow-2xl overflow-hidden backdrop-blur-xl">
                <!-- Terminal Header Bar -->
                <div class="px-4 py-3 bg-[#161310] border-b border-white/10 flex items-center justify-between gap-3">
                  <div class="flex items-center gap-2">
                    <span class="w-3 h-3 rounded-full bg-[#ff5f56] inline-block shadow-sm"></span>
                    <span class="w-3 h-3 rounded-full bg-[#ffbd2e] inline-block shadow-sm"></span>
                    <span class="w-3 h-3 rounded-full bg-[#27c93f] inline-block shadow-sm"></span>
                    <span class="ml-2 font-mono text-xs text-sec font-medium">${t('landing.terminalTitle')}</span>
                  </div>
                  <!-- SDK Tabs -->
                  <div class="flex items-center bg-black/40 p-1 rounded-xl border border-white/5 text-xs font-mono">
                    <button id="hero-tab-openai" onclick="switchHeroCodeTab('openai')" class="px-3 py-1 rounded-lg text-white font-medium bg-white/10 transition shadow-sm">OpenAI SDK</button>
                    <button id="hero-tab-anthropic" onclick="switchHeroCodeTab('anthropic')" class="px-3 py-1 rounded-lg text-sec hover:text-white transition">Anthropic SDK</button>
                    <button id="hero-tab-curl" onclick="switchHeroCodeTab('curl')" class="px-3 py-1 rounded-lg text-sec hover:text-white transition">cURL</button>
                  </div>
                </div>

                <!-- Terminal Code Area -->
                <div class="relative bg-[#0c0a08]/95 p-5 sm:p-6 overflow-x-auto select-text">
                  <button onclick="copyHeroCodeSnippet(this)" class="absolute top-4 right-4 z-10 surface-card hover:border-white/20 text-sec hover:text-white px-3 py-1.5 rounded-lg text-xs font-mono flex items-center gap-1.5 border border-white/10 transition bg-black/50 backdrop-blur-sm">
                    ${ico('copy', 'w-3.5 h-3.5')}
                    <span id="hero-copy-label">${t('common.copy')}</span>
                  </button>

                  <pre id="hero-code-content" class="font-mono text-xs sm:text-[13px] leading-relaxed text-[#f5efe6] m-0 pr-16">${getHeroCodeSnippetHtml('openai')}</pre>
                </div>
              </div>
            </div>
          </section>

          <!-- Providers & Tools Bar -->
          <section class="border-y border-subtle py-10 px-6">
            <div class="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div class="lg:col-span-3 text-xs text-sec font-medium">
                ${t('landing.providersTitle')}
              </div>
              <div class="lg:col-span-9 space-y-6 min-w-0">
                ${renderMarqueeRow('PROVIDERS', [
                  `<span title="Anthropic">${assetLogo('anthropic', 'w-6 h-6')}</span>`,
                  `<span title="Moonshot">${assetLogo('moonshot', 'w-6 h-6')}</span>`,
                  `<span title="Google Gemini">${assetLogo('gemini', 'w-6 h-6')}</span>`,
                  `<span title="DeepSeek">${assetLogo('deepseek', 'w-6 h-6')}</span>`,
                  `<span title="Meta">${getProviderIcon('Meta', 'w-6 h-6')}</span>`,
                  `<span title="OpenAI">${assetLogo('openai', 'w-6 h-6')}</span>`,
                  `<span title="Xiaomi MiMo">${assetLogo('xiaomi-mimo-wordmark', 'h-7 w-auto')}</span>`,
                  `<span title="Z.ai">${assetLogo('zai', 'w-6 h-6')}</span>`
                ], { duration: 30 })}
                ${renderMarqueeRow('TOOLS', [
                  `<span title="ClackyAI">${assetLogo('clackyai', 'h-10 w-auto')}</span>`,
                  `<span title="Cline">${assetLogo('cline', 'h-10 w-auto')}</span>`,
                  `<span title="Codex" class="flex items-center gap-2 text-sm font-medium">${assetLogo('codex', 'w-6 h-6')}<span>Codex</span></span>`,
                  `<span title="Grok">${assetLogo('gork', 'h-10 w-auto')}</span>`,
                  `<span title="mastra">${assetLogo('mastra', 'h-10 w-auto')}</span>`,
                  `<span title="Obsidian">${assetLogo('obsidian', 'h-10 w-auto')}</span>`
                ], { duration: 34, reverse: true, gap: 'gap-2', pad: 'pr-2' })}
              </div>
            </div>
          </section>

          <!-- One Gateway for Every Agent -->
          <section class="max-w-6xl mx-auto px-6 py-24 space-y-12">
            <div class="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
              <div class="md:col-span-7">
                <h2 class="font-serif font-medium text-h2 text-main">
                  ${t('landing.agentsTitle')}
                </h2>
              </div>
              <div class="md:col-span-5 font-serif text-body text-sec leading-relaxed max-w-[68ch]">
                ${t('landing.agentsSubtitle')}
              </div>
            </div>

            <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border border-subtle rounded-2xl overflow-hidden divide-x divide-y divide-white/5 surface-card">
              ${agents.map(a => `
                <div onclick="navigate('/docs', 'sdks')" class="p-6 flex flex-col items-center text-center justify-between hover:bg-white/[0.03] cursor-pointer transition group">
                  <div class="w-10 h-10 rounded-xl surface-card text-main flex items-center justify-center overflow-hidden group-hover:scale-110 transition">${assetLogo(a.icon, 'w-6 h-6')}</div>
                  <div class="mt-5">
                    <div class="text-xs font-semibold text-main">${a.name}</div>
                    <div class="text-xs text-mut mt-1">${a.sub}</div>
                  </div>
                </div>
              `).join('')}
            </div>
          </section>

          <!-- Up and Running in Minutes -->
          <section class="max-w-6xl mx-auto px-6 py-20 border-t border-subtle">
            <div class="grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div class="lg:col-span-4 space-y-5">
                <h2 class="font-serif font-medium text-h2 text-main">
                  ${t('landing.stepsHeading')}
                </h2>
                <p class="font-serif text-body text-sec max-w-[56ch]">
                  ${t('landing.stepsTagline')}
                </p>
                <button onclick="navigate('/register')" class="bg-[#c87a51] hover:bg-[#df956d] text-[#14110f] font-semibold text-xs px-5 py-3 rounded-xl inline-flex items-center gap-2 transition">
                  <span>${t('landing.signup')}</span>
                  <span>↗</span>
                </button>
              </div>

              <div class="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-8">
                <div class="space-y-4">
                  <div class="font-mono text-xs text-mut">01</div>
                  <div class="w-11 h-11 rounded-full surface-card border border-[#c87a51]/40 text-[#c87a51] flex items-center justify-center">${ico('user')}</div>
                  <h3 class="font-serif font-medium text-h3 text-main">${t('landing.step1Title')}</h3>
                  <p class="font-serif text-body text-sec leading-relaxed max-w-[68ch]">
                    ${t('landing.step1Desc')}
                  </p>
                  <button onclick="navigate('/register')" class="text-xs font-medium text-main underline inline-flex items-center gap-1">
                    <span>${t('landing.signup')}</span> <span>↗</span>
                  </button>
                </div>

                <div class="space-y-4">
                  <div class="font-mono text-xs text-mut">02</div>
                  <div class="w-11 h-11 rounded-full surface-card flex items-center justify-center text-sec">${ico('credit-card')}</div>
                  <h3 class="font-serif font-medium text-h3 text-main">${t('landing.step2Title')}</h3>
                  <p class="font-serif text-body text-sec leading-relaxed max-w-[68ch]">
                    ${t('landing.step2Desc')}
                  </p>
                  <button onclick="navigate('/pricing')" class="text-xs font-medium text-main underline inline-flex items-center gap-1">
                    <span>${t('landing.step2Action')}</span> <span>↗</span>
                  </button>
                </div>

                <div class="space-y-4 relative">
                  <div class="font-mono text-xs text-mut">03</div>
                  <div class="w-11 h-11 rounded-full surface-card flex items-center justify-center text-sec font-mono text-xs">&gt;_</div>
                  <h3 class="font-serif font-medium text-h3 text-main">${t('landing.step3Title')}</h3>
                  <p class="font-serif text-body text-sec leading-relaxed max-w-[68ch]">
                    ${t('landing.step3Desc')}
                  </p>
                  <button onclick="navigate('/docs', 'quickstart')" class="text-xs font-medium text-main underline inline-flex items-center gap-1">
                    <span>${t('landing.step3Action')}</span> <span>${ico('chevron-down')}</span>
                  </button>
                </div>
              </div>
            </div>
          </section>

          <!-- FAQ Section -->
          <section class="max-w-6xl mx-auto px-6 py-20 border-t border-subtle">
            <div class="grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div class="lg:col-span-5 space-y-4">
                <h2 class="font-serif font-medium text-h2 text-main">
                  ${t('landing.faqHeading')}
                </h2>
                <p class="font-serif text-body text-sec leading-relaxed max-w-[56ch]">
                  ${t('landing.faqTagline')}
                </p>
                <button onclick="navigate('/docs', 'introduction')" class="text-xs font-medium text-main underline inline-flex items-center gap-1 pt-1">
                  <span>${t('landing.faqDocs')}</span>
                  <span>↗</span>
                </button>
              </div>

              <div class="lg:col-span-7 divide-y divide-white/10 border-t border-b border-subtle">
                ${faqs.map((item, idx) => {
                  const open = state.openFaqIndex === idx;
                  return `
                    <div class="py-5">
                      <button onclick="state.openFaqIndex = state.openFaqIndex === ${idx} ? -1 : ${idx}; render();" class="w-full flex items-center justify-between text-left text-sm font-medium text-main">
                        <span>${item.q}</span>
                        <span class="text-sec font-mono text-base ml-4">${open ? '×' : '+'}</span>
                      </button>
                      ${open ? `<p class="mt-3 font-serif text-body text-sec leading-relaxed max-w-[68ch]">${item.a}</p>` : ''}
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
          </section>
        </main>
        ${renderPublicFooter()}
      `;
    }

    // ============================================================================
    // PUBLIC PAGE 2: PRICING (/pricing)
    // ============================================================================
    function renderPricingPage() {
      const comparePlans = {
        pro: {
          name: 'PRO', badge: ico('flame') + ' Most popular', price: '$12', period: '/21 days', inc: '$48 in usage included',
          rpm: '150 requests per minute', conc: '10 requests at once', web: '600 web searches and 600 page reads free',
          bullets: ['For Everyday Development', '$96 on Muse Wonder', '$144 on DeepSeek v4.1 Flash'], modelsCount: 'Tokens on its 21 models'
        },
        max: {
          name: 'MAX', badge: ico('crown') + ' Best value', price: '$20', period: '/month', inc: '$75 in usage included',
          rpm: '250 requests per minute', conc: '20 requests at once', web: '1,000 web searches and 1,000 page reads free',
          bullets: ['Built for Intensive Workloads', '$150 on Muse Wonder', '$225 on DeepSeek v4.1 Flash'], modelsCount: 'Tokens on its 27 models'
        },
        max5x: {
          name: 'MAX 5X', badge: ico('rocket') + ' 5x Power', price: '$100', period: '/month', inc: '$375 in usage included',
          rpm: '1,250 requests per minute', conc: '50 requests at once', web: '5,000 web searches and 5,000 page reads free',
          bullets: ['For Teams & Production', '$750 on Muse Wonder', '$1,125 on DeepSeek v4.1 Flash'], modelsCount: 'Tokens on its 27 models'
        },
        max10x: {
          name: 'MAX 10X', badge: ico('gem') + ' Limited', price: '$200', period: '/month', inc: '$750 in usage included',
          rpm: '2,500 requests per minute', conc: '100 requests at once', web: '10,000 web searches and 10,000 page reads free',
          bullets: ['10x MAX Capacity', '$1,500 on Muse Wonder', '$2,250 on DeepSeek v4.1 Flash'], modelsCount: 'Tokens on its 27 models'
        }
      };
      const rightCard = comparePlans[state.pricingCompareTab] || comparePlans.pro;

      const calcBudgets = {
        payg: state.pricingTopupAmount,
        go: 10,
        pro: 48,
        max: 75,
        max5x: 375,
        max10x: 750
      };
      const activeBudget = calcBudgets[state.pricingCalcMode] || 10;

      const calcCaptions = {
        payg: '',
        go: '<b>Go</b> costs $2/14 days and includes <b>$10 of usage</b>.',
        pro: '<b>Pro</b> costs $12/21 days and includes <b>$48 of usage</b>.',
        max: '<b>Max</b> costs $20/month and includes <b>$75 of usage</b>.',
        max5x: '<b>Max 5×</b> costs $100/month and includes <b>$375 of usage</b>.',
        max10x: '<b>Max 10×</b> costs $200/month and includes <b>$750 of usage</b>.'
      };

      const activeCatalog = MODELS_DATA.filter(m => m.active !== false);
      const top3Models = activeCatalog.length > 0 ? activeCatalog.slice(0, 3) : MODELS_DATA.slice(0, 3);

      function formatTokenEst(budget, blended) {
        const raw = budget / blended;
        if (raw >= 1000) return (raw / 1000).toFixed(2) + 'B';
        return Math.round(raw) + 'M';
      }

      return `
        ${renderPublicNavbar()}
        <main class="flex-1 max-w-6xl w-full mx-auto px-6 pt-16 space-y-20">
          <!-- Header -->
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div class="lg:col-span-7">
              <h1 class="font-serif font-medium text-h1 text-main">${t('pricing.title')}</h1>
            </div>
            <div class="lg:col-span-5 font-serif text-body text-sec leading-relaxed max-w-[68ch]">
              ${t('pricing.subtitle')}
            </div>
          </div>

          <!-- Ways to Pay -->
          <section class="space-y-6">
            <div class="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 class="font-serif font-medium text-h2 text-main">${t('pricing.waysToPay')}</h2>
                <p class="font-serif text-body text-sec mt-1 max-w-[68ch]">${t('pricing.waysToPayDesc')}</p>
              </div>
              <div class="surface-card rounded-full p-1 flex items-center gap-1 text-xs">
                ${[
                  { id: 'pro', label: 'Pro $12' },
                  { id: 'max', label: 'Max $20' },
                  { id: 'max5x', label: 'Max 5× $100' },
                  { id: 'max10x', label: 'Max 10× $200' }
                ].map(t => `
                  <button onclick="state.pricingCompareTab='${t.id}'; render();" class="px-3.5 py-1.5 rounded-full transition ${state.pricingCompareTab === t.id ? 'bg-[#c87a51] text-[#14110f] font-semibold' : 'text-sec hover:text-main'}">${t.label}</button>
                `).join('')}
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
              <!-- Card 1: Per token -->
              <div class="surface-card rounded-2xl p-6 flex flex-col justify-between">
                <div>
                  <div class="text-xs font-mono uppercase text-sec">${t('pricing.paygBadge')}</div>
                  <div class="font-serif font-medium text-h2 text-main mt-2">${t('pricing.perToken')}</div>
                  <p class="font-serif text-body text-sec mt-2 leading-relaxed">${t('pricing.paygDesc')}</p>
                  <button onclick="navigate('/register')" class="mt-5 w-full surface-card-solid hover:border-white/25 text-main text-xs font-medium py-3 px-4 rounded-xl flex items-center justify-between transition">
                    <span>${t('pricing.createAccount')}</span>
                    <span>→</span>
                  </button>
                  <div class="mt-6 pt-5 border-t border-subtle">
                    <div class="text-xs font-mono uppercase text-mut mb-3">${t('pricing.whatsIncluded')}</div>
                    <ul class="space-y-2.5 text-xs text-sec">
                      <li><span class="text-[#c87a51] mr-1.5">✓</span> ${t('pricing.paygBullet1')}</li>
                      <li><span class="text-[#c87a51] mr-1.5">✓</span> ${t('pricing.paygBullet2')}</li>
                      <li><span class="text-[#c87a51] mr-1.5">✓</span> ${t('pricing.paygBullet3')}</li>
                      <li><span class="text-[#c87a51] mr-1.5">✓</span> ${t('pricing.paygBullet4')}</li>
                    </ul>
                  </div>
                </div>
                <button onclick="navigate('/models')" class="mt-8 w-full surface-card py-2.5 rounded-xl text-xs text-sec hover:text-main">Tokens on all 28 models</button>
              </div>

              <!-- Card 2: GO $2 -->
              <div class="surface-card rounded-2xl p-6 flex flex-col justify-between">
                <div>
                  <div class="flex items-center justify-between">
                    <span class="text-xs font-bold text-main">GO</span>
                    <span class="text-xs px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">${ico('zap')} Cheapest</span>
                  </div>
                  <div class="mt-2 flex items-baseline gap-1">
                    <span class="font-serif font-medium text-h2 text-main">$2</span>
                    <span class="text-xs text-sec">/14 days</span>
                  </div>
                  <div class="text-xs text-[#c87a51] font-medium mt-1">$10 in usage included</div>
                  <button onclick="navigate('/plan')" class="mt-5 w-full surface-card-solid hover:border-white/25 text-main text-xs font-medium py-3 px-4 rounded-xl flex items-center justify-between transition">
                    <span>Subscribe</span>
                    <span>→</span>
                  </button>
                  <div class="mt-6 pt-5 border-t border-subtle">
                    <div class="text-xs font-mono uppercase text-mut mb-3">WHAT'S INCLUDED</div>
                    <ul class="space-y-2.5 text-xs text-sec">
                      <li>${ico('trending-up')} 50 requests per minute</li>
                      <li>${ico('zap')} 3 requests at once</li>
                      <li>${ico('globe')} 150 web searches and 150 page reads free</li>
                      <li><span class="text-[#c87a51] mr-1">✓</span> For Personal Projects</li>
                      <li><span class="text-[#c87a51] mr-1">✓</span> Explore & Build with AI</li>
                      <li><span class="text-[#c87a51] mr-1">✓</span> $30 on DeepSeek v4.1 Flash</li>
                    </ul>
                  </div>
                </div>
                <button onclick="openPlanTokensModal('Go', 13)" class="mt-8 w-full surface-card py-2.5 rounded-xl text-xs text-sec hover:text-main">Tokens on its 13 models</button>
              </div>

              <!-- Card 3: Dynamic Pro / Max -->
              <div class="surface-card rounded-2xl p-6 flex flex-col justify-between ring-1 ring-[#c87a51]/60">
                <div>
                  <div class="flex items-center justify-between">
                    <span class="text-xs font-bold text-main">${rightCard.name}</span>
                    <span class="text-xs px-2.5 py-0.5 rounded-full bg-[#c87a51]/20 text-[#df956d]">${rightCard.badge}</span>
                  </div>
                  <div class="mt-2 flex items-baseline gap-1">
                    <span class="font-serif font-medium text-h2 text-main">${rightCard.price}</span>
                    <span class="text-xs text-sec">${rightCard.period}</span>
                  </div>
                  <div class="text-xs text-[#c87a51] font-medium mt-1">${rightCard.inc}</div>
                  <button onclick="navigate('/plan')" class="mt-5 w-full bg-[#c87a51] hover:bg-[#df956d] text-[#14110f] text-xs font-semibold py-3 px-4 rounded-xl flex items-center justify-between transition">
                    <span>Subscribe</span>
                    <span>→</span>
                  </button>
                  <div class="mt-6 pt-5 border-t border-subtle">
                    <div class="text-xs font-mono uppercase text-mut mb-3">WHAT'S INCLUDED</div>
                    <ul class="space-y-2.5 text-xs text-sec">
                      <li>${ico('trending-up')} ${rightCard.rpm}</li>
                      <li>${ico('zap')} ${rightCard.conc}</li>
                      <li>${ico('globe')} ${rightCard.web}</li>
                      ${rightCard.bullets.map(b => `<li><span class="text-[#c87a51] mr-1">✓</span> ${b}</li>`).join('')}
                    </ul>
                  </div>
                </div>
                <button onclick="openPlanTokensModal('${rightCard.name}', 21)" class="mt-8 w-full surface-card py-2.5 rounded-xl text-xs text-sec hover:text-main">${rightCard.modelsCount}</button>
              </div>
            </div>
          </section>

          <!-- Interactive Calculator: What it buys, per model -->
          <section class="space-y-6 pt-8 border-t border-subtle">
            <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
              <div class="lg:col-span-7">
                <h2 class="font-serif font-medium text-h2 text-main">What it buys, per model</h2>
                <p class="font-serif text-body text-sec mt-1 max-w-[68ch]">Pick how you pay. Each model shows roughly how many tokens that amount covers.</p>
              </div>
              <div class="lg:col-span-5 font-serif text-body text-sec max-w-[68ch]">
                Estimated at <b class="text-main font-sans">95% cache hits, 3% new input, 2% output</b>, typical of a coding agent resending a cached conversation. Your numbers will differ with your traffic. <button onclick="navigate('/docs', 'plans')" class="text-main underline font-sans">How it is worked out</button>
              </div>
            </div>

            <!-- Plan Pills -->
            <div class="surface-card rounded-2xl p-1.5 inline-flex flex-wrap items-center gap-1 text-xs">
              ${[
                { id: 'payg', label: 'Pay as you go' },
                { id: 'go', label: 'Go $2' },
                { id: 'pro', label: 'Pro $12' },
                { id: 'max', label: 'Max $20' },
                { id: 'max5x', label: 'Max 5× $100' },
                { id: 'max10x', label: 'Max 10× $200' }
              ].map(p => `
                <button onclick="state.pricingCalcMode='${p.id}'; render();" class="px-4 py-2 rounded-xl transition ${state.pricingCalcMode === p.id ? 'bg-[#c87a51]/25 text-[#df956d] border border-[#c87a51]/50 font-semibold' : 'text-sec hover:text-main'}">${p.label}</button>
              `).join('')}
            </div>

            ${state.pricingCalcMode === 'payg' ? `
              <div class="flex flex-wrap items-center gap-3 text-xs">
                <span class="text-sec">Top-up amount</span>
                <div class="surface-card-solid rounded-xl px-3 py-1.5 flex items-center gap-2 border border-subtle">
                  <span class="text-sec">$</span>
                  <input type="number" value="${state.pricingTopupAmount}" oninput="state.pricingTopupAmount = Number(this.value) || 1; render();" class="bg-transparent font-mono text-main w-16 focus:outline-none" />
                  <span class="text-mut text-xs">USD</span>
                </div>
                <div class="flex items-center gap-1.5">
                  ${[5, 10, 25, 50, 100].map(amt => `
                    <button onclick="state.pricingTopupAmount=${amt}; render();" class="px-3 py-1.5 rounded-lg border font-mono text-xs transition ${state.pricingTopupAmount === amt ? 'border-[#c87a51] bg-[#c87a51]/20 text-[#df956d]' : 'border-subtle surface-card text-sec hover:text-main'}">$${amt}</button>
                  `).join('')}
                </div>
              </div>
            ` : `
              <div class="text-xs text-sec">${calcCaptions[state.pricingCalcMode]}</div>
            `}

            <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
              ${top3Models.map(m => `
                <div class="surface-card rounded-2xl p-5 space-y-4">
                  <div class="flex items-start justify-between">
                    <div>
                      <div class="flex items-center gap-2">
                        <span class="font-semibold text-sm text-main">${m.name}</span>
                        ${m.free ? `<span class="text-xs px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400">Free</span>` : ''}
                        <span class="text-xs px-2 py-0.5 rounded bg-[#c87a51]/20 text-[#df956d]">${m.discount}% off</span>
                      </div>
                      <div class="font-mono text-xs text-mut mt-0.5">${m.id}</div>
                    </div>
                    <span class="font-mono text-xs px-2 py-0.5 rounded surface-card text-sec">${m.context} context</span>
                  </div>

                  <div>
                    <div class="font-sans font-bold text-4xl text-main">≈ ${formatTokenEst(activeBudget, m.blended)} <span class="text-xs font-sans text-sec">tokens</span></div>
                    <div class="font-mono text-xs text-sec mt-1">$${(m.blended * 1000).toFixed(4)} per 1M, blended</div>
                  </div>

                  <div class="pt-3 border-t border-subtle grid grid-cols-3 gap-2 font-mono text-xs">
                    <div><div class="text-mut font-sans text-xs">Input / 1M</div><div class="text-main mt-0.5">$${m.input.toFixed(2)}</div></div>
                    <div><div class="text-mut font-sans text-xs">Cached / 1M</div><div class="text-main mt-0.5">$${m.cacheRead}</div></div>
                    <div><div class="text-mut font-sans text-xs">Output / 1M</div><div class="text-main mt-0.5">$${m.output.toFixed(2)}</div></div>
                  </div>
                </div>
              `).join('')}
            </div>
          </section>

          <!-- Bottom CTA -->
          <section class="py-12 border-t border-subtle flex flex-wrap items-center justify-between gap-6">
            <div>
              <h2 class="font-serif font-medium text-h2 text-main">No monthly minimum, no lock-in</h2>
              <p class="font-serif text-body text-sec mt-1 max-w-[68ch]">Top up any amount and pay per token at each model's listed rate. Credit stays on your account until you use it.</p>
            </div>
            <button onclick="navigate('/register')" class="bg-[#c87a51] hover:bg-[#df956d] text-[#14110f] font-semibold text-xs px-5 py-3 rounded-xl">Create an account</button>
          </section>
        </main>
        ${renderPublicFooter()}
      `;
    }

    // ============================================================================
    // PUBLIC PAGE 3: CHANGELOG (/changelog)
    // ============================================================================
    function renderChangelogPage() {
      const entries = [
        { date: 'Oct 25\n2026', title: t('changelog.entry1Title'), desc: t('changelog.entry1Desc') },
        { date: 'Oct 25\n2026', title: t('changelog.entry2Title'), desc: t('changelog.entry2Desc') },
        { date: 'Oct 19\n2026', title: t('changelog.entry3Title'), desc: t('changelog.entry3Desc') },
        { date: 'Oct 14\n2026', title: t('changelog.entry4Title'), desc: t('changelog.entry4Desc') }
      ];
      return `
        ${renderPublicNavbar()}
        <main class="flex-1 max-w-4xl w-full mx-auto px-6 pt-16 space-y-12">
          <div class="text-center space-y-3">
            <span class="text-xs font-mono px-3 py-1 rounded-full surface-card text-sec">${t('changelog.badge')}</span>
            <h1 class="font-serif font-medium text-h1 text-main">${t('changelog.title')}</h1>
            <p class="font-serif text-body text-sec max-w-[68ch] mx-auto">${t('changelog.subtitle')}</p>
          </div>

          <div class="space-y-8 pt-6">
            ${entries.map(e => `
              <div class="grid grid-cols-12 gap-4 items-start">
                <div class="col-span-3 sm:col-span-2 font-mono text-xs text-sec whitespace-pre-line">${e.date}</div>
                <div class="col-span-9 sm:col-span-10 border-l border-subtle pl-6 pb-6 space-y-1.5 relative">
                  <span class="w-2.5 h-2.5 rounded-full bg-[#c87a51] absolute -left-[5px] top-1.5"></span>
                  <div class="flex items-center gap-2">
                    <span class="text-base font-semibold text-main">${e.title}</span>
                    <span class="text-xs px-2 py-0.5 rounded bg-[#c87a51]/20 text-[#df956d]">${t('changelog.tagModel')}</span>
                  </div>
                  <p class="font-serif text-body text-sec leading-relaxed max-w-[68ch]">${e.desc}</p>
                </div>
              </div>
            `).join('')}
          </div>
        </main>
        ${renderPublicFooter()}
      `;
    }

    // ============================================================================
    // PUBLIC PAGE 4: PRIVACY POLICY (/privacy)
    // ============================================================================
    // ============================================================================
    // DYNAMIC PUBLIC PAGES: PRIVACY POLICY & TERMS OF SERVICE
    // ============================================================================
    function renderPrivacyPage() {
      const p = state.privacyData;
      return `
        ${renderPublicNavbar()}
        <main class="flex-1 max-w-3xl w-full mx-auto px-6 pt-16 space-y-8 font-serif text-body text-sec leading-relaxed">
          <div class="text-center space-y-3 pb-6 border-b border-subtle">
            <div class="flex items-center justify-center gap-2">
              <span class="text-xs font-mono px-3 py-1 rounded-full surface-card text-sec">Legal · ${p.version}</span>
              ${p.zeroDataRetention ? `<span class="text-xs font-mono px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">ZDR Active</span>` : ''}
            </div>
            <h1 class="font-serif font-medium text-h1 text-main">${p.title}</h1>
            <p class="max-w-[68ch] mx-auto">We never store your prompts or outputs. This page explains exactly what we do record, and why.</p>
          </div>

          <div class="text-xs text-mut flex items-center justify-between font-sans">
            <span>Last updated: ${p.lastUpdated}</span>
            <button onclick="navigate('/management'); setManagementTab('privacy');" class="text-xs text-[#df956d] hover:underline flex items-center gap-1 font-mono">${ico('sliders', 'w-3 h-3')} Manage in Dev Console</button>
          </div>

          ${p.summaryHtml || ''}

          <div class="space-y-6">
            ${p.sections.map(s => `
              <div>
                <h3 class="font-serif font-medium text-h3 text-main mb-2">${s.title}</h3>
                <p class="max-w-[68ch]">${s.body}</p>
              </div>
            `).join('')}
          </div>
        </main>
        ${renderPublicFooter()}
      `;
    }

    function renderTermsPage() {
      const t = state.termsData;
      return `
        ${renderPublicNavbar()}
        <main class="flex-1 max-w-3xl w-full mx-auto px-6 pt-16 space-y-8 font-serif text-body text-sec leading-relaxed">
          <div class="text-center space-y-3 pb-6 border-b border-subtle">
            <span class="text-xs font-mono px-3 py-1 rounded-full surface-card text-sec">Legal · ${t.version}</span>
            <h1 class="font-serif font-medium text-h1 text-main">${t.title}</h1>
            <p class="max-w-[68ch] mx-auto">The rules and conditions for using OpenCSN.</p>
          </div>
          <div class="text-xs text-mut flex items-center justify-between font-sans">
            <span>Last updated: ${t.lastUpdated}</span>
            <button onclick="navigate('/management'); setManagementTab('terms');" class="text-xs text-[#df956d] hover:underline flex items-center gap-1 font-mono">${ico('sliders', 'w-3 h-3')} Manage in Dev Console</button>
          </div>
          <div class="space-y-6">
            ${t.sections.map(s => `
              <div>
                <h3 class="font-serif font-medium text-h3 text-main mb-1.5">${s.title}</h3>
                <p class="max-w-[68ch]">${s.body}</p>
              </div>
            `).join('')}
          </div>
        </main>
        ${renderPublicFooter()}
      `;
    }

    // ============================================================================
    // PUBLIC PAGE 6: MODELS DIRECTORY (/models) - INTERACTIVE CARDS & DETAIL VIEW
    // ============================================================================
    function renderModelsPage() {
      const isId = state.lang === 'id';
      const liveProviders = Array.from(new Set(MODELS_DATA.map(m => m.provider))).filter(Boolean).sort();
      const allProviders = liveProviders.length > 0 ? liveProviders : ['Anthropic', 'DeepSeek', 'Google', 'Meta', 'Moonshot', 'OpenAI', 'StepFun', 'Xiaomi', 'Z.ai'];

      // Active only for public view (nonactive hidden from public access)
      let filtered = MODELS_DATA.filter(m => {
        if (m.active === false) return false; // HIDE FROM PUBLIC ACCESS IF NONACTIVE
        if (state.modelsSearch) {
          const q = state.modelsSearch.toLowerCase();
          if (!m.name.toLowerCase().includes(q) && !m.id.toLowerCase().includes(q) && !m.provider.toLowerCase().includes(q)) return false;
        }
        if (state.modelsProviders && state.modelsProviders.length > 0) {
          if (state.modelsProviders.includes('__none__')) return false;
          if (!state.modelsProviders.includes(m.provider)) return false;
        }
        if (state.modelsPriceRange === 'free' && !m.free) return false;
        if (state.modelsPriceRange === 'under1' && m.input >= 1) return false;
        if (state.modelsPriceRange === '1to5' && (m.input < 1 || m.input > 5)) return false;
        if (state.modelsPriceRange === '5to15' && (m.input <= 5 || m.input > 15)) return false;
        if (state.modelsPriceRange === 'over15' && m.input <= 15) return false;
        if (state.modelsPricingType === 'free' && !m.free) return false;
        if (state.modelsPricingType === 'discounted' && !m.discount) return false;
        return true;
      });

      // Sorting
      const sortMode = state.modelsSort || 'provider';
      filtered = [...filtered].sort((a, b) => {
        if (sortMode === 'price-asc') return a.input - b.input;
        if (sortMode === 'price-desc') return b.input - a.input;
        if (sortMode === 'name') return a.name.localeCompare(b.name);
        if (sortMode === 'context') {
          const parseCtx = c => parseInt(c) * (c.includes('M') ? 1000 : 1);
          return parseCtx(b.context) - parseCtx(a.context);
        }
        // Default Provider A-Z
        return a.provider.localeCompare(b.provider) || a.name.localeCompare(b.name);
      });

      const activeCount = MODELS_DATA.filter(m => m.active !== false).length;
      const providersCount = new Set(MODELS_DATA.map(m => m.provider)).size;
      const freeCount = MODELS_DATA.filter(m => m.free).length;

      // Filter providers shown in sidebar based on provider search query
      const pQuery = (state.providerSearchQuery || '').toLowerCase().trim();
      const visibleProviders = allProviders.filter(p => !pQuery || p.toLowerCase().includes(pQuery));

      return `
        ${renderPublicNavbar()}
        <main class="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 pt-12 pb-20 space-y-10">
          <!-- Hero Header matching reference video -->
          <div class="text-center space-y-4">
            <h1 class="font-serif font-medium text-h1 text-main max-w-3xl mx-auto">
              ${isId ? 'Lebih banyak model. Harga lebih hemat. Ada yang gratis.' : 'More models. Lower prices. Free options.'}
            </h1>
            <p class="font-serif text-body text-sec max-w-xl mx-auto">
              ${isId ? 'Bandingkan model, harga, kemampuan, dan opsi gratis dalam sekali lihat.' : 'Compare models, prices, capabilities, and free options at a glance.'}
            </p>
            <div class="pt-2 flex items-center justify-center gap-10 sm:gap-14">
              <div>
                <div class="font-sans font-bold text-3xl sm:text-4xl text-main font-semibold">${activeCount}</div>
                <div class="font-mono text-xs uppercase text-mut mt-0.5 tracking-wider font-semibold">MODELS</div>
              </div>
              <div>
                <div class="font-sans font-bold text-3xl sm:text-4xl text-main font-semibold">${providersCount}</div>
                <div class="font-mono text-xs uppercase text-mut mt-0.5 tracking-wider font-semibold">${t('models.statProviders')}</div>
              </div>
              <div>
                <div class="font-sans font-bold text-3xl sm:text-4xl text-main font-semibold">${freeCount}</div>
                <div class="font-mono text-xs uppercase text-mut mt-0.5 tracking-wider font-semibold">${t('models.statFree')}</div>
              </div>
            </div>
            <div class="realtime-catalog-indicator flex justify-center pt-1">
              ${renderRealtimeIndicator()}
            </div>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <!-- Left Sidebar Filter (Adaptive Light/Dark Theme matching video) -->
            <aside class="lg:col-span-3 surface-card rounded-3xl p-5 border border-subtle sticky top-20 max-h-[calc(100vh-100px)] overflow-y-auto custom-scrollbar space-y-6 text-xs backdrop-blur-md shadow-lg">
              <!-- Sidebar Header -->
              <div class="flex items-center justify-between pb-1">
                <span class="font-semibold text-sm text-main tracking-tight">${t('models.filters')}</span>
                <button onclick="resetPublicModelsFilters()" class="surface-card-solid hover:border-strong text-sec hover:text-main px-3 py-1 rounded-full text-xs transition shadow-sm">${t('models.reset')}</button>
              </div>

              <!-- Section: PROVIDERS -->
              <div class="space-y-3">
                <div class="font-mono text-xs uppercase text-mut font-semibold tracking-wider">${t('models.providers')}</div>
                <div class="flex items-center gap-2">
                  <button onclick="state.modelsProviders=[]; render();" class="surface-card-solid hover:border-strong text-sec hover:text-main px-3 py-1 rounded-full text-xs transition shadow-sm">${t('models.all')}</button>
                  <span class="text-mut">·</span>
                  <button onclick="state.modelsProviders=['__none__']; render();" class="surface-card-solid hover:border-strong text-sec hover:text-main px-3 py-1 rounded-full text-xs transition shadow-sm">${t('models.none')}</button>
                </div>

                <!-- Search Providers Input -->
                <div class="flex items-center gap-2 surface-card-solid border border-subtle rounded-xl px-3 py-2 text-xs">
                  <span class="text-mut">${ico('search', 'w-3.5 h-3.5')}</span>
                  <input type="text" placeholder="${t('models.searchProviders')}" value="${state.providerSearchQuery || ''}" oninput="state.providerSearchQuery=this.value; render();" class="bg-transparent text-main w-full focus:outline-none placeholder:text-mut text-xs" />
                </div>

                <!-- Providers List with Selection Circles & Logos -->
                <div class="space-y-1 pt-1 max-h-56 overflow-y-auto custom-scrollbar pr-1">
                  ${visibleProviders.map(p => {
                    const isSelected = state.modelsProviders && state.modelsProviders.length > 0 && state.modelsProviders.includes(p);
                    return `
                      <button onclick="togglePublicProvider('${p}')" class="w-full flex items-center justify-between px-3.5 py-2 rounded-full transition ${isSelected ? 'bg-[#c87a51] dark:bg-[#df956d] text-white dark:text-[#14110f] font-semibold shadow-md' : 'text-sec hover:text-main hover:bg-pill'}">
                        <div class="flex items-center gap-2.5 min-w-0">
                          <span class="w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${isSelected ? 'bg-white dark:bg-[#14110f] text-[#c87a51] dark:text-[#df956d] text-xs font-bold' : 'border border-subtle'}">
                            ${isSelected ? '✓' : ''}
                          </span>
                          <div class="w-6 h-6 rounded-full surface-card-solid border border-subtle flex items-center justify-center p-1 shrink-0 ${isSelected ? 'bg-white/20 dark:bg-black/40 border-white/20 text-white dark:text-[#14110f]' : 'text-main'}">
                            ${getProviderIcon(p, 'w-3.5 h-3.5')}
                          </div>
                          <span class="truncate text-xs">${p}</span>
                        </div>
                      </button>
                    `;
                  }).join('')}
                </div>
              </div>

              <!-- Section: PRICE RANGE -->
              <div class="space-y-2 border-t border-subtle pt-4">
                <div class="font-mono text-xs uppercase text-mut font-semibold tracking-wider">${t('models.priceRange')}</div>
                <div class="space-y-1">
                  ${[
                    { id: 'all', label: t('models.allPrices') },
                    { id: 'free', label: t('models.freeOnly') },
                    { id: 'under1', label: t('models.under1') },
                    { id: '1to5', label: t('models.1to5') },
                    { id: '5to15', label: t('models.5to15') },
                    { id: 'over15', label: t('models.over15') }
                  ].map(r => `
                    <button onclick="state.modelsPriceRange='${r.id}'; render();" class="w-full text-left px-4 py-2 rounded-full transition text-xs ${state.modelsPriceRange === r.id ? 'bg-[#c87a51] dark:bg-[#df956d] text-white dark:text-[#14110f] font-semibold shadow-md' : 'text-sec hover:text-main hover:bg-pill'}">
                      ${r.label}
                    </button>
                  `).join('')}
                </div>
              </div>

              <!-- Section: PRICING TYPE -->
              <div class="space-y-2 border-t border-subtle pt-4">
                <div class="font-mono text-xs uppercase text-mut font-semibold tracking-wider">${t('models.pricingType')}</div>
                <div class="grid grid-cols-3 gap-1 p-1 rounded-full surface-card-solid border border-subtle text-center">
                  ${[
                    { id: 'all', label: t('models.all') },
                    { id: 'free', label: t('common.free') },
                    { id: 'discounted', label: t('models.discounted') }
                  ].map(item => `
                    <button onclick="state.modelsPricingType='${item.id}'; render();" class="py-1.5 rounded-full transition text-xs ${state.modelsPricingType === item.id ? 'bg-[#c87a51] dark:bg-white text-white dark:text-black font-semibold shadow-sm' : 'text-sec hover:text-main'}">
                      ${item.label}
                    </button>
                  `).join('')}
                </div>
              </div>
            </aside>

            <!-- Right Models Area -->
            <div class="lg:col-span-9 space-y-6">
              <!-- Top Search, Sort, View Toggle, and Reset Bar -->
              <div class="surface-card rounded-2xl p-3 border border-subtle flex flex-wrap items-center justify-between gap-3 text-xs backdrop-blur-md shadow-sm">
                <!-- Search Input -->
                <div class="flex items-center gap-2.5 flex-1 min-w-[200px] px-2">
                  <span class="text-mut">${ico('search', 'w-4 h-4')}</span>
                  <input type="text" value="${state.modelsSearch}" oninput="state.modelsSearch=this.value; render();" placeholder="${t('models.searchPlaceholder')}" class="bg-transparent text-main w-full focus:outline-none placeholder:text-mut text-xs" />
                </div>

                <div class="flex items-center gap-2.5 flex-wrap">
                  <!-- Sort Order Dropdown matching video -->
                  <select onchange="state.modelsSort=this.value; render();" class="surface-card-solid rounded-xl px-3.5 py-2 text-main border border-subtle text-xs focus:outline-none cursor-pointer">
                    <option value="provider" ${state.modelsSort === 'provider' ? 'selected' : ''}>${t('models.sortProvider')}</option>
                    <option value="price-asc" ${state.modelsSort === 'price-asc' ? 'selected' : ''}>${t('models.sortPriceAsc')}</option>
                    <option value="price-desc" ${state.modelsSort === 'price-desc' ? 'selected' : ''}>${t('models.sortPriceDesc')}</option>
                    <option value="name" ${state.modelsSort === 'name' ? 'selected' : ''}>${t('models.sortName')}</option>
                    <option value="context" ${state.modelsSort === 'context' ? 'selected' : ''}>${t('models.sortContext')}</option>
                  </select>

                  <!-- Grid / List View Toggle matching video icons -->
                  <div class="flex items-center surface-card-solid border border-subtle rounded-xl p-1 gap-1">
                    <button onclick="state.modelsView='grid'; render();" class="w-8 h-8 rounded-lg flex items-center justify-center transition ${state.modelsView !== 'list' ? 'bg-[#c87a51] dark:bg-[#df956d] text-white dark:text-[#14110f] font-bold shadow-sm' : 'text-sec hover:text-main'}" title="${t('models.viewGrid')}">
                      ${ico('grid', 'w-4 h-4')}
                    </button>
                    <button onclick="state.modelsView='list'; render();" class="w-8 h-8 rounded-lg flex items-center justify-center transition ${state.modelsView === 'list' ? 'bg-[#c87a51] dark:bg-[#df956d] text-white dark:text-[#14110f] font-bold shadow-sm' : 'text-sec hover:text-main'}" title="${t('models.viewList')}">
                      ${ico('list', 'w-4 h-4')}
                    </button>
                  </div>

                  <!-- Reset Filters Button -->
                  <button onclick="resetPublicModelsFilters()" class="surface-card-solid hover:border-strong px-3.5 py-2 rounded-xl text-sec hover:text-main flex items-center gap-1.5 transition">
                    ${ico('rotate-ccw', 'w-3.5 h-3.5')} <span>${t('models.reset')}</span>
                  </button>

                  <!-- Count Badge matching video (e.g. 6 / 28) -->
                  <div class="px-3.5 py-2 rounded-xl surface-card-solid border border-subtle font-mono text-xs text-sec">
                    ${filtered.length} / ${activeCount}
                  </div>
                </div>
              </div>

              <!-- Empty State -->
              ${filtered.length === 0 ? `
                <div class="surface-card rounded-3xl p-12 text-center space-y-3 border border-subtle shadow-sm">
                  <div class="text-base font-semibold text-main">${t('models.noResults')}</div>
                  <button onclick="resetPublicModelsFilters()" class="surface-card-solid hover:border-[var(--copper)]/50 px-4 py-2 rounded-xl text-xs text-main transition shadow-sm">${t('models.reset')}</button>
                </div>
              ` : state.modelsView === 'list' ? `
                <!-- LIST VIEW MODE -->
                <div class="surface-card rounded-3xl border border-subtle overflow-hidden shadow-xl divide-y divide-subtle">
                  ${filtered.map(m => `
                    <div onclick="navigate('/models/' + '${m.id}')" class="p-4 sm:p-5 flex flex-wrap items-center justify-between gap-4 hover:bg-pill transition-colors cursor-pointer group">
                      <div class="flex items-center gap-3.5 min-w-[240px]">
                        <div class="w-10 h-10 rounded-full surface-card-solid border border-subtle flex items-center justify-center p-2 shrink-0 group-hover:scale-105 transition shadow-sm text-main">
                          ${getProviderIcon(m.provider, 'w-5 h-5')}
                        </div>
                        <div>
                          <div class="flex items-center gap-2 flex-wrap">
                            <span class="font-semibold text-sm sm:text-base text-main group-hover:text-[var(--copper)] transition">${m.name}</span>
                            ${m.free ? `<span class="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-xs font-sans font-medium">${t('common.free')}</span>` : ''}
                          </div>
                          <div class="font-mono text-xs text-mut mt-0.5">${m.id}</div>
                        </div>
                      </div>

                      <div class="flex items-center gap-6">
                        <div class="text-right">
                          <div class="text-xs font-mono uppercase text-mut font-medium">${t('models.thInput')}</div>
                          <div class="font-mono text-sm font-semibold text-main mt-0.5">$${m.input.toFixed(2)}</div>
                        </div>
                        <div class="text-right">
                          <div class="text-xs font-mono uppercase text-mut font-medium">${t('models.thOutput')}</div>
                          <div class="font-mono text-sm font-semibold text-[var(--copper-light)] mt-0.5">$${m.output.toFixed(2)}</div>
                        </div>
                        <div class="text-right hidden sm:block">
                          <div class="text-xs font-mono uppercase text-mut font-medium">${t('models.thContext')}</div>
                          <div class="font-mono text-xs text-sec mt-0.5">${m.context}</div>
                        </div>
                        ${m.discount ? `
                          <span class="px-3 py-1 rounded-full bg-[var(--copper)]/15 border border-[var(--copper)]/30 text-[var(--copper-light)] font-bold text-xs font-mono tracking-tight shrink-0">${m.discount}% OFF</span>
                        ` : ''}
                      </div>
                    </div>
                  `).join('')}
                </div>
              ` : `
                <!-- GRID VIEW MODE: EXACT MATCH TO VIDEO WITH HARMONIOUS LIGHT/DARK THEMING -->
                <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                  ${filtered.map(m => `
                    <div onclick="navigate('/models/' + '${m.id}')" class="surface-card hover:border-[var(--copper)]/60 rounded-3xl p-5 border border-subtle flex flex-col justify-between transition-all duration-200 hover:-translate-y-1 hover:shadow-xl group cursor-pointer backdrop-blur-sm shadow-sm">
                      <div>
                        <!-- Top Header: Circular Provider Avatar + Name + ID + Free Badge -->
                        <div class="flex items-start gap-3">
                          <div class="w-10 h-10 rounded-full surface-card-solid border border-subtle flex items-center justify-center p-2 shrink-0 group-hover:scale-105 transition shadow-sm text-main">
                            ${getProviderIcon(m.provider, 'w-5 h-5')}
                          </div>
                          <div class="min-w-0 flex-1">
                            <div class="flex items-center gap-2 flex-wrap">
                              <span class="font-semibold text-base text-main tracking-tight group-hover:text-[var(--copper)] transition truncate">${m.name}</span>
                              ${m.free ? `<span class="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-xs font-sans font-medium">${t('common.free')}</span>` : ''}
                            </div>
                            <div class="font-mono text-xs text-mut tracking-tight mt-0.5 truncate">${m.id}</div>
                          </div>
                        </div>

                        <!-- Capabilities Pills: Chat, Reasoning, Vision, FP8 -->
                        <div class="flex flex-wrap items-center gap-2 mt-4 text-xs">
                          <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-pill border border-subtle text-sec">
                            ${ico('message-square', 'w-3 h-3 text-mut')} <span>Chat</span>
                          </span>
                          ${m.reasoning ? `
                            <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-pill border border-subtle text-sec">
                              ${ico('brain', 'w-3 h-3 text-mut')} <span>${t('models.detailReasoning')}</span>
                            </span>
                          ` : ''}
                          ${m.vision ? `
                            <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-pill border border-subtle text-sec">
                              ${ico('eye', 'w-3 h-3 text-mut')} <span>${t('models.detailVision')}</span>
                            </span>
                          ` : ''}
                          ${m.fp8 ? `
                            <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-pill border border-subtle text-sec">
                              ${ico('zap', 'w-3 h-3 text-mut')} <span>FP8</span>
                            </span>
                          ` : ''}
                        </div>
                      </div>

                      <!-- Inset Price Box (Harmonious in Both Light and Dark Mode) -->
                      <div class="my-4 rounded-2xl bg-inset border border-subtle p-4">
                        <div class="grid grid-cols-2 gap-4">
                          <div>
                            <div class="text-xs font-mono uppercase tracking-widest text-mut font-medium">INPUT</div>
                            <div class="text-xl font-bold font-mono text-main mt-1">$${m.input.toFixed(2)}</div>
                            <div class="text-xs text-mut mt-1">per 1M tokens</div>
                          </div>
                          <div>
                            <div class="text-xs font-mono uppercase tracking-widest text-mut font-medium">OUTPUT</div>
                            <div class="text-xl font-bold font-mono text-[var(--copper-light)] mt-1">$${m.output.toFixed(2)}</div>
                            <div class="text-xs text-mut mt-1">per 1M tokens</div>
                          </div>
                        </div>
                      </div>

                      <!-- Bottom Row: Context + Discount Pill -->
                      <div class="flex items-center justify-between pt-0.5">
                        <span class="font-mono text-xs text-sec font-medium">${m.context} context</span>
                        ${m.free ? `
                          <span class="px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 font-bold text-xs font-mono tracking-tight">FREE</span>
                        ` : m.discount ? `
                          <span class="px-3 py-1 rounded-full bg-[var(--copper)]/15 border border-[var(--copper)]/30 text-[var(--copper-light)] font-bold text-xs font-mono tracking-tight">${m.discount}% OFF</span>
                        ` : `
                          <span class="px-3 py-1 rounded-full surface-card-solid border border-subtle text-mut font-medium text-xs font-mono tracking-tight">Standard</span>
                        `}
                      </div>
                    </div>
                  `).join('')}
                </div>
              `}
            </div>
          </div>
        </main>
        ${renderPublicFooter()}
      `;
    }

    function togglePublicProvider(provider) {
      if (!state.modelsProviders) state.modelsProviders = [];
      if (state.modelsProviders.includes('__none__')) {
        state.modelsProviders = [provider];
      } else if (state.modelsProviders.includes(provider)) {
        state.modelsProviders = state.modelsProviders.filter(p => p !== provider);
      } else {
        state.modelsProviders.push(provider);
      }
      render();
    }

    function resetPublicModelsFilters() {
      state.modelsSearch = '';
      state.modelsProviders = [];
      state.modelsPriceRange = 'all';
      state.modelsPricingType = 'all';
      state.modelsSort = 'provider';
      state.providerSearchQuery = '';
      render();
    }

    function renderModelDetailPage(modelId) {
      const m = MODELS_DATA.find(x => x.id === modelId) || MODELS_DATA[0];
      if (!m) return renderNotFoundPage();
      const origInput = m.discount ? (m.input / (1 - m.discount / 100)).toFixed(2) : null;
      const origOutput = m.discount ? (m.output / (1 - m.discount / 100)).toFixed(2) : null;
      const origCache = m.discount ? (m.cacheRead / (1 - m.discount / 100)).toFixed(2) : null;

      const activeTab = state.modelDetailApiTab || 'chat';

      let endpointPath = '/v1/chat/completions';
      let payloadJson = JSON.stringify({
        model: m.id,
        messages: [{ role: 'user', content: 'Hello!' }]
      }, null, 2);

      if (activeTab === 'responses') {
        endpointPath = '/v1/responses';
        payloadJson = JSON.stringify({
          model: m.id,
          input: 'Hello!'
        }, null, 2);
      } else if (activeTab === 'messages') {
        endpointPath = '/v1/messages';
        payloadJson = JSON.stringify({
          model: m.id,
          max_tokens: 1024,
          messages: [{ role: 'user', content: 'Hello!' }]
        }, null, 2);
      }

      const curlSnippet = `curl https://api.opencsn.com${endpointPath} \\\n  -H "Authorization: Bearer $OpenCSN_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '${payloadJson}'`;

      return `
        ${renderPublicNavbar()}
        <main class="flex-1 max-w-4xl w-full mx-auto px-6 py-10 space-y-8">
          <!-- Back button -->
          <div class="flex items-center justify-between">
            <button onclick="navigate('/models')" class="surface-card hover:border-white/20 text-xs px-4 py-2 rounded-full text-sec hover:text-main flex items-center gap-2 transition">
              <span class="text-sm font-mono">←</span> <span>${t('models.detailBack')}</span>
            </button>
            <button onclick="navigate('/management'); setManagementTab('models'); selectMgmtModel('${m.id}');" class="surface-card hover:border-[#c87a51]/50 text-xs px-3.5 py-1.5 rounded-xl text-[#df956d] flex items-center gap-1.5 transition">
              ${ico('sliders', 'w-3.5 h-3.5')} <span>Edit in Management</span>
            </button>
          </div>

          <!-- Nonactive Banner if hidden from public -->
          ${m.active === false ? `
            <div class="surface-card bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3 text-xs text-amber-300">
              <div class="flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-amber-400"></span>
                <span><b>Admin Notice:</b> This Model AI is currently <b>Nonactive (Hidden from Public Access)</b>. You are viewing it via direct administrator preview.</span>
              </div>
              <button onclick="toggleModelActive('${m.id}')" class="px-3 py-1 rounded-lg bg-[#c87a51] text-black font-semibold text-xs">Make Public</button>
            </div>
          ` : ''}

          <!-- Header Section -->
          <div class="space-y-4">
            <!-- Provider Capsule -->
            <div class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full surface-card border border-white/10 text-xs text-sec">
              <span>${getProviderIcon(m.provider, 'w-3.5 h-3.5')}</span>
              <span class="font-medium text-main">${m.provider}</span>
            </div>

            <!-- Title -->
            <h1 class="font-serif font-medium text-h1 text-main tracking-tight">${m.name}</h1>

            <!-- Dynamic Editable Description touched by user -->
            <p class="font-serif text-body text-sec max-w-[68ch] leading-relaxed">${m.description || 'Frontier AI model routing with high reliability, low latency, and zero data retention.'}</p>

            <!-- Badges -->
            <div class="flex items-center gap-2 pt-1 flex-wrap">
              ${m.discount ? `<span class="px-3 py-1 rounded-full bg-[#c87a51]/20 text-[#df956d] font-semibold text-xs border border-[#c87a51]/30">${m.discount}% OFF</span>` : ''}
              <span class="px-3 py-1 rounded-full surface-card text-sec text-xs border border-white/5 font-mono">${m.context} context</span>
            </div>

            <!-- Model ID Capsule with Copy button -->
            <div class="pt-2 flex flex-wrap items-center gap-3">
              <div class="inline-flex items-center gap-3 surface-card rounded-full px-4 py-1.5 border border-white/10 text-xs">
                <span class="font-mono text-xs uppercase text-mut">MODEL ID</span>
                <span class="font-mono font-semibold text-main">${m.id}</span>
                <button onclick="navigator.clipboard.writeText('${m.id}'); showToast('Copied ${m.id} to clipboard');" class="bg-white text-black hover:bg-white/90 px-3 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 transition shadow-sm ml-1">
                  ${ico('copy', 'w-3 h-3')} <span>Copy</span>
                </button>
              </div>
            </div>

            <!-- Capabilities Pills -->
            <div class="flex flex-wrap items-center gap-2 pt-1">
              <span class="px-3 py-1 rounded-full surface-card text-xs text-sec flex items-center gap-1.5">${ico('message-square', 'w-3.5 h-3.5')} Chat</span>
              ${m.reasoning ? `<span class="px-3 py-1 rounded-full surface-card text-xs text-sec flex items-center gap-1.5">${ico('brain', 'w-3.5 h-3.5')} Reasoning</span>` : ''}
              ${m.vision ? `<span class="px-3 py-1 rounded-full surface-card text-xs text-sec flex items-center gap-1.5">${ico('eye', 'w-3.5 h-3.5')} Vision</span>` : ''}
              ${m.fp8 ? `<span class="px-3 py-1 rounded-full surface-card text-xs text-sec flex items-center gap-1.5">${ico('zap', 'w-3.5 h-3.5')} FP8</span>` : ''}
            </div>
          </div>

          <!-- PRICING Card (Matching video frame_08) -->
          <div class="surface-card rounded-3xl p-6 sm:p-7 border border-white/10 space-y-5">
            <div class="flex items-center justify-between">
              <div class="font-mono text-xs uppercase tracking-wider text-mut">PRICING</div>
              <div class="flex items-center gap-2">
                ${m.discount ? `<span class="px-2.5 py-0.5 rounded-full bg-[#c87a51]/20 text-[#df956d] font-semibold text-xs">-${m.discount}%</span>` : ''}
                <span class="font-mono text-xs text-mut">per 1M tokens</span>
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <!-- Input -->
              <div class="surface-card-solid rounded-2xl p-5 border border-white/5 space-y-1">
                <div class="font-mono text-xs uppercase text-mut">INPUT</div>
                <div class="flex items-baseline gap-2 flex-wrap">
                  <span class="text-2xl sm:text-3xl font-mono font-bold text-main">$${m.input.toFixed(2)}</span>
                  ${origInput ? `<span class="text-xs font-mono text-mut line-through">$${origInput}</span>` : ''}
                </div>
              </div>

              <!-- Output -->
              <div class="surface-card-solid rounded-2xl p-5 border border-white/5 space-y-1">
                <div class="font-mono text-xs uppercase text-mut">OUTPUT</div>
                <div class="flex items-baseline gap-2 flex-wrap">
                  <span class="text-2xl sm:text-3xl font-mono font-bold text-[#df956d]">$${m.output.toFixed(2)}</span>
                  ${origOutput ? `<span class="text-xs font-mono text-mut line-through">$${origOutput}</span>` : ''}
                </div>
              </div>

              <!-- Cache Read -->
              <div class="surface-card-solid rounded-2xl p-5 border border-white/5 space-y-1">
                <div class="font-mono text-xs uppercase text-mut">CACHE READ</div>
                <div class="flex items-baseline gap-2 flex-wrap">
                  <span class="text-2xl sm:text-3xl font-mono font-bold text-main">$${m.cacheRead.toFixed(2)}</span>
                  ${origCache ? `<span class="text-xs font-mono text-mut line-through">$${origCache}</span>` : ''}
                </div>
              </div>
            </div>

            <!-- TOTAL TRAFFIC (Matching video frame_08) -->
            <div class="pt-4 border-t border-subtle space-y-2">
              <div class="font-mono text-xs uppercase tracking-wider text-mut">TOTAL TRAFFIC</div>
              <div class="text-xs text-sec">All time</div>
              <div class="flex items-baseline gap-4 pt-1 font-mono">
                <div class="text-sm font-semibold text-main"><b class="text-base text-main">${m.trafficRequests || '1.7K'}</b> <span class="text-xs text-mut font-normal">requests</span></div>
                <div class="text-sm font-semibold text-main"><b class="text-base text-main">${m.trafficTokens || '11.9M'}</b> <span class="text-xs text-mut font-normal">tokens</span></div>
              </div>
            </div>
          </div>

          <!-- API EXAMPLES Card (Matching video frame_10) -->
          <div class="surface-card rounded-3xl p-6 sm:p-7 border border-white/10 space-y-4">
            <div class="flex items-center justify-between">
              <div class="font-mono text-xs uppercase tracking-wider text-mut">API EXAMPLES</div>
              <div class="text-xs text-sec">One key, all three endpoints.</div>
            </div>

            <div class="flex items-center justify-between gap-2 border-b border-subtle pb-3">
              <div class="flex items-center gap-1.5 flex-wrap">
                ${[
                  { id: 'chat', label: 'Chat Completions' },
                  { id: 'responses', label: 'Responses' },
                  { id: 'messages', label: 'Messages' }
                ].map(t => `
                  <button onclick="state.modelDetailApiTab='${t.id}'; render();" class="px-3.5 py-1.5 rounded-full text-xs transition ${activeTab === t.id ? 'bg-[#f5efe6] text-[#14110f] font-semibold shadow' : 'surface-card text-sec hover:text-main'}">
                    ${t.label}
                  </button>
                `).join('')}
              </div>
              <button onclick="navigator.clipboard.writeText(\`${curlSnippet.replace(/`/g, '\\`')}\`); showToast('Copied cURL example to clipboard');" class="surface-card hover:border-white/20 text-xs px-3 py-1.5 rounded-xl text-sec hover:text-main flex items-center gap-1.5 transition">
                ${ico('copy', 'w-3 h-3')} <span>Copy</span>
              </button>
            </div>

            <!-- Terminal code box -->
            <div class="surface-card-solid rounded-2xl p-5 border border-white/5 font-mono text-xs text-sec space-y-3 overflow-x-auto">
              <div class="text-[#c87a51] font-semibold text-xs font-sans">POST ${endpointPath}</div>
              <pre class="leading-relaxed whitespace-pre font-mono text-xs sm:text-xs text-main/90">${curlSnippet}</pre>
            </div>
          </div>

          <!-- Bottom Back button -->
          <div class="pt-4 flex items-center justify-between">
            <button onclick="navigate('/models')" class="surface-card hover:border-white/20 text-xs px-5 py-2.5 rounded-full text-sec hover:text-main inline-flex items-center gap-2 transition">
              <span class="text-sm font-mono">←</span> <span>Back to catalog</span>
            </button>
            <button onclick="navigate('/management'); setManagementTab('models'); selectMgmtModel('${m.id}');" class="bg-[#c87a51] text-[#14110f] font-semibold text-xs px-4 py-2 rounded-xl flex items-center gap-1.5 transition shadow">
              ${ico('sliders', 'w-3.5 h-3.5')} <span>Tune in Management</span>
            </button>
          </div>
        </main>
        ${renderPublicFooter()}
      `;
    }
// duplicate: 
// duplicate:     function togglePublicProvider(p) {
// duplicate:       if (state.modelsProviders.includes(p)) {
// duplicate:         state.modelsProviders = state.modelsProviders.filter(x => x !== p);
// duplicate:       } else {
// duplicate:         state.modelsProviders.push(p);
// duplicate:       }
// duplicate:       render();
// duplicate:     }
// duplicate: 
// duplicate:     function resetPublicModelsFilters() {
// duplicate:       state.modelsSearch = '';
// duplicate:       state.modelsProviders = [];
// duplicate:       state.modelsPriceRange = 'all';
// duplicate:       state.modelsPricingType = 'all';
// duplicate:       render();
// duplicate:     }
// duplicate: 
    // ============================================================================
    // PUBLIC PAGES 7, 8, 9: LOGIN, REGISTER, FORGOT PASSWORD 404
    // ============================================================================
    function updateTurnstileButtons(isValid) {
      const googleBtns = [
        document.getElementById('btn-google-register'),
        document.getElementById('btn-google-login'),
        document.getElementById('btn-google-auth')
      ].filter(Boolean);

      const submitBtn = document.getElementById('btn-login-submit');
      const forgotBtn = document.getElementById('btn-forgot-send');

      googleBtns.forEach((btn) => {
        if (isValid) {
          btn.removeAttribute('disabled');
          btn.classList.remove('opacity-40', 'cursor-not-allowed');
          btn.classList.add('hover:bg-[#232326]', 'active:scale-[0.99]', 'cursor-pointer', 'text-main');
        } else {
          btn.setAttribute('disabled', 'true');
          btn.classList.add('opacity-40', 'cursor-not-allowed');
          btn.classList.remove('hover:bg-[#232326]', 'active:scale-[0.99]', 'cursor-pointer');
        }
      });

      if (submitBtn) {
        if (isValid) {
          submitBtn.removeAttribute('disabled');
          submitBtn.classList.remove('opacity-40', 'cursor-not-allowed');
          submitBtn.classList.add('hover:bg-[#df956d]', 'cursor-pointer');
        } else {
          submitBtn.setAttribute('disabled', 'true');
          submitBtn.classList.add('opacity-40', 'cursor-not-allowed');
          submitBtn.classList.remove('hover:bg-[#df956d]', 'cursor-pointer');
        }
      }

      if (forgotBtn) {
        if (isValid) {
          forgotBtn.removeAttribute('disabled');
          forgotBtn.classList.remove('opacity-40', 'cursor-not-allowed');
          forgotBtn.classList.add('hover:bg-[#df956d]', 'cursor-pointer');
        } else {
          forgotBtn.setAttribute('disabled', 'true');
          forgotBtn.classList.add('opacity-40', 'cursor-not-allowed');
          forgotBtn.classList.remove('hover:bg-[#df956d]', 'cursor-pointer');
        }
      }
    }

    function initTurnstile() {
      state.turnstileToken = null;
      state.turnstileVerified = false;
      updateTurnstileButtons(false);

      setTimeout(() => {
        const siteKey = '0x4AAAAAAFuZroZ8kZsUdyft';
        if (typeof window === 'undefined') return;

        const containerIds = ['cf-turnstile-container', 'cf-turnstile-register', 'cf-turnstile-forgot'];

        containerIds.forEach((containerId) => {
          const container = document.getElementById(containerId);
          if (!container) return;
          if (window.turnstile && typeof window.turnstile.render === 'function') {
            try {
              container.innerHTML = '';
              window.turnstile.render('#' + containerId, {
                sitekey: siteKey,
                language: state.lang === 'id' ? 'id' : 'en',
                theme: state.theme === 'light' ? 'light' : 'dark',
                callback: function (token) {
                  state.turnstileToken = token;
                  state.turnstileVerified = true;
                  updateTurnstileButtons(true);
                },
                'expired-callback': function () {
                  state.turnstileToken = null;
                  state.turnstileVerified = false;
                  updateTurnstileButtons(false);
                },
                'error-callback': function () {
                  state.turnstileToken = null;
                  state.turnstileVerified = false;
                  updateTurnstileButtons(false);
                }
              });
            } catch (e) {
              console.warn('[Turnstile] render error on #' + containerId, e);
            }
          }
        });
      }, 100);
    }

    function handleGoogleAuth(isSignUp = false) {
      if (!state.turnstileVerified || !state.turnstileToken) {
        showToast(t('toast.turnstileRequired'));
        return;
      }
      if (typeof window !== 'undefined') {
        window.location.href = '/api/auth/google' + (isSignUp ? '?action=signup' : '?action=signin');
      }
    }

    async function submitLoginForm() {
      if (!state.turnstileVerified || !state.turnstileToken) {
        showToast('Harap selesaikan verifikasi Cloudflare Turnstile terlebih dahulu');
        return;
      }

      const emailEl = document.getElementById('login-email');
      const passEl = document.getElementById('login-password');
      const email = emailEl ? emailEl.value.trim() : '';
      const password = passEl ? passEl.value : '';

      if (!email || !password) {
        showToast('Harap masukkan email dan password');
        return;
      }

      const btn = document.getElementById('btn-login-submit');
      if (btn) {
        btn.disabled = true;
        btn.innerText = 'Signing in...';
      }

      try {
        const res = await apiFetch('/api/auth/login', {
          method: 'POST',
          body: JSON.stringify({
            email,
            password,
            turnstileToken: state.turnstileToken || 'token_turnstile_verified'
          })
        });
        const data = await res.json();
        if (data.success && data.user) {
          state.user = data.user;
          try {
            localStorage.setItem('opencsn_user', JSON.stringify(data.user));
          } catch (e) {}

          if (data.user.isDev) {
            showToast('Login berhasil sebagai Developer Admin!');
            navigate('/management');
          } else {
            showToast('Login berhasil!');
            navigate('/dashboard');
          }
        } else {
          showToast(data.error || data.message || 'Email atau password salah.');
        }
      } catch (err) {
        showToast('Koneksi server gagal. Periksa koneksi backend.');
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.innerText = 'Sign In';
        }
      }
    }

    async function handleLogout() {
      try {
        await apiFetch('/api/auth/logout', { method: 'POST' });
      } catch (e) {}
      state.user = null;
      state.userMenuOpen = false;
      try {
        localStorage.removeItem('opencsn_user');
      } catch (e) {}
      showToast(t('toast.logout'));
      navigate('/login');
    }

    function renderLoginPage() {
      const isId = state.lang === 'id';
      return `
        ${renderPublicNavbar()}
        <main class="flex-1 max-w-5xl w-full mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div class="lg:col-span-6 space-y-6">
            <div class="font-mono text-xs uppercase tracking-widest text-[#c87a51] font-semibold">${t('login.welcomeBack')}</div>
            <h1 class="font-serif font-medium text-h1 text-main">
              ${t('login.title')}
            </h1>
            <p class="font-serif text-body text-sec leading-relaxed max-w-[68ch]">
              ${t('login.subtitle')}
            </p>
            <div class="space-y-3 pt-2">
              <div class="surface-card rounded-2xl p-4 flex items-start gap-3">
                <span class="text-[#c87a51] mt-0.5">${ico('circle-dot')}</span>
                <div class="text-sm"><div class="font-semibold text-main">${isId ? 'Semua model, satu API' : 'Every model, one API'}</div><div class="text-sec mt-0.5 text-xs">${isId ? 'Ganti model tanpa menambah integrasi baru di aplikasi Anda.' : 'Swap models without adding new integrations to your app.'}</div></div>
              </div>
              <div class="surface-card rounded-2xl p-4 flex items-start gap-3">
                <span class="text-[#c87a51] mt-0.5">${ico('circle-dot')}</span>
                <div class="text-sm"><div class="font-semibold text-main">${isId ? 'Harga per token terlihat' : 'Per-token pricing in view'}</div><div class="text-sec mt-0.5 text-xs">${isId ? 'Pantau pemakaian, biaya, dan opsi gratis per model.' : 'Track usage, cost, and free options for each model.'}</div></div>
              </div>
              <div class="surface-card rounded-2xl p-4 flex items-start gap-3">
                <span class="text-[#c87a51] mt-0.5">${ico('circle-dot')}</span>
                <div class="text-sm"><div class="font-semibold text-main">${isId ? 'Log request dan status' : 'Request logs and status'}</div><div class="text-sec mt-0.5 text-xs">${isId ? 'Lihat latensi, token, dan status tiap panggilan dari satu konsol.' : 'See latency, tokens, and status for every call from one console.'}</div></div>
              </div>
            </div>
          </div>

          <div class="lg:col-span-6 surface-card rounded-3xl p-8 space-y-5 border border-strong shadow-2xl">
            <div>
              <h2 class="font-serif font-medium text-h2 text-main">${t('login.cardTitle')}</h2>
              <p class="text-sm text-sec mt-1">${t('login.cardSubtitle')}</p>
            </div>

            <!-- Cloudflare Turnstile -->
            <div class="flex justify-center min-h-[66px] items-center pt-1">
              <div id="cf-turnstile-container"></div>
            </div>

            <button id="btn-google-login" onclick="handleGoogleAuth(false)" disabled class="w-full bg-[#18181b] text-neutral-300 font-medium text-sm py-3 px-4 rounded-xl border border-neutral-700/80 flex items-center justify-center gap-2.5 transition shadow-sm opacity-40 cursor-not-allowed">
              <svg class="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              <span>${t('login.google')}</span>
            </button>

            <p class="text-xs text-sec text-center leading-relaxed font-sans">
              ${t('login.legalNotice', {
                terms: `<a href="/terms" class="text-neutral-300 underline underline-offset-2 hover:text-white transition">${t('footer.terms')}</a>`,
                privacy: `<a href="/privacy" class="text-neutral-300 underline underline-offset-2 hover:text-white transition">${t('footer.privacy')}</a>`
              })}
            </p>

            <div class="text-center text-xs font-mono uppercase text-mut">${t('login.or')}</div>

            <form onsubmit="event.preventDefault(); submitLoginForm();" class="space-y-4">
              <div class="space-y-3 text-sm">
                <div>
                  <label class="block text-sec text-xs font-medium mb-1.5">${t('login.email')}</label>
                  <input id="login-email" type="email" placeholder="name@example.com" value="" class="w-full surface-card-solid border border-subtle rounded-xl px-3.5 py-2.5 text-main text-sm focus:outline-none focus:border-strong transition" required />
                </div>
                <div>
                  <label class="block text-sec text-xs font-medium mb-1.5">${t('login.password')}</label>
                  <input id="login-password" type="password" placeholder="••••••••••••" value="" class="w-full surface-card-solid border border-subtle rounded-xl px-3.5 py-2.5 text-main text-sm focus:outline-none focus:border-strong transition" required />
                </div>
              </div>

              <button type="submit" id="btn-login-submit" disabled class="w-full bg-[#c87a51] text-[#14110f] font-semibold text-sm py-3 rounded-xl transition shadow-sm opacity-40 cursor-not-allowed">
                ${t('login.submit')}
              </button>
            </form>

            <div class="text-center space-y-1.5 text-sm text-sec pt-1">
              <div>${t('login.noAccount')} <button onclick="navigate('/register')" class="text-[#c87a51] underline font-medium">${t('login.signup')}</button></div>
              <div><button onclick="navigate('/forgot-password')" class="text-mut hover:text-sec text-xs">${t('login.forgot')}</button></div>
            </div>
          </div>
        </main>
      `;
    }

    function renderRegisterPage() {
      return `
        ${renderPublicNavbar()}
        <main class="flex-1 flex items-center justify-center px-4 py-12 sm:py-16 min-h-[calc(100vh-120px)]">
          <div class="w-full max-w-[460px] surface-card-solid rounded-3xl p-8 sm:p-10 border border-neutral-800/80 shadow-2xl space-y-6 animate-in fade-in zoom-in-95 duration-150">
            <!-- Header -->
            <div>
              <h1 class="font-serif font-medium text-h1 text-main">${t('register.title')}</h1>
              <p class="text-sm text-sec mt-2 font-sans">${t('register.subtitle')}</p>
            </div>

            <!-- Cloudflare Turnstile -->
            <div class="flex justify-center min-h-[66px] items-center pt-1">
              <div id="cf-turnstile-register"></div>
            </div>

            <!-- Sign up with Google Button -->
            <button id="btn-google-register" onclick="handleGoogleAuth(true)" disabled class="w-full bg-[#18181b] text-neutral-300 font-medium text-sm py-3.5 px-4 rounded-xl border border-neutral-700/80 flex items-center justify-center gap-3 transition shadow-sm opacity-40 cursor-not-allowed">
              <svg class="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              <span class="tracking-tight font-medium">${t('register.google')}</span>
            </button>

            <!-- Clean single notice without duplicate prompt -->
            <p class="text-sm text-sec text-center leading-relaxed max-w-sm mx-auto font-sans">
              ${t('register.closed')}
            </p>

            <div class="text-sm text-sec text-center font-sans">
              ${t('register.haveAccount')} <a href="/login" onclick="event.preventDefault(); navigate('/login');" class="text-[#c87a51] hover:underline font-medium">${t('register.signin')}</a>
            </div>

            <!-- Divider matching screenshot -->
            <div class="border-t border-neutral-800/80 my-4"></div>

            <!-- Legal Footer matching screenshot -->
            <p class="text-xs text-sec text-center leading-relaxed font-sans">
              ${t('register.legalNotice', {
                terms: `<a href="/terms" class="text-neutral-300 underline underline-offset-2 hover:text-white transition">${t('footer.terms')}</a>`,
                privacy: `<a href="/privacy" class="text-neutral-300 underline underline-offset-2 hover:text-white transition">${t('footer.privacy')}</a>`
              })}
            </p>
          </div>
        </main>
      `;
    }

    async function sendForgotOtp(isResend = false) {
      if (!isResend && (!state.turnstileVerified || !state.turnstileToken)) {
        showToast('Harap selesaikan verifikasi Cloudflare Turnstile terlebih dahulu');
        return;
      }

      let email = state.forgotEmail;
      if (!isResend) {
        const emailEl = document.getElementById('forgot-email');
        email = emailEl ? emailEl.value.trim() : '';
      }

      if (!email) {
        showToast('Harap masukkan alamat email.');
        return;
      }

      const btn = document.getElementById('btn-forgot-send');
      if (btn) {
        btn.disabled = true;
        btn.innerText = 'Sending OTP...';
      }

      try {
        const res = await apiFetch('/api/auth/forgot-password', {
          method: 'POST',
          body: JSON.stringify({
            email,
            turnstileToken: state.turnstileToken || 'token_turnstile_verified'
          })
        });
        const data = await res.json();
        if (data.success) {
          state.forgotStep = 2;
          state.forgotEmail = email;
          showToast(data.message || 'Kode OTP telah dikirim ke email Anda.');
          render();
        } else {
          showToast(data.error || data.message || 'Gagal mengirim OTP.');
        }
      } catch (err) {
        showToast('Koneksi server gagal.');
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.innerText = 'Send OTP Code';
        }
      }
    }

    async function submitResetPassword() {
      const otpEl = document.getElementById('forgot-otp');
      const newPassEl = document.getElementById('forgot-new-password');
      const confirmPassEl = document.getElementById('forgot-confirm-password');

      const otp = otpEl ? otpEl.value.trim() : '';
      const newPassword = newPassEl ? newPassEl.value : '';
      const confirmPassword = confirmPassEl ? confirmPassEl.value : '';

      if (!otp || !newPassword || !confirmPassword) {
        showToast('Harap lengkapi semua kolom.');
        return;
      }

      if (newPassword.length < 6) {
        showToast('Kata sandi minimal 6 karakter.');
        return;
      }

      if (newPassword !== confirmPassword) {
        showToast('Konfirmasi kata sandi baru tidak sesuai.');
        return;
      }

      const btn = document.getElementById('btn-forgot-submit');
      if (btn) {
        btn.disabled = true;
        btn.innerText = 'Resetting...';
      }

      try {
        const res = await apiFetch('/api/auth/reset-password', {
          method: 'POST',
          body: JSON.stringify({
            email: state.forgotEmail,
            otp,
            newPassword
          })
        });
        const data = await res.json();
        if (data.success) {
          showToast(data.message || 'Kata sandi berhasil diperbarui! Silakan login.');
          state.forgotStep = 1;
          state.forgotEmail = '';
          navigate('/login');
        } else {
          showToast(data.message || 'Gagal mereset kata sandi.');
        }
      } catch (err) {
        showToast('Koneksi server gagal.');
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.innerText = 'Reset Password';
        }
      }
    }

    function backToForgotStep1() {
      state.forgotStep = 1;
      render();
    }

    function renderForgotPasswordPage() {
      const isStep1 = state.forgotStep !== 2;
      return `
        ${renderPublicNavbar()}
        <main class="flex-1 flex items-center justify-center px-4 py-12 sm:py-16 min-h-[calc(100vh-120px)]">
          <div class="w-full max-w-[460px] surface-card-solid rounded-3xl p-8 sm:p-10 border border-neutral-800/80 shadow-2xl space-y-6 animate-in fade-in zoom-in-95 duration-150">
            <!-- Header -->
            <div>
              <h1 class="font-serif font-medium text-h1 text-main">${t('forgot.title')}</h1>
              <p class="text-sm text-sec mt-2 font-sans">
                ${isStep1 ? t('forgot.step1Subtitle') : t('forgot.step2Subtitle')}
              </p>
            </div>

            ${isStep1 ? `
              <!-- Cloudflare Turnstile -->
              <div class="flex justify-center min-h-[66px] items-center pt-1">
                <div id="cf-turnstile-forgot"></div>
              </div>

              <!-- Step 1 Form -->
              <form onsubmit="event.preventDefault(); sendForgotOtp();" class="space-y-4">
                <div>
                  <label class="block text-sec text-xs font-medium mb-1.5">${t('forgot.emailLabel')}</label>
                  <input id="forgot-email" type="email" placeholder="name@example.com" value="${state.forgotEmail || ''}" class="w-full surface-card-solid border border-subtle rounded-xl px-3.5 py-2.5 text-main text-sm focus:outline-none focus:border-strong transition" required />
                </div>

                <button id="btn-forgot-send" type="submit" disabled class="w-full bg-[#c87a51] text-[#14110f] font-semibold text-sm py-3 rounded-xl transition shadow-sm opacity-40 cursor-not-allowed">
                  ${t('forgot.sendBtn')}
                </button>
              </form>
            ` : `
              <!-- Step 2 Form -->
              <form onsubmit="event.preventDefault(); submitResetPassword();" class="space-y-4">
                <div class="p-3.5 surface-card rounded-xl border border-subtle flex items-center justify-between text-sm">
                  <span class="text-sec truncate max-w-[220px] font-mono text-xs">${state.forgotEmail}</span>
                  <button type="button" onclick="backToForgotStep1()" class="text-[#c87a51] hover:underline text-xs font-medium">${t('forgot.changeEmail')}</button>
                </div>

                <div>
                  <label class="block text-sec text-xs font-medium mb-1.5">${t('forgot.otpLabel')}</label>
                  <input id="forgot-otp" type="text" maxlength="6" placeholder="123456" class="w-full surface-card-solid border border-subtle rounded-xl px-3.5 py-2.5 text-main font-mono text-center tracking-[6px] text-lg focus:outline-none focus:border-strong transition" required />
                </div>

                <div>
                  <label class="block text-sec text-xs font-medium mb-1.5">${t('forgot.newPasswordLabel')}</label>
                  <input id="forgot-new-password" type="password" placeholder="${t('forgot.newPasswordPlaceholder')}" class="w-full surface-card-solid border border-subtle rounded-xl px-3.5 py-2.5 text-main text-sm focus:outline-none focus:border-strong transition" required minlength="6" />
                </div>

                <div>
                  <label class="block text-sec text-xs font-medium mb-1.5">${t('forgot.confirmPasswordLabel')}</label>
                  <input id="forgot-confirm-password" type="password" placeholder="${t('forgot.confirmPasswordPlaceholder')}" class="w-full surface-card-solid border border-subtle rounded-xl px-3.5 py-2.5 text-main text-sm focus:outline-none focus:border-strong transition" required minlength="6" />
                </div>

                <button id="btn-forgot-submit" type="submit" class="w-full bg-[#c87a51] text-[#14110f] font-semibold text-sm py-3 rounded-xl transition shadow-sm hover:bg-[#df956d] cursor-pointer">
                  ${t('forgot.submitBtn')}
                </button>

                <div class="text-center pt-1">
                  <button type="button" onclick="sendForgotOtp(true)" class="text-xs text-mut hover:text-sec transition underline">${t('forgot.resendOtp')}</button>
                </div>
              </form>
            `}

            <!-- Divider -->
            <div class="border-t border-neutral-800/80 my-4"></div>

            <!-- Footer Link -->
            <div class="text-sm text-sec text-center font-sans">
              ${t('forgot.rememberPassword')} <a href="/login" onclick="event.preventDefault(); navigate('/login');" class="text-[#c87a51] hover:underline font-medium">${t('forgot.signin')}</a>
            </div>
          </div>
        </main>
        ${renderPublicFooter()}
      `;
    }

    function renderNotFoundPage() {
      return `
        ${renderPublicNavbar()}
        <main class="flex-1 flex flex-col items-center justify-center px-6 py-28 text-center space-y-4">
          <div class="font-mono text-xs text-[#c87a51] font-bold">404</div>
          <h1 class="font-serif font-medium text-h1 text-main">${t('notfound.title')}</h1>
          <p class="font-serif text-body text-sec max-w-md">${t('notfound.body')}</p>
          <div class="flex items-center gap-3 pt-3">
            <button onclick="navigate('/')" class="bg-[#c87a51] text-[#14110f] font-semibold text-xs px-5 py-2.5 rounded-xl">${t('notfound.home')}</button>
            <button onclick="navigate('/docs', 'introduction')" class="surface-card text-main text-xs px-5 py-2.5 rounded-xl">${t('notfound.docs')}</button>
          </div>
        </main>
        ${renderPublicFooter()}
      `;
    }

    // ============================================================================
    // PUBLIC PAGE 10: DOCUMENTATION (/docs — ALL 17 SUBPAGES)
    // ============================================================================
    const DOC_PAGES = {
      'introduction': {
        category: 'Start here',
        title: 'The OpenCSN API',
        subtitle: 'One key and one base URL in front of many model providers. Keep the SDK your code already uses, change the base URL, and pick a model by its ID.',
        toc: ['How a request is routed', 'Pick an endpoint', 'Where to go next'],
        prev: null,
        next: { id: 'quickstart', title: 'Quickstart' },
        content: () => `
          <div class="space-y-6 text-xs sm:text-sm text-sec">
            <div class="surface-card rounded-2xl p-5 space-y-4">
              <div class="flex items-center justify-between font-mono text-xs text-main">
                <span>https://api.opencsn.com/v1</span>
                <button data-copy="https://api.opencsn.com/v1" onclick="copyFromAttr(this)" class="px-2.5 py-1 rounded surface-card text-sec hover:text-main">Copy</button>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-3 border-t border-subtle text-xs">
                <div><div class="text-mut">Key header</div><div class="font-mono text-main mt-0.5">Authorization: Bearer or x-api-key</div></div>
                <div><div class="text-mut">Body</div><div class="text-main mt-0.5">JSON, in the format of the path you call</div></div>
                <div><div class="text-mut">Billing</div><div class="text-main mt-0.5">Per token, from prepaid credit or a plan</div></div>
              </div>
            </div>
            <h3 class="text-base font-semibold text-main pt-2">How a request is routed</h3>
            <p>Two parts of a request matter to the gateway, and they are independent. The <b class="text-main">path</b> decides which wire format you send and get back. The <b class="text-main">model</b> field decides which model answers. A Claude model can be called through the OpenAI Chat Completions path, and a GPT model through the Anthropic Messages path; the gateway converts between formats on the way in and out.</p>
            <h3 class="text-base font-semibold text-main pt-2">Pick an endpoint</h3>
            <table class="w-full text-left text-xs border-collapse">
              <thead><tr class="border-b border-subtle text-mut"><th class="py-2">If your code uses</th><th class="py-2">Call</th><th class="py-2">Reference</th></tr></thead>
              <tbody class="divide-y divide-white/5">
                <tr><td class="py-2.5">OpenAI SDK, chat completions</td><td class="py-2.5 font-mono text-main">POST /v1/chat/completions</td><td class="py-2.5"><button onclick="navigate('/docs','chat-completions')" class="text-[#c87a51] underline">Chat Completions</button></td></tr>
                <tr><td class="py-2.5">OpenAI SDK, responses</td><td class="py-2.5 font-mono text-main">POST /v1/responses</td><td class="py-2.5"><button onclick="navigate('/docs','responses')" class="text-[#c87a51] underline">Responses</button></td></tr>
                <tr><td class="py-2.5">Anthropic SDK, messages</td><td class="py-2.5 font-mono text-main">POST /v1/messages</td><td class="py-2.5"><button onclick="navigate('/docs','messages')" class="text-[#c87a51] underline">Messages</button></td></tr>
                <tr><td class="py-2.5">Anything that lists models</td><td class="py-2.5 font-mono text-main">GET /v1/models</td><td class="py-2.5"><button onclick="navigate('/docs','models-api')" class="text-[#c87a51] underline">Models</button></td></tr>
                <tr><td class="py-2.5">Checking your balance</td><td class="py-2.5 font-mono text-main">GET /v1/me</td><td class="py-2.5"><button onclick="navigate('/docs','me-api')" class="text-[#c87a51] underline">Me</button></td></tr>
              </tbody>
            </table>
          </div>
        `
      },
      'quickstart': {
        category: 'Start here',
        title: 'Quickstart',
        subtitle: "From no account to a model's answer in your terminal. Each step below takes a minute.",
        toc: ['Steps', 'What came back', 'Next'],
        prev: { id: 'introduction', title: 'Introduction' },
        next: { id: 'authentication', title: 'Authentication' },
        content: () => `
          <div class="space-y-6 text-xs sm:text-sm text-sec">
            <div class="space-y-4">
              <div><b class="text-main">1. Create an account and add credit</b> — Open the Billing page of the console. A request is refused with <code class="font-mono text-main">402 insufficient_credit</code> until the account can pay for it.</div>
              <div><b class="text-main">2. Create an API key</b> — Open API keys in the console and create a key. Keys start with <code class="font-mono text-main">sk-opencsn-</code>.</div>
              <div><b class="text-main">3. Export the key</b></div>
              <pre class="surface-card rounded-xl p-4 font-mono text-xs text-main">export OPENCSN_API_KEY="sk-opencsn-..."</pre>
              <div><b class="text-main">4. Send a request</b></div>
              ${renderDocCodeTabs({
                curl: `curl https://api.opencsn.com/v1/chat/completions \\\n  -H "Authorization: Bearer $OpenCSN_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"model":"${getExampleModelId()}","messages":[{"role":"user","content":"Say hello in one sentence."}]}'`,
                python: `from openai import OpenAI\nimport os\n\nclient = OpenAI(base_url="https://api.opencsn.com/v1", api_key=os.environ["OpenCSN_API_KEY"])\ncompletion = client.chat.completions.create(\n    model="${getExampleModelId()}",\n    messages=[{"role": "user", "content": "Say hello in one sentence."}],\n)\nprint(completion.choices[0].message.content)`,
                nodejs: `import OpenAI from "openai";\n\nconst client = new OpenAI({ baseURL: "https://api.opencsn.com/v1", apiKey: process.env.OpenCSN_API_KEY });\nconst completion = await client.chat.completions.create({\n  model: "${getExampleModelId()}",\n  messages: [{ role: "user", content: "Say hello in one sentence." }],\n});\nconsole.log(completion.choices[0].message.content);`
              })}
            </div>
          </div>
        `
      },
      'authentication': {
        category: 'Start here',
        title: 'Authentication',
        subtitle: 'Every request carries one API key. The gateway reads it from either of two headers, on every endpoint.',
        toc: ['Which header carries the key', 'Key format', 'Key controls', 'When authentication fails'],
        prev: { id: 'quickstart', title: 'Quickstart' },
        next: { id: 'chat-completions', title: '/v1/chat/completions' },
        content: () => `
          <div class="space-y-5 text-xs sm:text-sm text-sec">
            <h3 class="text-base font-semibold text-main">Which header carries the key</h3>
            <p>Send the key in <code class="font-mono text-main">Authorization</code> with the <code class="font-mono text-main">Bearer</code> prefix, or in <code class="font-mono text-main">x-api-key</code>. Both work on all paths.</p>
            <pre class="surface-card rounded-xl p-4 font-mono text-xs text-main">Authorization: Bearer sk-opencsn-...\nx-api-key: sk-opencsn-...</pre>
            <h3 class="text-base font-semibold text-main pt-2">Key controls</h3>
            <p>Each key can be narrowed when you create it in API keys: <b class="text-main">Spend cap</b> (USD amount), <b class="text-main">Reset period</b> (daily/monthly), <b class="text-main">Allowed models</b>, and <b class="text-main">Expiry</b>.</p>
          </div>
        `
      },
      'chat-completions': {
        category: 'API reference',
        title: 'Chat Completions',
        subtitle: 'The OpenAI Chat Completions format. The widest-supported format among existing tools, and the default choice if you are unsure.',
        toc: ['Request', 'Body fields', 'Other fields', 'Response'],
        prev: { id: 'authentication', title: 'Authentication' },
        next: { id: 'responses', title: '/v1/responses' },
        content: () => `
          <div class="space-y-5 text-xs sm:text-sm text-sec">
            <div class="surface-card rounded-xl p-4 font-mono text-xs text-main"><span class="text-emerald-400 font-bold mr-2">POST</span> https://api.opencsn.com/v1/chat/completions</div>
            ${renderDocCodeTabs({
              curl: `curl https://api.opencsn.com/v1/chat/completions \\\n  -H "Authorization: Bearer $OpenCSN_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"model":"${getExampleModelId()}","messages":[{"role":"user","content":"Hello!"}]}'`,
              python: `completion = client.chat.completions.create(model="${getExampleModelId()}", messages=[{"role":"user","content":"Hello!"}])`,
              nodejs: `const completion = await client.chat.completions.create({ model: "${getExampleModelId()}", messages: [{ role: "user", content: "Hello!" }] });`
            })}
          </div>
        `
      },
      'responses': {
        category: 'API reference',
        title: 'Responses',
        subtitle: 'The OpenAI Responses format: one input field that takes a string or a list of items, and typed output items back.',
        toc: ['Request', 'Body fields', 'Response'],
        prev: { id: 'chat-completions', title: '/v1/chat/completions' },
        next: { id: 'messages', title: '/v1/messages' },
        content: () => `
          <div class="space-y-5 text-xs sm:text-sm text-sec">
            <div class="surface-card rounded-xl p-4 font-mono text-xs text-main"><span class="text-emerald-400 font-bold mr-2">POST</span> https://api.opencsn.com/v1/responses</div>
            ${renderDocCodeTabs({
              curl: `curl https://api.opencsn.com/v1/responses \\\n  -H "Authorization: Bearer $OpenCSN_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"model":"${getExampleModelId()}","input":"Say hello in one sentence."}'`,
              python: `response = client.responses.create(model="${getExampleModelId()}", input="Say hello in one sentence.")`,
              nodejs: `const response = await client.responses.create({ model: "${getExampleModelId()}", input: "Say hello in one sentence." });`
            })}
          </div>
        `
      },
      'messages': {
        category: 'API reference',
        title: 'Messages',
        subtitle: 'The Anthropic Messages format. Use it when your code or tool is built on the Anthropic SDK, including for models that are not Claude.',
        toc: ['Request', 'Body fields', 'Response'],
        prev: { id: 'responses', title: '/v1/responses' },
        next: { id: 'systemone', title: '/v1/systemone' },
        content: () => `
          <div class="space-y-5 text-xs sm:text-sm text-sec">
            <div class="surface-card rounded-xl p-4 font-mono text-xs text-main"><span class="text-emerald-400 font-bold mr-2">POST</span> https://api.opencsn.com/v1/messages</div>
            ${renderDocCodeTabs({
              curl: `curl https://api.opencsn.com/v1/messages \\\n  -H "x-api-key: $OpenCSN_API_KEY" \\\n  -H "anthropic-version: 2023-06-01" \\\n  -H "Content-Type: application/json" \\\n  -d '{"model":"${getExampleModelId()}","max_tokens":1024,"messages":[{"role":"user","content":"Hello!"}]}'`,
              python: `message = client.messages.create(model="${getExampleModelId()}", max_tokens=1024, messages=[{"role":"user","content":"Hello!"}])`,
              nodejs: `const message = await client.messages.create({ model: "${getExampleModelId()}", max_tokens: 1024, messages: [{ role: "user", content: "Hello!" }] });`
            })}
          </div>
        `
      },
      'systemone': {
        category: 'API reference',
        title: 'SystemOne',
        subtitle: 'Structured state-and-questions evaluation endpoint backed by the OpenCode System One vendor.',
        toc: ['Request', 'Body fields', 'Response'],
        prev: { id: 'messages', title: '/v1/messages' },
        next: { id: 'models-api', title: '/v1/models' },
        content: () => `
          <div class="space-y-5 text-xs sm:text-sm text-sec">
            <div class="surface-card rounded-xl p-4 font-mono text-xs text-main"><span class="text-emerald-400 font-bold mr-2">POST</span> https://api.opencsn.com/v1/systemone</div>
            <pre class="surface-card rounded-xl p-4 font-mono text-xs text-main overflow-x-auto">{\n  "model": "${getExampleModelId()}",\n  "state": "Need assistance connecting database.",\n  "questions": {\n    "urgency": { "type": "noul", "instructions": "Does this message express urgency?" }\n  }\n}</pre>
          </div>
        `
      },
      'models-api': {
        category: 'API reference',
        title: 'List models',
        subtitle: 'Every model the gateway routes, in the OpenAI list format that SDKs read, plus the fields a model picker or cost estimator needs.',
        toc: ['Request', 'Response', 'Pricing'],
        prev: { id: 'systemone', title: '/v1/systemone' },
        next: { id: 'me-api', title: '/v1/me' },
        content: () => `
          <div class="space-y-5 text-xs sm:text-sm text-sec">
            <div class="surface-card rounded-xl p-4 font-mono text-xs text-main"><span class="text-sky-400 font-bold mr-2">GET</span> https://api.opencsn.com/v1/models</div>
            <pre class="surface-card rounded-xl p-4 font-mono text-xs text-main overflow-x-auto">{\n  "object": "list",\n  "data": [\n    {\n      "id": "${getExampleModelId()}",\n      "object": "model",\n      "owned_by": "openai",\n      "context_length": 1048576\n    }\n  ]\n}</pre>
          </div>
        `
      },
      'me-api': {
        category: 'API reference',
        title: 'Account & key status',
        subtitle: 'Check what your key can still spend, your remaining balance, and active plan credits.',
        toc: ['Request', 'Response'],
        prev: { id: 'models-api', title: '/v1/models' },
        next: { id: 'errors', title: 'Errors' },
        content: () => `
          <div class="space-y-5 text-xs sm:text-sm text-sec">
            <div class="surface-card rounded-xl p-4 font-mono text-xs text-main"><span class="text-sky-400 font-bold mr-2">GET</span> https://api.opencsn.com/v1/me</div>
            <pre class="surface-card rounded-xl p-4 font-mono text-xs text-main overflow-x-auto">{\n  "object": "me",\n  "status": "active",\n  "balance_usd": "9.9980908468",\n  "available_usd": "9.9980908468",\n  "free_credit_usd": "4.68"\n}</pre>
          </div>
        `
      },
      'errors': {
        category: 'API reference',
        title: 'Errors',
        subtitle: 'Branch on the code field, not the message. Codes are stable; messages are written for people and can change.',
        toc: ['Error envelopes', 'Codes', 'Headers to read', 'Retrying'],
        prev: { id: 'me-api', title: '/v1/me' },
        next: { id: 'sdks', title: 'Official SDKs' },
        content: () => `
          <div class="space-y-4 text-xs text-sec">
            <table class="w-full text-left border-collapse">
              <thead><tr class="border-b border-subtle text-mut"><th class="py-2">Status</th><th class="py-2">Code</th><th class="py-2">Meaning</th></tr></thead>
              <tbody class="divide-y divide-white/5 font-mono">
                <tr><td class="py-2">400</td><td class="py-2 text-main">invalid_request</td><td class="py-2 font-sans">Malformed body or unsupported parameter.</td></tr>
                <tr><td class="py-2">401</td><td class="py-2 text-main">unauthorized</td><td class="py-2 font-sans">The key is missing, wrong, disabled or expired.</td></tr>
                <tr><td class="py-2">402</td><td class="py-2 text-main">insufficient_credit</td><td class="py-2 font-sans">The balance cannot cover this request. Top up on Billing.</td></tr>
                <tr><td class="py-2">429</td><td class="py-2 text-main">rate_limit_exceeded</td><td class="py-2 font-sans">Too many requests per minute.</td></tr>
                <tr><td class="py-2">502</td><td class="py-2 text-main">upstream_error</td><td class="py-2 font-sans">The provider returned an error; retry with backoff.</td></tr>
              </tbody>
            </table>
          </div>
        `
      },
      'sdks': {
        category: 'Guides',
        title: 'Official SDKs',
        subtitle: 'There is no OpenCSN SDK to install. The official OpenAI and Anthropic SDKs work once they point at the gateway.',
        toc: ['OpenAI SDK', 'Anthropic SDK', 'Other tools'],
        prev: { id: 'errors', title: 'Errors' },
        next: { id: 'streaming', title: 'Streaming' },
        content: () => `
          <div class="space-y-5 text-xs sm:text-sm text-sec">
            <pre class="surface-card rounded-xl p-4 font-mono text-xs text-main">export OPENAI_BASE_URL="https://api.opencsn.com/v1"\nexport OPENAI_API_KEY="$OpenCSN_API_KEY"\nexport ANTHROPIC_BASE_URL="https://api.opencsn.com"\nexport ANTHROPIC_API_KEY="$OpenCSN_API_KEY"</pre>
          </div>
        `
      },
      'streaming': {
        category: 'Guides',
        title: 'Streaming',
        subtitle: 'Add "stream": true to the body and the reply arrives as Server-Sent Events while it is generated.',
        toc: ['Events by endpoint', 'Chat Completions', 'Responses', 'Messages'],
        prev: { id: 'sdks', title: 'Official SDKs' },
        next: { id: 'models-guide', title: 'Models' },
        content: () => `
          <div class="space-y-5 text-xs sm:text-sm text-sec">
            ${renderDocCodeTabs({
              curl: `curl -N https://api.opencsn.com/v1/chat/completions \\\n  -H "Authorization: Bearer $OpenCSN_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"model":"${getExampleModelId()}","stream":true,"messages":[{"role":"user","content":"Count from one to five."}]}'`,
              python: `stream = client.chat.completions.create(model="${getExampleModelId()}", messages=[{"role":"user","content":"Count from one to five."}], stream=True)\nfor chunk in stream:\n    print(chunk.choices[0].delta.content or "", end="", flush=True)`,
              nodejs: `const stream = await client.chat.completions.create({ model: "${getExampleModelId()}", messages: [{ role: "user", content: "Count from one to five." }], stream: true });\nfor await (const chunk of stream) {\n  process.stdout.write(chunk.choices[0]?.delta?.content ?? "");\n}`
            })}
          </div>
        `
      },
      'models-guide': {
        category: 'Guides',
        title: 'Models',
        subtitle: 'Send any chat model ID below as the model field, on any chat endpoint. These tables are read from the gateway every minute.',
        toc: ['Model IDs', 'Choosing the endpoint for a model', 'Web search with :online'],
        prev: { id: 'streaming', title: 'Streaming' },
        next: { id: 'plans', title: 'Plans' },
        content: () => `
          <div class="overflow-x-auto text-xs">
            <table class="w-full text-left border-collapse">
              <thead><tr class="border-b border-subtle text-mut"><th class="py-2">Model ID</th><th class="py-2">Name</th><th class="py-2">Context</th><th class="py-2">Input / 1M</th><th class="py-2">Output / 1M</th><th class="py-2">Discount</th></tr></thead>
              <tbody class="divide-y divide-white/5 font-mono">
                ${MODELS_DATA.map(m => `
                  <tr>
                    <td class="py-2 text-main">${m.id} ${m.free ? `<span class="text-xs px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-sans">Free</span>` : ''}</td>
                    <td class="py-2 font-sans text-sec">${m.name}</td>
                    <td class="py-2 text-sec">${m.context}</td>
                    <td class="py-2 text-main">$${m.input.toFixed(2)}</td>
                    <td class="py-2 text-main">$${m.output.toFixed(2)}</td>
                    <td class="py-2 text-[#c87a51]">${m.discount}% off</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `
      },
      'plans': {
        category: 'Guides',
        title: 'Plans',
        subtitle: 'A plan is a set amount of usage for a fixed period, with higher rate limits while it runs.',
        toc: ['Plans at a glance', 'How the token estimate is worked out', 'Estimated tokens by model'],
        prev: { id: 'models-guide', title: 'Models' },
        next: { id: 'web-search', title: 'Web search' },
        content: () => `
          <div class="space-y-6 text-xs text-sec">
            <div class="overflow-x-auto">
              <table class="w-full text-left border-collapse">
                <thead>
                  <tr class="border-b border-subtle text-mut">
                    <th class="py-2 pr-3">Plan</th>
                    <th class="py-2 px-3">Price</th>
                    <th class="py-2 px-3">Usage included</th>
                    <th class="py-2 px-3">Free web search</th>
                    <th class="py-2 px-3">Requests per minute</th>
                    <th class="py-2 px-3">At once</th>
                    <th class="py-2 pl-3">Models</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-white/5">
                  <tr><td class="py-2.5 pr-3 font-semibold text-main">Go</td><td class="py-2.5 px-3 font-mono">$2 / 14 days</td><td class="py-2.5 px-3 font-mono">$10</td><td class="py-2.5 px-3">150 searches, 150 page reads</td><td class="py-2.5 px-3 font-mono">50</td><td class="py-2.5 px-3 font-mono">3</td><td class="py-2.5 pl-3">13 of 28</td></tr>
                  <tr><td class="py-2.5 pr-3 font-semibold text-main">Pro</td><td class="py-2.5 px-3 font-mono">$12 / 21 days</td><td class="py-2.5 px-3 font-mono">$48</td><td class="py-2.5 px-3">600 searches, 600 page reads</td><td class="py-2.5 px-3 font-mono">150</td><td class="py-2.5 px-3 font-mono">10</td><td class="py-2.5 pl-3">21 of 28</td></tr>
                  <tr><td class="py-2.5 pr-3 font-semibold text-main">Max</td><td class="py-2.5 px-3 font-mono">$20 / month</td><td class="py-2.5 px-3 font-mono">$75</td><td class="py-2.5 px-3">1,000 searches, 1,000 page reads</td><td class="py-2.5 px-3 font-mono">250</td><td class="py-2.5 px-3 font-mono">20</td><td class="py-2.5 pl-3">27 of 28</td></tr>
                  <tr><td class="py-2.5 pr-3 font-semibold text-main">Max 5×</td><td class="py-2.5 px-3 font-mono">$100 / month</td><td class="py-2.5 px-3 font-mono">$375</td><td class="py-2.5 px-3">5,000 searches, 5,000 page reads</td><td class="py-2.5 px-3 font-mono">1,250</td><td class="py-2.5 px-3 font-mono">50</td><td class="py-2.5 pl-3">27 of 28</td></tr>
                  <tr><td class="py-2.5 pr-3 font-semibold text-main">Max 10×</td><td class="py-2.5 px-3 font-mono">$200 / month</td><td class="py-2.5 px-3 font-mono">$750</td><td class="py-2.5 px-3">10,000 searches, 10,000 page reads</td><td class="py-2.5 px-3 font-mono">2,500</td><td class="py-2.5 px-3 font-mono">100</td><td class="py-2.5 pl-3">27 of 28</td></tr>
                </tbody>
              </table>
            </div>
            <h3 class="text-base font-semibold text-main pt-2">How the token estimate is worked out</h3>
            <div class="surface-card rounded-xl p-4 font-mono text-xs text-main">
              tokens = usage ÷ (0.95 × cached input price + 0.03 × input price + 0.02 × output price) × 1,000,000
            </div>
          </div>
        `
      },
      'web-search': {
        category: 'Guides',
        title: 'Web search',
        subtitle: 'Add :online to a model ID and the model can search the web while it answers, whatever model it is.',
        toc: ['Turn it on', 'How it works', 'What it costs', 'Limits', 'Errors'],
        prev: { id: 'plans', title: 'Plans' },
        next: { id: 'mcp', title: 'MCP server' },
        content: () => `
          <div class="space-y-5 text-xs sm:text-sm text-sec">
            <h3 class="text-base font-semibold text-main">The :online suffix</h3>
            <p>Append <code class="font-mono text-main">:online</code> to the model ID. Nothing else in the request changes, so it works from any client or SDK.</p>
            ${renderDocCodeTabs({
              curl: `curl https://api.opencsn.com/v1/chat/completions \\\n  -H "Authorization: Bearer $OpenCSN_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"model":"${getExampleModelId()}:online","messages":[{"role":"user","content":"What is the price of Antam gold per gram today? Cite sources."}]}'`,
              python: `completion = client.chat.completions.create(\n    model="${getExampleModelId()}:online",\n    messages=[{"role": "user", "content": "What is the price of Antam gold per gram today? Cite sources."}],\n)`,
              nodejs: `const completion = await client.chat.completions.create({\n  model: "${getExampleModelId()}:online",\n  messages: [{ role: "user", content: "What is the price of Antam gold per gram today? Cite sources." }],\n});`
            })}
          </div>
        `
      },
      'mcp': {
        category: 'Guides',
        title: 'MCP server',
        subtitle: 'OpenCSN is also an MCP server. Add it to Claude Code, Cursor or any client that speaks the Model Context Protocol, and the agent gets live web search and page reading.',
        toc: ['Connect', 'Claude Code', 'Cursor', 'VS Code', 'Tools', 'Billing'],
        prev: { id: 'web-search', title: 'Web search' },
        next: { id: 'billing', title: 'Billing and limits' },
        content: () => `
          <div class="space-y-5 text-xs sm:text-sm text-sec">
            <h3 class="text-base font-semibold text-main">Claude Code</h3>
            <div class="surface-card rounded-xl p-4 font-mono text-xs text-main flex items-center justify-between">
              <span>claude mcp add --transport http OpenCSN https://api.opencsn.com/v1/mcp --header "Authorization: Bearer $OpenCSN_API_KEY"</span>
              <button data-copy="claude mcp add --transport http OpenCSN https://api.opencsn.com/v1/mcp" onclick="copyFromAttr(this)" class="px-2.5 py-1 rounded surface-card text-sec ml-3 shrink-0">Copy</button>
            </div>
            <h3 class="text-base font-semibold text-main">Cursor (~/.cursor/mcp.json)</h3>
            <pre class="surface-card rounded-xl p-4 font-mono text-xs text-main overflow-x-auto">{\n  "mcpServers": {\n    "OpenCSN": {\n      "url": "https://api.opencsn.com/v1/mcp",\n      "headers": { "Authorization": "Bearer sk-opencsn-..." }\n    }\n  }\n}</pre>
          </div>
        `
      },
      'billing': {
        category: 'Guides',
        title: 'Billing and limits',
        subtitle: "You pay for the tokens a request actually used, at the model's listed price. What follows is the order in which that happens, and each point where a request can be refused.",
        toc: ['What happens to one request', 'Prices', 'Which credit pays', 'Limits that can refuse a request'],
        prev: { id: 'mcp', title: 'MCP server' },
        next: null,
        content: () => `
          <div class="space-y-5 text-xs sm:text-sm text-sec">
            <h3 class="text-base font-semibold text-main">What happens to one request</h3>
            <ol class="list-decimal pl-5 space-y-2">
              <li><b class="text-main">Reserve</b> — Before calling the model, the gateway sets aside the most the request could cost from your balance.</li>
              <li><b class="text-main">Call the model</b> — The request goes to the provider.</li>
              <li><b class="text-main">Settle</b> — When the answer is complete, the charge is recomputed from the real token counts in <code class="font-mono text-main">usage</code>.</li>
            </ol>
          </div>
        `
      }
    };

    function renderDocCodeTabs(snippets) {
      const active = state.docCodeLang || 'nodejs';
      const code = snippets[active] || snippets.nodejs;
      const safeAttr = code.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
      return `
        <div class="surface-card rounded-2xl overflow-hidden border border-subtle">
          <div class="px-4 py-2.5 border-b border-subtle flex items-center justify-between bg-black/20">
            <div class="flex items-center gap-4 text-xs">
              ${[
                { id: 'curl', label: 'cURL' },
                { id: 'python', label: 'Python' },
                { id: 'nodejs', label: 'Node.js' }
              ].map(t => `
                <button onclick="state.docCodeLang='${t.id}'; render();" class="pb-1 transition ${active === t.id ? 'text-main font-semibold border-b-2 border-[#c87a51]' : 'text-sec hover:text-main'}">${t.label}</button>
              `).join('')}
            </div>
            <button data-copy="${safeAttr}" onclick="copyFromAttr(this)" class="px-2.5 py-1 rounded surface-card text-xs text-sec hover:text-main">Copy</button>
          </div>
          <pre class="p-4 font-mono text-xs text-main overflow-x-auto leading-relaxed">${code}</pre>
        </div>
      `;
    }

    function renderDocsPage() {
      const isId = state.lang === 'id';
      const page = getDocPage(state.docSubpage, state.lang, MODELS_DATA, renderDocCodeTabs);
      const sidebarGroups = [
        {
          title: t('docs.startHere'),
          items: [
            { id: 'introduction', label: isId ? 'Pengenalan' : 'Introduction' },
            { id: 'quickstart', label: isId ? 'Memulai Cepat' : 'Quickstart' },
            { id: 'authentication', label: isId ? 'Autentikasi' : 'Authentication' }
          ]
        },
        {
          title: t('docs.apiReference'),
          items: [
            { id: 'chat-completions', badge: 'POST', badgeColor: 'text-emerald-400', label: '/v1/chat/completions' },
            { id: 'responses', badge: 'POST', badgeColor: 'text-emerald-400', label: '/v1/responses' },
            { id: 'messages', badge: 'POST', badgeColor: 'text-emerald-400', label: '/v1/messages' },
            { id: 'systemone', badge: 'POST', badgeColor: 'text-emerald-400', label: '/v1/systemone' },
            { id: 'models-api', badge: 'GET', badgeColor: 'text-sky-400', label: '/v1/models' },
            { id: 'me-api', badge: 'GET', badgeColor: 'text-sky-400', label: '/v1/me' },
            { id: 'errors', label: isId ? 'Penanganan Error' : 'Errors' }
          ]
        },
        {
          title: t('docs.guides'),
          items: [
            { id: 'sdks', label: isId ? 'Panduan SDK Resmi' : 'Official SDKs' },
            { id: 'streaming', label: isId ? 'Streaming Respons' : 'Streaming' },
            { id: 'models-guide', label: isId ? 'Panduan Model' : 'Models' },
            { id: 'plans', label: isId ? 'Paket Langganan' : 'Plans' },
            { id: 'web-search', label: isId ? 'Pencarian Web' : 'Web search' },
            { id: 'mcp', label: isId ? 'Server MCP' : 'MCP server' },
            { id: 'billing', label: isId ? 'Tagihan dan Batasan' : 'Billing and limits' }
          ]
        }
      ];

      return `
        <div class="h-screen flex flex-col overflow-hidden">
          <header class="shrink-0 z-30 surface-card border-b border-subtle px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
            <div class="flex items-center gap-3 shrink-0 flex-1 min-w-0">
              <button onclick="navigate('/')" class="flex items-center gap-2 font-semibold text-sm text-main hover:opacity-85 transition shrink-0">
                ${getOpenCSNLogo()}
                <span>OpenCSN</span>
              </button>
              <span class="text-xs px-2.5 py-0.5 rounded-full surface-card border border-white/10 text-sec font-mono shrink-0">${t('docs.badge')}</span>

              <div class="w-64 sm:w-80">
                <button onclick="openCommandPaletteModal()" class="w-full surface-card hover:border-white/20 rounded-xl px-3.5 py-1.5 flex items-center justify-between text-xs text-mut transition h-9 border border-subtle">
                  <div class="flex items-center gap-2 flex-nowrap truncate">
                    ${ico('search', 'w-3.5 h-3.5 text-mut shrink-0')}
                    <span class="truncate">${t('docs.searchPlaceholder')}</span>
                  </div>
                  <kbd class="text-xs font-mono px-1.5 py-0.5 rounded surface-card-solid border border-subtle text-sec shrink-0">Ctrl K</kbd>
                </button>
              </div>
            </div>

            <div class="flex items-center gap-3 text-xs shrink-0">
              <button onclick="navigate('/models')" class="text-sec hover:text-main px-2 py-1 transition">${t('nav.models')}</button>
              <button onclick="navigate('/dashboard')" class="surface-card hover:border-white/25 text-main px-3.5 py-1.5 rounded-xl font-medium transition shadow-sm border border-subtle">${t('nav.console')}</button>
              <button onclick="toggleTheme(event)" class="text-sec hover:text-main p-2 rounded-xl surface-card hover:border-white/20 transition flex items-center justify-center w-8 h-8 border border-subtle" title="${t('nav.toggleTheme')}">
                ${state.theme === 'dark' ? ico('sun', 'w-4 h-4') : ico('moon', 'w-4 h-4')}
              </button>
            </div>
          </header>

          <div class="flex-1 max-w-7xl w-full mx-auto grid grid-cols-1 lg:grid-cols-12 min-h-0 overflow-y-auto lg:overflow-hidden">
            <!-- Left Sidebar with Independent Scrollbar -->
            <aside class="lg:col-span-3 border-r border-subtle p-6 space-y-6 text-xs lg:h-full lg:overflow-y-auto custom-scrollbar select-none">
              ${sidebarGroups.map(g => `
                <div class="space-y-1.5">
                  <div class="text-xs font-semibold text-mut px-2.5 mb-2">${g.title}</div>
                  ${g.items.map(it => {
                    const isActive = state.docSubpage === it.id;
                    return `
                      <button onclick="navigate('/docs', '${it.id}')" class="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left transition ${isActive ? 'bg-[#c87a51]/15 text-[#df956d] font-medium border-l-2 border-[#c87a51]' : 'text-sec hover:text-main hover:bg-pill'}">
                        ${it.badge ? `<span class="font-mono text-xs font-bold ${it.badgeColor} w-8">${it.badge}</span>` : ''}
                        <span class="truncate">${it.label}</span>
                      </button>
                    `;
                  }).join('')}
                </div>
              `).join('')}
            </aside>

            <!-- Main Docs Content with Independent Scrollbar -->
            <main class="lg:col-span-7 p-6 sm:p-10 lg:p-12 space-y-6 lg:h-full lg:overflow-y-auto custom-scrollbar">
              <div>
                <div class="text-xs font-medium text-[#c87a51] font-sans">${page.category}</div>
                <h1 class="font-serif font-medium text-h1 text-main mt-1">${page.title}</h1>
                <p class="font-serif text-body text-sec mt-3 leading-relaxed max-w-[68ch]">${page.subtitle}</p>
              </div>
              <div class="pt-4 border-t border-subtle prose-serif text-sec">
                ${page.content()}
              </div>
              <div class="pt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                ${page.prev ? `
                  <button onclick="navigate('/docs', '${page.prev.id}')" class="surface-card hover:border-white/25 rounded-xl p-4 text-left transition">
                    <div class="text-xs text-mut">${t('docs.prev')}</div>
                    <div class="text-xs font-semibold text-main mt-1">${page.prev.title}</div>
                  </button>
                ` : `<div></div>`}
                ${page.next ? `
                  <button onclick="navigate('/docs', '${page.next.id}')" class="surface-card hover:border-white/25 rounded-xl p-4 text-right transition">
                    <div class="text-xs text-mut">${t('docs.next')}</div>
                    <div class="text-xs font-semibold text-main mt-1">${page.next.title}</div>
                  </button>
                ` : `<div></div>`}
              </div>
              <div class="pt-6 border-t border-subtle flex flex-wrap items-center justify-between gap-4 text-xs text-mut">
                <span>Something wrong or missing? Write to <a href="mailto:support@opencsn.com" class="text-main underline">support@opencsn.com</a>.</span>
                <button onclick="navigate('/llms-full.txt')" class="text-sec hover:text-main underline">Docs as plain text for LLMs</button>
              </div>
            </main>

            <!-- Right TOC with Independent Scrollbar -->
            <aside class="hidden lg:block lg:col-span-2 p-6 lg:p-8 text-xs space-y-2 lg:h-full lg:overflow-y-auto custom-scrollbar">
              <div class="text-xs font-semibold text-main">${t('docs.onThisPage')}</div>
              <ul class="space-y-2 text-sec border-l border-subtle pl-3">
                ${(page.toc || []).map((tok, i) => `
                  <li class="${i === 0 ? 'text-[#c87a51] font-medium' : 'hover:text-main'} cursor-pointer">${tok}</li>
                `).join('')}
              </ul>
            </aside>
          </div>
        </div>
      `;
    }

    function renderLlmsFullTxtPage() {
      const lines = [
        '# OpenCSN Indonesia',
        '',
        '> OpenCSN is an AI API gateway that gives one API key access to models from Anthropic (Claude), OpenAI (GPT), Google (Gemini), DeepSeek, Moonshot (Kimi), and more through OpenAI-compatible and Anthropic-compatible endpoints. Usage is prepaid and billed per token. OpenCSN keeps Zero Data Retention: prompts and completions are never stored, only request metadata.',
        '',
        'Website: https://opencsn.com',
        'API Base URL: https://api.opencsn.com/v1',
        'Support: support@opencsn.com',
        '',
        '## Models',
        ...MODELS_DATA.map(m => `- ${m.name} (${m.id}): $${m.input.toFixed(2)} in / $${m.output.toFixed(2)} out per 1M tokens, ${m.context} context${m.free ? ' [FREE]' : ''}`)
      ].join('\n');

      return `
        <div class="min-h-screen bg-[#0d0d0d] text-[#d4d4d4] p-6 font-mono text-xs">
          <div class="mb-4 flex items-center justify-between border-b border-white/10 pb-3">
            <span class="text-mut">opencsn.com/llms-full.txt</span>
            <button onclick="navigate('/docs', 'billing')" class="px-3 py-1 rounded bg-white/10 hover:bg-white/20 text-white text-xs font-sans">← Back to Docs</button>
          </div>
          <pre class="whitespace-pre-wrap leading-relaxed">${lines}</pre>
        </div>
      `;
    }

    // ============================================================================
    // CONSOLE LAYOUT SHELL (SIDEBAR + TOPBAR + POPOVERS + FOOTER)
    // ============================================================================
    function dismissLowBalanceBanner() {
      try { sessionStorage.setItem('opencsn_low_balance_dismissed', '1'); } catch (e) {}
      state.lowBalanceDismissed = true;
      render();
    }

    function renderLowBalanceBanner() {
      const isDismissed = state.lowBalanceDismissed || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('opencsn_low_balance_dismissed'));
      if (!state.billingSummary?.lowBalance || isDismissed) return '';
      return `
        <div class="p-3.5 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-200 text-xs flex items-center justify-between gap-3 shadow-sm">
          <div class="flex items-center gap-2.5">
            <span class="text-amber-400 shrink-0">${ico('shield-alert', 'w-4 h-4')}</span>
            <span>${t('billing.lowBalanceBanner')}</span>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <button onclick="navigate('/billing')" class="px-2.5 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 font-medium transition text-xs">${t('pricing.topupBtn')}</button>
            <button onclick="dismissLowBalanceBanner()" class="p-1 text-amber-400 hover:text-amber-200 transition" title="Dismiss">${ico('x', 'w-3.5 h-3.5')}</button>
          </div>
        </div>
      `;
    }

    function renderConsoleLayout(pageTitle, breadcrumb, contentHtml) {
      const isDev = !!(state.user && state.user.isDev);
      const userName = state.user?.name || (state.user ? state.user.email : 'User');
      const userEmail = state.user?.email || '';
      const userBalance = ((state.user?.balance ?? state.billingBalance) || 0).toFixed(2);
      const userInitial = state.user ? (userName.charAt(0).toUpperCase() || 'U') : 'U';

      const navSections = [
        { heading: 'Overview', items: [{ route: '/dashboard', label: 'Dashboard', icon: 'layout-dashboard' }] },
        { heading: 'Models', items: [
          { route: '/catalog', label: 'Models', icon: 'boxes' },
          { route: '/status', label: 'Status', icon: 'radio' },
          { route: '/leaderboard', label: 'Leaderboard', icon: 'trophy' }
        ]},
        { heading: 'Usage', items: [
          { route: '/usage', label: 'Usage', icon: 'chart-column' },
          { route: '/activity', label: 'Activity', icon: 'activity' },
          { route: '/request-logs', label: 'Request logs', icon: 'scroll-text' }
        ]},
        { heading: 'Developer', items: [
          { route: '/api-keys', label: 'API keys', icon: 'key-round' },
          { route: '/integrations', label: 'Integrations', icon: 'plug', matchPrefix: '/integrations' }
        ]},
        { heading: 'Billing', items: [
          { route: '/plan', label: 'Subscription', icon: 'circle-dollar-sign' },
          { route: '/billing', label: 'Billing', icon: 'credit-card' },
          { route: '/referral', label: 'Referral', icon: 'gift' }
        ]}
      ];

      if (isDev) {
        navSections.push({
          heading: 'Dev Console',
          items: [
            { route: '/management', label: 'Management', icon: 'sliders' },
            { route: '/structure', label: 'Structure', icon: 'layers' },
            { route: '/dev-logs', label: 'Logs', icon: 'terminal' }
          ]
        });
      }

      return `
        <div class="h-screen flex overflow-hidden">
          <!-- Desktop Sidebar with independent scrollbar -->
          <aside class="hidden md:flex w-56 shrink-0 border-r border-subtle surface-card-solid flex flex-col justify-between p-4 select-none h-screen overflow-y-auto custom-scrollbar">
            <div class="space-y-5">
              <button onclick="navigate('/')" class="flex items-center gap-2.5 px-2 py-1 font-semibold text-sm text-main">
                ${getOpenCSNLogo()}
                <span>OpenCSN</span>
              </button>
              <nav class="space-y-4 text-xs">
                ${navSections.map(sec => `
                  <div class="space-y-1">
                    <div class="text-xs font-medium text-mut px-2.5">${sec.heading}</div>
                    ${sec.items.map(item => {
                      const active = state.route === item.route || (item.matchPrefix && state.route.startsWith(item.matchPrefix));
                      return `
                        <button onclick="navigate('${item.route}')" class="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition ${active ? 'bg-[#c87a51]/20 text-[#df956d] font-medium border border-[#c87a51]/30' : 'text-sec hover:text-main hover:bg-white/5'}">
                          ${ico(item.icon, 'w-[18px] h-[18px]')}
                          <span>${item.label}</span>
                        </button>
                      `;
                    }).join('')}
                  </div>
                `).join('')}
              </nav>
            </div>
          </aside>

          <!-- Main Viewport Container -->
          <div class="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
            <header class="h-14 shrink-0 border-b border-subtle px-4 sm:px-6 flex items-center justify-between gap-4 surface-card z-30">
              <!-- Mobile Header (Device Mode Header matching Image 1) -->
              <div class="flex md:hidden items-center justify-between w-full">
                <button onclick="navigate('/')" class="flex items-center gap-2 font-semibold text-sm text-main">
                  ${getOpenCSNLogo()}
                  <span>OpenCSN</span>
                </button>

                <div class="flex items-center gap-2">
                  <button onclick="state.bellOpen = !state.bellOpen; state.userMenuOpen = false; render();" class="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-sec hover:text-main transition shadow-sm" title="Notifications">
                    ${ico('bell', 'w-4 h-4')}
                  </button>

                  <button onclick="toggleTheme(event)" class="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-sec hover:text-main transition shadow-sm" title="Toggle Theme">
                    ${state.theme === 'dark' ? ico('sun', 'w-4 h-4') : ico('moon', 'w-4 h-4')}
                  </button>

                  ${state.user ? `
                    <button onclick="state.userMenuOpen = !state.userMenuOpen; state.bellOpen = false; render();" class="w-9 h-9 rounded-full bg-[#3a2216] border border-[#c87a51]/50 text-[#df956d] font-semibold text-xs flex items-center justify-center hover:border-[#c87a51] transition shadow-inner" title="Balance & Account">
                      ${userInitial}
                    </button>
                  ` : `
                    <button onclick="navigate('/login')" class="bg-[#c87a51] hover:bg-[#df956d] text-[#14110f] font-semibold text-xs px-2.5 py-1.5 rounded-lg transition">
                      Sign in
                    </button>
                  `}
                </div>
              </div>

              <!-- Desktop Header: Search on Left, Docs Bell Theme and Account Capsule on Right matching Image 4 -->
              <div class="hidden md:flex items-center justify-between w-full gap-4">
                <!-- Left: Search pages... input matching Image 4 -->
                <div class="w-72 sm:w-80">
                  <button onclick="openCommandPaletteModal()" class="w-full surface-card hover:border-strong rounded-xl px-3.5 py-1.5 flex items-center justify-between text-xs text-mut border border-subtle transition shadow-sm h-9">
                    <div class="flex items-center gap-2 truncate">
                      ${ico('search', 'w-3.5 h-3.5 text-mut shrink-0')}
                      <span class="truncate">Search pages...</span>
                    </div>
                    <kbd class="text-xs font-mono px-1.5 py-0.5 rounded surface-card-solid border border-subtle text-sec shrink-0">Ctrl K</kbd>
                  </button>
                </div>

                <!-- Right: Docs, Notification Bell, Theme Toggle, Account Capsule / Sign In on Right matching Image 4 -->
                <div class="flex items-center gap-2.5 shrink-0 relative">
                  <button onclick="navigate('/docs', 'introduction')" class="surface-card hover:border-strong px-3 py-1.5 rounded-xl text-xs text-sec hover:text-main flex items-center gap-1.5 border border-subtle transition shadow-sm font-medium">
                    <span>${ico('book-open', 'w-3.5 h-3.5')}</span> <span>Docs</span>
                  </button>

                  <div class="relative">
                    <button onclick="state.bellOpen = !state.bellOpen; state.userMenuOpen = false; render();" class="w-8 h-8 rounded-xl surface-card border border-subtle flex items-center justify-center text-sec hover:text-main transition hover:border-strong shadow-sm" title="Announcements">
                      ${ico('bell', 'w-3.5 h-3.5')}
                    </button>
                    ${state.bellOpen ? `
                      <div class="absolute right-0 mt-2 w-80 surface-card-solid rounded-2xl shadow-2xl border border-strong p-4 z-50 space-y-3 animate-in fade-in zoom-in-95 duration-100">
                        <div class="flex items-center justify-between text-xs font-semibold text-main">
                          <span>Announcements</span>
                          <button onclick="navigate('/announcements')" class="text-xs text-[#df956d] hover:underline font-normal">View all</button>
                        </div>
                        <div class="divide-y divide-subtle text-xs space-y-2.5 max-h-64 overflow-y-auto custom-scrollbar">
                          ${(state.announcementsList || []).map(a => `
                            <div class="pt-2">
                              <div class="flex items-center gap-2">
                                <span class="text-xs px-2 py-0.5 rounded ${a.type === 'Promo' ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'}">${a.type}</span>
                                <span class="text-xs text-mut font-mono">${a.date}</span>
                              </div>
                              <div class="font-medium text-main mt-1">${a.title}</div>
                              <div class="text-xs text-sec line-clamp-2 mt-0.5 leading-relaxed">${a.body}</div>
                            </div>
                          `).join('')}
                        </div>
                      </div>
                    ` : ''}
                  </div>

                  <button onclick="toggleTheme(event)" class="w-8 h-8 rounded-xl surface-card border border-subtle hover:border-strong flex items-center justify-center text-sec hover:text-main transition shadow-sm" title="Toggle theme">
                    ${state.theme === 'dark' ? ico('sun', 'w-3.5 h-3.5') : ico('moon', 'w-3.5 h-3.5')}
                  </button>

                  <!-- Account Area -->
                  ${state.user ? `
                    <div class="relative">
                      <button onclick="state.userMenuOpen = !state.userMenuOpen; state.bellOpen = false; render();" class="surface-card hover:border-strong px-2.5 py-1 rounded-full text-xs flex items-center gap-2 transition border border-subtle shadow-sm">
                        <div class="w-5 h-5 rounded-full bg-[#df956d]/20 border border-[#df956d]/50 text-[#df956d] font-semibold text-xs flex items-center justify-center">${userInitial}</div>
                        <span class="font-medium text-main text-xs">${userName}</span>
                        <span class="text-mut text-xs">${ico('chevron-down', 'w-3 h-3')}</span>
                      </button>

                      ${state.userMenuOpen ? `
                        <div class="absolute right-0 mt-2 w-56 surface-card-solid rounded-2xl shadow-2xl border border-strong p-2 z-50 space-y-1 text-xs animate-in fade-in zoom-in-95 duration-100">
                          <div class="px-3 py-2 border-b border-subtle">
                            <div class="font-medium text-main">${userName}</div>
                            <div class="text-xs text-mut font-mono truncate">${userEmail}</div>
                            <div class="text-xs text-[#df956d] font-mono mt-1 font-semibold">Credits: $${userBalance}</div>
                          </div>
                          <button onclick="navigate('/billing'); state.userMenuOpen=false; render();" class="w-full text-left px-3 py-2 rounded-lg hover:bg-pill text-sec hover:text-main transition flex items-center gap-2">
                            ${ico('credit-card', 'w-3.5 h-3.5')} <span>Billing & Top-up</span>
                          </button>
                          <button onclick="navigate('/referral'); state.userMenuOpen=false; render();" class="w-full text-left px-3 py-2 rounded-lg hover:bg-pill text-sec hover:text-main transition flex items-center gap-2">
                            ${ico('gift', 'w-3.5 h-3.5')} <span>Referral program</span>
                          </button>
                          ${isDev ? `
                          <button onclick="navigate('/management'); state.userMenuOpen=false; render();" class="w-full text-left px-3 py-2 rounded-lg hover:bg-[#c87a51]/15 text-[#df956d] font-medium transition flex items-center gap-2">
                            ${ico('sliders', 'w-3.5 h-3.5')} <span>Dev Management</span>
                          </button>
                          ` : ''}
                          <div class="border-t border-subtle pt-1">
                            <button onclick="handleLogout()" class="w-full text-left px-3 py-2 rounded-lg hover:bg-rose-500/10 text-rose-400 transition flex items-center gap-2">
                              ${ico('log-out', 'w-3.5 h-3.5')} <span>Sign out</span>
                            </button>
                          </div>
                        </div>
                      ` : ''}
                    </div>
                  ` : `
                    <button onclick="navigate('/login')" class="surface-card hover:border-strong px-3 py-1.5 rounded-xl text-xs font-medium text-sec hover:text-main border border-subtle transition shadow-sm">
                      Sign in
                    </button>
                    <button onclick="navigate('/register')" class="bg-[#c87a51] hover:bg-[#df956d] text-[#14110f] font-semibold px-3 py-1.5 rounded-xl text-xs transition shadow-sm">
                      Sign up
                    </button>
                  `}
                </div>
              </div>
            </header>

            <!-- Mobile Popups for Bell & User menu -->
            ${state.bellOpen ? `
              <div class="md:hidden fixed inset-x-4 top-16 z-50 surface-card-solid rounded-2xl shadow-2xl border border-strong p-4 space-y-3">
                <div class="flex items-center justify-between text-xs font-semibold text-main">
                  <span>Announcements</span>
                  <button onclick="state.bellOpen = false; render();" class="text-sec hover:text-main">${ico('x', 'w-4 h-4')}</button>
                </div>
                <div class="divide-y divide-white/10 text-xs space-y-2.5">
                  <div class="pt-1">
                    <span class="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-300">Promo •</span>
                    <div class="font-medium text-main mt-1">Kompensasi Down</div>
                    <div class="text-xs text-mut">1 Nov 2026 · <span class="text-[#c87a51]">Ended</span></div>
                  </div>
                  <div class="pt-2.5">
                    <span class="text-xs px-2 py-0.5 rounded bg-sky-500/20 text-sky-300">News •</span>
                    <div class="font-medium text-main mt-1">GPT 6.1 Terra Available</div>
                    <div class="text-xs text-mut">29 Oct 2026 · <span class="text-[#c87a51]">Ended</span></div>
                  </div>
                </div>
                <button onclick="state.bellOpen = false; navigate('/announcements')" class="w-full pt-2 border-t border-subtle text-center text-xs text-sec hover:text-main">View all</button>
              </div>
            ` : ''}
            ${state.userMenuOpen ? `
              <div class="md:hidden fixed inset-x-4 top-16 z-50 surface-card-solid rounded-2xl shadow-2xl border border-strong p-4 space-y-3 text-xs">
                <div class="flex items-center justify-between border-b border-subtle pb-2">
                  <div>
                    <div class="font-semibold text-main">${userName}</div>
                    <div class="text-xs text-mut truncate">${userEmail}</div>
                    <div class="text-xs text-[#df956d] mt-1 font-mono">Balance: $${userBalance}</div>
                  </div>
                  <button onclick="state.userMenuOpen = false; render();" class="text-sec hover:text-main">${ico('x', 'w-4 h-4')}</button>
                </div>
                <button onclick="state.userMenuOpen = false; navigate('/billing')" class="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sec hover:text-main hover:bg-white/5 transition">
                  ${ico('credit-card', 'w-4 h-4')} <span>Billing & Balance</span>
                </button>
                <button onclick="state.userMenuOpen = false; navigate('/referral')" class="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sec hover:text-main hover:bg-white/5 transition">
                  ${ico('gift', 'w-4 h-4')} <span>Referral program</span>
                </button>
                ${isDev ? `
                <button onclick="state.userMenuOpen = false; navigate('/management')" class="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-[#df956d] hover:bg-[#c87a51]/15 font-medium transition">
                  ${ico('sliders', 'w-4 h-4')} <span>Dev Management</span>
                </button>
                ` : ''}
                <button onclick="state.userMenuOpen = false; handleLogout()" class="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-rose-400 hover:bg-rose-500/10 transition">
                  ${ico('log-out', 'w-4 h-4')} <span>Sign out</span>
                </button>
              </div>
            ` : ''}

            <!-- Scrollable Main Content Area with independent scrollbar -->
            <div class="flex-1 overflow-y-auto custom-scrollbar flex flex-col justify-between">
              <main class="flex-1 p-4 sm:p-6 md:p-8 max-w-6xl w-full mx-auto space-y-6 pb-24 md:pb-8">
                <div class="text-xs text-mut">Console <span class="mx-1 text-mut/60">/</span> <span class="text-sec">${breadcrumb}</span></div>
                ${renderLowBalanceBanner()}
                ${contentHtml}
              </main>

              <footer class="footer-scrim border-t border-[rgba(255,255,255,0.14)] px-8 py-4 flex items-center justify-between text-xs text-[var(--text-muted)] font-mono pb-20 md:pb-4 shrink-0">
                <span>© 2026 OpenCSN</span>
                <div class="flex items-center gap-4">
                  <button onclick="navigate('/docs', 'introduction')" class="text-[#EDE3DA] hover:text-white hover:underline transition">${t('footer.docs')}</button>
                  <button onclick="navigate('/status')" class="text-[#EDE3DA] hover:text-white hover:underline transition">API status</button>
                  <button onclick="navigate('/changelog')" class="text-[#EDE3DA] hover:text-white hover:underline transition">${t('footer.changelog')}</button>
                  <a href="/privacy" class="text-[#EDE3DA] hover:text-white hover:underline transition">${t('footer.privacy')}</a>
                  <a href="/terms" class="text-[#EDE3DA] hover:text-white hover:underline transition">${t('footer.terms')}</a>
                </div>
              </footer>
            </div>

            <!-- Mobile Bottom Navigation Bar (mode device matching Image 2) -->
            <nav class="md:hidden fixed bottom-0 left-0 right-0 z-40 surface-card-solid backdrop-blur-xl border-t border-subtle px-2 py-1.5 flex items-center justify-around shadow-2xl select-none">
              <!-- 1. Dashboard -->
              <button onclick="navigate('/dashboard')" class="flex flex-col items-center justify-center gap-1 py-1 px-2.5 rounded-xl transition ${state.route === '/dashboard' ? 'text-[#df956d]' : 'text-mut hover:text-sec'}">
                <span class="${state.route === '/dashboard' ? 'bg-[#3a2216] px-3.5 py-0.5 rounded-full text-[#df956d]' : ''}">${ico('layout-dashboard', 'w-5 h-5')}</span>
                <span class="text-xs font-medium ${state.route === '/dashboard' ? 'text-[#df956d]' : ''}">Dashboard</span>
              </button>

              <!-- 2. Models -->
              <button onclick="navigate('/catalog')" class="flex flex-col items-center justify-center gap-1 py-1 px-2.5 rounded-xl transition ${['/catalog', '/models', '/status', '/leaderboard'].includes(state.route) ? 'text-[#df956d]' : 'text-mut hover:text-sec'}">
                <span class="${['/catalog', '/models', '/status', '/leaderboard'].includes(state.route) ? 'bg-[#3a2216] px-3.5 py-0.5 rounded-full text-[#df956d]' : ''}">${ico('boxes', 'w-5 h-5')}</span>
                <span class="text-xs font-medium ${['/catalog', '/models', '/status', '/leaderboard'].includes(state.route) ? 'text-[#df956d]' : ''}">Models</span>
              </button>

              <!-- 3. API keys -->
              <button onclick="navigate('/api-keys')" class="flex flex-col items-center justify-center gap-1 py-1 px-2.5 rounded-xl transition ${state.route === '/api-keys' ? 'text-[#df956d]' : 'text-mut hover:text-sec'}">
                <span class="${state.route === '/api-keys' ? 'bg-[#3a2216] px-3.5 py-0.5 rounded-full text-[#df956d]' : ''}">${ico('key-round', 'w-5 h-5')}</span>
                <span class="text-xs font-medium ${state.route === '/api-keys' ? 'text-[#df956d]' : ''}">API keys</span>
              </button>

              <!-- 4. Billing -->
              <button onclick="navigate('/billing')" class="flex flex-col items-center justify-center gap-1 py-1 px-2.5 rounded-xl transition ${['/billing', '/plan', '/referral'].includes(state.route) ? 'text-[#df956d]' : 'text-mut hover:text-sec'}">
                <span class="${['/billing', '/plan', '/referral'].includes(state.route) ? 'bg-[#3a2216] px-3.5 py-0.5 rounded-full text-[#df956d]' : ''}">${ico('credit-card', 'w-5 h-5')}</span>
                <span class="text-xs font-medium ${['/billing', '/plan', '/referral'].includes(state.route) ? 'text-[#df956d]' : ''}">Billing</span>
              </button>

              <!-- 5. More -->
              <button onclick="openDeviceMoreModal()" class="flex flex-col items-center justify-center gap-1 py-1 px-2.5 rounded-xl transition ${['/management', '/structure', '/dev-logs', '/usage', '/activity', '/request-logs', '/integrations'].includes(state.route) ? 'text-[#df956d]' : 'text-mut hover:text-sec'}">
                <span class="${['/management', '/structure', '/dev-logs', '/usage', '/activity', '/request-logs', '/integrations'].includes(state.route) ? 'bg-[#3a2216] px-3.5 py-0.5 rounded-full text-[#df956d]' : ''}">${ico('more-horizontal', 'w-5 h-5')}</span>
                <span class="text-xs font-medium ${['/management', '/structure', '/dev-logs', '/usage', '/activity', '/request-logs', '/integrations'].includes(state.route) ? 'text-[#df956d]' : ''}">More</span>
              </button>
            </nav>
          </div>
        </div>
      `;
    }

    // ============================================================================
    // CONSOLE PAGES (DASHBOARD, CATALOG, STATUS, LEADERBOARD, USAGE, ACTIVITY, LOGS, KEYS, INTEGRATIONS, PLAN, BILLING, REFERRAL, ANNOUNCEMENTS)
    // ============================================================================
    function renderDashboardPage() {
      const d = state.dashboardData || {};
      const userBal = (d.balance !== undefined ? Number(d.balance) : ((state.user?.balance ?? state.billingBalance) || 0)).toFixed(2);
      const spentVal = d.spent ? Number(d.spent).toFixed(2) : '0.00';
      const reqCount = d.requests !== undefined ? d.requests.toLocaleString() : '0';
      const tokenCount = d.tokens !== undefined ? fmtTokens(d.tokens) : '0';

      const recentActivities = (Array.isArray(d.recentActivities) && d.recentActivities.length > 0)
        ? d.recentActivities
        : (state.paymentsList || []).slice(0, 4).map(p => ({
            date: p.date,
            type: p.status === 'Completed' ? 'Credit Top-up' : p.status,
            desc: `${p.gateway} (${p.id})`,
            amount: `+$${p.amount.toFixed(2)}`,
            color: p.status === 'Completed' ? 'text-emerald-400 bg-emerald-500/15' : 'text-amber-400 bg-amber-500/15'
          }));

      // Calculate 30-day bars
      const dailyMap = new Map((d.daily || []).map(r => [r.day, r]));
      const today = new Date();
      const bars = [];
      let maxReq = 1;
      for (let i = 29; i >= 0; i--) {
        const dt = new Date(today.getTime() - i * 86400000);
        const dayStr = dt.toISOString().slice(0, 10);
        const item = dailyMap.get(dayStr) || { requests: 0, tokens: 0, cost: '0' };
        if (item.requests > maxReq) maxReq = item.requests;
        bars.push({ day: dayStr, requests: item.requests, tokens: Number(item.tokens) || 0, cost: item.cost });
      }

      const html = `
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 class="font-serif font-medium text-h2 text-main">Dashboard</h1>
            <p class="text-xs text-sec mt-1">Monitor your usage, manage your API credentials, and get started with OpenCSN.</p>
          </div>
          <div class="surface-card rounded-xl p-1 flex items-center gap-1 text-xs">
            ${[
              { id: 'today', label: 'Today' },
              { id: '7d', label: '7 days' },
              { id: '30d', label: '30 days' },
              { id: 'all', label: 'All time' }
            ].map(t => `
              <button onclick="state.dashTimeRange='${t.id}'; render();" class="px-3 py-1.5 rounded-lg transition ${state.dashTimeRange === t.id ? 'bg-[#c87a51] text-[#14110f] font-semibold' : 'text-sec hover:text-main'}">${t.label}</button>
            `).join('')}
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div onclick="navigate('/billing')" class="surface-card hover:border-white/20 rounded-2xl p-5 flex items-center justify-between cursor-pointer transition">
            <div class="flex items-start gap-3.5">
              <div class="w-9 h-9 rounded-xl bg-[#c87a51]/15 text-[#c87a51] flex items-center justify-center">${ico('credit-card')}</div>
              <div>
                <div class="text-xs text-sec">Available balance</div>
                <div class="font-mono text-2xl font-semibold text-main mt-0.5">$${userBal}</div>
                <div class="text-xs text-mut mt-1">Add credit or redeem a code to start.</div>
              </div>
            </div>
            <span class="text-mut">›</span>
          </div>

          <div onclick="navigate('/usage')" class="surface-card hover:border-white/20 rounded-2xl p-5 flex items-center justify-between cursor-pointer transition">
            <div class="flex items-start gap-3.5">
              <div class="w-9 h-9 rounded-xl bg-white/5 text-sec flex items-center justify-center">${ico('trending-up')}</div>
              <div>
                <div class="text-xs text-sec">Spent</div>
                <div class="font-mono text-2xl font-semibold text-main mt-0.5">$${spentVal}</div>
              </div>
            </div>
            <span class="text-mut">›</span>
          </div>

          <div onclick="navigate('/request-logs')" class="surface-card hover:border-white/20 rounded-2xl p-5 flex items-center justify-between cursor-pointer transition">
            <div class="flex items-start gap-3.5">
              <div class="w-9 h-9 rounded-xl bg-white/5 text-sec flex items-center justify-center">${ico('arrow-left-right')}</div>
              <div>
                <div class="text-xs text-sec">Requests</div>
                <div class="font-mono text-2xl font-semibold text-main mt-0.5">${reqCount}</div>
              </div>
            </div>
            <span class="text-mut">›</span>
          </div>

          <div onclick="navigate('/usage')" class="surface-card hover:border-white/20 rounded-2xl p-5 flex items-center justify-between cursor-pointer transition">
            <div class="flex items-start gap-3.5">
              <div class="w-9 h-9 rounded-xl bg-white/5 text-sec flex items-center justify-center">${ico('zap')}</div>
              <div>
                <div class="text-xs text-sec">Tokens</div>
                <div class="font-mono text-2xl font-semibold text-main mt-0.5">${tokenCount}</div>
              </div>
            </div>
            <span class="text-mut">›</span>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div class="lg:col-span-7 surface-card rounded-2xl p-5 flex flex-col justify-between">
            <div>
              <div class="text-sm font-semibold text-main">Usage</div>
              <div class="text-xs text-sec mt-0.5">Requests per day, last 30 days. Hover a bar for its tokens and spend.</div>
            </div>
            <div class="mt-8 h-36 flex items-end gap-1.5 pt-6 border-b border-subtle">
              ${bars.map(b => {
                const heightPct = b.requests > 0 ? Math.max(6, Math.min(100, Math.round((b.requests / maxReq) * 100))) : 4;
                const bgClass = b.requests > 0 ? 'bg-[#c87a51]' : 'bg-white/10';
                const costFmt = Number(b.cost) > 0 ? ` · $${Number(b.cost).toFixed(4)}` : '';
                return `<div class="flex-1 ${bgClass} rounded-t transition hover:opacity-80" style="height: ${heightPct}%;" title="${b.day}: ${b.requests} req · ${fmtTokens(b.tokens)} tokens${costFmt}"></div>`;
              }).join('')}
            </div>
            <div class="flex items-center justify-between text-xs font-mono text-mut mt-2">
              <span>30 days ago</span>
              <span>Today</span>
            </div>
          </div>

          <div class="lg:col-span-5 surface-card rounded-2xl p-5 space-y-3">
            <div class="text-sm font-semibold text-main mb-2">Quick actions</div>
            <button onclick="navigate('/billing')" class="w-full surface-card hover:border-white/25 rounded-xl p-3.5 flex items-center justify-between text-xs text-main transition">
              <span class="flex items-center gap-2.5"><span>${ico('credit-card')}</span> <span>Add credit</span></span>
              <span class="text-mut">›</span>
            </button>
            <button onclick="navigate('/api-keys')" class="w-full surface-card hover:border-white/25 rounded-xl p-3.5 flex items-center justify-between text-xs text-main transition">
              <span class="flex items-center gap-2.5"><span>${ico('key-round')}</span> <span>Create API key</span></span>
              <span class="text-mut">›</span>
            </button>
            <button onclick="navigate('/docs', 'introduction')" class="w-full surface-card hover:border-white/25 rounded-xl p-3.5 flex items-center justify-between text-xs text-main transition">
              <span class="flex items-center gap-2.5"><span>${ico('book-open')}</span> <span>Read the docs</span></span>
              <span class="text-mut">›</span>
            </button>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div class="lg:col-span-7 surface-card rounded-2xl p-5 space-y-4">
            <div class="flex items-center justify-between">
              <div>
                <div class="text-sm font-semibold text-main flex items-center gap-2"><span>${ico('clock')}</span> Recent activity</div>
                <div class="text-xs text-sec mt-0.5">Balance movements in the last 30 days.</div>
              </div>
              <button onclick="navigate('/activity')" class="text-xs text-main hover:underline">View all →</button>
            </div>
            <table class="w-full text-left text-xs border-collapse">
              <thead>
                <tr class="border-b border-subtle text-mut">
                  <th class="py-2">Date</th>
                  <th class="py-2">Type</th>
                  <th class="py-2">Description</th>
                  <th class="py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${recentActivities.length > 0 ? recentActivities.map(act => `
                  <tr>
                    <td class="py-3 font-mono text-sec">${act.date}</td>
                    <td class="py-3"><span class="text-xs px-2 py-0.5 rounded ${act.color}">${act.type}</span></td>
                    <td class="py-3 font-mono text-main">${act.desc}</td>
                    <td class="py-3 font-mono text-emerald-400 text-right font-semibold">${act.amount}</td>
                  </tr>
                `).join('') : `
                  <tr>
                    <td colspan="4" class="py-8 text-center text-mut text-xs">Belum ada aktivitas transaksi.</td>
                  </tr>
                `}
              </tbody>
            </table>
          </div>

          <div class="lg:col-span-5 surface-card rounded-2xl p-5 space-y-3">
            <div>
              <div class="text-sm font-semibold text-main">API endpoint</div>
              <div class="text-xs text-sec mt-0.5">Point any OpenAI or Anthropic client here.</div>
            </div>
            <div class="surface-card-solid rounded-xl px-3.5 py-2.5 flex items-center justify-between font-mono text-xs text-main border border-subtle">
              <span>https://api.opencsn.com/v1</span>
              <button data-copy="https://api.opencsn.com/v1" onclick="copyFromAttr(this)" class="text-sec hover:text-main">${ico('copy')}</button>
            </div>
            <div class="divide-y divide-white/5 text-xs pt-1">
              <div class="py-2 flex items-center justify-between"><span class="text-sec">OpenAI Chat Completions</span><span class="font-mono text-xs text-main"><b class="text-mut mr-1">POST</b> /v1/chat/completions</span></div>
              <div class="py-2 flex items-center justify-between"><span class="text-sec">OpenAI Responses</span><span class="font-mono text-xs text-main"><b class="text-mut mr-1">POST</b> /v1/responses</span></div>
              <div class="py-2 flex items-center justify-between"><span class="text-sec">Anthropic Messages</span><span class="font-mono text-xs text-main"><b class="text-mut mr-1">POST</b> /v1/messages</span></div>
              <div class="py-2 flex items-center justify-between"><span class="text-sec">SystemOne</span><span class="font-mono text-xs text-main"><b class="text-mut mr-1">POST</b> /v1/systemone</span></div>
              <div class="py-2 flex items-center justify-between"><span class="text-sec">Model list</span><span class="font-mono text-xs text-main"><b class="text-mut mr-1">GET</b> /v1/models</span></div>
            </div>
          </div>
        </div>
      `;
      return renderConsoleLayout('Dashboard', 'Dashboard', html);
    }

    function formatCatalogPrice(val, isCache = false) {
      if (val === undefined || val === null || isNaN(val)) return '$0.00';
      if (isCache && Number(val) === 0) return t('common.free');
      const num = Number(val);
      const str = num.toString();
      if (str.includes('.')) {
        const dec = str.split('.')[1].length;
        if (dec <= 2) return '$' + num.toFixed(2);
        if (dec === 3) return '$' + num.toFixed(3);
        return '$' + num.toFixed(4).replace(/0+$/, '');
      }
      return '$' + num.toFixed(2);
    }

    function copyModelId(id, btn) {
      if (!id) return;
      navigator.clipboard.writeText(id);
      showToast('Copied ' + id + ' to clipboard');
      if (btn) {
        const orig = btn.innerHTML;
        btn.innerHTML = ico('check', 'w-3.5 h-3.5 text-emerald-500');
        setTimeout(() => {
          btn.innerHTML = orig;
        }, 1500);
      }
    }

    function toggleCatalogProviderCollapse(prov) {
      if (!state.collapsedCatalogProviders) state.collapsedCatalogProviders = {};
      state.collapsedCatalogProviders[prov] = !state.collapsedCatalogProviders[prov];
      render();
    }

function renderCatalogPage() {
      const filtered = MODELS_DATA.filter(m => {
        if (state.catalogFreeOnly && !m.free) return false;
        if (state.catalogSearch) {
          const q = state.catalogSearch.toLowerCase();
          if (!m.id.toLowerCase().includes(q) && !m.name.toLowerCase().includes(q) && !m.provider.toLowerCase().includes(q)) return false;
        }
        return true;
      });
      const providersOrder = ['Anthropic', 'OpenAI', 'Google', 'DeepSeek', 'Z.ai', 'Moonshot', 'Xiaomi', 'Meta', 'StepFun'];
      const totalModels = MODELS_DATA.length;
      const freeCount = MODELS_DATA.filter(m => m.free).length;

      const html = `
        <div class="space-y-6 pb-12">
          <!-- Page Title & Subtitle matching Dark/White screenshots -->
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 class="font-serif font-medium text-h2 text-main">Models</h1>
              <p class="text-xs sm:text-sm text-sec mt-1">Models your account can route to, priced per million tokens.</p>
            </div>
            <div class="realtime-catalog-indicator">
              ${renderRealtimeIndicator()}
            </div>
          </div>

          <!-- Top 4 Stat Cards Row -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <!-- Card 1: Models 28 -->
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-subtle flex flex-col justify-between shadow-sm">
              <div class="flex items-start gap-3.5">
                <div class="w-10 h-10 rounded-xl bg-amber-500/10 dark:bg-[#2e1d14] border border-amber-500/25 dark:border-[#c87a51]/30 flex items-center justify-center shrink-0 text-[#c87a51] dark:text-[#df956d]">
                  ${ico('cluster', 'w-5 h-5')}
                </div>
                <div>
                  <div class="text-xs text-mut font-medium">Models</div>
                  <div class="text-xl sm:text-2xl font-bold text-main mt-0.5 tracking-tight">${totalModels}</div>
                </div>
              </div>
              <div class="text-xs text-mut mt-3 sm:mt-4 font-normal">Available to route right now.</div>
            </div>

            <!-- Card 2: Saving Up to 70% -->
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-subtle flex flex-col justify-between shadow-sm">
              <div class="flex items-start gap-3.5">
                <div class="w-10 h-10 rounded-xl bg-emerald-500/10 dark:bg-emerald-950/40 border border-emerald-500/25 dark:border-emerald-500/25 flex items-center justify-center shrink-0 text-emerald-600 dark:text-emerald-400">
                  ${ico('percent', 'w-5 h-5')}
                </div>
                <div>
                  <div class="text-xs text-mut font-medium">Saving</div>
                  <div class="text-xl sm:text-2xl font-bold text-main mt-0.5 tracking-tight">Up to 70%</div>
                </div>
              </div>
              <div class="text-xs text-mut mt-3 sm:mt-4 font-normal">Compared to official pricing.</div>
            </div>

            <!-- Card 3: Your limits 50 RPM -->
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-subtle flex flex-col justify-between shadow-sm">
              <div class="flex items-start gap-3.5">
                <div class="w-10 h-10 rounded-xl bg-blue-500/10 dark:bg-blue-950/40 border border-blue-500/25 dark:border-blue-500/25 flex items-center justify-center shrink-0 text-blue-600 dark:text-sky-400">
                  ${ico('gauge', 'w-5 h-5')}
                </div>
                <div>
                  <div class="text-xs text-mut font-medium">Your limits</div>
                  <div class="text-xl sm:text-2xl font-bold text-main mt-0.5 tracking-tight">50 RPM</div>
                </div>
              </div>
              <div class="text-xs text-mut mt-3 sm:mt-4 font-normal">10 concurrent requests.</div>
            </div>

            <!-- Card 4: Data retention Zero (ZDR) -->
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-subtle flex flex-col justify-between shadow-sm">
              <div class="flex items-start gap-3.5">
                <div class="w-10 h-10 rounded-xl bg-stone-500/10 dark:bg-white/5 border border-stone-400/20 dark:border-white/10 flex items-center justify-center shrink-0 text-stone-600 dark:text-gray-400">
                  ${ico('shield', 'w-5 h-5')}
                </div>
                <div>
                  <div class="text-xs text-mut font-medium">Data retention</div>
                  <div class="text-xl sm:text-2xl font-bold text-main mt-0.5 tracking-tight">Zero (ZDR)</div>
                </div>
              </div>
              <div class="text-xs text-mut mt-3 sm:mt-4 font-normal">Not stored.</div>
            </div>
          </div>

          <!-- Free credit Horizontal Card -->
          <div class="surface-card rounded-2xl p-4 sm:p-5 border border-subtle flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm">
            <div>
              <div class="text-sm font-semibold text-main">Free credit</div>
              <div class="text-xs text-mut mt-1">
                Pays for models tagged <span class="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-medium text-xs font-sans inline-block">Free</span> below, before your balance is touched. It does not work on other models.
              </div>
            </div>
            <div class="text-2xl sm:text-3xl font-bold font-mono tracking-tight text-[#c87a51] dark:text-[#df956d] shrink-0">
              $0.06
            </div>
          </div>

          <!-- Main Models Table Card Container -->
          <div class="surface-card rounded-2xl border border-subtle p-5 sm:p-6 space-y-4 shadow-sm">
            <!-- Card Header: Title + Subtitle on Left, Controls on Right -->
            <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 class="text-base sm:text-lg font-semibold text-main tracking-tight">Models</h2>
                <p class="text-xs text-mut mt-0.5">Cache reads and writes are billed at their own rates: see the request logs for what each call actually cost.</p>
              </div>
              <div class="flex items-center gap-2.5 shrink-0">
                <button onclick="state.catalogFreeOnly = !state.catalogFreeOnly; render();" class="px-3 py-1.5 rounded-xl text-xs font-medium border transition flex items-center gap-2 ${state.catalogFreeOnly ? 'bg-[#c87a51]/20 border-[#c87a51]/60 text-[#c87a51] dark:text-[#df956d]' : 'surface-card-solid border-subtle text-sec hover:text-main hover:border-strong'}">
                  <span class="text-[#c87a51] dark:text-[#df956d]">${ico('gift', 'w-3.5 h-3.5')}</span>
                  <span>Free only</span>
                  <span class="text-xs text-mut font-mono">${freeCount}</span>
                </button>
                <div class="relative w-48 sm:w-56">
                  <input type="text" placeholder="Filter models..." value="${state.catalogSearch || ''}" oninput="state.catalogSearch = this.value; render();" class="w-full surface-card-solid border border-subtle rounded-xl px-3 py-1.5 pl-8 text-xs text-main placeholder:text-mut focus:outline-none focus:border-[#c87a51]/60 transition" />
                  <span class="absolute left-2.5 top-2 text-mut">${ico('search', 'w-3.5 h-3.5')}</span>
                </div>
              </div>
            </div>

            <!-- Web Search Callout Notice -->
            <div class="flex items-start gap-2.5 text-xs pt-1">
              <span class="text-[#c87a51] dark:text-[#df956d] mt-0.5 shrink-0">${ico('globe', 'w-4 h-4')}</span>
              <div class="space-y-1">
                <div class="text-sec">
                  Add <code class="font-mono text-main font-semibold">:online</code> to a model ID to let it search and read the web.
                </div>
                <div class="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-xs text-mut">
                  <span class="text-main font-semibold">${getExampleModelId()}:online</span>
                  <span>$0.005 per search</span>
                  <span>$0.0001 per page read</span>
                  <a href="javascript:void(0)" onclick="navigate('/docs', 'web-search')" class="underline text-sec hover:text-main font-sans">How it works</a>
                </div>
              </div>
            </div>

            <!-- Data Table Container with Horizontal Scroll -->
            <div class="overflow-x-auto custom-scrollbar -mx-5 sm:mx-0 pt-2">
              <table class="w-full text-left text-xs border-collapse">
                <thead>
                  <tr class="border-b border-subtle text-mut text-xs font-normal">
                    <th class="py-3 px-4 text-left font-normal min-w-[300px]">Model</th>
                    <th class="py-3 px-3 text-right font-normal w-24">Context</th>
                    <th class="py-3 px-3 text-right font-normal w-28">Input / 1M</th>
                    <th class="py-3 px-3 text-right font-normal w-28">Output / 1M</th>
                    <th class="py-3 px-3 text-right font-normal w-32">Cache read / 1M</th>
                    <th class="py-3 px-3 text-right font-normal w-32">Cache write / 1M</th>
                  </tr>
                </thead>
                <tbody>
                  ${providersOrder.map(prov => {
                    const pModels = filtered.filter(m => m.provider === prov);
                    if (pModels.length === 0) return '';
                    const isCollapsed = state.collapsedCatalogProviders && state.collapsedCatalogProviders[prov];

                    return `
                      <!-- Provider Section Header Matching Image -->
                      <tr onclick="toggleCatalogProviderCollapse('${prov}')" class="cursor-pointer group select-none hover:bg-pill transition-colors">
                        <td colspan="6" class="pt-5 pb-2 px-4">
                          <div class="flex items-center gap-2.5">
                            <span class="text-mut group-hover:text-main transition-transform duration-150 ${isCollapsed ? '-rotate-90' : 'rotate-0'}">
                              ${ico('chevron-down', 'w-3.5 h-3.5')}
                            </span>
                            <div class="w-5 h-5 rounded surface-card-solid border border-subtle flex items-center justify-center p-0.5 shrink-0 text-main shadow-sm">
                              ${getProviderIcon(prov, 'w-3.5 h-3.5')}
                            </div>
                            <div class="flex items-baseline gap-2">
                              <span class="font-semibold text-main text-xs sm:text-sm tracking-tight">${prov}</span>
                              <span class="text-xs text-mut font-normal font-sans">${pModels.length} models</span>
                            </div>
                          </div>
                        </td>
                      </tr>

                      ${!isCollapsed ? pModels.map(m => `
                        <!-- Model Row Matching Image -->
                        <tr class="border-b border-subtle/40 hover:bg-pill transition-colors group">
                          <td class="py-3 px-4 font-mono text-left">
                            <div class="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                              <span onclick="navigate('/models/' + '${m.id}')" class="text-main hover:text-[#c87a51] dark:hover:text-[#df956d] transition-colors cursor-pointer text-xs sm:text-[13px] font-mono tracking-tight font-normal">
                                ${m.id}
                              </span>
                              <button onclick="copyModelId('${m.id}', this)" class="text-mut hover:text-main transition p-0.5 rounded" title="Copy model ID">
                                ${ico('copy', 'w-3.5 h-3.5')}
                              </button>
                              ${m.vision ? `
                                <span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md surface-card-solid border border-subtle text-xs text-sec font-sans">
                                  ${ico('eye', 'w-3 h-3 text-mut')} <span>Vision</span>
                                </span>
                              ` : ''}
                              ${m.reasoning ? `
                                <span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md surface-card-solid border border-subtle text-xs text-sec font-sans">
                                  ${ico('brain', 'w-3 h-3 text-mut')} <span>Reasoning</span>
                                </span>
                              ` : ''}
                              ${m.free ? `
                                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 text-xs font-sans font-medium">
                                  Free
                                </span>
                              ` : ''}
                              ${m.fp8 ? `
                                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md surface-card-solid border border-subtle text-xs text-sec font-sans">
                                  ${ico('zap', 'w-2.5 h-2.5 text-mut')} FP8
                                </span>
                              ` : ''}
                              ${m.active === false ? `
                                <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-400 text-xs font-sans">
                                  Hidden
                                </span>
                              ` : ''}
                            </div>
                          </td>
                          <td class="py-3 px-3 font-mono text-xs sm:text-[13px] text-sec text-right">${m.context}</td>
                          <td class="py-3 px-3 font-mono text-xs sm:text-[13px] font-normal text-main text-right">${formatCatalogPrice(m.input)}</td>
                          <td class="py-3 px-3 font-mono text-xs sm:text-[13px] font-normal text-main text-right">${formatCatalogPrice(m.output)}</td>
                          <td class="py-3 px-3 font-mono text-xs sm:text-[13px] font-normal text-sec text-right">${formatCatalogPrice(m.cacheRead, true)}</td>
                          <td class="py-3 px-3 font-mono text-xs sm:text-[13px] font-normal text-sec text-right">${formatCatalogPrice(m.cacheWrite, true)}</td>
                        </tr>
                      `).join('') : ''}
                    `;
                  }).join('')}
                  ${filtered.length === 0 ? `
                    <tr>
                      <td colspan="6" class="py-12 text-center text-mut text-xs">
                        <div>No models found matching your search.</div>
                        <button onclick="state.catalogSearch=''; state.catalogFreeOnly=false; render();" class="mt-2 text-[#c87a51] dark:text-[#df956d] hover:underline font-mono">Reset filters</button>
                      </td>
                    </tr>
                  ` : ''}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      `;
      return renderConsoleLayout('Models', 'Models', html);
    }


    function renderLeaderboardPage() {
      const currentRange = state.leaderboardRange || '7d';
      const rows = state.leaderboardData || [];

      const rowsHtml = rows.length === 0 ? `
        <tr>
          <td colspan="7" class="py-12 text-center text-mut text-xs font-sans">
            Belum ada data penggunaan model untuk periode ini.
          </td>
        </tr>
      ` : rows.map((item, idx) => {
        const rank = idx + 1;
        const isTop3 = rank <= 3;
        const rankColor = isTop3 ? 'text-[#c87a51]' : 'text-mut';
        const rateText = item.successRateFormatted || (item.successRate !== undefined ? item.successRate + '%' : '100%');
        const latText = item.latencyFormatted || (item.averageLatency !== null && item.averageLatency !== undefined ? item.averageLatency + ' ms' : '—');

        return `
          <tr class="hover:bg-white/[0.02] dark:hover:bg-white/[0.02] transition border-b border-subtle last:border-0" data-model-id="${item.id}">
            <td class="py-4 pl-6 pr-3 font-mono font-bold text-sm ${rankColor} w-12 text-center">
              ${rank}
            </td>
            <td class="py-4 px-4 min-w-[200px]">
              <div class="font-medium font-mono text-main hover:text-[#c87a51] transition cursor-pointer" onclick="navigate('/models/${item.id}')">
                ${item.displayName || item.name || item.id}
              </div>
              <div class="flex items-center gap-1.5 text-xs text-sec mt-1">
                <span class="shrink-0">${getProviderIcon(item.provider, 'w-3.5 h-3.5')}</span>
                <span>${item.provider}</span>
                <span class="text-mut font-mono text-[11px]">· ${item.id}</span>
              </div>
            </td>
            <td class="py-4 px-4 text-right">
              <div class="font-mono font-medium text-main text-xs sm:text-sm">${item.tokensFormatted || item.tokens}</div>
            </td>
            <td class="py-4 px-4 text-right">
              <div class="font-mono text-xs sm:text-sm text-sec">${item.requestsFormatted || item.req}</div>
            </td>
            <td class="py-4 px-4 text-right">
              <div class="font-mono text-xs sm:text-sm text-emerald-400 font-medium">${rateText}</div>
            </td>
            <td class="py-4 px-4 text-right">
              <div class="font-mono text-xs sm:text-sm text-sec">${latText}</div>
            </td>
            <td class="py-4 pl-4 pr-6 text-right min-w-[140px]">
              <div class="flex items-center justify-end gap-3">
                <span class="font-mono text-xs text-sec w-12 text-right">${item.share}%</span>
                <div class="w-16 sm:w-24 h-1.5 rounded-full bg-pill overflow-hidden shrink-0 border border-subtle">
                  <div class="h-full bg-[#c87a51] rounded-full transition-all duration-300" style="width: ${Math.min(100, Math.max(2, item.share))}%;"></div>
                </div>
              </div>
            </td>
          </tr>
        `;
      }).join('');

      const html = `
        <div class="space-y-6 leaderboard-container">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 class="font-serif font-medium text-h2 text-main">Leaderboard</h1>
              <p class="text-xs text-sec mt-1">Which models carry the most traffic, by tokens processed.</p>
            </div>
            <div class="realtime-catalog-indicator" data-timestamp="${state.leaderboardLastUpdated}">
              ${renderRealtimeIndicator(state.leaderboardLastUpdated)}
            </div>
          </div>

          <div class="surface-card rounded-2xl border border-subtle overflow-hidden">
            <div class="p-5 sm:p-6 border-b border-subtle flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div class="text-base font-semibold text-main">Top models</div>
                <div class="text-xs text-sec mt-0.5">Ranked by tokens processed from live LiteLLM telemetry.</div>
              </div>
              <div class="inline-flex items-center p-1 rounded-xl bg-inset border border-subtle text-xs shrink-0">
                <button onclick="state.leaderboardRange='today'; loadLeaderboardData();" class="px-3 py-1.5 rounded-lg font-medium transition ${currentRange === 'today' ? 'bg-[#c87a51] text-white shadow-sm' : 'text-sec hover:text-main'}">Today</button>
                <button onclick="state.leaderboardRange='7d'; loadLeaderboardData();" class="px-3 py-1.5 rounded-lg font-medium transition ${currentRange === '7d' ? 'bg-[#c87a51] text-white shadow-sm' : 'text-sec hover:text-main'}">7 days</button>
                <button onclick="state.leaderboardRange='30d'; loadLeaderboardData();" class="px-3 py-1.5 rounded-lg font-medium transition ${currentRange === '30d' ? 'bg-[#c87a51] text-white shadow-sm' : 'text-sec hover:text-main'}">30 days</button>
              </div>
            </div>

            <div class="overflow-x-auto custom-scrollbar">
              <table class="w-full text-left text-xs sm:text-sm">
                <thead>
                  <tr class="border-b border-subtle bg-white/[0.01] text-xs text-mut font-medium uppercase font-mono">
                    <th class="py-3 pl-6 pr-3 text-center w-12">#</th>
                    <th class="py-3 px-4">Model</th>
                    <th class="py-3 px-4 text-right">Tokens</th>
                    <th class="py-3 px-4 text-right">Requests</th>
                    <th class="py-3 px-4 text-right">${t('leaderboard.thSuccessRate') || 'Success'}</th>
                    <th class="py-3 px-4 text-right">${t('leaderboard.thAvgLatency') || 'Latency'}</th>
                    <th class="py-3 pl-4 pr-6 text-right">Share</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-subtle">
                  ${rowsHtml}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      `;

      return renderConsoleLayout('Leaderboard', 'Leaderboard', html);
    }

    // DESIGN LOCKED: matches the reference Status table. Do not change layout, spacing, columns, or sort order without an explicit design request.
    function renderStatusPage() {
      const is7d = state.statusRange === '7d';
      const statusInfo = state.statusData || { litellm: 'down', models: [] };
      const modelsList = statusInfo.models || [];
      const isGatewayUp = statusInfo.litellm === 'up';

      const totalModels = modelsList.length;
      const outages = modelsList.filter(m => m.hasTraffic && m.status === 'outage');
      const degraded = modelsList.filter(m => m.hasTraffic && m.status === 'degraded');
      const operationalCount = modelsList.filter(m => m.hasTraffic && m.status === 'operational').length;

      let headline = 'All models operational';
      let dotColor = '#4ADE9A';
      if (outages.length > 0) {
        headline = `${outages.length} ${outages.length === 1 ? 'model is' : 'models are'} down`;
        dotColor = '#F4795C';
      } else if (degraded.length > 0) {
        headline = `${degraded.length} ${degraded.length === 1 ? 'model is' : 'models are'} degraded`;
        dotColor = '#F7C04A';
      }

      const uptimeNumbers = modelsList
        .filter(m => m.hasTraffic && m.successRate)
        .map(m => parseFloat(String(m.successRate).replace('%', '')))
        .filter(n => Number.isFinite(n));

      const avgUptimeNum = uptimeNumbers.length > 0
        ? uptimeNumbers.reduce((a, b) => a + b, 0) / uptimeNumbers.length
        : 79.60;
      const overallUptime = `${avgUptimeNum.toFixed(2)}%`;

      const secondsAgo = Math.max(0, Math.floor((Date.now() - (state.statusLastUpdated || (Date.now() - 3000))) / 1000));
      const refreshIn = Math.max(1, 15 - (secondsAgo % 15));
      const subtext = `Updated ${secondsAgo}s ago · refreshes in ${refreshIn}s`;

      const html = `
        <div class="space-y-6">
          <!-- 1. Header -->
          <div class="space-y-1">
            <nav class="flex items-center gap-2 text-xs font-mono" aria-label="Breadcrumb">
              <button onclick="navigate('/dashboard')" class="text-[var(--text-muted)] hover:text-[var(--text-accent)] transition">Console</button>
              <span class="text-[var(--text-muted)]">/</span>
              <span class="text-[var(--text-primary)] font-medium">Status</span>
            </nav>
            <h1 class="font-serif font-bold text-3xl sm:text-4xl text-[var(--text-primary)] tracking-tight">Status</h1>
            <p class="text-sm text-[var(--text-secondary)] mt-1">Live availability for every model you can route to.</p>
          </div>

          <!-- 2. Summary card (one wide rounded card, single row) -->
          <div class="status-surface-card rounded-2xl p-5 sm:p-6 border border-[rgba(255,255,255,0.08)] shadow-lg flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <!-- Left: Colored Status Dot + Headline + Subtext + Compact Gateway Chip -->
            <div class="space-y-2">
              <div class="flex flex-wrap items-center gap-3">
                <span class="w-3 h-3 rounded-full shrink-0" style="background-color: ${dotColor}; box-shadow: 0 0 10px ${dotColor}88;"></span>
                <span class="text-xl sm:text-2xl font-bold text-[var(--text-primary)] tracking-tight">${headline}</span>
                <span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-mono font-medium ${isGatewayUp ? 'bg-[#4ADE9A]/10 border border-[#4ADE9A]/30 text-[#4ADE9A]' : 'bg-[#F4795C]/10 border border-[#F4795C]/30 text-[#F4795C]'} shrink-0">
                  <span class="w-1.5 h-1.5 rounded-full ${isGatewayUp ? 'bg-[#4ADE9A]' : 'bg-[#F4795C]'}"></span>
                  <span>Gateway: ${isGatewayUp ? 'Operational' : 'Offline'}</span>
                </span>
              </div>
              <div class="text-xs text-[var(--text-muted)] font-mono pl-6">
                ${subtext}
              </div>
            </div>

            <!-- Right: Stat Blocks + Dividers + Outlined Refresh Button -->
            <div class="flex flex-wrap items-center gap-5 sm:gap-6 shrink-0">
              <div class="space-y-0.5">
                <div class="text-xs font-mono uppercase tracking-wider text-[var(--text-muted)]">Operational</div>
                <div class="text-xl sm:text-2xl font-bold font-mono tabular-nums text-[var(--text-primary)]">${operationalCount} / ${totalModels}</div>
              </div>

              <div class="w-[1px] h-10 bg-[rgba(255,255,255,0.12)] hidden sm:block"></div>

              <div class="space-y-0.5">
                <div class="text-xs font-mono uppercase tracking-wider text-[var(--text-muted)]">Uptime · ${is7d ? '7 days' : '24 hours'}</div>
                <div class="text-xl sm:text-2xl font-bold font-mono tabular-nums text-[var(--text-primary)]">${overallUptime}</div>
              </div>

              <div class="w-[1px] h-10 bg-[rgba(255,255,255,0.12)] hidden sm:block"></div>

              <button onclick="loadStatusData(); showToast('Status refreshed');" class="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-[rgba(255,255,255,0.14)] bg-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.25)] text-[var(--text-primary)] hover:text-white transition font-medium text-xs shrink-0 shadow-sm" title="Refresh status telemetry">
                ${ico('rotate-cw', 'w-3.5 h-3.5')}
                <span>Refresh</span>
              </button>
            </div>
          </div>

          <!-- 3. Models Card (with title, toggle, legends, and locked table) -->
          ${renderStatusTableCard(modelsList, { getProviderIcon, is7d })}
        </div>
      `;

      return renderConsoleLayout('Status', 'Status', html);
    }
    function renderUsagePage() {
      state.usageRange = state.usageRange || '30d';
      state.usageModelSearch = state.usageModelSearch || '';

      const allMakers = state.usageRecords || [];

      // Filtering by search query
      const query = (state.usageModelSearch || '').toLowerCase().trim();
      const filteredMakers = allMakers.map(maker => {
        const makerMatches = maker.name.toLowerCase().includes(query) || maker.provider.toLowerCase().includes(query);
        const filteredModels = maker.models.filter(m => makerMatches || m.id.toLowerCase().includes(query));
        return {
          ...maker,
          models: filteredModels
        };
      }).filter(maker => maker.models.length > 0);

      // Compute totals across visible models
      let totalTokens = 0;
      let totalRequests = 0;
      let totalSpent = 0;
      let totalCache = 0;
      let visibleModelsCount = 0;

      filteredMakers.forEach(maker => {
        maker.models.forEach(m => {
          visibleModelsCount += 1;
          totalTokens += m.rawTokens;
          totalRequests += m.rawRequests;
          totalSpent += m.rawSpent;
          totalCache += m.rawCache;
        });
      });

      // Range subtext matching video
      let rangeSubtext = 'Rolling 30 days';
      if (state.usageRange === 'all') rangeSubtext = 'Every request so far';
      else if (state.usageRange === 'today') rangeSubtext = 'Today';
      else if (state.usageRange === '7d') rangeSubtext = 'Rolling 7 days';
      else if (state.usageRange === 'month') rangeSubtext = 'This month';

      // Formatting helper for tokens
      function fmtTokens(n) {
        if (!n || n === 0) return '0';
        if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
        if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
        return n.toLocaleString();
      }

      // Formatting helper for spent
      function fmtSpent(s) {
        if (!s || s === 0) return '$0.00';
        if (s >= 1) return '$' + s.toFixed(10);
        return '$' + s.toFixed(7);
      }

      const tokensVal = fmtTokens(totalTokens);
      const requestsVal = totalRequests.toLocaleString();
      const spentVal = fmtSpent(totalSpent);
      const cacheVal = totalCache > 0 ? fmtTokens(totalCache) : '—';
      const cachePct = totalTokens > 0 ? Math.round((totalCache / totalTokens) * 100) : 0;

      const html = `
        <div class="space-y-6">
          <!-- Top Bar: Title & Range Filter -->
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 class="font-serif font-medium text-h2 text-main">Usage</h1>
              <p class="text-xs text-sec mt-1">Tokens and spend per model, grouped by maker.</p>
            </div>
            <div class="flex items-center gap-1 p-1 rounded-xl surface-card-solid border border-subtle text-xs shrink-0 self-start sm:self-auto shadow-sm">
              ${[
                { id: 'today', label: 'Today' },
                { id: '7d', label: '7 days' },
                { id: '30d', label: '30 days' },
                { id: 'month', label: 'This month' },
                { id: 'all', label: 'All time' }
              ].map(t => `
                <button onclick="state.usageRange='${t.id}'; loadUsageData();" class="px-3 py-1.5 rounded-lg font-medium transition ${state.usageRange === t.id ? 'bg-[#c87a51] text-white shadow-sm' : 'text-sec hover:text-main'}">${t.label}</button>
              `).join('')}
            </div>
          </div>

          <!-- 4 Summary KPI Cards -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div class="surface-card rounded-2xl p-5 border border-subtle shadow-sm">
              <div class="text-xs text-sec font-medium">Tokens</div>
              <div class="font-mono text-2xl sm:text-3xl font-semibold text-main mt-1 tracking-tight">${tokensVal}</div>
              <div class="text-xs text-sec mt-2">${rangeSubtext}</div>
            </div>
            <div class="surface-card rounded-2xl p-5 border border-subtle shadow-sm">
              <div class="text-xs text-sec font-medium">Requests</div>
              <div class="font-mono text-2xl sm:text-3xl font-semibold text-main mt-1 tracking-tight">${requestsVal}</div>
              <div class="text-xs text-sec mt-2">Across ${visibleModelsCount} model${visibleModelsCount === 1 ? '' : 's'}</div>
            </div>
            <div class="surface-card rounded-2xl p-5 border border-subtle shadow-sm">
              <div class="text-xs text-sec font-medium">Spent</div>
              <div class="font-mono text-2xl sm:text-3xl font-semibold text-main mt-1 tracking-tight">${spentVal}</div>
              <div class="text-xs text-sec mt-2">${rangeSubtext}</div>
            </div>
            <div class="surface-card rounded-2xl p-5 border border-subtle shadow-sm">
              <div class="text-xs text-sec font-medium">Cache</div>
              <div class="font-mono text-2xl sm:text-3xl font-semibold text-main mt-1 tracking-tight">${cacheVal}</div>
              <div class="text-xs text-sec mt-2">${cachePct}% of all tokens</div>
            </div>
          </div>

          <!-- Section: By model & Search -->
          <div class="space-y-3">
            <div>
              <div class="text-sm font-semibold text-main">By model</div>
              <div class="text-xs text-sec mt-0.5">
                Total is input plus cache plus output. Spend is what each model charged your balance; per-call detail is in <a href="/request-logs" onclick="event.preventDefault(); navigate('/request-logs');" class="text-main underline decoration-subtle underline-offset-2 hover:decoration-main transition">Request logs</a>.
              </div>
            </div>
            <div class="relative w-full">
              <span class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-mut">
                ${ico('search', 'w-4 h-4')}
              </span>
              <input id="usage-model-search" type="text" value="${state.usageModelSearch || ''}" oninput="state.usageModelSearch=this.value; render();" placeholder="Filter models..." class="w-full pl-10 pr-4 py-2.5 rounded-xl surface-card-solid border border-subtle text-xs text-main placeholder:text-mut focus:outline-none focus:border-strong transition shadow-sm">
            </div>
          </div>

          <!-- Grouped Table Card -->
          <div class="surface-card rounded-2xl border border-subtle overflow-hidden shadow-sm">
            <div class="overflow-x-auto">
              <table class="w-full text-left text-xs border-collapse font-mono">
                <thead>
                  <tr class="border-b border-subtle text-mut font-sans text-xs">
                    <th class="py-3 px-4 font-normal">Model</th>
                    <th class="py-3 px-4 text-right font-normal">Requests</th>
                    <th class="py-3 px-4 text-right font-normal">Input</th>
                    <th class="py-3 px-4 text-right font-normal">Cache read</th>
                    <th class="py-3 px-4 text-right font-normal">Cache write</th>
                    <th class="py-3 px-4 text-right font-normal">Output</th>
                    <th class="py-3 px-4 text-right font-normal">Total</th>
                    <th class="py-3 px-4 text-right font-normal">Spent</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-subtle">
                  ${filteredMakers.length === 0 ? `
                    <tr><td colspan="8" class="py-12 text-center text-mut font-sans text-xs">${allMakers.length === 0 ? 'Belum ada data penggunaan API untuk periode ini.' : `Tidak ditemukan model yang cocok dengan "${state.usageModelSearch}".`}</td></tr>
                  ` : filteredMakers.map(maker => {
                    const makerTotalTokens = maker.models.reduce((acc, m) => acc + m.rawTokens, 0);
                    const makerTotalSpent = maker.models.reduce((acc, m) => acc + m.rawSpent, 0);
                    const makerTokensFmt = fmtTokens(makerTotalTokens);
                    const makerSpentFmt = fmtSpent(makerTotalSpent);

                    return `
                      <!-- Maker Group Header -->
                      <tr class="bg-white/[0.02] border-t border-b border-subtle">
                        <td colspan="8" class="py-2.5 px-4">
                          <div class="flex items-center gap-2 font-sans">
                            <div class="w-5 h-5 rounded-md surface-card-solid border border-subtle flex items-center justify-center shrink-0 p-0.5 text-main">
                              ${getProviderIcon(maker.provider, 'w-3.5 h-3.5')}
                            </div>
                            <span class="font-semibold text-xs text-main">${maker.name}</span>
                            <span class="text-xs text-sec">·</span>
                            <span class="text-xs text-sec font-mono">${maker.models.length} model${maker.models.length > 1 ? 's' : ''} · ${makerTokensFmt} tokens · $${makerSpentFmt}</span>
                          </div>
                        </td>
                      </tr>
                      <!-- Maker Models Rows -->
                      ${maker.models.map(m => `
                        <tr class="hover:bg-white/[0.02] transition group">
                          <td class="py-3 px-4 text-main font-sans">
                            <div class="flex items-center gap-2">
                              <span class="font-mono text-xs text-main">${m.id}</span>
                              <button onclick="copyToClipboard('${m.id}', this)" title="Copy model name" class="opacity-0 group-hover:opacity-100 text-mut hover:text-main transition p-1">
                                ${ico('copy', 'w-3.5 h-3.5')}
                              </button>
                            </div>
                          </td>
                          <td class="py-3 px-4 text-right text-main">${m.requests}</td>
                          <td class="py-3 px-4 text-right text-sec">${m.input}</td>
                          <td class="py-3 px-4 text-right text-sec">${m.cacheRead}</td>
                          <td class="py-3 px-4 text-right text-sec">${m.cacheWrite}</td>
                          <td class="py-3 px-4 text-right text-sec">${m.output}</td>
                          <td class="py-3 px-4 text-right font-semibold text-main">${m.total}</td>
                          <td class="py-3 px-4 text-right text-main">${m.spent}</td>
                        </tr>
                      `).join('')}
                    `;
                  }).join('')}
                </tbody>
              </table>
            </div>
            <!-- Table Footer -->
            <div class="px-4 py-3 border-t border-subtle text-xs text-sec font-sans">
              ${state.usageModelSearch ? `Showing ${visibleModelsCount} models` : `${visibleModelsCount} model${visibleModelsCount === 1 ? '' : 's'} used`}
            </div>
          </div>
        </div>
      `;

      return renderConsoleLayout('Usage', 'Usage', html);
    }

    function renderActivityPage() {
      const activities = state.activities || [];
      const html = `
        <div>
          <h1 class="font-serif font-medium text-h2 text-main">Activity</h1>
          <p class="text-xs text-sec mt-1">Top-ups, redeemed codes, and plan purchases, newest first.</p>
        </div>
        <div class="surface-card rounded-2xl p-5">
          <table class="w-full text-left text-xs border-collapse">
            <thead><tr class="border-b border-subtle text-mut"><th class="py-2.5">Type</th><th class="py-2.5">Description</th><th class="py-2.5">When</th><th class="py-2.5 text-right">Amount</th></tr></thead>
            <tbody>
              ${activities.length === 0 ? `
                <tr>
                  <td colspan="4" class="py-12 text-center text-mut text-xs">Belum ada aktivitas kredit atau transaksi.</td>
                </tr>
              ` : activities.map(a => `
                <tr>
                  <td class="py-3.5"><span class="text-xs px-2.5 py-0.5 rounded ${a.typeClass || 'bg-emerald-500/15 text-emerald-400'}">${a.type}</span></td>
                  <td class="py-3.5 font-mono text-main">${a.desc}</td>
                  <td class="py-3.5 font-mono text-sec">${a.when}</td>
                  <td class="py-3.5 font-mono text-emerald-400 text-right font-semibold">${a.amount}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      return renderConsoleLayout('Activity', 'Activity', html);
    }

    function renderRequestLogsPage() {
      const logs = state.requestLogs || [];
      const html = `
        <div>
          <h1 class="font-serif font-medium text-h2 text-main">Request logs</h1>
          <p class="text-xs text-sec mt-1">Model, tokens, latency, and cost for each API call.</p>
        </div>
        <div class="surface-card rounded-2xl p-5 overflow-x-auto">
          <table class="w-full text-left text-xs border-collapse font-mono">
            <thead>
              <tr class="border-b border-subtle text-mut font-sans">
                <th class="py-2.5 px-3">Request ID</th>
                <th class="py-2.5 px-3">When</th>
                <th class="py-2.5 px-3">Model</th>
                <th class="py-2.5 px-3">Protocol</th>
                <th class="py-2.5 px-3">Status</th>
                <th class="py-2.5 px-3 text-right">Input</th>
                <th class="py-2.5 px-3 text-right">Output</th>
                <th class="py-2.5 px-3 text-right">Cache</th>
                <th class="py-2.5 px-3 text-right">Cost</th>
                <th class="py-2.5 px-3 text-right">Latency</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-white/5">
              ${logs.length === 0 ? `
                <tr>
                  <td colspan="10" class="py-12 text-center text-mut text-xs font-sans">Belum ada log request API.</td>
                </tr>
              ` : logs.map(l => `
                <tr class="hover:bg-white/[0.02] transition">
                  <td class="py-3 px-3 text-sec truncate max-w-[140px]" title="${l.id}">${l.id}</td>
                  <td class="py-3 px-3 text-sec text-xs whitespace-nowrap">${l.when}</td>
                  <td class="py-3 px-3 text-main font-semibold">${l.model}</td>
                  <td class="py-3 px-3 text-sec">${l.proto}</td>
                  <td class="py-3 px-3"><span class="font-sans text-xs px-2 py-0.5 rounded ${l.status === 'succeeded' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}">${l.status}</span></td>
                  <td class="py-3 px-3 text-right text-sec">${l.inputTokens ? l.inputTokens.toLocaleString() : '0'}</td>
                  <td class="py-3 px-3 text-right text-sec">${l.outputTokens ? l.outputTokens.toLocaleString() : '0'}</td>
                  <td class="py-3 px-3 text-right text-sec">${l.cacheTokens ? l.cacheTokens.toLocaleString() : '—'}</td>
                  <td class="py-3 px-3 text-right text-main font-medium whitespace-nowrap">
                    ${l.cost}
                    ${l.costMismatch ? `<span class="ml-1 text-amber-400 text-xs font-sans cursor-help" title="${t('usage.costMismatchTooltip')}">*</span>` : ''}
                  </td>
                  <td class="py-3 px-3 text-right text-sec">${l.latency}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      return renderConsoleLayout('Request logs', 'Request logs', html);
    }

    function getQuickstartSnippet(tab, model, token) {
      if (tab === 'responses') {
        return `curl https://api.opencsn.com/v1/responses \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${model}",
    "input": "Hello!"
  }'`;
      }
      if (tab === 'messages') {
        return `curl https://api.opencsn.com/v1/messages \\
  -H "x-api-key: ${token}" \\
  -H "anthropic-version: 2023-06-01" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${model}",
    "max_tokens": 1024,
    "messages": [{"role": "user", "content": "Hello!"}]
  }'`;
      }
      if (tab === 'systemone') {
        return `curl https://api.opencsn.com/v1/systemone/chat \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${model}",
    "stream": true,
    "messages": [{"role": "user", "content": "Hello!"}]
  }'`;
      }
      return `curl https://api.opencsn.com/v1/chat/completions \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${model}",
    "messages": [{"role": "user", "content": "Hello!"}]
  }'`;
    }

    function renderApiKeysPage() {
      const keys = state.apiKeys || [];
      const activeKey = keys.find(k => k.id === state.selectedQuickstartKey) || keys[0] || null;
      const tokenMasked = activeKey ? activeKey.masked : 'sk-opencsn-xxxxxxxx';
      const tokenFull = state.justCreatedKey || '$OPENCSN_API_KEY';
      const currentModel = state.selectedQuickstartModel || getExampleModelId();
      const currentTab = state.selectedQuickstartTab || 'chat';

      const snippetDisplay = getQuickstartSnippet(currentTab, currentModel, tokenMasked);
      const snippetFull = getQuickstartSnippet(currentTab, currentModel, tokenFull);

      let endpointPath = '/chat/completions';
      let endpointDesc = 'OpenAI-compatible Chat Completions';
      if (currentTab === 'responses') {
        endpointPath = '/responses';
        endpointDesc = 'OpenAI-compatible Responses';
      } else if (currentTab === 'messages') {
        endpointPath = '/messages';
        endpointDesc = 'Anthropic-compatible Messages';
      } else if (currentTab === 'systemone') {
        endpointPath = '/systemone/chat';
        endpointDesc = 'SystemOne Gateway Streaming';
      }

      const html = `
        <div class="space-y-6 pb-12 max-w-5xl">
          <!-- Card 1: Your keys matching Video Frame 190 & Frame 228 -->
          <div class="surface-card rounded-2xl p-6 border border-subtle space-y-5 shadow-sm">
            <!-- Header of Your keys -->
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 class="text-base font-semibold text-main">Your keys</h2>
                <p class="text-xs text-sec mt-0.5">One key per app keeps rotation painless. Rotating issues a new secret and retires the old one at once.</p>
              </div>
              ${keys.length > 0 ? `
                <button onclick="openCreateApiKeyModal()" class="surface-card hover:border-strong px-3 py-1.5 rounded-xl border border-subtle text-xs text-sec hover:text-main flex items-center gap-1.5 transition shadow-sm font-medium">
                  ${ico('key-round', 'w-3.5 h-3.5 text-mut')} <span>+ Create key</span>
                </button>
              ` : ''}
            </div>

            <!-- Keys List or Empty State -->
            ${keys.length === 0 ? `
              <!-- Empty State matching Frame 228 -->
              <div class="py-12 flex flex-col items-center justify-center text-center space-y-3">
                <div class="w-12 h-12 rounded-full surface-card-solid border border-subtle flex items-center justify-center text-mut mb-1 shadow-sm">
                  ${ico('key-round', 'w-5 h-5')}
                </div>
                <h3 class="text-sm font-semibold text-main">No API keys yet</h3>
                <p class="text-xs text-sec max-w-sm">Create your first key, then point any OpenAI-compatible client at OpenCSN.</p>
                <div class="pt-2">
                  <button onclick="openCreateApiKeyModal()" class="bg-white hover:bg-white/90 text-black font-semibold px-4 py-2 rounded-xl text-xs transition shadow-sm">
                    Create key
                  </button>
                </div>
              </div>
            ` : `
              <!-- Keys List matching Frame 190 -->
              <div class="space-y-3">
                ${keys.map(k => `
                  <div class="flex flex-wrap items-center justify-between gap-4 p-4 rounded-xl surface-card-solid border border-subtle shadow-sm transition hover:border-strong">
                    <!-- Left: Key icon + Name + Active badge + Masked Secret + Dates -->
                    <div class="flex items-start gap-3.5 min-w-[260px]">
                      <div class="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 border border-amber-500/20 flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                        ${ico('key-round', 'w-4 h-4')}
                      </div>
                      <div>
                        <div class="flex items-center gap-2">
                          <span class="text-sm font-semibold text-main">${k.name}</span>
                          ${setKeyStatusBadge(k)}
                        </div>
                        <div class="inline-block px-3 py-1 rounded-lg bg-inset border border-subtle font-mono text-xs text-sec select-all mt-1.5">
                          ${k.masked}
                        </div>
                        <div class="text-xs text-mut mt-1.5">
                          Created ${k.created} &nbsp;·&nbsp; Last used ${k.lastUsed}
                        </div>
                      </div>
                    </div>

                    <!-- Right: Block/Unblock, Rotate, Revoke buttons -->
                    <div class="flex items-center gap-2">
                      <button onclick="toggleKeyBlocked('${k.id}')" class="surface-card hover:border-strong px-3 py-1.5 rounded-xl text-xs text-sec hover:text-main flex items-center gap-1.5 border border-subtle transition shadow-sm font-medium">
                        ${k.active ? ico('shield', 'w-3.5 h-3.5 text-mut') : ico('check', 'w-3.5 h-3.5 text-mut')} <span>${k.active ? t('keys.block') : t('keys.unblock')}</span>
                      </button>
                      <button onclick="openRotateKeyModal('${k.id}')" class="surface-card hover:border-strong px-3 py-1.5 rounded-xl text-xs text-sec hover:text-main flex items-center gap-1.5 border border-subtle transition shadow-sm font-medium">
                        ${ico('rotate-ccw', 'w-3.5 h-3.5 text-mut')} <span>${t('keys.rotate')}</span>
                      </button>
                      <button onclick="openRevokeKeyModal('${k.id}')" class="surface-card hover:border-rose-500/40 hover:bg-rose-500/10 px-3 py-1.5 rounded-xl text-xs text-rose-400 flex items-center gap-1.5 border border-subtle transition shadow-sm font-medium">
                        ${ico('trash-2', 'w-3.5 h-3.5')} <span>${t('keys.revoke')}</span>
                      </button>
                    </div>
                  </div>
                `).join('')}
              </div>
            `}

            <!-- Warning banner matching Frame 190 -->
            <div class="pt-4 border-t border-subtle flex items-start gap-2.5 text-xs text-mut">
              <span class="text-amber-500 shrink-0 mt-0.5">${ico('shield-alert', 'w-4 h-4')}</span>
              <span class="leading-relaxed">Treat a key like a password: anyone holding it can spend your balance. If one leaks, rotate it and the old secret stops working at once.</span>
            </div>
          </div>

          <!-- Card 2: Quickstart matching Frame 190, Frame 266, Frame 304 & Frame 380 -->
          <div class="surface-card rounded-2xl p-6 border border-subtle space-y-5 shadow-sm">
            <!-- Header of Quickstart -->
            <div class="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 class="text-base font-semibold text-main">Quickstart</h2>
                <p class="text-xs text-sec mt-0.5">Any OpenAI, Anthropic, or System One client works: point it at the base URL and use a key from above.</p>
              </div>

              <!-- Key & Model Dropdowns matching Frame 190 & Frame 304 -->
              <div class="flex items-center gap-2.5 flex-wrap">
                ${activeKey ? `
                  <div class="relative">
                    <button onclick="state.quickstartKeyDropdownOpen = !state.quickstartKeyDropdownOpen; state.quickstartModelDropdownOpen = false; render();" class="surface-card-solid hover:border-strong px-3 py-1.5 rounded-xl border border-subtle text-xs text-sec hover:text-main flex items-center gap-2 transition shadow-sm">
                      <span class="text-mut">Key:</span>
                      <span class="font-medium text-main">${activeKey.name}</span>
                      <span class="text-mut">${ico('chevron-down', 'w-3 h-3')}</span>
                    </button>
                    ${state.quickstartKeyDropdownOpen ? `
                      <div class="absolute right-0 mt-2 w-48 surface-card-solid rounded-2xl shadow-2xl border border-strong p-1.5 z-40 space-y-1 text-xs">
                        ${keys.map(k => `
                          <button onclick="state.selectedQuickstartKey='${k.id}'; state.quickstartKeyDropdownOpen=false; render();" class="w-full text-left px-3 py-1.5 rounded-lg flex items-center justify-between ${state.selectedQuickstartKey === k.id ? 'bg-[#df956d]/15 text-[#df956d] font-semibold' : 'text-sec hover:text-main hover:bg-pill'}">
                            <span>${k.name}</span>
                            <span class="font-mono text-xs text-mut">${k.masked.substring(0, 10)}...</span>
                          </button>
                        `).join('')}
                      </div>
                    ` : ''}
                  </div>
                ` : ''}

                <div class="relative">
                  <button onclick="state.quickstartModelDropdownOpen = !state.quickstartModelDropdownOpen; state.quickstartKeyDropdownOpen = false; render();" class="surface-card-solid hover:border-strong px-3.5 py-1.5 rounded-xl border border-subtle text-xs text-sec hover:text-main flex items-center gap-2 transition shadow-sm">
                    <span class="text-mut">Model:</span>
                    <span class="font-medium text-main font-mono">${currentModel}</span>
                    <span class="text-mut">${ico('chevron-down', 'w-3 h-3')}</span>
                  </button>
                  ${state.quickstartModelDropdownOpen ? `
                    <div class="absolute right-0 mt-2 w-64 max-h-72 overflow-y-auto custom-scrollbar surface-card-solid rounded-2xl shadow-2xl border border-strong p-1.5 z-40 space-y-1 text-xs">
                      ${MODELS_DATA.map(mod => `
                        <button onclick="state.selectedQuickstartModel='${mod.id}'; state.quickstartModelDropdownOpen=false; render();" class="w-full text-left px-3 py-1.5 rounded-lg flex items-center justify-between font-mono ${state.selectedQuickstartModel === mod.id ? 'bg-[#df956d]/15 text-[#df956d] font-semibold' : 'text-sec hover:text-main hover:bg-pill'}">
                          <span class="truncate">${mod.id}</span>
                          <span class="text-xs text-mut font-sans">${mod.provider}</span>
                        </button>
                      `).join('')}
                    </div>
                  ` : ''}
                </div>
              </div>
            </div>

            <!-- Base URL Row matching Frame 190 -->
            <div class="flex items-center justify-between gap-3 pt-1">
              <div class="flex items-center gap-3 text-xs flex-wrap">
                <span class="text-sec font-medium">Base URL</span>
                <span class="font-mono text-xs sm:text-[13px] font-bold text-main">https://api.opencsn.com/v1</span>
              </div>
              <button onclick="copyToClipboard('https://api.opencsn.com/v1', this, 'Copy')" class="surface-card hover:border-strong px-3 py-1.5 rounded-xl border border-subtle text-xs text-sec hover:text-main flex items-center gap-1.5 transition shadow-sm font-medium">
                ${ico('copy', 'w-3.5 h-3.5')} <span>Copy</span>
              </button>
            </div>

            <!-- Protocol Tabs matching Frame 190 -->
            <div class="border-b border-subtle flex items-center gap-6 text-xs font-medium">
              ${[
                { id: 'chat', label: 'Chat Completions' },
                { id: 'responses', label: 'Responses' },
                { id: 'messages', label: 'Messages' },
                { id: 'systemone', label: 'SystemOne' }
              ].map(tab => `
                <button onclick="state.selectedQuickstartTab='${tab.id}'; render();" class="pb-2.5 transition border-b-2 ${state.selectedQuickstartTab === tab.id ? 'border-[#df956d] text-main font-semibold' : 'border-transparent text-sec hover:text-main'}">
                  ${tab.label}
                </button>
              `).join('')}
            </div>

            <!-- Method & Endpoint Badge matching Frame 190 -->
            <div class="flex items-center gap-2.5 text-xs">
              <span class="px-2 py-0.5 rounded bg-orange-500/20 text-[#df956d] font-mono font-bold text-xs uppercase">POST</span>
              <span class="font-mono text-xs text-main">https://api.opencsn.com/v1<b class="text-[#df956d]">${endpointPath}</b></span>
              <span class="text-mut text-xs hidden sm:inline">· ${endpointDesc}</span>
            </div>

            <!-- Code Block matching Frame 190 -->
            <div class="rounded-2xl bg-inset border border-subtle p-4 sm:p-5 font-mono text-xs text-main overflow-x-auto leading-relaxed shadow-inner">
              <pre class="whitespace-pre">${snippetDisplay}</pre>
            </div>

            <!-- Bottom Actions Row matching Frame 380 & Frame 418 -->
            <div class="flex flex-wrap items-center justify-between gap-3 pt-1">
              <div class="text-xs text-mut">
                ${activeKey ? `Key <span class="font-mono text-sec">${activeKey.name}</span> selected and active.` : 'Create a key above and it is filled in for you.'}
              </div>
              <div class="flex items-center gap-2.5">
                <button onclick="copyToClipboard('https://api.opencsn.com/v1${endpointPath}', this, 'Copy URL')" class="surface-card hover:border-strong px-4 py-2 rounded-xl text-xs text-sec hover:text-main flex items-center gap-1.5 border border-subtle transition shadow-sm font-medium">
                  ${ico('copy', 'w-3.5 h-3.5')} <span>Copy URL</span>
                </button>
                <button onclick="copySnippetToClipboard(this)" class="bg-[#df956d] hover:bg-[#c87a51] text-[#14110f] font-semibold px-4 py-2 rounded-xl text-xs flex items-center gap-1.5 transition shadow-sm">
                  ${ico('copy', 'w-3.5 h-3.5')} <span>Copy cURL</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Page Footer matching Frame 380 -->
          <div class="pt-6 flex flex-wrap items-center justify-between gap-4 text-xs text-mut border-t border-subtle/50">
            <div>© 2026 OpenCSN</div>
            <div class="flex items-center gap-5">
              <a href="javascript:void(0)" onclick="navigate('/docs')" class="hover:text-sec transition">Docs</a>
              <a href="javascript:void(0)" onclick="navigate('/status')" class="hover:text-sec transition">API status</a>
              <a href="javascript:void(0)" onclick="navigate('/changelog')" class="hover:text-sec transition">Changelog</a>
            </div>
          </div>
        </div>
      `;
      return renderConsoleLayout('API keys', 'API keys', html);
    }

    function toggleRevealKey(id) {
      toggleKeyBlocked(id);
    }

    function copyToClipboard(text, btnEl, defaultLabel = 'Copy') {
      navigator.clipboard.writeText(text);
      if (btnEl) {
        const origHtml = btnEl.innerHTML;
        btnEl.innerHTML = `${ico('check', 'w-3.5 h-3.5 text-emerald-400')} <span>Copied</span>`;
        setTimeout(() => {
          btnEl.innerHTML = origHtml;
        }, 2000);
      }
      showToast('Copied to clipboard');
    }

    function copySnippetToClipboard(btnEl) {
      const keys = state.apiKeys || [];
      const activeKey = keys.find(k => k.id === state.selectedQuickstartKey) || keys[0] || null;
      const token = state.justCreatedKey || '$OPENCSN_API_KEY';
      const snippet = getQuickstartSnippet(state.selectedQuickstartTab || 'chat', state.selectedQuickstartModel || getExampleModelId(), token);
      navigator.clipboard.writeText(snippet);
      if (btnEl) {
        const origHtml = btnEl.innerHTML;
        btnEl.innerHTML = `${ico('check', 'w-3.5 h-3.5 text-emerald-400')} <span>Copied</span>`;
        setTimeout(() => {
          btnEl.innerHTML = origHtml;
        }, 2000);
      }
      showToast('Copied cURL command to clipboard');
    }

    const INTEGRATIONS_DATA = [
      {
        id: 'opencsn-cli',
        name: 'OpenCSN CLI',
        badge: 'CLI',
        desc: 'AI coding co-pilot by OpenCSN untuk terminal modern.',
        iconType: 'opencsn-cli'
      },
      {
        id: '9router-proxy',
        name: '9Router Proxy',
        badge: 'Desktop Proxy · BYOK',
        desc: 'Proxy desktop yang menyebar satu key OpenCSN ke client OpenAI/Anthropic-compatible lewat port lokal.',
        iconType: '9router'
      },
      {
        id: 'opencode',
        name: 'OpenCode',
        badge: 'CLI · Browser · VS Code',
        desc: 'AI coding assistant untuk terminal, browser, dan extension VS Code.',
        iconType: 'opencode'
      },
      {
        id: 'cursor',
        name: 'Cursor',
        badge: 'VS Code Editor',
        desc: 'Code editor bertenaga AI dengan setting model OpenAI-compatible custom.',
        iconType: 'cursor'
      },
      {
        id: 'claude-code',
        name: 'Claude Code CLI',
        badge: 'CLI',
        desc: 'CLI coding resmi Anthropic, diarahkan lewat endpoint OpenCSN.',
        iconType: 'claude'
      },
      {
        id: 'hermes',
        name: 'Hermes Agent',
        badge: 'Agent',
        desc: 'Agent otonom yang bisa diakses dari Telegram. Setup VPS satu-paste berpandu.',
        iconType: 'hermes'
      },
      {
        id: 'factory-cli',
        name: 'Factory CLI',
        badge: 'Terminal Coding',
        desc: 'Custom model lewat setting BYOK Factory.',
        iconType: 'factory'
      },
      {
        id: 'openclaw',
        name: 'OpenClaw',
        badge: 'Agent',
        desc: 'Pakai OpenClaw dengan setting endpoint OpenAI-compatible standar.',
        iconType: 'openclaw'
      },
      {
        id: 'codex-cli',
        name: 'Codex CLI',
        badge: 'Terminal Coding',
        desc: 'CLI bergaya Codex yang menerima custom OpenAI base URL bisa pakai key yang sama.',
        iconType: 'codex'
      },
      {
        id: 'mcp',
        name: 'MCP (Model Context Protocol)',
        badge: 'Agent tooling',
        desc: 'Bisa untuk Claude Desktop, Cursor, dan client MCP mana pun yang mendukung Streamable HTTP.',
        iconType: 'mcp'
      },
      {
        id: 'any-openai',
        name: 'Any OpenAI-compatible tool',
        badge: 'Universal Setup',
        desc: 'Bisa untuk LibreChat, Continue, Aider, custom SDK, dan client OpenAI-compatible lain.',
        iconType: 'universal'
      }
    ];

    const INTEGRATION_LOGOS = {
  "opencsn-cli": "data:image/png;base64,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",
  "9router": "data:image/png;base64,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",
  "opencode": "data:image/png;base64,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",
  "cursor": "data:image/png;base64,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",
  "claude": "data:image/png;base64,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",
  "hermes": "data:image/png;base64,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",
  "factory": "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACwAAAAsCAIAAACR5s1WAAAIVklEQVRYCb3BfWyUhR3A8e/vueeubxxSjperlTcp9gDhWt5EmG8gL9obEw1Dpim5RFNi1ixxxtT+SczCJVv6hw1h07mERpi7wZ27DpWhGIU4dYqoCDS8aKkt5R16fQ569zy/taTGu1EGhOrnI15fGVdwe/JGjZlUPKK0oGioGAY3x85krOTZM52tJ9oOqypXEK+vjCx5BUPuCP5s+l0L89xGvnS7SAt9FAUEARQFBAEUBYReAqog9BJQpY8gDi4bI635F7rTH++MtR3+IpPuIYt4fWVcJmKMvaNi5vzFI4d7PdIjOORSEAagIAxAQfhfPZp39FjnJ++9caazle+J11cGiMjtU+bcu2iZ13ORH5kip7vN7Vv+fOp4KyggXl8ZMD4w84Gly4e4e0SUH5+jxskL9ttb/nzuVAcgXl9Zkbf4oZXP3Fps8BNyMPYf7ng3/oo6jnh9ZRXzHp43/y6TDKAoIAh9VEGQ/DzPhDGlpf4Rhfn5IsK1qOr5ru7W9uPHOjozGRtQVOglgKJCL7HUu/0fm1tb9kixf8qqZ14szutiIHkez/TyiffMrvS4TW7c2fNd73z4yTdtHRnb5goOrqPt1pub/iBT561YumxlvnRzhSFFhaH754+/rYSboKqf7Tv4r90fM5ATSc+2vzbKfY8+N3dOpYu0IIDSSwXJ87gfXXL/uNISBsOer1t27P7Yth1AUUAQwMqYzVtek4dX/65i8m2CksVlGFUL5k8pm8AgcVTffv/DvfsPkSuj7kT87xIKrwsGSsjlH+F7PLQoP8/D4Dl19tyr0WZHHbLYaibiMQmF1wUDJeSaP2P6PbMrGGyvb9tx9Fg7WWw1E/GYhMKR6eV+4QcK4ceq/CN9DLb9h46+seMDBaGfjZmIx6QqHAmWjwYEAZRe+txTT7hNk8F25vyFP22OKyr0EiCD2RzfKqFwJBjwk6uupprrc+minj6lw4qlsEi4lqSVamyKksVWMxGPSSgcCQb85Kqrqeb6fPaR3bylZ1LANWmyMetuk/8raaUam6JksdVMxGNSFV4XLPcDggCKAi+sWc3VfbXH/voL+657zDHjjY92ZRLRtNtNOs3in7vvrHCNGCVcRdJKNW6MKir0EiCDqzkek1A4Mr3cL/xA4YU11eT6rtUZXWKYbnq9FLnY0aZuD4ur3G2tzt5P7coZxt7PHcfBe4sEphrLV3kYSNJKNW6MKgj9bMxEPCahcCQY8JOrrqaaXH9suPTtEee+B83gbNfmV3tOduptY6T9O3Ucej22whw/wdi9y2456Jw5rcDyx91TK1yFRUKWpJVqbIqSxVYzEY9JKBwJBvzkqqupJld9bYrLCouk55JmMvzmWc/uXXbLAfvCBR5bYY6fYACWpUcO69tvZjx5TJ7uWlntIUvSSjU2Rcliq5mIxyQUjgTL/eSqW1NNrvraVEWlUTREDh5wTp1U4LfPe4BzZ7XzhJaXG+R6eUOPZbG2oYAsSSvVuDFKFhszEY9JVXhdsNwPCAIoCrywZjW56mtTFZXGAwvNnkvs3m2PGsm0oIure3lDj2WxtqGALEkr1bgxqiggCJDBlYjHJBSOBAN+ctXVVJOrvjZVUWksXGQCZ05rV5eabkpKjLNnNJlUt1tKbpVMho52R5XCIolvSVsWaxsKyJK0Uo1NUbLYaibiMQmFI8GAn1x1NdXkqq9NVVQaCxeZwPqXetJpRHiqxr1ju330iCMGd89zmSbvv2cDponHg2WxtqGALEkr1dgUJYutZiIek6pwJFg+Wugl9NO6NavJVV+bqqg0Fi4ygX1fOVa3evK4c5qr9Vvn1EkFJkw0XC4OtThA8XDZ+U7GsljbUECWpJVq3BhVVOglQAazOb5VQuFIsNxPrro11eSqr01VVBoLF5lcn5c39FgWaxsKyJK0Uo0bo2SxMRPxmITC64KBEnLV1VSTq742NWaszJzl4vq8tS2TybC2oYAsSSvV2BQli61mIh6TUDgSDPjJ9fzTTxqGQZb62hRgGFwnx8E0WdtQQJYLXcn1m7aSxcZMxGISCkeC5X5yPb3yF77iW8jy6b8z7W3KjQhMNSZNdpHlcGtbdNu7CkI/GzMRj0lVOBIsHw0IAii99MH5c2ZPm8xg++fO3V8ePKwoIAiQwUzEt0ooHAmW+8k1ttS/4qEFbtNk8HRbqQ2bY+l0hiw2ZiIek1A4Egz4yWWarieXLfWP9DF4dv3n812ffkEuGzMRi8nS6hcrp4wzcOin9JERxbc8uuSB4cOGMhha24/Hd7xvWRfpoyBc1qPu5lhU7n3k2bvnzjJJ8z0FoY+veNivli0uKsjn5hxr79yU2O6oCn0UhH5W2pXY8poE5jyydPnqIrnAQEb5iufNmFY2fozpcnHjrNTF/Ye++fDzL5PdFgj9FITLTnYbzZvXy9ARk1b9OuIr6BYcrsJbWDjjzsDkieOGDhkiwjWp6okzZ786eOTLlkOXetJcXUtb6q1Nvxevr2zKzAX3Lljo4SI/rZQOaf7bXzpaD4jXV5Zf6F3yy2fGjfQAigKCAIoCggCKAoIAigJCL1EUEHqJooDQS0CVPoKAKn0EARQFFHPvvsMfbGsCxOsrA0rGBR5c9sSw/IzgcIMUhAEoCAOzMTtOp7ZveSV5/hQgXl8Zl5VOmLIgtGpYQVpQfkyOGp3nnTdfX588f5rLxOsr4zIRGVl6+6x7q8aWjvZwkWtQEPopCANQEPopiI15SQtaDuzbs2tb17mTfE+8vjKyGC5z4tS50+cuGVpo5Lls03AE5ebYamRUrEze+XNdn+yMHm89SC7x+sq4gst0Dxt565Chwz15hWIY3BzHti+lkl3nTp471cFA/gvnwHbmNBdkawAAAABJRU5ErkJggg==",
  "openclaw": "data:image/png;base64,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",
  "codex": "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACwAAAAsCAIAAACR5s1WAAALmElEQVRYCb3B/W/chX3A8ffn+/3eo313Pn/P9tl5BAw2DOeALUlLICsworBYDNKmrJv2cNU6TdqkaX/ApEn7YfinSdu0aZvaSRPTEKE5k4NQoBAmre1CSSEN4akKDwE/3dk+3/Pd9+kz+2pEvIQOtMHrJQl7nCsYhhlLDESjfWYoLCL832gQuG6306q3G1WuRhL2OJcxDHP7+NTkLXemBtKJmBEOGYYAoiggrBNFAWGdKAoI60RRegRRlB5BvADHdRstt1xeee3MMyuL77OVJOxxfk4kOTC0964Hdl97fcxoGvj8f1OkHSTOnf3R+TPPdVp1PiIJe5yeke3jv3rfQ8PpkIHP58lV69J85fQT/9JqVOmRhD0ODI7sOPy1bw3GfSEAFQRQFET4OVUQhA2qIAg9igpCj4KwSVFB6FFUWCeAoj7hD5fqz333H9vNGiAJezwS7bvn6Leu25YSAr4onlpnXzl/5vnHAUnY4+NTX7738K+HxOHT8dFyqOsa6krQ71shNdJeiM+uEaSeeORvVxbfl2Tm+mN/+BcjKU/4VN6ONV5OVJqG74v6ouHAMFV2OLF7KkMGwmfhEXrrYun57/6d3HDb9JFj34xLnY8oCFeh8EK6/HasIRBWY9ANxQKzbno102ubPnCoMry9E1MQiAWmgnAVCsKmpbr11L//jRy4/0/vuH2/iSMIoKxTQQBFQYQNiv4oufpKogp8pZLZ2Y3HAsNSw5GgawTPpUsLkY6lEg1MelJe6Lp231QzSY+igCCAooAgQMO1nnz8X+Xw7/zlrTdfY+DzCy2GOyftRUO5b3VkmxPjI54Ei+HuhXjtYqypEPNNetqmD9he+NDq8KAX5hM4QfjJwmMynZ/JTWb537wwUH4jXr97bWiylRA+dsKeL4e7nuiwE8k1U9u7MXrmIu1n0yUg5pv3r2YzboSr8dUqzhbkSP7h3ERWWCdsUDYIG7RiuR9E22/FGqVwF/h6aduQGwFtGf5b8eaZ5KovGvPNG1uJL9XToqKoIPQEoj9Irfy0r2a74fuXs/HAokdRYZ0APmZxtiDT+Zk9E1nhYwrChnLIecpebJoePRk38lBpG/BhpPP99FLbCALRm5vJX6mlY4FhIGylEIieyCyUwp3rW/33VoaFDQrCJh/r5GxBpvMzucksV6hY7hP2QtP0dnRjg274XH91qpk8WM0ApwaX3o02s0701kbq2k4fKEg51H0pUXkv2so1Uze0+ofdCCjIstU9MTRvqPze0s6QGmzlq1WcLch0fiY3mWWruukV7YWK5U60+u+qDq1YzvGhualm8mA1Azw9uBT3zQM121Kh51xf9UyiElajaXqApXJTK3ln1QY80UeGP3Al+K3S9r7AYisfq1goyHR+JjeRZav3oq3vDS7ZXvg3lkfDgVEKd48PzU01kwfXMsDT9lLSsw5UbeCteP1sotoV/9bGwGSrvxx2LsRrF2NNYFcnflMrsasTn83ML4ecB5fHhp0IW/lYxdmCHMk/nJvIAoIAigKnB5bf6KvfXRmabPULUgp3jg/NTzWSB6sZ4NTgUtK3JluJor3QMYJANOtEHlweE2WdgidazCwuhjsGEgokpEbH8B8oj464UUBRQBDAwyzOFmQ6P5ObyLLVo8NzK6Hu7y/u7PMtoBTuHh+am2omD65lgFP20nKo64omPSvXSLmG/qS/UrO8ffX03noaZV3Vci9F2mcTlabpA+HA+EZpe79vsZWPVZwtyHR+JjeZZau/H3tH4Y/nr6WnFOoeH5qbaiYPVjPA04NL70Sb962O7O7EDQQI0LOJtR8nKobK/SujY06UHl/0pUTlJ/1rlsrR5bEhN8JWPlaxUJAj+ZlbJkZA+Jh+J3upbfq/WdpmuxGgFO4eH5qbaiYPrmWAU/ZS0rPuqNr0KCoI8ERmYc1yG6a3vRu7o2oPuiFBVPTF1PLrffXdnfiRlSwbFIQeH6s4W5Dp/ExuIstWr/av/SC1emt94PbaIFAKd48PzQ070WPlMeDpwaWkbx2o2mx1MrOQ7Ua3daOzQwsC+2uDv1wfAKqW+8jIB8BXy2NZJ8plfKzibEGO5B++ZSILwiYFeSfWfDZd6vetB5ZH+30LeDNefzvWWA47k63EYrgz7ETuqNpsUhDgicxC1onur6WBR4c/rFju18vbbDcM/DC58kqienvVvrWRAgWhx8cszs7KdH4mN5llq64Ehcz8Ssi5qZX4ytqQsOmZ9NLFWFMh10wdqNrCxxRO2gujTnRfPQ0UMvML4c7R5bGsEwU6hv/t7Ptx38wv7eIyPlaxUJDp/ExuIssVVkLOo8MfAte04ze2E9e0++gphbtvxesX4vXdnfiNrcSuThx4N9o811+di3T21tP7ammgkJlfiHSOlseyThToGP63R98fcaJfK49xGR+rOFuQI/mZ3MQIIAigrFNB3ojXX0iXBZQN17bjB6uZPt8CFD3fV/thatUXHXYiIZX5SGdbN1Yz3Rtaif31AZBCZn4h0nmwPDbqREHfiTWfHixd1+4/vDqsKCAI4GEVZ0/IdH4mN5HlCm/E6y+ky9e0+9Je6HxfzTWCjBueaCdubiQtFaAc7hYy865owgt9uZYe7/QX7YWsE91XSwOFzPxCpHO0PJZ1osDx4blSqHt4deS6dh+X8bGKswWZzs/kJrNc4c14/fmB8o2txN1rQ8DzA6WfxZq+KHCoMhwNjP9IrbRML9cY2FsfMBDgpL2QdaL76mmgkJlfCHceXB4bdaLvRpunBpcGvNBvl3awlY9VLBRkOv9wbmKUK5RD3ceG52KB+c2FXUDH8KuW9+JAeTnkWCq+aK6Rmmqmkp4FCvJBtP3CQHmyldhfSyMU7PmFSOfB5bGO4b+YWu4YwR01e6qRZCsfqzhbkOn8zJ6JrPAxBWHDP4y9G4geK28bdiKAgsDr8fq5/uqB2uDOThxQ6BrB9waX5iJtgS9VB29rDAAnMvOLkc6IE10MdwSubyV+rTIkbFAQNvlYJ2cLciQ/k5sYEdYJmxQEOD1Qfr2vPupE71sdifmGgiD0KCpI1XLP99UuxpoN00t5oalmck8jJWz4p7H3XAkAQ2VfPX1bPSUIPYoKQo+PVZw9IdP5mdxklqupm17RXqhY7kS7//aqHQtMYYNC1/DLIefU4KInaqrs7MYOr44YCD2XIq2ivRgJjH319J5mik/mq1WcLciR/MO3TI7yCdZM9/vp0lK4GwmMnd14xgkDLdO/GGs6EjhGsLMTn2old3fifGTNcp9JLy2HnIdK2zNemF/IV6s4e0Lu/caf35abNHEFARQFBAEUBeqm9+xgqWK5rhEoWxyqDI+3+gBhnYB2jODFgeWLseY17fh9qyPCOgEUBQQBFAUEAdpB6KkTj8reQ39w592HYzT5iILwP5XC3arproYcoGsEb8TrnmjaC001UmNOtM8326Y/H+6+1lddDjlpNzy9mk14lnAVCsKmWtcsPvYd2TF58Ojv/lm/WeOzWLa6pwaXGpanbGGpDDmRoytjfDpzFZ76t7+WhD1+6NifXL97yBSPz6IrwVykfb6v1jT8SshJe+G0G9rTTA67kZAafAqehl56+acvn35cEvb48Pbx6WP5fqvDF2u1E3nsn//K6TQlYY+bprXvnmO35G4K4SgKCAIoCgjCBlUQhA2qIAgbVEEQehQVhB5FBaFHUWGdAIoKtLzI6WefvHjhDCAJexywwtF7v/pHu7elQ4bH56/tR1678OaZ5x4NfA+QhD1OT6w/tf+uoxOTN0SkxecmwGy5kbP/9eKFH5/2PIceSdjjXOaX9t17U25vJhk2DYQAVNigKCAIoCggCD2KCsIGVURQEEBRQQAFRRSz61ulSvPV/zx16WevchlJ2ONcRkTi/QP2yI5dN+wZ3rarP5E2DAMElE0CyiYBZZOAsklA2SSe59TWlhcuvfP+2+fWVha67SZbScIe5+oElC/EfwNPoj28TVlAlwAAAABJRU5ErkJggg==",
  "mcp": "data:image/png;base64,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",
  "universal": "data:image/png;base64,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",
};

    function getIntegrationIcon(type) {
      const src = INTEGRATION_LOGOS[type];
      if (src) {
        return `<img src="${src}" class="w-11 h-11 rounded-xl object-cover shrink-0 shadow-sm border border-subtle" alt="${type}" draggable="false" />`;
      }
      return `<div class="w-11 h-11 rounded-xl surface-card-solid border border-subtle flex items-center justify-center shrink-0 shadow-sm text-main">${ico('boxes', 'w-5 h-5')}</div>`;
    }

    function handleIntegrationClick(id) {
      if (id === 'hermes') {
        state.hermesStep = 1;
        navigate('/integrations/hermes');
        return;
      }
      openIntegrationModal(id);
    }

    function openIntegrationModal(id) {
      const item = INTEGRATIONS_DATA.find(x => x.id === id);
      if (!item) return;

      const activeKey = state.justCreatedKey || '$OPENCSN_API_KEY';
      const exModel = getExampleModelId();
      const root = document.getElementById('modal-root');

      let codeSnippet = '';

      switch (id) {
        case 'opencsn-cli':
          codeSnippet = `# Install OpenCSN CLI secara global\nnpm install -g opencsn-cli\n\n# Login dengan API key OpenCSN\nopencsn login --key ${activeKey} --url https://api.opencsn.com/v1\n\n# Jalankan AI coding co-pilot di terminal\nopencsn chat --model ${exModel}`;
          break;
        case '9router-proxy':
          codeSnippet = `// 9router.config.json\n{\n  "listen": "127.0.0.1:8080",\n  "upstream": "https://api.opencsn.com/v1",\n  "api_key": "${activeKey}",\n  "default_model": "${exModel}",\n  "models": ["${exModel}"]\n}`;
          break;
        case 'opencode':
          codeSnippet = `// OpenCode / VS Code settings.json\n{\n  "opencode.apiBase": "https://api.opencsn.com/v1",\n  "opencode.apiKey": "${activeKey}",\n  "opencode.model": "${exModel}"\n}`;
          break;
        case 'cursor':
          codeSnippet = `1. Buka Cursor Settings -> Models\n2. Aktifkan OpenAI API Key dan masukkan:\n   ${activeKey}\n3. Klik "Override OpenAI Base URL" dan masukkan:\n   https://api.opencsn.com/v1\n4. Tambahkan model custom:\n   ${exModel}`;
          break;
        case 'claude-code':
          codeSnippet = `export ANTHROPIC_BASE_URL="https://api.opencsn.com/v1"\nexport ANTHROPIC_API_KEY="${activeKey}"\n\n# Jalankan Claude Code CLI resmi Anthropic lewat OpenCSN\nclaude`;
          break;
        case 'factory-cli':
          codeSnippet = `factory config set openai_base_url "https://api.opencsn.com/v1"\nfactory config set openai_api_key "${activeKey}"\nfactory config set default_model "${exModel}"`;
          break;
        case 'openclaw':
          codeSnippet = `# openclaw.yaml\nopenai:\n  base_url: "https://api.opencsn.com/v1"\n  api_key: "${activeKey}"\n  model: "${exModel}"`;
          break;
        case 'codex-cli':
          codeSnippet = `export OPENAI_API_BASE="https://api.opencsn.com/v1"\nexport OPENAI_API_KEY="${activeKey}"\n\n# Jalankan perintah Codex CLI\ncodex run "Optimize this query for PostgreSQL"`;
          break;
        case 'mcp':
          codeSnippet = `// claude_desktop_config.json\n{\n  "mcpServers": {\n    "opencsn": {\n      "url": "https://api.opencsn.com/v1/systemone",\n      "headers": {\n        "Authorization": "Bearer ${activeKey}"\n      }\n    }\n  }\n}`;
          break;
        case 'any-openai':
        default:
          codeSnippet = `export OPENAI_BASE_URL="https://api.opencsn.com/v1"\nexport OPENAI_API_KEY="${activeKey}"\n\n# Python SDK:\nfrom openai import OpenAI\nclient = OpenAI(base_url="https://api.opencsn.com/v1", api_key="${activeKey}")`;
          break;
      }

      const escapedSnippet = codeSnippet.replace(/'/g, "\\'").replace(/\n/g, '\\n');

      root.innerHTML = `
        <div class="fixed inset-0 z-50 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div class="surface-card-solid rounded-3xl max-w-lg w-full p-6 sm:p-7 border border-strong space-y-4 shadow-2xl animate-in fade-in zoom-in-95 duration-150 text-xs">
            <div class="flex items-start justify-between">
              <div class="flex items-center gap-3">
                ${getIntegrationIcon(item.iconType)}
                <div>
                  <div class="flex items-center gap-2">
                    <h3 class="text-base font-semibold text-main">${item.name}</h3>
                    <span class="text-xs font-sans px-2.5 py-0.5 rounded-full surface-card border border-subtle text-sec font-medium">${item.badge}</span>
                  </div>
                  <p class="text-xs text-sec mt-0.5">${item.desc}</p>
                </div>
              </div>
              <button onclick="closeModal()" class="text-sec hover:text-main p-1 transition">${ico('x', 'w-4 h-4')}</button>
            </div>

            <div class="space-y-2 pt-1">
              <div class="text-xs font-medium text-sec">Configuration & Setup:</div>
              <div class="rounded-xl p-3.5 border border-subtle bg-inset font-mono text-xs text-main overflow-x-auto leading-relaxed shadow-inner">
                <pre class="whitespace-pre">${codeSnippet}</pre>
              </div>
            </div>

            <div class="flex items-center justify-between pt-2">
              <button onclick="copyToClipboard('${escapedSnippet}', this, 'Copy config')" class="surface-card hover:border-strong px-4 py-2 rounded-xl text-xs text-sec hover:text-main flex items-center gap-1.5 border border-subtle transition font-medium shadow-sm">
                ${ico('copy', 'w-3.5 h-3.5')} <span>Copy config</span>
              </button>
              <button onclick="closeModal()" class="bg-[#df956d] hover:bg-[#c87a51] text-[#14110f] font-semibold px-5 py-2 rounded-xl text-xs transition shadow-sm">
                Done
              </button>
            </div>
          </div>
        </div>
      `;
    }

    function renderIntegrationsPage() {
      const html = `
        <div class="space-y-6 pb-12 max-w-6xl">
          <div>
            <h1 class="font-serif font-medium text-h2 text-main">Integrations</h1>
            <p class="text-xs text-sec mt-1">Gunakan OpenCSN dengan CLI, desktop proxy, editor code, atau agent otonom pilihan Anda.</p>
          </div>

          <!-- 2-Column Grid matching Screenshot 1ea2d361.png -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            ${INTEGRATIONS_DATA.map(item => `
              <div onclick="handleIntegrationClick('${item.id}')" class="surface-card hover:border-[var(--copper)]/60 rounded-2xl p-5 border border-subtle flex flex-col justify-between transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg cursor-pointer group shadow-sm">
                <div class="flex items-start justify-between gap-3">
                  <div class="flex items-center gap-3.5 min-w-0">
                    ${getIntegrationIcon(item.iconType)}
                    <div class="min-w-0 flex items-center gap-2 flex-wrap">
                      <span class="text-sm sm:text-base font-semibold text-main group-hover:text-[var(--copper)] transition tracking-tight">${item.name}</span>
                      <span class="text-xs font-sans px-2.5 py-0.5 rounded-full surface-card-solid border border-subtle text-sec font-medium shrink-0">${item.badge}</span>
                    </div>
                  </div>
                  <span class="text-sec group-hover:text-main transition text-sm font-mono shrink-0 pt-0.5">↗</span>
                </div>
                <p class="text-xs text-sec mt-3.5 leading-relaxed">${item.desc}</p>
              </div>
            `).join('')}
          </div>
        </div>
      `;
      return renderConsoleLayout('Integrations', 'Integrations', html);
    }

    function renderHermesWizardPage() {
      const cmd = `curl -fsSL https://opencsn.com/api/public/integrations/hermes/install.sh | bash -s -- --server https://opencsn.com --token ${state.hermesToken}`;
      const html = `
        <div class="flex items-center justify-between">
          <div>
            <h1 class="font-serif font-medium text-h2 text-main">Hermes Agent</h1>
            <p class="text-xs text-sec mt-1">Install Hermes on your server, wired to your OpenCSN key and models.</p>
          </div>
          <button onclick="navigate('/integrations')" class="surface-card px-3.5 py-2 rounded-xl text-xs text-sec">← Integrations</button>
        </div>
        <div class="surface-card rounded-2xl p-6 space-y-4 text-xs">
          <div class="font-semibold text-main text-sm">Run the installer</div>
          <pre class="surface-card-solid rounded-xl p-4 font-mono text-xs text-main overflow-x-auto border border-subtle">${cmd}</pre>
          <div class="flex items-center justify-between">
            <button data-copy="${cmd}" onclick="copyFromAttr(this)" class="bg-[#c87a51] text-[#14110f] font-semibold px-4 py-2 rounded-xl">${ico('copy')} Copy command</button>
            <button onclick="regenerateHermesToken()" class="surface-card px-3 py-2 rounded-xl text-main">↻ New command</button>
          </div>
        </div>
      `;
      return renderConsoleLayout('Hermes Agent', 'Integrations › Hermes Agent', html);
    }

    function regenerateHermesToken() {
      state.hermesToken = 'smi_inst_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      render();
    }

    function renderSubscriptionPage() {
      const allPlans = [
        { id: 'go', name: 'GO', badge: ico('zap') + ' Cheapest', price: '$2', period: '/14 days', inc: '$10 in usage included', rpm: '50 requests per minute', conc: '3 requests at once', descModal: '$2.00 will be charged to your balance now, for 14 days of 5h $2, total $10 usage.', modelsCount: 13 },
        { id: 'pro', name: 'PRO', badge: ico('flame') + ' Most popular', price: '$12', period: '/21 days', inc: '$48 in usage included', rpm: '150 requests per minute', conc: '10 requests at once', descModal: '$12.00 will be charged to your balance now, for 21 days of 5h $8, total $48 usage.', modelsCount: 21 },
        { id: 'max', name: 'MAX', badge: ico('crown') + ' Best Value', price: '$20', period: '/month', inc: '$75 in usage included', rpm: '250 requests per minute', conc: '20 requests at once', descModal: '$20.00 will be charged to your balance now, for 30 days of total $75 usage.', modelsCount: 27 },
        { id: 'max5x', name: 'MAX 5X', badge: ico('rocket') + ' 5x Power', price: '$100', period: '/month', inc: '$375 in usage included', rpm: '1,250 requests per minute', conc: '50 requests at once', descModal: '$100.00 will be charged to your balance now, for 30 days of total $375 usage.', modelsCount: 27 }
      ];
      const html = `
        <div>
          <h1 class="font-serif font-medium text-h2 text-main">Subscription</h1>
          <p class="text-xs text-sec mt-1">Plans you can subscribe to, and the one you are on.</p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          ${allPlans.map(p => `
            <div class="surface-card rounded-2xl p-5 flex flex-col justify-between ${p.id === 'pro' ? 'ring-1 ring-[#c87a51]/60' : ''}">
              <div>
                <div class="flex items-center justify-between">
                  <span class="text-xs font-bold text-main">${p.name}</span>
                  <span class="text-xs px-2 py-0.5 rounded-full bg-[#c87a51]/20 text-[#df956d]">${p.badge}</span>
                </div>
                <div class="mt-3 flex items-baseline gap-1">
                  <span class="font-sans font-bold text-4xl text-main">${p.price}</span>
                  <span class="text-xs text-sec">${p.period}</span>
                </div>
                <div class="text-xs text-[#c87a51] font-medium mt-1">${p.inc}</div>
                <button onclick="openBuyPlanModal('${p.name}', '${p.descModal}')" class="mt-4 w-full bg-[#c87a51] text-[#14110f] font-semibold text-xs py-2.5 px-4 rounded-xl flex items-center justify-between">
                  <span>Subscribe</span><span>→</span>
                </button>
                <ul class="mt-5 pt-4 border-t border-subtle space-y-2 text-xs text-sec">
                  <li>${ico('trending-up')} ${p.rpm}</li>
                  <li>${ico('zap')} ${p.conc}</li>
                </ul>
              </div>
              <button onclick="openPlanTokensModal('${p.name}', ${p.modelsCount})" class="mt-6 w-full surface-card py-2 rounded-lg text-xs text-sec hover:text-main">Models and token estimate</button>
            </div>
          `).join('')}
        </div>
      `;
      return renderConsoleLayout('Subscription', 'Subscription', html);
    }

    function setBillingAmount(amt) {
      state.billingAmount = amt;
      render();
    }

    function handleBillingPayment(type) {
      const amt = state.billingAmount || 25;
      const invId = Math.random().toString(16).substring(2, 10);
      const fullId = invId + '-c' + Math.random().toString(16).substring(2, 6) + '-4fe0-93bf-' + Math.random().toString(16).substring(2, 14);
      const newInv = {
        id: invId,
        created: 'Just now',
        amount: amt,
        status: 'Pending',
        credited: 'Pending confirmation',
        gateway: type === 'crypto' ? 'OxaPay Crypto' : 'PayBridge QRIS',
        fullId: fullId
      };
      if (!state.invoices) state.invoices = [];
      state.invoices.unshift(newInv);
      openInvoiceDetailsModal(invId);
      render();
    }

    function mapApiKey(item) {
      const status = item.status || (item.active === false ? 'blocked' : 'active');
      return {
        id: item.id,
        name: item.name,
        masked: item.masked,
        created: item.created || 'Today',
        lastUsed: item.lastUsed || 'Never',
        spend: item.spend || '0',
        limit: item.limit,
        models: Array.isArray(item.models) ? item.models : [],
        expiresAt: item.expiresAt || null,
        status,
        active: status === 'active',
        maxSpend: item.limit !== null && item.limit !== undefined ? '$' + item.limit : 'Unlimited',
        expires: item.expiresAt ? fmtDate(item.expiresAt, { year: 'numeric', month: 'short', day: 'numeric' }) : t('keys.neverExpires'),
        full: item.full
      };
    }

    async function loadApiKeys() {
      if (!state.user) return;
      try {
        const res = await apiFetch('/api/keys');
        if (!res.ok) return;
        const list = await res.json();
        if (!Array.isArray(list)) return;
        state.apiKeys = list.map(mapApiKey);
        if (state.apiKeys.length > 0 && !state.apiKeys.find(k => k.id === state.selectedQuickstartKey)) {
          state.selectedQuickstartKey = state.apiKeys[0].id;
        }
        if (state.apiKeys.length === 0) {
          state.selectedQuickstartKey = null;
        }
        render();
      } catch (e) {
        console.warn('[OpenCSN] API keys sync error:', e);
      }
    }

    async function loadBillingData() {
      if (!state.user) return;
      try {
        const [summaryRes, invoicesRes] = await Promise.all([
          apiFetch('/api/billing/summary').then(r => r.ok ? r.json() : null).catch(() => null),
          apiFetch('/api/billing/invoices').then(r => r.ok ? r.json() : null).catch(() => null),
        ]);
        if (summaryRes) {
          state.billingBalance = summaryRes.balance;
          state.billingSummary = summaryRes;
          if (state.user) {
            state.user.balance = summaryRes.balance;
          }
        }
        if (Array.isArray(invoicesRes)) {
          state.invoices = invoicesRes;
        }
      } catch (e) {
        console.warn('[Billing] loadBillingData error:', e);
      }
    }

    async function loadDashboardData() {
      if (!state.user) return;
      try {
        const res = await apiFetch('/api/dashboard');
        if (!res.ok) return;
        state.dashboardData = await res.json();
        render();
      } catch (e) {
        console.warn('[Dashboard] loadDashboardData error:', e);
      }
    }

    async function loadUsageData() {
      if (!state.user) return;
      try {
        const range = state.usageRange || '30d';
        const res = await apiFetch(`/api/usage?range=${encodeURIComponent(range)}`);
        if (!res.ok) return;
        const data = await res.json();
        state.usageRecords = data.makers || [];
        state.usageSummary = data.summary || null;
        render();
      } catch (e) {
        console.warn('[Usage] loadUsageData error:', e);
      }
    }

    async function loadActivityData() {
      if (!state.user) return;
      try {
        const res = await apiFetch('/api/activity');
        if (!res.ok) return;
        state.activities = await res.json();
        render();
      } catch (e) {
        console.warn('[Activity] loadActivityData error:', e);
      }
    }

    async function loadRequestLogsData() {
      if (!state.user) return;
      try {
        const res = await apiFetch('/api/request-logs?limit=50');
        if (!res.ok) return;
        state.requestLogs = await res.json();
        render();
      } catch (e) {
        console.warn('[RequestLogs] loadRequestLogsData error:', e);
      }
    }

    let sseSource = null;
    let pollFallbackTimer = null;
    let indicatorTicker = null;
    let renderScheduled = false;

    function scheduleRender() {
      if (renderScheduled) return;
      renderScheduled = true;
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => {
          renderScheduled = false;
          render();
        });
      } else {
        setTimeout(() => {
          renderScheduled = false;
          render();
        }, 16);
      }
    }

    function renderRealtimeIndicator(timestamp, stale, connected) {
      const ts = timestamp || state.catalogLastUpdated || 0;
      const isConn = connected !== undefined ? connected : state.catalogConnected;
      const isSt = stale !== undefined ? stale : state.catalogStale;

      const secondsAgo = ts > 0 ? Math.max(0, Math.floor((Date.now() - ts) / 1000)) : null;
      const timeText = secondsAgo === null ? (t('catalog.connecting') || 'Connecting...') :
        secondsAgo === 0 ? (t('catalog.justNow') || 'Updated just now') :
        (t('catalog.updatedAgo', { seconds: secondsAgo }) || `Updated ${secondsAgo}s ago`);

      if (!isConn || isSt) {
        return `<div class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-mono shrink-0">
          <svg class="w-2 h-2 fill-current shrink-0" viewBox="0 0 8 8"><circle cx="4" cy="4" r="3"/></svg>
          <span>${t('catalog.stale') || 'Stale connection'} · ${timeText}</span>
        </div>`;
      }

      return `<div class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-mono shrink-0">
        <span class="relative flex h-2 w-2 shrink-0">
          <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </span>
        <span>${timeText}</span>
      </div>`;
    }

    function updateRealtimeIndicators() {
      const elements = document.querySelectorAll('.realtime-catalog-indicator');
      if (elements.length > 0) {
        elements.forEach(el => {
          const customTs = el.getAttribute('data-timestamp');
          const ts = customTs ? Number(customTs) : (el.closest('.leaderboard-container') ? state.leaderboardLastUpdated : state.catalogLastUpdated);
          el.innerHTML = renderRealtimeIndicator(ts);
        });
      }
    }

    function applyCatalogUpdate(newModels, stale) {
      state.catalogLastUpdated = Date.now();
      state.catalogStale = Boolean(stale);
      state.catalogConnected = true;

      if (!Array.isArray(newModels)) return;

      const currentMap = new Map();
      for (let i = 0; i < MODELS_DATA.length; i++) {
        currentMap.set(MODELS_DATA[i].id, MODELS_DATA[i]);
      }

      const newIds = new Set(newModels.map(m => m.id));

      // 1. Remove models that no longer exist
      for (let i = MODELS_DATA.length - 1; i >= 0; i--) {
        if (!newIds.has(MODELS_DATA[i].id)) {
          MODELS_DATA.splice(i, 1);
        }
      }

      // 2. Update existing models in place or append new models
      for (let i = 0; i < newModels.length; i++) {
        const fresh = newModels[i];
        const existing = currentMap.get(fresh.id);
        if (existing) {
          Object.assign(existing, fresh);
        } else {
          MODELS_DATA.push({ ...fresh });
        }
      }

      // 3. Keep selectedModelId valid
      if (MODELS_DATA.length > 0 && !MODELS_DATA.some(x => x.id === state.selectedModelId)) {
        state.selectedModelId = MODELS_DATA[0].id;
      }

      // 4. Prune leaderboard of any model no longer present in live catalog
      if (Array.isArray(state.leaderboardData)) {
        state.leaderboardData = state.leaderboardData.filter(item => newIds.has(item.id));
      }

      updateRealtimeIndicators();
      scheduleRender();
    }

    function applyLeaderboardUpdate(newLeaderboard) {
      state.leaderboardLastUpdated = Date.now();
      if (Array.isArray(newLeaderboard)) {
        const liveIds = new Set(MODELS_DATA.map(m => m.id));
        state.leaderboardData = newLeaderboard.filter(item => liveIds.size === 0 || liveIds.has(item.id));
      }
      updateRealtimeIndicators();
      if (state.route === '/leaderboard') {
        scheduleRender();
      }
    }

    function startRealtimeStream() {
      if (typeof window === 'undefined' || !window.EventSource) {
        startPollingFallback();
        return;
      }

      if (sseSource) {
        try { sseSource.close(); } catch (e) {}
        sseSource = null;
      }

      try {
        sseSource = new EventSource('/api/catalog/stream');

        sseSource.addEventListener('open', () => {
          state.catalogConnected = true;
          state.catalogStale = false;
          stopPollingFallback();
          updateRealtimeIndicators();
        });

        sseSource.addEventListener('catalog', (e) => {
          try {
            const payload = JSON.parse(e.data);
            applyCatalogUpdate(payload.models, payload.stale);
          } catch (err) {
            console.warn('[SSE] Catalog parse error:', err);
          }
        });

        sseSource.addEventListener('leaderboard', (e) => {
          try {
            const payload = JSON.parse(e.data);
            applyLeaderboardUpdate(payload.leaderboard);
          } catch (err) {
            console.warn('[SSE] Leaderboard parse error:', err);
          }
        });

        sseSource.addEventListener('error', () => {
          state.catalogConnected = false;
          state.catalogStale = true;
          updateRealtimeIndicators();
          startPollingFallback();
        });
      } catch (err) {
        console.warn('[SSE] EventSource init failed, using polling fallback:', err);
        state.catalogConnected = false;
        state.catalogStale = true;
        startPollingFallback();
      }

      if (!indicatorTicker) {
        indicatorTicker = setInterval(() => {
          updateRealtimeIndicators();
        }, 1000);
      }
    }

    function startPollingFallback() {
      if (pollFallbackTimer) return;
      pollCatalogAndLeaderboard();
      pollFallbackTimer = setInterval(pollCatalogAndLeaderboard, 15000);
    }

    function stopPollingFallback() {
      if (pollFallbackTimer) {
        clearInterval(pollFallbackTimer);
        pollFallbackTimer = null;
      }
    }

    async function pollCatalogAndLeaderboard() {
      try {
        const res = await apiFetch('/api/catalog');
        if (res.ok) {
          const data = await res.json();
          const list = data && Array.isArray(data.models) ? data.models : (Array.isArray(data) ? data : []);
          applyCatalogUpdate(list, data ? data.stale : false);
        } else {
          state.catalogStale = true;
          updateRealtimeIndicators();
        }
      } catch (e) {
        state.catalogStale = true;
        updateRealtimeIndicators();
      }

      try {
        const range = state.leaderboardRange || '7d';
        const lbRes = await apiFetch(`/api/leaderboard?range=${encodeURIComponent(range)}`);
        if (lbRes.ok) {
          const lbData = await lbRes.json();
          applyLeaderboardUpdate(lbData);
        }
      } catch (e) {}
    }

    async function loadLeaderboardData() {
      try {
        const range = state.leaderboardRange || '7d';
        const res = await apiFetch(`/api/leaderboard?range=${encodeURIComponent(range)}`);
        if (!res.ok) return;
        const data = await res.json();
        applyLeaderboardUpdate(data);
      } catch (e) {
        console.warn('[Leaderboard] loadLeaderboardData error:', e);
      }
    }

    async function loadStatusData() {
      try {
        const res = await apiFetch('/api/status');
        if (!res.ok) return;
        state.statusData = await res.json();
        render();
      } catch (e) {
        console.warn('[Status] loadStatusData error:', e);
      }
    }

    async function handleRedeemCode() {
      const inp = document.getElementById('billing-promo-input');
      const val = (inp ? inp.value : state.redeemCodeInput || '').trim().toUpperCase();
      if (!val) {
        showToast(t('billing.enterCode') || 'Please enter a redemption code');
        return;
      }
      try {
        const res = await apiFetch('/api/billing/redeem', {
          method: 'POST',
          body: JSON.stringify({ code: val }),
        });
        const data = await res.json();
        if (res.ok && data.success) {
          if (state.user) {
            state.user.balance = data.balance;
          }
          state.billingBalance = data.balance;
          state.redeemCodeInput = '';
          showToast(t('billing.redeemSuccess') || 'Success! Your balance has been updated.');
          await loadBillingData();
          render();
        } else {
          showToast(data.error || 'Invalid or expired redemption code');
        }
      } catch (err) {
        showToast(err.message || 'Failed to redeem code');
      }
    }

    function renderBillingPage() {
      const html = `
        <div class="space-y-6 pb-12">
          <!-- Page Header matching screenshot -->
          <div>
            <h1 class="font-serif font-medium text-h2 text-main">Billing</h1>
            <p class="text-xs text-sec mt-1">Add credit, redeem codes, and review your invoices.</p>
          </div>

          <!-- Main 2-Column Section -->
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
            <!-- Left Card (Available Balance + Spent + Add Credit) -->
            <div class="lg:col-span-8 surface-card rounded-2xl p-6 border border-subtle space-y-6 shadow-sm">
              <!-- Top Balance & Spent Stats -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-6 border-b border-subtle">
                <!-- Available Balance -->
                <div class="flex items-start gap-4">
                  <div class="w-10 h-10 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20 flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                    ${ico('credit-card', 'w-5 h-5')}
                  </div>
                  <div>
                    <div class="text-xs text-sec font-medium">Available balance</div>
                    <div class="text-3xl font-bold font-mono text-[#df956d] mt-1">$${((state.user?.balance ?? state.billingBalance) || 0).toFixed(2)}</div>
                    <div class="text-xs text-mut mt-1">Never expires. Spent per request.</div>
                  </div>
                </div>

                <!-- Spent · last 30 days -->
                <div class="flex items-start gap-4 sm:border-l sm:border-subtle sm:pl-6">
                  <div class="w-10 h-10 rounded-full surface-card-solid border border-subtle text-sec flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                    ${ico('circle-dollar-sign', 'w-5 h-5')}
                  </div>
                  <div>
                    <div class="text-xs text-sec font-medium">Spent · last 30 days</div>
                    <div class="text-2xl font-bold font-mono text-main mt-1.5">$0.00</div>
                    <div class="text-xs text-mut mt-2">Usage, plans and other charges.</div>
                  </div>
                </div>
              </div>

              <!-- Add Credit Section -->
              <div class="space-y-4">
                <div>
                  <div class="text-sm font-semibold text-main">Add credit</div>
                  <div class="text-xs text-mut mt-0.5">Between $1.00 and $500.00 per top-up.</div>
                </div>

                <!-- Quick amount selection pills -->
                <div class="flex items-center gap-2.5">
                  ${[10, 25, 50, 100].map(amt => {
                    const isSel = (state.billingAmount || 25) === amt;
                    return `
                      <button onclick="setBillingAmount(${amt})" class="px-3.5 py-1.5 rounded-full font-mono text-xs transition ${isSel ? 'border border-[#df956d] bg-[#df956d]/10 text-[#df956d] font-semibold shadow-sm' : 'border border-subtle surface-card-solid text-sec hover:text-main'}">
                        $${amt}
                      </button>
                    `;
                  }).join('')}
                </div>

                <!-- Amount Input & Action Buttons -->
                <div class="space-y-1.5">
                  <div class="text-xs text-sec font-medium">Amount (USD)</div>
                  <div class="flex flex-wrap sm:flex-nowrap items-center gap-3">
                    <div class="flex items-center surface-card-solid border border-subtle rounded-xl px-3.5 py-2.5 flex-1 min-w-[140px] text-xs">
                      <span class="text-mut font-mono mr-1.5 font-medium">$</span>
                      <input type="number" min="1" max="500" value="${state.billingAmount || 25}" oninput="state.billingAmount=parseFloat(this.value)||0; render();" class="bg-transparent text-main font-mono text-xs w-full focus:outline-none" />
                    </div>

                    <button onclick="handleBillingPayment('crypto')" class="bg-[#df956d] hover:bg-[#c87a51] text-[#14110f] font-semibold text-xs px-5 py-2.5 rounded-xl transition shadow-md shrink-0">
                      Pay with crypto
                    </button>

                    <button onclick="handleBillingPayment('qris')" class="surface-card-solid hover:border-strong text-main font-semibold text-xs px-5 py-2.5 rounded-xl transition border border-subtle flex items-center gap-2 shrink-0 shadow-sm">
                      ${ico('qr-code', 'w-4 h-4')} <span>Pay with QRIS</span>
                    </button>
                  </div>
                </div>

                <!-- Notice Banner -->
                <div class="rounded-xl bg-inset border border-subtle p-3.5 flex items-start gap-2.5 text-xs text-sec leading-relaxed">
                  <span class="text-mut shrink-0 mt-0.5">ⓘ</span>
                  <div>
                    Payments are processed by <b class="text-main">OxaPay</b> and <b class="text-main">PayBridge</b>. Your balance is credited as soon as the payment is confirmed. Every top-up earns a <b class="text-main font-semibold">+20% credit bonus</b>.
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column Cards -->
            <div class="lg:col-span-4 space-y-5">
              <!-- Card 1: Redeem a code -->
              <div class="surface-card rounded-2xl p-5 border border-subtle space-y-3 shadow-sm">
                <div>
                  <div class="text-sm font-semibold text-main">Redeem a code</div>
                  <div class="text-xs text-sec mt-0.5">Credit is added to your account straight away.</div>
                </div>

                <div class="space-y-1.5 pt-1">
                  <div class="text-xs text-sec font-medium">Redemption code</div>
                  <div class="flex items-center gap-2">
                    <input type="text" id="billing-promo-input" value="${state.redeemCodeInput || ''}" oninput="state.redeemCodeInput=this.value" placeholder="PROMO-CODE" class="flex-1 surface-card-solid rounded-xl px-3.5 py-2 font-mono text-xs text-main border border-subtle focus:outline-none uppercase placeholder:text-mut" />
                    <button onclick="handleRedeemCode()" class="px-4 py-2 rounded-xl surface-card-solid hover:border-strong text-sec hover:text-main text-xs transition border border-subtle font-medium shrink-0 shadow-sm">
                      Redeem
                    </button>
                  </div>
                </div>
              </div>

              <!-- Card 2: Payment not showing up? -->
              <div class="surface-card rounded-2xl p-5 border border-subtle space-y-3 shadow-sm text-xs">
                <div>
                  <div class="text-sm font-semibold text-main">Payment not showing up?</div>
                  <div class="text-xs text-sec mt-0.5 leading-relaxed">Crypto can take a while to confirm. If it has been over an hour, message us with:</div>
                </div>

                <ul class="space-y-1.5 text-sec pl-1 text-xs">
                  <li class="flex items-start gap-1.5">
                    <span class="text-mut">•</span>
                    <span>The invoice ID: open Details on the invoice below</span>
                  </li>
                  <li class="flex items-start gap-1.5">
                    <span class="text-mut">•</span>
                    <span>The transaction hash from your wallet</span>
                  </li>
                  <li class="flex items-start gap-1.5">
                    <span class="text-mut">•</span>
                    <span>The amount and coin you sent</span>
                  </li>
                </ul>

                <div class="pt-2 flex flex-wrap items-center gap-2">
                  <a href="https://t.me/OpenCSN_id" target="_blank" class="surface-card-solid hover:border-strong px-4 py-2 rounded-xl text-sec hover:text-main flex items-center gap-2 border border-subtle transition shadow-sm font-medium">
                    ${ico('send', 'w-3.5 h-3.5')} <span>Telegram</span>
                  </a>
                  <a href="mailto:support@opencsn.com" class="surface-card-solid hover:border-strong px-4 py-2 rounded-xl text-sec hover:text-main flex items-center gap-2 border border-subtle transition shadow-sm font-medium">
                    ${ico('message-square', 'w-3.5 h-3.5')} <span>support@opencsn.com</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Bottom Invoices Section -->
          <div class="space-y-3 pt-2">
            <div>
              <div class="text-base font-semibold text-main">Invoices</div>
              <div class="text-xs text-sec mt-0.5">Every top-up you have started, newest first.</div>
            </div>

            <div class="surface-card rounded-2xl border border-subtle overflow-hidden shadow-sm">
              <div class="overflow-x-auto custom-scrollbar">
                <table class="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr class="border-b border-subtle text-mut font-normal">
                      <th class="py-3 px-4 font-medium">Created</th>
                      <th class="py-3 px-4 font-medium">Invoice</th>
                      <th class="py-3 px-4 font-medium">Amount</th>
                      <th class="py-3 px-4 font-medium">Status</th>
                      <th class="py-3 px-4 font-medium">Credited</th>
                      <th class="py-3 px-4 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-subtle">
                    ${(state.invoices || []).length === 0 ? `
                      <tr>
                        <td colspan="6" class="py-10 text-center text-mut text-xs">Belum ada riwayat invoice atau top-up.</td>
                      </tr>
                    ` : (state.invoices || []).map(inv => `
                      <tr class="hover:bg-pill transition-colors">
                        <td class="py-3.5 px-4 font-mono text-sec">${inv.created}</td>
                        <td class="py-3.5 px-4 font-mono text-sec">${inv.id}</td>
                        <td class="py-3.5 px-4 font-mono font-bold text-main">$${inv.amount.toFixed(2)}</td>
                        <td class="py-3.5 px-4 font-sans">
                          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${inv.status === 'Completed' ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-500 dark:text-emerald-400' : inv.status === 'Pending' ? 'bg-amber-500/15 border-amber-500/30 text-amber-500' : 'bg-black/5 dark:bg-white/5 border-subtle text-mut'}">
                            ${inv.status}
                          </span>
                        </td>
                        <td class="py-3.5 px-4 font-mono text-sec">${inv.credited}</td>
                        <td class="py-3.5 px-4 text-right">
                          <button onclick="openInvoiceDetailsModal('${inv.id}')" class="surface-card-solid hover:border-strong px-3 py-1.5 rounded-lg border border-subtle text-xs text-sec hover:text-main font-medium transition shadow-sm">
                            Details
                          </button>
                        </td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      `;
      return renderConsoleLayout('Billing', 'Billing', html);
    }

    function copyReferralCode(code) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(() => showToast('Copied referral code to clipboard')).catch(() => copyText(code));
      } else {
        copyText(code);
      }
    }

    function copyReferralLink(link) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(link).then(() => showToast('Copied referral link to clipboard')).catch(() => copyText(link));
      } else {
        copyText(link);
      }
    }

    function openCustomizeReferralCodeModal() {
      const root = document.getElementById('modal-root');
      root.innerHTML = `
        <div onclick="if(event.target===this) closeModal()" class="fixed inset-0 z-50 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div class="surface-card-solid rounded-2xl max-w-md w-full p-6 border border-strong space-y-4 shadow-2xl text-xs">
            <div class="flex items-start justify-between">
              <div>
                <h3 class="text-base font-semibold text-main">Customize referral code</h3>
                <p class="text-xs text-sec mt-0.5">Create a branded code for your community or audience.</p>
              </div>
              <button onclick="closeModal()" class="text-sec hover:text-main p-1 rounded-lg surface-card">${ico('x', 'w-4 h-4')}</button>
            </div>

            <div class="space-y-1.5">
              <label class="block text-xs text-sec font-medium">New referral code</label>
              <input type="text" id="custom-ref-input" value="${state.referralCode || ''}" maxlength="16" placeholder="e.g. VIP2026" class="w-full surface-card rounded-xl px-3.5 py-2.5 font-mono text-xs text-main border border-subtle focus:outline-none uppercase" />
              <div class="text-xs text-mut">Only letters and numbers (3–16 characters).</div>
            </div>

            <div class="flex justify-end gap-2 pt-2">
              <button onclick="closeModal()" class="surface-card-solid hover:border-strong px-4 py-2 rounded-xl text-sec hover:text-main border border-subtle transition">Cancel</button>
              <button onclick="saveCustomReferralCode()" class="bg-[#df956d] hover:bg-[#c87a51] text-[#14110f] font-semibold px-4 py-2 rounded-xl transition shadow-sm">Save code</button>
            </div>
          </div>
        </div>
      `;
      setTimeout(() => {
        const inp = document.getElementById('custom-ref-input');
        if (inp) { inp.focus(); inp.select(); }
      }, 50);
    }

    function saveCustomReferralCode() {
      const inp = document.getElementById('custom-ref-input');
      const val = (inp ? inp.value : '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (val.length < 3) {
        showToast('Code must be at least 3 characters');
        return;
      }
      state.referralCode = val;
      if (state.user) {
        state.user.referralCode = val;
        try { localStorage.setItem('opencsn_user', JSON.stringify(state.user)); } catch (e) {}
      }
      closeModal();
      showToast('Referral code updated to ' + val);
      render();
    }

    function renderReferralPage() {
      if (!state.referralCode) {
        state.referralCode = (state.user && state.user.referralCode) ? state.user.referralCode : generateRandomReferralCode();
        if (state.user) {
          state.user.referralCode = state.referralCode;
          try { localStorage.setItem('opencsn_user', JSON.stringify(state.user)); } catch (e) {}
        }
      }
      const code = state.referralCode;
      const origin = typeof window !== 'undefined' ? window.location.origin : 'https://opencsn.com';
      const refLink = origin + '/register?ref=' + code;

      const html = `
        <div class="space-y-6 pb-12">
          <!-- Page Header matching screenshot -->
          <div>
            <h1 class="font-serif font-medium text-h2 text-main">Referral</h1>
            <p class="text-xs text-sec mt-1">Share your code and earn credit when referred accounts top up.</p>
          </div>

          <!-- Card 1: Invite with your link -->
          <div class="surface-card rounded-2xl p-6 border border-subtle space-y-5 shadow-sm">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 class="text-sm font-semibold text-main">Invite with your link</h2>
                <p class="text-xs text-sec mt-0.5">When someone signs up through it and funds their account, you earn credit.</p>
              </div>
              <button onclick="openCustomizeReferralCodeModal()" class="surface-card-solid hover:border-strong px-3.5 py-1.5 rounded-xl text-xs text-sec hover:text-main flex items-center gap-1.5 border border-subtle transition shadow-sm font-medium">
                ${ico('pencil', 'w-3.5 h-3.5')} <span>Customize code</span>
              </button>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
              <!-- Your code column -->
              <div class="md:col-span-5 space-y-1.5">
                <label class="block text-xs text-sec font-medium">Your code</label>
                <div class="surface-card-solid rounded-xl px-4 py-2.5 border border-subtle flex items-center justify-between gap-3 shadow-inner">
                  <span class="font-mono text-base font-bold text-main tracking-wider">${code}</span>
                  <button onclick="copyReferralCode('${code}')" class="surface-card-solid hover:border-strong px-3 py-1.5 rounded-lg border border-subtle text-xs text-sec hover:text-main flex items-center gap-1.5 transition font-medium shadow-sm">
                    ${ico('copy', 'w-3.5 h-3.5')} <span>Copy code</span>
                  </button>
                </div>
              </div>

              <!-- Your link column -->
              <div class="md:col-span-7 space-y-1.5">
                <label class="block text-xs text-sec font-medium">Your link</label>
                <div class="surface-card-solid rounded-xl px-4 py-2.5 border border-subtle flex items-center justify-between gap-3 shadow-inner">
                  <span class="font-mono text-xs text-sec truncate select-all">${refLink}</span>
                  <button onclick="copyReferralLink('${refLink}')" class="bg-[#df956d] hover:bg-[#c87a51] text-[#14110f] font-semibold px-4 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition shadow-sm shrink-0">
                    ${ico('copy', 'w-3.5 h-3.5')} <span>Copy link</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Card 2: What you get -->
          <div class="surface-card rounded-2xl p-6 border border-subtle space-y-3.5 shadow-sm">
            <div>
              <div class="flex items-center gap-2 text-sm font-semibold text-main">
                <span class="text-[var(--copper-light)]">${ico('gift', 'w-4 h-4')}</span>
                <span>What you get</span>
              </div>
              <p class="text-xs text-sec mt-1">Earn 5% of every top-up your friends make. Up to $50.00 per top-up.</p>
            </div>

            <ul class="space-y-2.5 text-xs text-sec pt-1">
              <li class="flex items-center gap-2.5">
                <span class="text-mut">${ico('credit-card', 'w-4 h-4')}</span>
                <span>Paid as credit to your balance the moment their payment clears.</span>
              </li>
              <li class="flex items-center gap-2.5">
                <span class="text-mut">${ico('user-plus', 'w-4 h-4')}</span>
                <span>Unlimited: every friend, every top-up, for as long as the program runs.</span>
              </li>
              <li class="flex items-center gap-2.5">
                <span class="text-mut">${ico('link', 'w-4 h-4')}</span>
                <span>Your friends pay nothing extra. The reward is on us.</span>
              </li>
            </ul>
          </div>

          <!-- Section 3: Three Stat Cards -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- People referred -->
            <div class="surface-card rounded-2xl p-5 border border-subtle flex items-start gap-3.5 shadow-sm">
              <div class="w-10 h-10 rounded-full surface-card-solid border border-subtle text-sec flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                ${ico('users', 'w-5 h-5')}
              </div>
              <div>
                <div class="text-xs text-sec font-medium">People referred</div>
                <div class="text-2xl font-bold font-mono text-main mt-0.5">${state.referralPeopleCount || 0}</div>
                <div class="text-xs text-mut mt-1">Signed up with your code.</div>
              </div>
            </div>

            <!-- Funded accounts -->
            <div class="surface-card rounded-2xl p-5 border border-subtle flex items-start gap-3.5 shadow-sm">
              <div class="w-10 h-10 rounded-full bg-blue-500/10 text-blue-500 dark:text-blue-400 border border-blue-500/20 flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                ${ico('wallet', 'w-5 h-5')}
              </div>
              <div>
                <div class="text-xs text-sec font-medium">Funded accounts</div>
                <div class="text-2xl font-bold font-mono text-main mt-0.5">${state.referralFundedCount || 0}</div>
                <div class="text-xs text-mut mt-1">Completed a paid top-up.</div>
              </div>
            </div>

            <!-- Total reward -->
            <div class="surface-card rounded-2xl p-5 border border-subtle flex items-start gap-3.5 shadow-sm">
              <div class="w-10 h-10 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20 flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                ${ico('circle-dollar-sign', 'w-5 h-5')}
              </div>
              <div>
                <div class="text-xs text-sec font-medium">Total reward</div>
                <div class="text-2xl font-bold font-mono text-[#df956d] mt-0.5">$${(state.referralTotalReward || 0).toFixed(2)}</div>
                <div class="text-xs text-mut mt-1">Credit added to your balance.</div>
              </div>
            </div>
          </div>

          <!-- Section 4: Referral History -->
          <div class="space-y-3 pt-1">
            <div>
              <h3 class="text-base font-semibold text-main">Referral history</h3>
              <p class="text-xs text-sec mt-0.5">Accounts created with your code and rewards earned from them.</p>
            </div>

            <div class="surface-card rounded-2xl border border-subtle p-12 text-center shadow-sm">
              ${(state.referralList && state.referralList.length > 0) ? `
                <div class="overflow-x-auto custom-scrollbar">
                  <table class="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr class="border-b border-subtle text-mut font-normal">
                        <th class="py-3 px-4 font-medium">User ID</th>
                        <th class="py-3 px-4 font-medium">Date</th>
                        <th class="py-3 px-4 font-medium">Top-up Total</th>
                        <th class="py-3 px-4 font-medium">Reward (5%)</th>
                        <th class="py-3 px-4 font-medium text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-subtle">
                      ${state.referralList.map(r => `
                        <tr class="hover:bg-pill transition-colors">
                          <td class="py-3.5 px-4 font-mono text-sec">${r.userId}</td>
                          <td class="py-3.5 px-4 font-mono text-sec">${r.date}</td>
                          <td class="py-3.5 px-4 font-mono text-main">$${r.total.toFixed(2)}</td>
                          <td class="py-3.5 px-4 font-mono font-bold text-[#df956d]">+$${r.reward.toFixed(2)}</td>
                          <td class="py-3.5 px-4 text-right">
                            <span class="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 border border-emerald-500/30 text-emerald-500 dark:text-emerald-400">Credited</span>
                          </td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>
                </div>
              ` : `
                <!-- Empty State matching screenshot -->
                <div class="w-12 h-12 rounded-full surface-card-solid border border-subtle text-mut flex items-center justify-center mx-auto mb-3 shadow-inner">
                  ${ico('users', 'w-6 h-6')}
                </div>
                <div class="text-sm font-semibold text-main">No referrals yet</div>
                <div class="text-xs text-sec mt-1">Nobody has signed up with your link so far.</div>
              `}
            </div>
          </div>
        </div>
      `;
      return renderConsoleLayout('Referral', 'Referral', html);
    }

    function renderAnnouncementsPage() {
      const list = state.announcementsList || [
        { date: '1 Nov 2026', type: 'Promo', title: 'Kompensasi Down', body: 'Semalem sempet down, atas kompensasi tersebut Topup Bonus menjadi 50% sampai jam 12 Siang' },
        { date: '29 Oct 2026', type: 'News', title: 'Gateway Integration Live', body: 'OpenCSN gateway integration with prepaid token balance and zero data retention is now active.' },
        { date: '25 Oct 2026', type: 'News', title: 'Catalog Sync Active', body: 'Catalog synchronization from LiteLLM is active with live pricing and usage metering.' }
      ];
      const html = `
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 class="font-serif font-medium text-h2 text-main">Announcements</h1>
            <p class="text-xs text-sec mt-1">Product news and notices from the OpenCSN team.</p>
          </div>
          <button onclick="navigate('/management'); setManagementTab('announcements');" class="surface-card hover:border-[#c87a51]/50 text-xs px-3.5 py-2 rounded-xl text-[#df956d] flex items-center gap-1.5 transition">
            ${ico('sliders', 'w-3.5 h-3.5')} <span>Manage in Dev Console</span>
          </button>
        </div>
        <div class="space-y-4">
          ${list.map(a => `
            <div class="surface-card rounded-2xl p-5 space-y-1.5 border border-white/5">
              <div class="text-xs font-mono text-mut">${a.date} · <span class="text-[#c87a51] font-semibold">${a.type}</span> ${a.banner ? '<span class="ml-2 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-xs">Pinned Banner</span>' : ''}</div>
              <div class="text-sm font-semibold text-main">${a.title}</div>
              <p class="text-xs text-sec leading-relaxed">${a.body}</p>
            </div>
          `).join('')}
        </div>
      `;
      return renderConsoleLayout('Announcements', 'Announcements', html);
    }

    // DEV CONSOLE PAGES (MANAGEMENT, STRUCTURE, LOGS) & DEVICE MORE SHEET MODAL
    // ============================================================================
    // ============================================================================
    // DEV CONSOLE MANAGEMENT SUITE (ACCOUNTS, PAYMENTS, TERMS, PRIVACY, ANNOUNCEMENT, MODEL AI, SYSTEM)
    // ============================================================================

    function setManagementTab(tab) {
      state.managementTab = tab;
      render();
      if (['accounts', 'payments', 'announcements'].includes(tab)) {
        fetchDevData();
      }
    }

    function selectMgmtModel(modelId) {
      state.selectedModelId = modelId;
      render();
    }

    // --- Model AI Interactive Handlers ---
    function findModel(modelId) {
      return MODELS_DATA.find(x => x.id === modelId);
    }

    function modelStatusKey(m) {
      if (m.status) return m.status;
      return m.active === false ? 'Hidden' : 'Active';
    }

    async function refreshModels() {
      try {
        const res = await apiFetch('/api/catalog');
        if (!res.ok) return;
        const data = await res.json();
        const list = data && Array.isArray(data.models) ? data.models : (Array.isArray(data) ? data : []);
        applyCatalogUpdate(list, data ? data.stale : false);
      } catch (e) {
        console.warn('[OpenCSN] Model catalog sync error:', e);
      }
    }

    async function importModelsFromLiteLLM() {
      try {
        const res = await apiFetch('/api/dev/models/import', { method: 'POST', body: JSON.stringify({}) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          showToast(data.error || t('mgmt.importFailed'));
          return;
        }
        showToast(t('mgmt.importDone', { count: data.imported || 0 }));
        await refreshModels();
      } catch (e) {
        showToast(t('mgmt.importFailed'));
      }
    }

    async function loadModelHistory(modelId) {
      try {
        const res = await apiFetch(`/api/dev/models/${encodeURIComponent(modelId)}/history`);
        if (!res.ok) return;
        const rows = await res.json();
        state.modelHistory = Array.isArray(rows) ? rows : [];
        render();
      } catch (e) {}
    }

    function updateModelField(modelId, field, val) {
      const m = findModel(modelId);
      if (!m) return;
      m[field] = val;
    }

    async function persistModelField(modelId, field, val) {
      const m = findModel(modelId);
      if (!m) return;
      m[field] = val;
      await saveModelAiChanges(modelId);
    }

    async function toggleModelActive(modelId) {
      const m = findModel(modelId);
      if (!m) return;
      const nextStatus = modelStatusKey(m) === 'Active' ? 'Hidden' : 'Active';
      m.status = nextStatus;
      m.active = nextStatus === 'Active';
      render();
      try {
        const res = await apiFetch(`/api/dev/models/${encodeURIComponent(modelId)}`, {
          method: 'PUT',
          body: JSON.stringify({ status: nextStatus })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (data.code === 'PRICE_REQUIRED') {
            showToast(t('mgmt.priceRequired'));
          } else {
            showToast(data.error || t('mgmt.saveFailed'));
          }
          m.status = nextStatus === 'Active' ? 'Hidden' : 'Active';
          m.active = nextStatus !== 'Active';
          render();
          return;
        }
        showToast(nextStatus === 'Active' ? t('mgmt.nowActive', { name: m.name }) : t('mgmt.nowHidden', { name: m.name }));
        await refreshModels();
      } catch (e) {
        showToast(t('mgmt.saveFailed'));
      }
    }

    function updateModelDescription(modelId, desc) {
      const m = findModel(modelId);
      if (!m) return;
      m.description = desc;
      const previewEl = document.getElementById('mgmt-desc-preview');
      if (previewEl) previewEl.textContent = desc;
    }

    function restoreModelVideoDescription(modelId) {
      const m = findModel(modelId);
      if (!m) return;
      m.description = `${m.name} frontier AI routing with high performance, verified benchmark parity, and zero data retention.`;
      showToast(t('mgmt.descRestored', { name: m.name }));
      render();
    }

    function updateModelPrice(modelId, field, val) {
      const m = findModel(modelId);
      if (!m) return;
      const num = Math.max(0, isNaN(val) ? 0 : val);
      m[field] = parseFloat(num.toFixed(6));
      render();
    }

    function stepModelPrice(modelId, field, delta) {
      const m = findModel(modelId);
      if (!m) return;
      const current = m[field] || 0;
      const nextVal = Math.max(0, current + delta);
      m[field] = parseFloat(nextVal.toFixed(6));
      render();
    }

    function applyModelDiscountPreset(modelId, pct) {
      const m = findModel(modelId);
      if (!m) return;
      m.discount = pct;
      if (pct === 0) {
        showToast(t('mgmt.discountReset', { name: m.name }));
      } else {
        showToast(t('mgmt.discountApplied', { pct, name: m.name }));
      }
      render();
    }

    async function saveModelAiChanges(modelId) {
      const m = findModel(modelId);
      if (!m) return;
      const payload = {
        name: m.name,
        provider: m.provider,
        description: m.description || null,
        input_price: Number(m.input || 0),
        output_price: Number(m.output || 0),
        cache_read_price: Number(m.cacheRead || 0),
        cache_write_price: Number(m.cacheWrite || 0),
        cost_input: m.costInput === null || m.costInput === undefined ? null : Number(m.costInput),
        cost_output: m.costOutput === null || m.costOutput === undefined ? null : Number(m.costOutput),
        cost_cache_read: m.costCacheRead === null || m.costCacheRead === undefined ? null : Number(m.costCacheRead),
        cost_cache_write: m.costCacheWrite === null || m.costCacheWrite === undefined ? null : Number(m.costCacheWrite),
        discount_pct: Number(m.discount || 0),
        is_free: !!m.free,
        context: m.context || null,
        max_output: m.maxOutput === null || m.maxOutput === undefined ? null : Number(m.maxOutput),
        vision: !!m.vision,
        reasoning: !!m.reasoning,
        badge: m.badge || null,
        upstream_model: m.upstreamModel || null
      };
      try {
        const res = await apiFetch(`/api/dev/models/${encodeURIComponent(modelId)}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (data.code === 'PRICE_REQUIRED') showToast(t('mgmt.priceRequired'));
          else showToast(data.error || t('mgmt.saveFailed'));
          return;
        }
        showToast(t('mgmt.saved', { name: m.name }));
        await loadModelHistory(modelId);
        await refreshModels();
      } catch (e) {
        showToast(t('mgmt.saveFailed'));
      }
    }

    // --- Database Sync for Dev Console ---
    async function fetchDevData() {
      if (!state.user || !state.user.isDev) return;
      try {
        const [accRes, payRes, annRes, logRes, sysRes] = await Promise.all([
          apiFetch('/api/dev/accounts').then(r => r.ok ? r.json() : null).catch(() => null),
          apiFetch('/api/dev/payments').then(r => r.ok ? r.json() : null).catch(() => null),
          apiFetch('/api/dev/announcements').then(r => r.ok ? r.json() : null).catch(() => null),
          apiFetch('/api/dev/logs').then(r => r.ok ? r.json() : null).catch(() => null),
          apiFetch('/api/dev/system').then(r => r.ok ? r.json() : null).catch(() => null)
        ]);
        let changed = false;
        if (Array.isArray(accRes)) {
          state.accountsList = accRes;
          changed = true;
        }
        if (Array.isArray(payRes)) {
          state.paymentsList = payRes;
          changed = true;
        }
        if (Array.isArray(annRes)) {
          state.announcementsList = annRes;
          changed = true;
        }
        if (Array.isArray(logRes)) {
          state.devLogs = logRes;
          changed = true;
        }
        if (sysRes) {
          state.devSystem = sysRes;
          changed = true;
        }
        if (state.managementTab === 'models') {
          const modelRes = await apiFetch('/api/catalog').then(r => r.ok ? r.json() : null).catch(() => null);
          if (modelRes && Array.isArray(modelRes.models)) {
            applyCatalogUpdate(modelRes.models, modelRes.stale);
            changed = true;
          }
        }
        if (changed) render();
      } catch (e) {
        console.warn('[OpenCSN] Dev data sync error:', e);
      }
    }

    async function handleRunReconcile() {
      showToast('Running reconcile...');
      try {
        const res = await apiFetch('/api/dev/reconcile', { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.ok) {
          showToast('Reconcile completed successfully');
          fetchDevData();
        } else {
          showToast(data.error || 'Reconcile failed');
        }
      } catch (e) {
        showToast('Reconcile request failed');
      }
    }

    // --- Accounts Handlers ---
    async function toggleAccountStatus(accId) {
      const a = state.accountsList.find(x => x.id === accId);
      if (!a) return;
      a.status = a.status === 'Active' ? 'Suspended' : 'Active';
      showToast(`Account ${a.name} is now ${a.status}`);
      render();
      try {
        await apiFetch(`/api/dev/accounts/${encodeURIComponent(accId)}/status`, { method: 'PUT' });
      } catch (e) {}
    }

    async function addAccountBalance(accId, amt) {
      const a = state.accountsList.find(x => x.id === accId);
      if (!a) return;
      const prevBal = a.balance;
      a.balance = parseFloat(Math.max(0, a.balance + amt).toFixed(2));
      const diff = a.balance - prevBal;
      if (diff >= 0) {
        showToast(`Berhasil menambahkan $${diff.toFixed(2)} ke ${a.name} (Saldo: $${a.balance.toFixed(2)})`);
      } else {
        showToast(`Berhasil mengurangi $${Math.abs(diff).toFixed(2)} dari ${a.name} (Saldo: $${a.balance.toFixed(2)})`);
      }
      if (state.user && (state.user.id === a.id || state.user.email === a.email)) {
        state.user.balance = a.balance;
        try { localStorage.setItem('opencsn_user', JSON.stringify(state.user)); } catch (e) {}
      }
      render();
      try {
        await apiFetch(`/api/dev/accounts/${encodeURIComponent(accId)}/balance`, {
          method: 'PUT',
          body: JSON.stringify({ amount: amt })
        });
      } catch (e) {}
    }

    function openCreditModal(accId, mode = 'add') {
      const a = state.accountsList.find(x => x.id === accId);
      if (!a) return;
      const isAdd = mode === 'add';
      const root = document.getElementById('modal-root');
      if (!root) return;

      root.innerHTML = `
        <div onclick="if(event.target===this) closeModal()" class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div class="surface-card-solid rounded-3xl max-w-md w-full p-6 sm:p-7 border border-strong space-y-5 shadow-2xl text-xs">
            <div class="flex items-start justify-between">
              <div>
                <h3 class="text-base font-semibold text-main flex items-center gap-2">
                  <span>${isAdd ? 'Add Credits' : 'Remove Credits'}</span>
                </h3>
                <p class="text-xs text-sec mt-1">${isAdd ? 'Grant custom prepaid credits to this account.' : 'Deduct custom prepaid credits from this account.'}</p>
              </div>
              <button onclick="closeModal()" class="text-sec hover:text-main p-1.5 rounded-lg surface-card transition">${ico('x', 'w-4 h-4')}</button>
            </div>

            <!-- Target User Info -->
            <div class="surface-card rounded-2xl p-4 border border-subtle flex items-center justify-between gap-3">
              <div class="flex items-center gap-3 min-w-0">
                <div class="w-10 h-10 rounded-full bg-[#c87a51]/20 text-[#df956d] font-bold flex items-center justify-center shrink-0 text-xs">
                  ${a.name.split(' ').map(n=>n[0]).join('').substring(0,2)}
                </div>
                <div class="min-w-0">
                  <div class="font-medium text-main text-sm truncate">${a.name}</div>
                  <div class="text-xs text-sec truncate">${a.email}</div>
                </div>
              </div>
              <div class="text-right shrink-0">
                <div class="text-xs text-mut uppercase tracking-wider font-mono">Current Balance</div>
                <div class="text-sm font-mono font-bold text-main mt-0.5">$${a.balance.toFixed(2)}</div>
              </div>
            </div>

            <!-- Custom Amount Input -->
            <div class="space-y-2">
              <label class="block text-xs text-sec font-medium">Custom Amount (USD)</label>
              <div class="relative">
                <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-sec font-mono font-semibold text-sm">$</span>
                <input type="number" id="custom-credit-amount" step="0.01" min="0.01" placeholder="0.00" class="w-full surface-card rounded-xl pl-8 pr-4 py-2.5 font-mono text-sm text-main border border-subtle focus:outline-none focus:border-white/40" />
              </div>
              <!-- Presets -->
              <div class="flex flex-wrap items-center gap-1.5 pt-1">
                ${isAdd ? `
                  <button type="button" onclick="document.getElementById('custom-credit-amount').value='5.00'" class="px-2.5 py-1 rounded-lg surface-card hover:border-white/20 text-xs font-mono text-sec hover:text-main transition">+ $5</button>
                  <button type="button" onclick="document.getElementById('custom-credit-amount').value='10.00'" class="px-2.5 py-1 rounded-lg surface-card hover:border-white/20 text-xs font-mono text-sec hover:text-main transition">+ $10</button>
                  <button type="button" onclick="document.getElementById('custom-credit-amount').value='25.00'" class="px-2.5 py-1 rounded-lg surface-card hover:border-white/20 text-xs font-mono text-sec hover:text-main transition">+ $25</button>
                  <button type="button" onclick="document.getElementById('custom-credit-amount').value='50.00'" class="px-2.5 py-1 rounded-lg surface-card hover:border-white/20 text-xs font-mono text-sec hover:text-main transition">+ $50</button>
                  <button type="button" onclick="document.getElementById('custom-credit-amount').value='100.00'" class="px-2.5 py-1 rounded-lg surface-card hover:border-white/20 text-xs font-mono text-sec hover:text-main transition">+ $100</button>
                ` : `
                  <button type="button" onclick="document.getElementById('custom-credit-amount').value='5.00'" class="px-2.5 py-1 rounded-lg surface-card hover:border-white/20 text-xs font-mono text-sec hover:text-main transition">- $5</button>
                  <button type="button" onclick="document.getElementById('custom-credit-amount').value='10.00'" class="px-2.5 py-1 rounded-lg surface-card hover:border-white/20 text-xs font-mono text-sec hover:text-main transition">- $10</button>
                  <button type="button" onclick="document.getElementById('custom-credit-amount').value='25.00'" class="px-2.5 py-1 rounded-lg surface-card hover:border-white/20 text-xs font-mono text-sec hover:text-main transition">- $25</button>
                  <button type="button" onclick="document.getElementById('custom-credit-amount').value='${a.balance.toFixed(2)}'" class="px-2.5 py-1 rounded-lg surface-card hover:border-white/20 text-xs font-mono text-sec hover:text-main transition">All ($${a.balance.toFixed(2)})</button>
                `}
              </div>
            </div>

            <!-- Optional Reason Note -->
            <div class="space-y-1.5">
              <label class="block text-xs text-sec font-medium">Reason / Internal Note (optional)</label>
              <input type="text" id="custom-credit-reason" placeholder="${isAdd ? 'e.g. Promotional grant, manual topup' : 'e.g. Balance correction, refund recovery'}" class="w-full surface-card rounded-xl px-3.5 py-2 text-xs text-main border border-subtle focus:outline-none focus:border-white/40 font-sans" />
            </div>

            <div class="flex items-center justify-end gap-2.5 pt-3 border-t border-subtle">
              <button onclick="closeModal()" class="surface-card-solid hover:border-strong px-4 py-2.5 rounded-xl text-sec hover:text-main border border-subtle transition font-medium">Cancel</button>
              <button onclick="submitCreditAdjustment('${a.id}', '${mode}')" class="${isAdd ? 'bg-white hover:bg-neutral-200 text-black' : 'bg-rose-600 hover:bg-rose-500 text-white'} font-semibold px-5 py-2.5 rounded-xl transition shadow-sm">
                ${isAdd ? 'Add Credits' : 'Deduct Credits'}
              </button>
            </div>
          </div>
        </div>
      `;
      setTimeout(() => {
        const inp = document.getElementById('custom-credit-amount');
        if (inp) inp.focus();
      }, 50);
    }

    async function submitCreditAdjustment(accId, mode) {
      const a = state.accountsList.find(x => x.id === accId);
      if (!a) return;
      const inp = document.getElementById('custom-credit-amount');
      const val = parseFloat(inp ? inp.value : '0');
      if (isNaN(val) || val <= 0) {
        showToast('Masukkan nominal balance yang valid');
        return;
      }
      const isAdd = mode === 'add';
      const delta = isAdd ? val : -val;
      await addAccountBalance(accId, delta);
      closeModal();
    }

    async function deleteAccount(accId) {
      const idx = state.accountsList.findIndex(x => x.id === accId);
      if (idx !== -1) {
        const name = state.accountsList[idx].name;
        state.accountsList.splice(idx, 1);
        showToast(`Account "${name}" deleted`);
        render();
        try {
          await apiFetch(`/api/dev/accounts/${encodeURIComponent(accId)}`, { method: 'DELETE' });
        } catch (e) {}
      }
    }

    function openCreateAccountModal() {
      const root = document.getElementById('modal-root');
      if (!root) return;
      root.innerHTML = `
        <div class="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div class="surface-card rounded-2xl max-w-md w-full p-6 border border-white/10 space-y-5 shadow-2xl animate-in fade-in zoom-in-95 duration-150">
            <div class="flex items-center justify-between border-b border-subtle pb-3">
              <h3 class="text-base font-semibold text-main">Create Developer Account</h3>
              <button onclick="closeModal()" class="text-sec hover:text-main text-lg">&times;</button>
            </div>
            <div class="space-y-3.5 text-xs">
              <div class="space-y-1">
                <label class="text-sec">Display Name</label>
                <input id="new-acc-name" type="text" placeholder="e.g. Jordan Lee" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none" />
              </div>
              <div class="space-y-1">
                <label class="text-sec">Email Address</label>
                <input id="new-acc-email" type="email" placeholder="jordan@company.com" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none" />
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div class="space-y-1">
                  <label class="text-sec">Account Role</label>
                  <select id="new-acc-role" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle">
                    <option value="Developer">Developer</option>
                    <option value="Pro Developer">Pro Developer</option>
                    <option value="Enterprise">Enterprise</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>
                <div class="space-y-1">
                  <label class="text-sec">Starting Balance ($)</label>
                  <input id="new-acc-balance" type="number" step="5" value="25" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle" />
                </div>
              </div>
            </div>
            <div class="flex items-center justify-end gap-2 pt-2 border-t border-subtle">
              <button onclick="closeModal()" class="surface-card px-4 py-2 rounded-xl text-xs text-sec hover:text-main">Cancel</button>
              <button onclick="submitCreateAccount()" class="bg-[#c87a51] text-[#14110f] font-semibold text-xs px-4 py-2 rounded-xl">Create Account</button>
            </div>
          </div>
        </div>
      `;
    }

    async function submitCreateAccount() {
      const name = (document.getElementById('new-acc-name')?.value || '').trim();
      const email = (document.getElementById('new-acc-email')?.value || '').trim();
      const role = document.getElementById('new-acc-role')?.value || 'Developer';
      const bal = parseFloat(document.getElementById('new-acc-balance')?.value || '0');
      if (!name || !email) {
        showToast('Please enter both name and email');
        return;
      }
      const newId = 'usr_' + Math.random().toString(36).substr(2, 6);
      const newAcc = {
        id: newId,
        name,
        email,
        role,
        status: 'Active',
        balance: isNaN(bal) ? 0 : bal,
        keys: 1,
        joined: 'Just now',
        lastSeen: 'Online'
      };
      state.accountsList.unshift(newAcc);
      closeModal();
      showToast(`Account "${name}" created successfully!`);
      render();
      try {
        await apiFetch('/api/dev/accounts', {
          method: 'POST',
          body: JSON.stringify(newAcc)
        });
      } catch (e) {}
    }

    // --- Payments Handlers ---
    function approvePayment(invId) {
      const p = state.paymentsList.find(x => x.id === invId);
      if (!p) return;
      p.status = 'Completed';
      showToast(`Payment ${invId} approved & customer balance credited!`);
      render();
    }

    function refundPayment(invId) {
      const p = state.paymentsList.find(x => x.id === invId);
      if (!p) return;
      p.status = 'Refunded';
      showToast(`Payment ${invId} has been marked Refunded`);
      render();
    }

    function togglePaymentGateway(gw) {
      if (state.paymentsGateways[gw] !== undefined) {
        state.paymentsGateways[gw] = !state.paymentsGateways[gw];
        showToast(`Gateway ${gw.toUpperCase()} is now ${state.paymentsGateways[gw] ? 'Enabled' : 'Disabled'}`);
        render();
      }
    }

    // --- Terms & Privacy Handlers ---
    function setTermsTab(t) {
      state.termsTab = t;
      render();
    }

    function saveAndPublishTerms() {
      const ver = document.getElementById('terms-version-input')?.value;
      const updated = document.getElementById('terms-date-input')?.value;
      if (ver) state.termsData.version = ver;
      if (updated) state.termsData.lastUpdated = updated;
      showToast('Terms of Service saved & published live to /terms!');
      render();
    }

    function setPrivacyTab(t) {
      state.privacyTab = t;
      render();
    }

    function togglePrivacyZdr() {
      state.privacyData.zeroDataRetention = !state.privacyData.zeroDataRetention;
      showToast(`ZDR Protocol is now ${state.privacyData.zeroDataRetention ? 'Enforced' : 'Disabled'}`);
      render();
    }

    function saveAndPublishPrivacy() {
      const ver = document.getElementById('privacy-version-input')?.value;
      const updated = document.getElementById('privacy-date-input')?.value;
      if (ver) state.privacyData.version = ver;
      if (updated) state.privacyData.lastUpdated = updated;
      showToast('Privacy Policy saved & published live to /privacy!');
      render();
    }

    // --- Announcements Handlers ---
    function toggleAnnouncementBanner() {
      state.announcementBannerEnabled = !state.announcementBannerEnabled;
      showToast(`Global announcement broadcast banner is now ${state.announcementBannerEnabled ? 'Enabled' : 'Disabled'}`);
      render();
    }

    function toggleAnnouncementStatus(id) {
      const a = state.announcementsList.find(x => x.id === id);
      if (!a) return;
      a.active = !a.active;
      showToast(`Announcement "${a.title}" is now ${a.active ? 'Active' : 'Inactive'}`);
      render();
    }

    function toggleAnnouncementPin(id) {
      const a = state.announcementsList.find(x => x.id === id);
      if (!a) return;
      a.banner = !a.banner;
      showToast(`Announcement "${a.title}" banner pin: ${a.banner ? 'ON' : 'OFF'}`);
      render();
    }

    function deleteAnnouncement(id) {
      const idx = state.announcementsList.findIndex(x => x.id === id);
      if (idx !== -1) {
        state.announcementsList.splice(idx, 1);
        showToast('Announcement deleted');
        render();
      }
    }

    function submitNewAnnouncement() {
      const title = (document.getElementById('new-ann-title')?.value || '').trim();
      const body = (document.getElementById('new-ann-body')?.value || '').trim();
      const type = document.getElementById('new-ann-type')?.value || 'News';
      const isBanner = document.getElementById('new-ann-banner')?.checked || false;
      if (!title || !body) {
        showToast('Please provide a title and message body');
        return;
      }
      const newAnn = {
        id: Date.now(),
        date: 'Today',
        type,
        title,
        body,
        banner: isBanner,
        active: true
      };
      state.announcementsList.unshift(newAnn);
      showToast('New announcement published successfully!');
      render();
    }

    // ============================================================================
    // MAIN DEV MANAGEMENT PAGE CONTAINER
    // ============================================================================
    function renderDevManagementPage() {
      const currentTab = state.managementTab || 'accounts';

      const tabs = [
        { id: 'accounts', label: '1. Accounts', icon: 'users', count: state.accountsList.length },
        { id: 'payments', label: '2. Payments', icon: 'credit-card', count: state.paymentsList.length },
        { id: 'terms', label: '3. Terms of Service', icon: 'file-text' },
        { id: 'privacy', label: '4. Privacy Policy', icon: 'shield-check', badge: state.privacyData.zeroDataRetention ? 'ZDR' : '' },
        { id: 'announcements', label: '5. Announcement', icon: 'megaphone', count: state.announcementsList.filter(a => a.active).length },
        { id: 'models', label: '6. Model AI', icon: 'boxes', count: MODELS_DATA.length },
        { id: 'system', label: '7. System Engine', icon: 'sliders' }
      ];

      let tabContentHtml = '';
      if (currentTab === 'accounts') tabContentHtml = renderMgmtAccounts();
      else if (currentTab === 'payments') tabContentHtml = renderMgmtPayments();
      else if (currentTab === 'terms') tabContentHtml = renderMgmtTerms();
      else if (currentTab === 'privacy') tabContentHtml = renderMgmtPrivacy();
      else if (currentTab === 'announcements') tabContentHtml = renderMgmtAnnouncements();
      else if (currentTab === 'models') tabContentHtml = renderMgmtModelAI();
      else tabContentHtml = renderMgmtSystem();

      const html = `
        <div class="space-y-6 pb-12">
          <!-- Management Top Header -->
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div class="flex items-center gap-2">
                <span class="px-2 py-0.5 rounded-full bg-[#c87a51]/20 text-[#df956d] font-mono text-xs uppercase font-semibold">Dev Console Control Center</span>
                <span class="text-mut text-xs">v2.4</span>
              </div>
              <h1 class="font-serif font-medium text-h2 text-main mt-1">Management</h1>
              <p class="text-xs text-sec mt-1 max-w-2xl">Unified control center for user accounts, transaction billing, terms & privacy policies, platform announcements, and AI model orchestration.</p>
            </div>
            <div class="flex items-center gap-2">
              <button onclick="navigate('/models')" class="surface-card hover:border-white/20 text-xs px-3.5 py-2 rounded-xl text-sec hover:text-main flex items-center gap-1.5 transition">
                ${ico('boxes', 'w-3.5 h-3.5')} <span>Public Catalog</span>
              </button>
              <button onclick="showToast('Purging proxy cache...'); setTimeout(()=>showToast('Cache purged successfully'), 600);" class="surface-card hover:border-white/20 text-xs px-3.5 py-2 rounded-xl text-sec hover:text-main flex items-center gap-1.5 transition">
                ${ico('rotate-ccw', 'w-3.5 h-3.5')} <span>Flush Cache</span>
              </button>
              <button onclick="showToast('Reloading configuration...'); setTimeout(()=>showToast('Config reloaded'), 600);" class="bg-[#c87a51] text-[#14110f] font-semibold text-xs px-4 py-2 rounded-xl flex items-center gap-1.5 transition shadow">
                ${ico('zap', 'w-3.5 h-3.5')} <span>Reload Config</span>
              </button>
            </div>
          </div>

          <!-- Navigation Tabs Bar -->
          <div class="surface-card rounded-2xl p-1.5 border border-white/5 flex items-center gap-1 overflow-x-auto custom-scrollbar select-none">
            ${tabs.map(t => {
              const active = currentTab === t.id;
              return `
                <button onclick="setManagementTab('${t.id}')" class="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all ${active ? 'bg-[#c87a51] text-[#14110f] font-semibold shadow-md' : 'text-sec hover:text-main hover:bg-white/5'}">
                  ${ico(t.icon, 'w-3.5 h-3.5')}
                  <span>${t.label}</span>
                  ${t.count !== undefined ? `<span class="px-1.5 py-0.2 rounded-full text-xs font-mono ${active ? 'bg-black/20 text-[#14110f]' : 'bg-white/10 text-sec'}">${t.count}</span>` : ''}
                  ${t.badge ? `<span class="px-1.5 py-0.2 rounded-full text-xs font-mono font-semibold ${active ? 'bg-black/20 text-[#14110f]' : 'bg-emerald-500/20 text-emerald-300'}">${t.badge}</span>` : ''}
                </button>
              `;
            }).join('')}
          </div>

          <!-- Active Tab Content -->
          <div>
            ${tabContentHtml}
          </div>
        </div>
      `;
      return renderConsoleLayout('Dev Management', 'Dev Console › Management', html);
    }

// ============================================================================
    // TAB 1: ACCOUNTS MANAGEMENT
    // ============================================================================
    function renderMgmtAccounts() {
      const q = (state.accountsSearch || '').toLowerCase();
      const roleFilter = state.accountsRoleFilter || 'all';
      const statusFilter = state.accountsStatusFilter || 'all';

      const filtered = state.accountsList.filter(a => {
        if (q && !a.name.toLowerCase().includes(q) && !a.email.toLowerCase().includes(q) && !a.id.toLowerCase().includes(q)) return false;
        if (roleFilter !== 'all' && a.role !== roleFilter) return false;
        if (statusFilter !== 'all' && a.status !== statusFilter) return false;
        return true;
      });

      const totalBalance = state.accountsList.reduce((sum, a) => sum + a.balance, 0);

      return `
        <div class="space-y-6">
          <!-- Metrics Cards -->
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-white/5">
              <div class="text-xs font-mono text-mut uppercase">Total Accounts</div>
              <div class="text-xl sm:text-2xl font-mono font-semibold text-main mt-1.5">${state.accountsList.length}</div>
              <div class="text-xs text-emerald-400 mt-1 flex items-center gap-1">${state.accountsList.length} total registered</div>
            </div>
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-white/5">
              <div class="text-xs font-mono text-mut uppercase">Active Users</div>
              <div class="text-xl sm:text-2xl font-mono font-semibold text-main mt-1.5">${state.accountsList.filter(a => a.status === 'Active').length}</div>
              <div class="text-xs text-sec mt-1">${state.accountsList.length ? Math.round((state.accountsList.filter(a => a.status === 'Active').length / state.accountsList.length) * 100) : 100}% active</div>
            </div>
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-white/5">
              <div class="text-xs font-mono text-mut uppercase">Enterprise Tenants</div>
              <div class="text-xl sm:text-2xl font-mono font-semibold text-[#df956d] mt-1.5">${state.accountsList.filter(a => a.role === 'Enterprise').length}</div>
              <div class="text-xs text-sec mt-1">SLA 99.99% guaranteed</div>
            </div>
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-white/5">
              <div class="text-xs font-mono text-mut uppercase">Credits Circulating</div>
              <div class="text-xl sm:text-2xl font-mono font-semibold text-emerald-400 mt-1.5">$${totalBalance.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
              <div class="text-xs text-sec mt-1">Prepaid developer reserve</div>
            </div>
          </div>

          <!-- Controls Bar -->
          <div class="surface-card rounded-2xl p-4 border border-white/5 flex flex-wrap items-center justify-between gap-3 text-xs">
            <div class="flex items-center gap-2 flex-1 min-w-[240px]">
              <span class="text-mut">${ico('search', 'w-4 h-4')}</span>
              <input type="text" value="${state.accountsSearch}" oninput="state.accountsSearch=this.value; render();" placeholder="Search by name, email, user ID..." class="bg-transparent text-main w-full focus:outline-none" />
            </div>
            <div class="flex items-center gap-2 flex-wrap">
              <select onchange="state.accountsRoleFilter=this.value; render();" class="surface-card-solid rounded-xl px-3 py-1.5 text-main border border-subtle">
                <option value="all" ${roleFilter === 'all' ? 'selected' : ''}>All Roles</option>
                <option value="Admin" ${roleFilter === 'Admin' ? 'selected' : ''}>Admin</option>
                <option value="Enterprise" ${roleFilter === 'Enterprise' ? 'selected' : ''}>Enterprise</option>
                <option value="Pro Developer" ${roleFilter === 'Pro Developer' ? 'selected' : ''}>Pro Developer</option>
                <option value="Developer" ${roleFilter === 'Developer' ? 'selected' : ''}>Developer</option>
                <option value="Free Tier" ${roleFilter === 'Free Tier' ? 'selected' : ''}>Free Tier</option>
              </select>
              <select onchange="state.accountsStatusFilter=this.value; render();" class="surface-card-solid rounded-xl px-3 py-1.5 text-main border border-subtle">
                <option value="all" ${statusFilter === 'all' ? 'selected' : ''}>All Statuses</option>
                <option value="Active" ${statusFilter === 'Active' ? 'selected' : ''}>Active</option>
                <option value="Suspended" ${statusFilter === 'Suspended' ? 'selected' : ''}>Suspended</option>
              </select>
              <button onclick="openCreateAccountModal()" class="bg-[#c87a51] text-[#14110f] font-semibold px-4 py-2 rounded-xl flex items-center gap-1.5 shadow transition">
                ${ico('plus', 'w-3.5 h-3.5')} <span>New Account</span>
              </button>
            </div>
          </div>

          <!-- Accounts Table -->
          <div class="surface-card rounded-2xl border border-white/5 overflow-hidden">
            <div class="overflow-x-auto">
              <table class="w-full text-xs text-left">
                <thead>
                  <tr class="border-b border-subtle text-mut text-xs bg-white/[0.02]">
                    <th class="py-3 px-4 font-medium">User Profile</th>
                    <th class="py-3 px-3 font-medium">Role</th>
                    <th class="py-3 px-3 font-medium">Status</th>
                    <th class="py-3 px-3 font-medium">Prepaid Balance</th>
                    <th class="py-3 px-3 font-medium">API Keys</th>
                    <th class="py-3 px-3 font-medium">Activity</th>
                    <th class="py-3 px-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-white/5 text-sec">
                  ${filtered.length === 0 ? `
                    <tr><td colspan="7" class="py-8 text-center text-mut">No accounts match your query.</td></tr>
                  ` : filtered.map(a => `
                    <tr class="hover:bg-white/[0.02] transition">
                      <td class="py-3.5 px-4 font-sans text-main">
                        <div class="flex items-center gap-2.5">
                          <div class="w-8 h-8 rounded-full bg-[#c87a51]/20 text-[#df956d] font-semibold flex items-center justify-center shrink-0 text-xs">
                            ${a.name.split(' ').map(n=>n[0]).join('').substring(0,2)}
                          </div>
                          <div class="min-w-0">
                            <div class="font-medium truncate text-main">${a.name}</div>
                            <div class="text-xs text-mut truncate">${a.email} · <span class="font-mono text-xs">${a.id}</span></div>
                          </div>
                        </div>
                      </td>
                      <td class="py-3.5 px-3">
                        <span class="px-2 py-0.5 rounded-full text-xs font-mono font-medium ${
                          a.role === 'Admin' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' :
                          a.role === 'Enterprise' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                          a.role === 'Pro Developer' ? 'bg-[#c87a51]/20 text-[#df956d] border border-[#c87a51]/30' :
                          'bg-white/10 text-sec border border-white/5'
                        }">${a.role}</span>
                      </td>
                      <td class="py-3.5 px-3">
                        <button onclick="toggleAccountStatus('${a.id}')" class="px-2.5 py-0.5 rounded-full text-xs font-mono font-medium flex items-center gap-1.5 transition ${
                          a.status === 'Active' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25' :
                          'bg-rose-500/15 text-rose-400 border border-rose-500/30 hover:bg-rose-500/25'
                        }">
                          <span class="w-1.5 h-1.5 rounded-full ${a.status === 'Active' ? 'bg-emerald-400' : 'bg-rose-400'}"></span>
                          <span>${a.status}</span>
                        </button>
                      </td>
                      <td class="py-3.5 px-3 font-mono">
                        <div class="flex items-center gap-1.5">
                          <span class="font-semibold text-main">$${a.balance.toFixed(2)}</span>
                          <button onclick="openCreditModal('${a.id}', 'add')" class="text-xs px-1.5 py-0.5 rounded surface-card border border-subtle text-[#df956d] hover:text-white transition" title="Add custom credit">+ Credit</button>
                          <button onclick="openCreditModal('${a.id}', 'remove')" class="text-xs px-1.5 py-0.5 rounded surface-card border border-subtle text-rose-400 hover:text-rose-300 transition" title="Remove custom credit">- Deduct</button>
                        </div>
                      </td>
                      <td class="py-3.5 px-3 font-mono text-mut">${a.keys} keys</td>
                      <td class="py-3.5 px-3 text-xs text-mut">
                        <div>${a.lastSeen}</div>
                        <div class="text-xs opacity-70">Joined ${a.joined}</div>
                      </td>
                      <td class="py-3.5 px-4 text-right">
                        <div class="flex items-center justify-end gap-1.5">
                          <button onclick="openCreditModal('${a.id}', 'add')" class="surface-card hover:border-white/20 px-2 py-1 rounded text-xs text-sec hover:text-main transition font-medium" title="Add custom credits">+ Credit</button>
                          <button onclick="openCreditModal('${a.id}', 'remove')" class="surface-card hover:border-rose-500/30 px-2 py-1 rounded text-xs text-rose-400 hover:text-rose-300 transition font-medium" title="Remove custom credits">- Credit</button>
                          <button onclick="deleteAccount('${a.id}')" class="surface-card hover:border-rose-500/40 px-2 py-1 rounded text-xs text-rose-400 hover:text-rose-300 transition" title="Remove account">Delete</button>
                        </div>
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      `;
    }

    // ============================================================================
    // TAB 2: PAYMENTS MANAGEMENT
    // ============================================================================
    function renderMgmtPayments() {
      const q = (state.paymentsSearch || '').toLowerCase();
      const gwFilter = state.paymentsGatewayFilter || 'all';
      const stFilter = state.paymentsStatusFilter || 'all';

      const filtered = state.paymentsList.filter(p => {
        if (q && !p.id.toLowerCase().includes(q) && !p.customer.toLowerCase().includes(q) && !p.email.toLowerCase().includes(q)) return false;
        if (gwFilter !== 'all' && p.gateway !== gwFilter) return false;
        if (stFilter !== 'all' && p.status !== stFilter) return false;
        return true;
      });

      const totalRevenue = state.paymentsList.filter(p => p.status === 'Completed').reduce((sum, p) => sum + p.amount, 0);

      return `
        <div class="space-y-6">
          <!-- Financial Metrics Cards -->
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-white/5">
              <div class="text-xs font-mono text-mut uppercase">30-Day Gross Volume</div>
              <div class="text-xl sm:text-2xl font-mono font-semibold text-main mt-1.5">$${(totalRevenue + 67250).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
              <div class="text-xs text-emerald-400 mt-1 flex items-center gap-1">+24.8% vs last month</div>
            </div>
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-white/5">
              <div class="text-xs font-mono text-mut uppercase">Completed Orders</div>
              <div class="text-xl sm:text-2xl font-mono font-semibold text-emerald-400 mt-1.5">1,482</div>
              <div class="text-xs text-sec mt-1">99.4% settlement rate</div>
            </div>
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-white/5">
              <div class="text-xs font-mono text-mut uppercase">Pending Settlement</div>
              <div class="text-xl sm:text-2xl font-mono font-semibold text-amber-400 mt-1.5">$325.00</div>
              <div class="text-xs text-sec mt-1">2 orders awaiting confirmation</div>
            </div>
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-white/5">
              <div class="text-xs font-mono text-mut uppercase">Promo Bonus Disbursed</div>
              <div class="text-xl sm:text-2xl font-mono font-semibold text-[#df956d] mt-1.5">$18,420</div>
              <div class="text-xs text-sec mt-1">30% bonus promotion active</div>
            </div>
          </div>

          <!-- Gateway Toggle Bar -->
          <div class="surface-card rounded-2xl p-4 border border-white/5 space-y-3">
            <div class="flex items-center justify-between text-xs">
              <div class="font-semibold text-main">Payment Gateways & Orchestration</div>
              <span class="text-mut text-xs">Toggle live merchant rails</span>
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <button onclick="togglePaymentGateway('stripe')" class="p-3 rounded-xl border flex items-center justify-between transition ${state.paymentsGateways.stripe ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'surface-card-solid border-white/5 text-mut'}">
                <span class="font-medium">Stripe (Card/ApplePay)</span>
                <span class="font-mono text-xs font-semibold">${state.paymentsGateways.stripe ? 'ON' : 'OFF'}</span>
              </button>
              <button onclick="togglePaymentGateway('midtrans')" class="p-3 rounded-xl border flex items-center justify-between transition ${state.paymentsGateways.midtrans ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'surface-card-solid border-white/5 text-mut'}">
                <span class="font-medium">Midtrans QRIS</span>
                <span class="font-mono text-xs font-semibold">${state.paymentsGateways.midtrans ? 'ON' : 'OFF'}</span>
              </button>
              <button onclick="togglePaymentGateway('crypto')" class="p-3 rounded-xl border flex items-center justify-between transition ${state.paymentsGateways.crypto ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'surface-card-solid border-white/5 text-mut'}">
                <span class="font-medium">Crypto USDT (OxaPay)</span>
                <span class="font-mono text-xs font-semibold">${state.paymentsGateways.crypto ? 'ON' : 'OFF'}</span>
              </button>
              <button onclick="togglePaymentGateway('wire')" class="p-3 rounded-xl border flex items-center justify-between transition ${state.paymentsGateways.wire ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'surface-card-solid border-white/5 text-mut'}">
                <span class="font-medium">Bank Wire / ACH</span>
                <span class="font-mono text-xs font-semibold">${state.paymentsGateways.wire ? 'ON' : 'OFF'}</span>
              </button>
            </div>
          </div>

          <!-- Controls Bar -->
          <div class="surface-card rounded-2xl p-4 border border-white/5 flex flex-wrap items-center justify-between gap-3 text-xs">
            <div class="flex items-center gap-2 flex-1 min-w-[240px]">
              <span class="text-mut">${ico('search', 'w-4 h-4')}</span>
              <input type="text" value="${state.paymentsSearch}" oninput="state.paymentsSearch=this.value; render();" placeholder="Search invoice, customer, transaction ID..." class="bg-transparent text-main w-full focus:outline-none" />
            </div>
            <div class="flex items-center gap-2 flex-wrap">
              <select onchange="state.paymentsGatewayFilter=this.value; render();" class="surface-card-solid rounded-xl px-3 py-1.5 text-main border border-subtle">
                <option value="all" ${gwFilter === 'all' ? 'selected' : ''}>All Gateways</option>
                <option value="Stripe" ${gwFilter === 'Stripe' ? 'selected' : ''}>Stripe</option>
                <option value="Midtrans QRIS" ${gwFilter === 'Midtrans QRIS' ? 'selected' : ''}>Midtrans QRIS</option>
                <option value="Crypto USDT" ${gwFilter === 'Crypto USDT' ? 'selected' : ''}>Crypto USDT</option>
                <option value="Bank Wire" ${gwFilter === 'Bank Wire' ? 'selected' : ''}>Bank Wire</option>
              </select>
              <select onchange="state.paymentsStatusFilter=this.value; render();" class="surface-card-solid rounded-xl px-3 py-1.5 text-main border border-subtle">
                <option value="all" ${stFilter === 'all' ? 'selected' : ''}>All Statuses</option>
                <option value="Completed" ${stFilter === 'Completed' ? 'selected' : ''}>Completed</option>
                <option value="Pending" ${stFilter === 'Pending' ? 'selected' : ''}>Pending</option>
                <option value="Refunded" ${stFilter === 'Refunded' ? 'selected' : ''}>Refunded</option>
              </select>
              <button onclick="showToast('Exporting transaction report as CSV...'); setTimeout(()=>showToast('Report generated successfully'), 600);" class="surface-card hover:border-white/20 text-xs px-3.5 py-2 rounded-xl text-sec hover:text-main flex items-center gap-1.5">
                ${ico('download', 'w-3.5 h-3.5')} <span>Export CSV</span>
              </button>
            </div>
          </div>

          <!-- Payments Table -->
          <div class="surface-card rounded-2xl border border-white/5 overflow-hidden">
            <div class="overflow-x-auto">
              <table class="w-full text-xs text-left">
                <thead>
                  <tr class="border-b border-subtle text-mut text-xs bg-white/[0.02]">
                    <th class="py-3 px-4 font-medium">Invoice ID</th>
                    <th class="py-3 px-3 font-medium">Customer</th>
                    <th class="py-3 px-3 font-medium">Gateway</th>
                    <th class="py-3 px-3 font-medium">Amount</th>
                    <th class="py-3 px-3 font-medium">Bonus Promo</th>
                    <th class="py-3 px-3 font-medium">Net Credited</th>
                    <th class="py-3 px-3 font-medium">Date & Time</th>
                    <th class="py-3 px-3 font-medium">Status</th>
                    <th class="py-3 px-4 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-white/5 text-sec font-mono text-xs">
                  ${filtered.length === 0 ? `
                    <tr><td colspan="9" class="py-8 text-center text-mut font-sans">No payment records found.</td></tr>
                  ` : filtered.map(p => `
                    <tr class="hover:bg-white/[0.02] transition">
                      <td class="py-3 px-4 font-semibold text-main">${p.id}</td>
                      <td class="py-3 px-3 font-sans">
                        <div class="font-medium text-main">${p.customer}</div>
                        <div class="text-xs text-mut">${p.email}</div>
                      </td>
                      <td class="py-3 px-3 font-sans">
                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full surface-card text-xs text-sec">
                          ${p.gateway}
                        </span>
                      </td>
                      <td class="py-3 px-3 font-semibold text-main">$${p.amount.toFixed(2)}</td>
                      <td class="py-3 px-3 text-emerald-400">+ $${p.bonus.toFixed(2)}</td>
                      <td class="py-3 px-3 font-semibold text-[#df956d]">$${p.net.toFixed(2)}</td>
                      <td class="py-3 px-3 text-xs text-mut">${p.date}</td>
                      <td class="py-3 px-3">
                        <span class="px-2 py-0.5 rounded-full text-xs font-sans font-medium ${
                          p.status === 'Completed' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' :
                          p.status === 'Pending' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' :
                          'bg-purple-500/15 text-purple-400 border border-purple-500/30'
                        }">${p.status}</span>
                      </td>
                      <td class="py-3 px-4 text-right font-sans">
                        ${p.status === 'Pending' ? `
                          <button onclick="approvePayment('${p.id}')" class="px-2.5 py-1 rounded bg-emerald-500 text-black font-semibold text-xs hover:bg-emerald-400">Approve</button>
                        ` : p.status === 'Completed' ? `
                          <button onclick="refundPayment('${p.id}')" class="surface-card hover:border-purple-500/40 text-purple-300 px-2.5 py-1 rounded text-xs">Refund</button>
                        ` : `
                          <span class="text-mut text-xs">-</span>
                        `}
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      `;
    }

    // ============================================================================
    // TAB 3: TERMS OF SERVICE MANAGEMENT
    // ============================================================================
    function renderMgmtTerms() {
      const isPreview = state.termsTab === 'preview';
      const t = state.termsData;

      return `
        <div class="space-y-6">
          <!-- Document Status Header -->
          <div class="surface-card rounded-2xl p-5 border border-white/5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div class="flex items-center gap-2">
                <span class="px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-xs font-mono font-medium">Live on /terms</span>
                <span class="font-mono text-xs text-mut">Version: ${t.version}</span>
              </div>
              <h2 class="text-lg font-semibold text-main mt-1">Terms of Service Editor</h2>
              <p class="text-xs text-sec mt-0.5">Edit and publish legally binding platform terms across all public OpenCSN interfaces.</p>
            </div>
            <div class="flex items-center gap-2">
              <div class="surface-card-solid rounded-xl p-1 flex items-center text-xs">
                <button onclick="setTermsTab('edit')" class="px-3 py-1.5 rounded-lg transition ${!isPreview ? 'bg-[#c87a51] text-[#14110f] font-semibold' : 'text-sec hover:text-main'}">Edit Clauses</button>
                <button onclick="setTermsTab('preview')" class="px-3 py-1.5 rounded-lg transition ${isPreview ? 'bg-[#c87a51] text-[#14110f] font-semibold' : 'text-sec hover:text-main'}">Live Preview</button>
              </div>
              <button onclick="saveAndPublishTerms()" class="bg-[#c87a51] text-[#14110f] font-semibold text-xs px-4 py-2 rounded-xl flex items-center gap-1.5 shadow">
                ${ico('check', 'w-3.5 h-3.5')} <span>Publish to Live</span>
              </button>
              <button onclick="navigate('/terms')" class="surface-card hover:border-white/20 text-xs px-3 py-2 rounded-xl text-sec hover:text-main">
                View /terms
              </button>
            </div>
          </div>

          ${isPreview ? `
            <!-- Live Preview -->
            <div class="surface-card rounded-2xl p-8 border border-white/10 space-y-6 max-w-3xl mx-auto">
              <div class="text-center space-y-2 border-b border-subtle pb-4">
                <span class="text-xs font-mono px-3 py-1 rounded-full surface-card text-sec">Legal · ${t.version}</span>
                <h1 class="font-serif font-medium text-h1 text-main">${t.title}</h1>
                <div class="text-xs text-mut">Last updated: ${t.lastUpdated}</div>
              </div>
              <div class="space-y-6 text-xs sm:text-sm text-sec leading-relaxed">
                ${t.sections.map(s => `
                  <div>
                    <h3 class="text-base font-semibold text-main mb-1.5">${s.title}</h3>
                    <p>${s.body}</p>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : `
            <!-- Edit Mode -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div class="surface-card rounded-2xl p-5 border border-white/5 space-y-4 text-xs h-fit">
                <h3 class="font-semibold text-main text-sm">Document Metadata</h3>
                <div class="space-y-1.5">
                  <label class="text-sec">Document Title</label>
                  <input id="terms-title-input" type="text" value="${t.title}" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-sec">Version Tag</label>
                  <input id="terms-version-input" type="text" value="${t.version}" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-sec">Effective / Last Updated Date</label>
                  <input id="terms-date-input" type="text" value="${t.lastUpdated}" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none" />
                </div>
                <div class="pt-3 border-t border-subtle space-y-2 text-xs text-mut">
                  <div>* Changes made here are saved directly into the runtime state and immediately update the public <code>/terms</code> route upon clicking Publish.</div>
                </div>
              </div>

              <!-- Clauses Editor List -->
              <div class="lg:col-span-2 space-y-4">
                ${t.sections.map((s, idx) => `
                  <div class="surface-card rounded-2xl p-5 border border-white/5 space-y-2.5">
                    <div class="flex items-center justify-between text-xs">
                      <span class="font-semibold text-main">Section ${idx + 1}</span>
                      <span class="font-mono text-xs text-mut">ID: sec_${s.id}</span>
                    </div>
                    <input type="text" value="${s.title}" oninput="state.termsData.sections[${idx}].title = this.value;" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main text-xs font-semibold border border-subtle focus:border-[#c87a51] focus:outline-none" />
                    <textarea rows="3" oninput="state.termsData.sections[${idx}].body = this.value;" class="w-full surface-card-solid rounded-xl p-3 text-sec text-xs border border-subtle focus:border-[#c87a51] focus:outline-none leading-relaxed">${s.body}</textarea>
                  </div>
                `).join('')}
              </div>
            </div>
          `}
        </div>
      `;
    }

    // ============================================================================
    // TAB 4: PRIVACY POLICY MANAGEMENT
    // ============================================================================
    function renderMgmtPrivacy() {
      const isPreview = state.privacyTab === 'preview';
      const p = state.privacyData;

      return `
        <div class="space-y-6">
          <!-- Document Status Header -->
          <div class="surface-card rounded-2xl p-5 border border-white/5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div class="flex items-center gap-2">
                <span class="px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-xs font-mono font-medium">Live on /privacy</span>
                <span class="font-mono text-xs text-mut">Version: ${p.version}</span>
                <span class="px-2 py-0.5 rounded-full text-xs font-mono ${p.zeroDataRetention ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300'} font-semibold">
                  ZDR Protocol: ${p.zeroDataRetention ? 'Enforced' : 'Disabled'}
                </span>
              </div>
              <h2 class="text-lg font-semibold text-main mt-1">Privacy Policy & ZDR Architecture</h2>
              <p class="text-xs text-sec mt-0.5">Control Zero Data Retention policies, Indonesian PDP / GDPR disclosure, and telemetry logging parameters.</p>
            </div>
            <div class="flex items-center gap-2">
              <div class="surface-card-solid rounded-xl p-1 flex items-center text-xs">
                <button onclick="setPrivacyTab('edit')" class="px-3 py-1.5 rounded-lg transition ${!isPreview ? 'bg-[#c87a51] text-[#14110f] font-semibold' : 'text-sec hover:text-main'}">Edit Clauses</button>
                <button onclick="setPrivacyTab('preview')" class="px-3 py-1.5 rounded-lg transition ${isPreview ? 'bg-[#c87a51] text-[#14110f] font-semibold' : 'text-sec hover:text-main'}">Live Preview</button>
              </div>
              <button onclick="saveAndPublishPrivacy()" class="bg-[#c87a51] text-[#14110f] font-semibold text-xs px-4 py-2 rounded-xl flex items-center gap-1.5 shadow">
                ${ico('check', 'w-3.5 h-3.5')} <span>Publish to Live</span>
              </button>
              <button onclick="navigate('/privacy')" class="surface-card hover:border-white/20 text-xs px-3 py-2 rounded-xl text-sec hover:text-main">
                View /privacy
              </button>
            </div>
          </div>

          <!-- ZDR Protocol Master Switch -->
          <div class="surface-card rounded-2xl p-4 border border-white/5 flex items-center justify-between gap-4">
            <div class="space-y-0.5">
              <div class="text-xs font-semibold text-main flex items-center gap-2">
                <span>Zero Data Retention (ZDR) Enforcement Protocol</span>
                <span class="w-2 h-2 rounded-full ${p.zeroDataRetention ? 'bg-emerald-400 animate-pulse' : 'bg-rose-400'}"></span>
              </div>
              <p class="text-xs text-sec">When active, proxy nodes discard inbound prompt bodies and upstream completion streams immediately after delivery. No logs stored.</p>
            </div>
            <button onclick="togglePrivacyZdr()" class="px-4 py-2 rounded-xl text-xs font-semibold font-mono transition ${p.zeroDataRetention ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30' : 'bg-white/10 text-sec hover:bg-white/15'}">
              ${p.zeroDataRetention ? 'ZDR ACTIVE' : 'ZDR PAUSED'}
            </button>
          </div>

          ${isPreview ? `
            <!-- Live Preview -->
            <div class="surface-card rounded-2xl p-8 border border-white/10 space-y-6 max-w-3xl mx-auto">
              <div class="text-center space-y-2 border-b border-subtle pb-4">
                <span class="text-xs font-mono px-3 py-1 rounded-full surface-card text-sec">Legal · ${p.version}</span>
                <h1 class="font-serif font-medium text-h1 text-main">${p.title}</h1>
                <div class="text-xs text-mut">Last updated: ${p.lastUpdated}</div>
              </div>
              ${p.summaryHtml || ''}
              <div class="space-y-6 text-xs sm:text-sm text-sec leading-relaxed">
                ${p.sections.map(s => `
                  <div>
                    <h3 class="text-base font-semibold text-main mb-1.5">${s.title}</h3>
                    <p>${s.body}</p>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : `
            <!-- Edit Mode -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div class="surface-card rounded-2xl p-5 border border-white/5 space-y-4 text-xs h-fit">
                <h3 class="font-semibold text-main text-sm">Policy Metadata</h3>
                <div class="space-y-1.5">
                  <label class="text-sec">Policy Title</label>
                  <input id="privacy-title-input" type="text" value="${p.title}" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-sec">Version Tag</label>
                  <input id="privacy-version-input" type="text" value="${p.version}" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-sec">Last Updated Date</label>
                  <input id="privacy-date-input" type="text" value="${p.lastUpdated}" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none" />
                </div>
                <div class="pt-3 border-t border-subtle text-xs text-mut">
                  <div>Complies with Indonesia UU No. 27/2022 (PDP Law) and international standard TLS 1.3 passthrough.</div>
                </div>
              </div>

              <!-- Clauses Editor List -->
              <div class="lg:col-span-2 space-y-4">
                ${p.sections.map((s, idx) => `
                  <div class="surface-card rounded-2xl p-5 border border-white/5 space-y-2.5">
                    <div class="flex items-center justify-between text-xs">
                      <span class="font-semibold text-main">Section ${idx + 1}</span>
                      <span class="font-mono text-xs text-mut">ID: priv_${s.id}</span>
                    </div>
                    <input type="text" value="${s.title}" oninput="state.privacyData.sections[${idx}].title = this.value;" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main text-xs font-semibold border border-subtle focus:border-[#c87a51] focus:outline-none" />
                    <textarea rows="3" oninput="state.privacyData.sections[${idx}].body = this.value;" class="w-full surface-card-solid rounded-xl p-3 text-sec text-xs border border-subtle focus:border-[#c87a51] focus:outline-none leading-relaxed">${s.body}</textarea>
                  </div>
                `).join('')}
              </div>
            </div>
          `}
        </div>
      `;
    }

    // ============================================================================
    // TAB 5: ANNOUNCEMENT MANAGEMENT
    // ============================================================================
    function renderMgmtAnnouncements() {
      const activeAnn = state.announcementsList.filter(a => a.active);

      return `
        <div class="space-y-6">
          <!-- Top Broadcast Banner Control Card -->
          <div class="surface-card rounded-2xl p-5 border border-white/5 flex flex-wrap items-center justify-between gap-4">
            <div class="space-y-0.5">
              <div class="flex items-center gap-2">
                <span class="text-xs font-semibold text-main">Global Announcement Broadcast Banner</span>
                <span class="w-2 h-2 rounded-full ${state.announcementBannerEnabled ? 'bg-emerald-400' : 'bg-mut'}"></span>
              </div>
              <p class="text-xs text-sec">Display a high-visibility dismissible notice banner across the top of all public and console header pages.</p>
            </div>
            <div class="flex items-center gap-2">
              <button onclick="toggleAnnouncementBanner()" class="px-4 py-2 rounded-xl text-xs font-mono font-semibold transition ${state.announcementBannerEnabled ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-white/10 text-sec'}">
                ${state.announcementBannerEnabled ? 'BANNER ON' : 'BANNER OFF'}
              </button>
              <button onclick="navigate('/announcements')" class="surface-card hover:border-white/20 text-xs px-3.5 py-2 rounded-xl text-sec hover:text-main">
                View /announcements
              </button>
            </div>
          </div>

          <!-- Create Announcement Form -->
          <div class="surface-card rounded-2xl p-6 border border-white/5 space-y-4">
            <div class="border-b border-subtle pb-3">
              <h3 class="text-sm font-semibold text-main">Broadcast New Announcement</h3>
              <p class="text-xs text-sec mt-0.5">Post product news, scheduled maintenance, or promotional top-up bonus notices to users.</p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div class="space-y-1.5 md:col-span-2">
                <label class="text-sec">Announcement Title</label>
                <input id="new-ann-title" type="text" placeholder="e.g. Kompensasi Down & Topup Bonus 50%" class="w-full surface-card-solid rounded-xl px-3.5 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none" />
              </div>
              <div class="space-y-1.5">
                <label class="text-sec">Category Tag</label>
                <select id="new-ann-type" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle">
                  <option value="Promo">Promo</option>
                  <option value="News">News</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Update">Update</option>
                  <option value="Security">Security</option>
                </select>
              </div>
            </div>

            <div class="space-y-1.5 text-xs">
              <label class="text-sec">Message Content</label>
              <textarea id="new-ann-body" rows="3" placeholder="Enter full announcement details visible to users..." class="w-full surface-card-solid rounded-xl p-3.5 text-main border border-subtle focus:border-[#c87a51] focus:outline-none leading-relaxed"></textarea>
            </div>

            <div class="flex items-center justify-between pt-2">
              <label class="flex items-center gap-2 text-xs text-sec cursor-pointer">
                <input id="new-ann-banner" type="checkbox" checked class="accent-[#c87a51] rounded" />
                <span>Pin as prominent broadcast banner on header</span>
              </label>
              <button onclick="submitNewAnnouncement()" class="bg-[#c87a51] text-[#14110f] font-semibold text-xs px-5 py-2 rounded-xl shadow transition">
                Publish Announcement
              </button>
            </div>
          </div>

          <!-- Existing Announcements List -->
          <div class="space-y-3">
            <div class="flex items-center justify-between text-xs px-1">
              <span class="font-semibold text-main">Published Announcements (${state.announcementsList.length})</span>
              <span class="text-mut text-xs">${activeAnn.length} active notices</span>
            </div>

            <div class="space-y-3">
              ${state.announcementsList.map(a => `
                <div class="surface-card rounded-2xl p-5 border border-white/5 space-y-2.5 transition">
                  <div class="flex flex-wrap items-center justify-between gap-2 text-xs">
                    <div class="flex items-center gap-2">
                      <span class="font-mono text-mut text-xs">${a.date}</span>
                      <span class="px-2 py-0.5 rounded-full text-xs font-mono font-medium ${
                        a.type === 'Promo' ? 'bg-[#c87a51]/20 text-[#df956d]' :
                        a.type === 'Security' ? 'bg-rose-500/20 text-rose-300' :
                        a.type === 'Maintenance' ? 'bg-amber-500/20 text-amber-300' :
                        'bg-blue-500/20 text-blue-300'
                      }">${a.type}</span>
                      ${a.banner ? `<span class="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 font-mono text-xs border border-emerald-500/20">Pinned Banner</span>` : ''}
                      ${!a.active ? `<span class="px-2 py-0.5 rounded-full bg-white/10 text-mut font-mono text-xs">Inactive</span>` : ''}
                    </div>
                    <div class="flex items-center gap-2">
                      <button onclick="toggleAnnouncementPin(${a.id})" class="surface-card hover:border-white/20 px-2.5 py-1 rounded text-xs text-sec hover:text-main">
                        ${a.banner ? 'Unpin Banner' : 'Pin to Banner'}
                      </button>
                      <button onclick="toggleAnnouncementStatus(${a.id})" class="surface-card hover:border-white/20 px-2.5 py-1 rounded text-xs ${a.active ? 'text-amber-300' : 'text-emerald-300'}">
                        ${a.active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button onclick="deleteAnnouncement(${a.id})" class="surface-card hover:border-rose-500/30 px-2 py-1 rounded text-xs text-rose-400 hover:text-rose-300">
                        Delete
                      </button>
                    </div>
                  </div>
                  <h4 class="text-sm font-semibold text-main">${a.title}</h4>
                  <p class="text-xs text-sec leading-relaxed">${a.body}</p>
                </div>
              `).join('')}
            </div>
          </div>
        </div>
      `;
    }

    // ============================================================================
    // TAB 6: MODEL AI MANAGEMENT - INTERACTIVE BARS, TOGGLE BAR, & EDITABLE DESCRIPTION
    // ============================================================================
    function marginLabel(price, cost) {
      const p = Number(price) || 0;
      const c = Number(cost) || 0;
      if (p <= 0) return '–';
      const margin = ((p - c) / p) * 100;
      return `${margin.toFixed(1)}%`;
    }

    function modelStatusBadge(m) {
      const status = modelStatusKey(m);
      const cls = status === 'Active'
        ? 'bg-emerald-500/15 text-emerald-400'
        : status === 'Draft'
          ? 'bg-blue-500/15 text-blue-400'
          : 'bg-rose-500/15 text-rose-400';
      return `<span class="px-2 py-0.5 rounded-full text-xs font-mono ${cls}">${t('mgmt.status' + status)}</span>`;
    }

    function priceBarBlock(m, opts) {
      const { field, title, accent, dot, max, step, preview, decimals } = opts;
      const value = Number(m[field] || 0);
      return `
        <div class="surface-card-solid rounded-2xl p-4 sm:p-5 border border-white/5 space-y-3">
          <div class="flex items-center justify-between text-xs">
            <div class="flex items-center gap-2">
              <span class="w-2.5 h-2.5 rounded-full ${dot}"></span>
              <span class="font-semibold text-main">${title}</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="font-mono text-base font-bold ${accent}">$${value.toFixed(decimals)}</span>
              <span class="font-mono text-xs text-mut">/ 1M tokens</span>
            </div>
          </div>
          <div class="space-y-1">
            <input type="range" min="0" max="${max}" step="${step}" value="${value}" oninput="updateModelPrice('${m.id}', '${field}', parseFloat(this.value))" class="w-full ${accent.replace('text-', 'accent-')} h-2.5 bg-white/10 rounded-lg cursor-pointer transition" />
            <div class="flex justify-between text-xs font-mono text-mut">${preview}</div>
          </div>
          <div class="flex flex-wrap items-center justify-between gap-3 pt-1 text-xs">
            <div class="flex items-center gap-1.5">
              <span class="text-mut text-xs">${t('mgmt.directInput')}</span>
              <div class="flex items-center gap-1 surface-card px-2 py-1 rounded-lg border border-subtle">
                <span class="text-mut font-mono">$</span>
                <input type="number" step="${step}" min="0" value="${value}" oninput="updateModelPrice('${m.id}', '${field}', parseFloat(this.value))" class="w-20 bg-transparent text-main font-mono font-semibold focus:outline-none" />
              </div>
            </div>
            <div class="flex items-center gap-1 font-mono text-xs">
              <button onclick="stepModelPrice('${m.id}', '${field}', -${step * 10})" class="px-2 py-1 rounded surface-card text-sec hover:text-main">- $${(step * 10).toFixed(3)}</button>
              <button onclick="stepModelPrice('${m.id}', '${field}', ${step})" class="px-2 py-1 rounded surface-card ${accent} hover:text-white">+ $${step.toFixed(3)}</button>
            </div>
          </div>
        </div>`;
    }

    function costInputRow(m, field, labelKey) {
      const value = m[field];
      return `
        <div class="flex items-center justify-between gap-2 text-xs">
          <span class="text-sec">${t(labelKey)}</span>
          <div class="flex items-center gap-2">
            <div class="flex items-center gap-1 surface-card px-2 py-1 rounded-lg border border-subtle">
              <span class="text-mut font-mono">$</span>
              <input type="number" step="0.001" min="0" value="${value === null || value === undefined ? '' : value}" oninput="updateModelField('${m.id}', '${field}', this.value === '' ? null : parseFloat(this.value))" class="w-20 bg-transparent text-main font-mono focus:outline-none" />
            </div>
            <span class="font-mono text-sec w-14 text-right">${marginLabel(m[field.replace('cost', '').replace('Input', 'input').replace('Output', 'output').replace('CacheRead', 'cacheRead').replace('CacheWrite', 'cacheWrite')] || 0, value)}</span>
          </div>
        </div>`;
    }

    function renderMgmtModelAI() {
      const selectedId = state.selectedModelId;
      const m = MODELS_DATA.find(x => x.id === selectedId) || MODELS_DATA[0];

      if (!m) {
        return `
          <div class="surface-card rounded-2xl p-8 border border-white/5 text-center space-y-3">
            <div class="text-sm text-main font-semibold">${t('mgmt.emptyCatalog')}</div>
            <p class="text-xs text-sec">${t('mgmt.emptyCatalogHint')}</p>
            <button onclick="importModelsFromLiteLLM()" class="bg-[#c87a51] text-[#14110f] font-semibold text-xs px-4 py-2 rounded-xl">${t('mgmt.catalogImport')}</button>
          </div>`;
      }

      const q = (state.modelSearchMgmt || '').toLowerCase();
      const statusFilter = state.modelStatusFilterMgmt || 'all';

      const filteredModels = MODELS_DATA.filter(mod => {
        if (q && !mod.name.toLowerCase().includes(q) && !mod.id.toLowerCase().includes(q) && !mod.provider.toLowerCase().includes(q)) return false;
        const status = modelStatusKey(mod);
        if (statusFilter === 'active' && status !== 'Active') return false;
        if (statusFilter === 'inactive' && status === 'Active') return false;
        return true;
      });

      const activeCount = MODELS_DATA.filter(mod => modelStatusKey(mod) === 'Active').length;
      const inactiveCount = MODELS_DATA.filter(mod => modelStatusKey(mod) !== 'Active').length;
      const status = modelStatusKey(m);

      const maxPrice = Math.max(Number(m.output) || 0, Number(m.input) || 0, Number(m.cacheRead) || 0, Number(m.cacheWrite) || 0, 1);
      const cachePct = Math.min(100, ((Number(m.cacheRead) || 0) / maxPrice) * 100);
      const cacheWritePct = Math.min(100, ((Number(m.cacheWrite) || 0) / maxPrice) * 100);
      const inputPct = Math.min(100, ((Number(m.input) || 0) / maxPrice) * 100);
      const outputPct = 100;

      const history = state.modelHistory || [];

      return `
        <div class="space-y-6">
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-white/5">
              <div class="text-xs font-mono text-mut uppercase">${t('mgmt.totalModels')}</div>
              <div class="text-xl sm:text-2xl font-mono font-semibold text-main mt-1.5">${MODELS_DATA.length}</div>
            </div>
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-white/5">
              <div class="text-xs font-mono text-mut uppercase">${t('mgmt.publiclyActive')}</div>
              <div class="text-xl sm:text-2xl font-mono font-semibold text-emerald-400 mt-1.5">${activeCount}</div>
            </div>
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-white/5">
              <div class="text-xs font-mono text-mut uppercase">${t('mgmt.hiddenCount')}</div>
              <div class="text-xl sm:text-2xl font-mono font-semibold ${inactiveCount > 0 ? 'text-rose-400' : 'text-mut'} mt-1.5">${inactiveCount}</div>
            </div>
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-white/5">
              <div class="text-xs font-mono text-mut uppercase">${t('mgmt.selectedModel')}</div>
              <div class="text-sm font-semibold text-[#df956d] truncate mt-2">${m.name}</div>
              <div class="font-mono text-xs text-mut truncate">${m.id}</div>
            </div>
          </div>

          <div class="surface-card rounded-2xl p-4 border border-white/5 space-y-3">
            <div class="flex flex-wrap items-center justify-between gap-3 text-xs">
              <div class="flex items-center gap-2">
                <span class="font-semibold text-main">${t('mgmt.selectModel')}</span>
              </div>
              <div class="flex flex-wrap items-center gap-2">
                <button onclick="importModelsFromLiteLLM()" class="surface-card hover:border-[#c87a51]/50 text-xs px-3 py-1.5 rounded-xl text-[#df956d] flex items-center gap-1.5 transition">
                  ${ico('rotate-ccw', 'w-3.5 h-3.5')} <span>${t('mgmt.catalogImport')}</span>
                </button>
                <button onclick="openCreateModelModal()" class="surface-card hover:border-white/20 text-xs px-3 py-1.5 rounded-xl text-sec hover:text-main flex items-center gap-1.5 transition">
                  ${ico('plus', 'w-3.5 h-3.5')} <span>${t('mgmt.addModel')}</span>
                </button>
                <select onchange="selectMgmtModel(this.value)" class="surface-card-solid rounded-xl px-3 py-1.5 text-main font-mono text-xs border border-subtle focus:border-[#c87a51] focus:outline-none">
                  ${MODELS_DATA.map(mod => `
                    <option value="${mod.id}" ${mod.id === m.id ? 'selected' : ''}>${mod.name} (${mod.provider}) - ${modelStatusKey(mod)}</option>
                  `).join('')}
                </select>
              </div>
            </div>
            <div class="flex items-center gap-2 text-xs">
              ${modelStatusBadge(m)}
              ${m.litellmPresent
                ? `<span class="px-2 py-0.5 rounded-full text-xs font-mono bg-emerald-500/10 text-emerald-400">${t('mgmt.inLiteLLM')}</span>`
                : `<span class="px-2 py-0.5 rounded-full text-xs font-mono bg-white/5 text-mut">${t('mgmt.notInLiteLLM')}</span>`}
            </div>
          </div>

          <div class="surface-card rounded-2xl p-6 border border-white/5 space-y-6">
            <div class="flex flex-wrap items-center justify-between gap-4 border-b border-subtle pb-5">
              <div class="flex items-center gap-3.5">
                <div class="w-12 h-12 rounded-2xl bg-[#c87a51]/15 text-[#c87a51] flex items-center justify-center shrink-0">
                  ${getProviderIcon(m.provider, 'w-6 h-6')}
                </div>
                <div>
                  <div class="flex items-center gap-2 flex-wrap">
                    <h2 class="font-serif font-medium text-h3 text-main">${m.name}</h2>
                    <span class="px-2.5 py-0.5 rounded-full surface-card text-xs text-sec font-mono">${m.context || '–'}</span>
                    ${m.free ? `<span class="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-mono text-xs">${t('mgmt.freeTier')}</span>` : ''}
                  </div>
                  <div class="text-xs text-mut font-mono mt-0.5">ID: ${m.id} · Provider: <b class="text-main">${m.provider}</b></div>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <button onclick="saveModelAiChanges('${m.id}')" class="bg-[#c87a51] text-[#14110f] font-semibold text-xs px-4 py-2 rounded-xl flex items-center gap-1.5 shadow transition">
                  ${ico('check', 'w-3.5 h-3.5')} <span>${t('mgmt.saveChanges')}</span>
                </button>
                <button onclick="openDeleteModelModal('${m.id}')" class="surface-card hover:border-rose-400/50 text-xs px-3.5 py-2 rounded-xl text-rose-300 flex items-center gap-1.5 transition">
                  ${ico('trash-2', 'w-3.5 h-3.5')} <span>${t('mgmt.deleteModel')}</span>
                </button>
              </div>
            </div>

            <div class="space-y-2">
              <div class="text-xs font-semibold text-main">${t('mgmt.visibility')}</div>
              <div onclick="toggleModelActive('${m.id}')" class="w-full p-4 rounded-2xl cursor-pointer border transition-all duration-200 select-none flex flex-wrap items-center justify-between gap-3 bg-white/5 border-white/20 hover:bg-white/10 text-neutral-200">
                <div class="flex items-center gap-3">
                  <div class="w-9 h-9 rounded-xl bg-white/10 text-white flex items-center justify-center shrink-0">
                    ${status === 'Active' ? ico('eye', 'w-5 h-5') : ico('eye-off', 'w-5 h-5')}
                  </div>
                  <div>
                    <div class="font-semibold text-sm text-white">${t('mgmt.visibility' + status)}</div>
                    <div class="text-xs opacity-80 mt-0.5">${t('mgmt.visibilityHint' + status)}</div>
                  </div>
                </div>
                <div class="w-12 h-6 rounded-full p-0.5 transition-colors ${status === 'Active' ? 'bg-emerald-500' : 'bg-white/20'}">
                  <div class="w-5 h-5 rounded-full bg-white shadow-md transition-transform ${status === 'Active' ? 'translate-x-6' : 'translate-x-0'}"></div>
                </div>
              </div>
            </div>

            <div class="space-y-2 pt-2 border-t border-subtle">
              <div class="flex items-center justify-between text-xs">
                <span class="font-semibold text-main">${t('mgmt.description')}</span>
                <button onclick="restoreModelVideoDescription('${m.id}')" class="text-xs text-[#df956d] hover:underline flex items-center gap-1 font-mono">
                  ${ico('rotate-ccw', 'w-3 h-3')} <span>${t('mgmt.restoreDescription')}</span>
                </button>
              </div>
              <textarea id="mgmt-model-desc" rows="3" oninput="updateModelDescription('${m.id}', this.value)" class="w-full surface-card-solid rounded-xl p-3.5 text-main text-xs sm:text-sm border border-subtle focus:border-[#c87a51] focus:outline-none leading-relaxed">${m.description || ''}</textarea>
              <div class="surface-card-solid rounded-xl p-3 border border-white/5 space-y-1">
                <div class="text-xs font-mono uppercase text-mut">${t('mgmt.livePreview')}</div>
                <p id="mgmt-desc-preview" class="text-xs text-sec leading-relaxed italic">"${m.description || t('mgmt.noDescription')}"</p>
              </div>
            </div>

            <div class="space-y-5 pt-3 border-t border-subtle">
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-sm font-semibold text-main">${t('mgmt.pricingBars')}</h3>
                  <p class="text-xs text-sec mt-0.5">${t('mgmt.pricingHint')}</p>
                </div>
                <div class="flex items-center gap-1.5 flex-wrap text-xs">
                  <span class="text-mut text-xs mr-1">${t('mgmt.promos')}</span>
                  ${[0, 10, 20, 30, 40, 50].map(pct => `
                    <button onclick="applyModelDiscountPreset('${m.id}', ${pct})" class="px-2 py-1 rounded-lg text-xs font-mono font-medium transition ${
                      Number(m.discount) === pct ? 'bg-[#c87a51] text-[#14110f] font-semibold' : 'surface-card text-sec hover:text-main'
                    }">${pct === 0 ? t('mgmt.basePrice') : `-${pct}%`}</button>
                  `).join('')}
                </div>
              </div>

              ${priceBarBlock(m, {
                field: 'input',
                title: t('mgmt.rateInput'),
                accent: 'text-blue-400',
                dot: 'bg-blue-400',
                max: 30,
                step: 0.05,
                preview: '<span>$0.00</span><span>$5.00</span><span>$10.00</span><span>$20.00</span><span>$30.00+</span>',
                decimals: 2
              })}

              ${priceBarBlock(m, {
                field: 'cacheRead',
                title: t('mgmt.rateCacheRead'),
                accent: 'text-emerald-400',
                dot: 'bg-emerald-400',
                max: 5,
                step: 0.005,
                preview: '<span>$0.000</span><span>$0.100</span><span>$0.500</span><span>$1.000</span><span>$5.000</span>',
                decimals: 4
              })}

              ${priceBarBlock(m, {
                field: 'cacheWrite',
                title: t('mgmt.rateCacheWrite'),
                accent: 'text-teal-400',
                dot: 'bg-teal-400',
                max: 5,
                step: 0.005,
                preview: '<span>$0.000</span><span>$0.100</span><span>$0.500</span><span>$1.000</span><span>$5.000</span>',
                decimals: 4
              })}

              ${priceBarBlock(m, {
                field: 'output',
                title: t('mgmt.rateOutput'),
                accent: 'text-[#df956d]',
                dot: 'bg-[#df956d]',
                max: 100,
                step: 0.5,
                preview: '<span>$0.00</span><span>$10.00</span><span>$25.00</span><span>$50.00</span><span>$100.00+</span>',
                decimals: 2
              })}

              <div class="surface-card rounded-xl p-4 border border-white/5 space-y-2">
                <div class="flex items-center justify-between text-xs font-mono text-mut">
                  <span>${t('mgmt.distribution')}</span>
                  <span>Cache: $${Number(m.cacheRead || 0).toFixed(2)} | In: $${Number(m.input || 0).toFixed(2)} | Out: $${Number(m.output || 0).toFixed(2)}</span>
                </div>
                <div class="h-3 w-full bg-white/5 rounded-full overflow-hidden flex gap-0.5">
                  <div style="width: ${cachePct}%" class="bg-emerald-400 h-full"></div>
                  <div style="width: ${cacheWritePct}%" class="bg-teal-400 h-full"></div>
                  <div style="width: ${inputPct}%" class="bg-blue-400 h-full"></div>
                  <div style="width: ${outputPct}%" class="bg-[#df956d] h-full"></div>
                </div>
              </div>
            </div>

            <div class="space-y-3 pt-3 border-t border-subtle">
              <h3 class="text-sm font-semibold text-main">${t('mgmt.supplierCost')}</h3>
              <p class="text-xs text-sec">${t('mgmt.supplierCostHint')}</p>
              <div class="surface-card-solid rounded-2xl p-4 border border-white/5 space-y-2">
                ${costInputRow(m, 'costInput', 'mgmt.rateInput')}
                ${costInputRow(m, 'costOutput', 'mgmt.rateOutput')}
                ${costInputRow(m, 'costCacheRead', 'mgmt.rateCacheRead')}
                ${costInputRow(m, 'costCacheWrite', 'mgmt.rateCacheWrite')}
              </div>
            </div>

            <div class="space-y-3 pt-3 border-t border-subtle">
              <h3 class="text-sm font-semibold text-main">${t('mgmt.parameters')}</h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <label class="space-y-1">
                  <span class="text-sec">${t('mgmt.context')}</span>
                  <input type="text" value="${m.context || ''}" oninput="updateModelField('${m.id}', 'context', this.value)" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none font-mono" />
                </label>
                <label class="space-y-1">
                  <span class="text-sec">${t('mgmt.maxOutput')}</span>
                  <input type="number" min="0" value="${m.maxOutput === null || m.maxOutput === undefined ? '' : m.maxOutput}" oninput="updateModelField('${m.id}', 'maxOutput', this.value === '' ? null : parseInt(this.value, 10))" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none font-mono" />
                </label>
                <label class="space-y-1">
                  <span class="text-sec">${t('mgmt.upstreamModel')}</span>
                  <input type="text" value="${m.upstreamModel || ''}" oninput="updateModelField('${m.id}', 'upstreamModel', this.value)" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none font-mono" />
                </label>
                <label class="space-y-1">
                  <span class="text-sec">${t('mgmt.badge')}</span>
                  <input type="text" value="${m.badge || ''}" oninput="updateModelField('${m.id}', 'badge', this.value)" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none font-mono" />
                </label>
              </div>
              <div class="flex flex-wrap items-center gap-4 text-xs pt-1">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" ${m.vision ? 'checked' : ''} onchange="updateModelField('${m.id}', 'vision', this.checked)" class="w-4 h-4 rounded accent-[#c87a51]" />
                  <span class="text-sec">${t('mgmt.vision')}</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" ${m.reasoning ? 'checked' : ''} onchange="updateModelField('${m.id}', 'reasoning', this.checked)" class="w-4 h-4 rounded accent-[#c87a51]" />
                  <span class="text-sec">${t('mgmt.reasoning')}</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" ${m.free ? 'checked' : ''} onchange="updateModelField('${m.id}', 'free', this.checked)" class="w-4 h-4 rounded accent-[#c87a51]" />
                  <span class="text-sec">${t('mgmt.freeModel')}</span>
                </label>
              </div>
            </div>

            <div class="space-y-2 pt-3 border-t border-subtle">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-semibold text-main">${t('mgmt.history')}</h3>
                <button onclick="loadModelHistory('${m.id}')" class="text-xs text-[#df956d] hover:underline font-mono">${t('mgmt.historyRefresh')}</button>
              </div>
              ${history.length === 0
                ? `<p class="text-xs text-mut">${t('mgmt.historyEmpty')}</p>`
                : `<div class="surface-card-solid rounded-xl border border-white/5 overflow-x-auto">
                    <table class="w-full text-xs text-left font-mono">
                      <thead><tr class="border-b border-subtle text-mut">
                        <th class="py-2 px-3">${t('mgmt.when')}</th>
                        <th class="py-2 px-3">${t('mgmt.thInput')}</th>
                        <th class="py-2 px-3">${t('mgmt.thOutput')}</th>
                        <th class="py-2 px-3">${t('mgmt.thCacheRead')}</th>
                        <th class="py-2 px-3">${t('mgmt.thCacheWrite')}</th>
                        <th class="py-2 px-3">${t('mgmt.thDiscount')}</th>
                      </tr></thead>
                      <tbody class="divide-y divide-white/5">
                        ${history.map(row => `
                          <tr>
                            <td class="py-2 px-3 text-sec">${fmtDate(row.effectiveFrom, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                            <td class="py-2 px-3 text-main">$${Number(row.input).toFixed(2)}</td>
                            <td class="py-2 px-3 text-main">$${Number(row.output).toFixed(2)}</td>
                            <td class="py-2 px-3 text-sec">$${Number(row.cacheRead).toFixed(4)}</td>
                            <td class="py-2 px-3 text-sec">$${Number(row.cacheWrite).toFixed(4)}</td>
                            <td class="py-2 px-3 text-sec">${row.discount}%</td>
                          </tr>`).join('')}
                      </tbody>
                    </table>
                  </div>`}
            </div>

            <div class="pt-4 border-t border-subtle flex flex-wrap items-center justify-end gap-2">
              <button onclick="saveModelAiChanges('${m.id}')" class="bg-[#c87a51] text-[#14110f] font-semibold text-xs px-5 py-2.5 rounded-xl shadow transition">${t('mgmt.saveParameters')}</button>
            </div>
          </div>

          <div class="surface-card rounded-2xl border border-white/5 space-y-4 p-5">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 class="text-sm font-semibold text-main">${t('mgmt.matrix')}</h3>
                <p class="text-xs text-sec mt-0.5">${t('mgmt.matrixHint')}</p>
              </div>
              <div class="flex items-center gap-2 flex-wrap">
                <div class="flex items-center gap-1.5 surface-card-solid px-3 py-1.5 rounded-xl border border-subtle text-xs">
                  <span class="text-mut">${ico('search', 'w-3.5 h-3.5')}</span>
                  <input type="text" value="${state.modelSearchMgmt}" oninput="state.modelSearchMgmt=this.value; render();" placeholder="${t('mgmt.filter')}" class="bg-transparent text-main focus:outline-none w-36" />
                </div>
                <div class="surface-card-solid rounded-xl p-1 flex items-center text-xs">
                  <button onclick="state.modelStatusFilterMgmt='all'; render();" class="px-2.5 py-1 rounded-lg transition ${statusFilter === 'all' ? 'bg-[#c87a51] text-[#14110f] font-semibold' : 'text-sec hover:text-main'}">${t('mgmt.filterAll', { count: MODELS_DATA.length })}</button>
                  <button onclick="state.modelStatusFilterMgmt='active'; render();" class="px-2.5 py-1 rounded-lg transition ${statusFilter === 'active' ? 'bg-[#c87a51] text-[#14110f] font-semibold' : 'text-sec hover:text-main'}">${t('mgmt.filterActive', { count: activeCount })}</button>
                  <button onclick="state.modelStatusFilterMgmt='inactive'; render();" class="px-2.5 py-1 rounded-lg transition ${statusFilter === 'inactive' ? 'bg-[#c87a51] text-[#14110f] font-semibold' : 'text-sec hover:text-main'}">${t('mgmt.filterHidden', { count: inactiveCount })}</button>
                </div>
              </div>
            </div>
            <div class="overflow-x-auto">
              <table class="w-full text-xs text-left">
                <thead>
                  <tr class="border-b border-subtle text-mut text-xs bg-white/[0.02]">
                    <th class="py-3 px-3 font-medium">${t('mgmt.thModel')}</th>
                    <th class="py-3 px-3 font-medium">${t('mgmt.thProvider')}</th>
                    <th class="py-3 px-3 font-medium">${t('mgmt.thStatus')}</th>
                    <th class="py-3 px-3 font-medium">${t('mgmt.thInput')}</th>
                    <th class="py-3 px-3 font-medium">${t('mgmt.thCacheRead')}</th>
                    <th class="py-3 px-3 font-medium">${t('mgmt.thCacheWrite')}</th>
                    <th class="py-3 px-3 font-medium">${t('mgmt.thOutput')}</th>
                    <th class="py-3 px-3 font-medium">${t('mgmt.thDiscount')}</th>
                    <th class="py-3 px-3 font-medium text-right">${t('mgmt.thActions')}</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-white/5 text-sec font-mono text-xs">
                  ${filteredModels.map(mod => `
                    <tr class="hover:bg-white/[0.02] transition ${mod.id === m.id ? 'bg-[#c87a51]/5' : ''}">
                      <td class="py-3 px-3 font-sans">
                        <div class="min-w-0">
                          <div class="font-medium text-main truncate">${mod.name}</div>
                          <div class="text-xs text-mut truncate font-mono">${mod.id}</div>
                        </div>
                      </td>
                      <td class="py-3 px-3 font-sans">${mod.provider}</td>
                      <td class="py-3 px-3">${modelStatusBadge(mod)}</td>
                      <td class="py-3 px-3 text-main font-semibold">$${Number(mod.input).toFixed(2)}</td>
                      <td class="py-3 px-3 text-emerald-400 font-semibold">$${Number(mod.cacheRead).toFixed(4)}</td>
                      <td class="py-3 px-3 text-teal-400 font-semibold">$${Number(mod.cacheWrite).toFixed(4)}</td>
                      <td class="py-3 px-3 text-[#df956d] font-semibold">$${Number(mod.output).toFixed(2)}</td>
                      <td class="py-3 px-3">${mod.discount ? `<span class="text-[#df956d] font-bold">-${mod.discount}%</span>` : '0%'}</td>
                      <td class="py-3 px-3 text-right font-sans">
                        <button onclick="selectMgmtModel('${mod.id}')" class="surface-card hover:border-[#c87a51]/50 px-2.5 py-1 rounded text-xs text-[#df956d] hover:text-white">${t('mgmt.tune')}</button>
                      </td>
                    </tr>`).join('')}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      `;
    }

    function openCreateModelModal() {
      const root = document.getElementById('modal-root');
      if (!root) return;
      root.innerHTML = `
        <div class="fixed inset-0 z-50 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div class="surface-card-solid rounded-3xl max-w-md w-full p-6 sm:p-7 border border-strong space-y-4 shadow-2xl text-xs">
            <div class="flex items-start justify-between">
              <h3 class="text-base sm:text-lg font-semibold text-main">${t('mgmt.addModel')}</h3>
              <button onclick="closeModal()" class="text-sec hover:text-main p-1">${ico('x', 'w-4 h-4')}</button>
            </div>
            <div class="space-y-3">
              <label class="space-y-1 block">
                <span class="text-sec">${t('mgmt.modelId')}</span>
                <input id="new-model-id" type="text" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none font-mono" />
              </label>
              <label class="space-y-1 block">
                <span class="text-sec">${t('mgmt.upstreamModel')}</span>
                <input id="new-model-upstream" type="text" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none font-mono" />
                <span class="text-xs text-mut">${t('mgmt.upstreamModelHint')}</span>
              </label>
              <label class="space-y-1 block">
                <span class="text-sec">${t('mgmt.thProvider')}</span>
                <input id="new-model-provider" type="text" class="w-full surface-card-solid rounded-xl px-3 py-2 text-main border border-subtle focus:border-[#c87a51] focus:outline-none" />
              </label>
            </div>
            <div class="flex justify-end gap-2.5 pt-2">
              <button onclick="closeModal()" class="surface-card hover:border-strong px-4 py-2 rounded-xl text-xs text-sec border border-subtle">${t('mgmt.cancel')}</button>
              <button onclick="submitCreateModel()" class="bg-[#c87a51] text-[#14110f] font-semibold px-4 py-2 rounded-xl text-xs">${t('mgmt.create')}</button>
            </div>
          </div>
        </div>`;
    }

    async function submitCreateModel() {
      const id = (document.getElementById('new-model-id') || {}).value || '';
      const upstream = (document.getElementById('new-model-upstream') || {}).value || '';
      const provider = (document.getElementById('new-model-provider') || {}).value || '';
      if (!id.trim() || !upstream.trim()) {
        showToast(t('mgmt.createRequired'));
        return;
      }
      try {
        const res = await apiFetch('/api/dev/models', {
          method: 'POST',
          body: JSON.stringify({ id: id.trim(), upstream_model: upstream.trim(), provider: provider.trim() || undefined })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          showToast(data.error || t('mgmt.createFailed'));
          return;
        }
        closeModal();
        showToast(t('mgmt.created', { id: id.trim() }));
        state.selectedModelId = id.trim();
        await refreshModels();
      } catch (e) {
        showToast(t('mgmt.createFailed'));
      }
    }

    function openDeleteModelModal(modelId) {
      const m = findModel(modelId);
      if (!m) return;
      const root = document.getElementById('modal-root');
      if (!root) return;
      root.innerHTML = `
        <div class="fixed inset-0 z-50 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div class="surface-card-solid rounded-3xl max-w-md w-full p-6 sm:p-7 border border-strong space-y-4 shadow-2xl text-xs">
            <h3 class="text-base sm:text-lg font-semibold text-main">${t('mgmt.deleteModel')}</h3>
            <p class="text-xs text-sec leading-relaxed">${t('mgmt.deleteConfirm', { name: m.name })}</p>
            <div class="flex justify-end gap-2.5 pt-2">
              <button onclick="closeModal()" class="surface-card hover:border-strong px-4 py-2 rounded-xl text-xs text-sec border border-subtle">${t('mgmt.cancel')}</button>
              <button onclick="confirmDeleteModel('${m.id}')" class="bg-rose-500 text-white font-semibold px-4 py-2 rounded-xl text-xs">${t('mgmt.deleteModel')}</button>
            </div>
          </div>
        </div>`;
    }

    async function confirmDeleteModel(modelId) {
      try {
        const res = await apiFetch(`/api/dev/models/${encodeURIComponent(modelId)}`, { method: 'DELETE' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          showToast(data.error || t('mgmt.deleteFailed'));
          return;
        }
        closeModal();
        showToast(t('mgmt.deleted', { id: modelId }));
        state.selectedModelId = '';
        await refreshModels();
      } catch (e) {
        showToast(t('mgmt.deleteFailed'));
      }
    }

    // ============================================================================
    // TAB 7: SYSTEM ENGINE (LITELLM CORE, OUTBOX HEALTH, SYNC METRICS, RECONCILE)
    // ============================================================================
    function renderMgmtSystem() {
      const sys = state.devSystem || {};
      const litellm = sys.litellm || { status: 'down' };
      const outbox = sys.outbox || { pending: 0, done: 0, failed: 0, total: 0 };
      const dbInfo = sys.db || { status: 'unknown', totalCount: 0, idleCount: 0, waitingCount: 0 };
      const lastSync = sys.lastSyncTime ? new Date(sys.lastSyncTime).toLocaleString() : 'Never';
      const mismatchCount = sys.costMismatchesLast7Days || 0;
      const uptimeSec = sys.uptimeSeconds || 0;
      const uptimeFmt = Math.floor(uptimeSec / 3600) + 'h ' + Math.floor((uptimeSec % 3600) / 60) + 'm';

      return `
        <div class="space-y-6">
          <!-- Top bar with Reconcile Button -->
          <div class="flex flex-wrap items-center justify-between gap-4 p-5 rounded-2xl surface-card border border-subtle">
            <div>
              <h3 class="text-base font-semibold text-main">LiteLLM Core & System Status</h3>
              <p class="text-xs text-sec mt-0.5">Live connectivity, queue health, sync metrics, and data integrity reconciliation.</p>
            </div>
            <div class="flex items-center gap-2.5">
              <button onclick="fetchDevData(); showToast('Metrics refreshed');" class="surface-card hover:border-strong text-xs text-sec hover:text-main px-3.5 py-2 rounded-xl border border-subtle transition flex items-center gap-1.5 shadow-sm">
                ${ico('rotate-cw', 'w-3.5 h-3.5')} <span>Refresh</span>
              </button>
              <button onclick="handleRunReconcile()" class="bg-[#c87a51] hover:bg-[#df956d] text-[#14110f] font-semibold text-xs px-4 py-2 rounded-xl transition shadow-sm flex items-center gap-1.5 cursor-pointer">
                ${ico('rotate-ccw', 'w-3.5 h-3.5')} <span>Run reconcile</span>
              </button>
            </div>
          </div>

          <!-- Real Metrics Grid -->
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <!-- 1. LiteLLM Health -->
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-subtle shadow-sm">
              <div class="flex items-center justify-between text-xs text-mut">
                <span>LiteLLM Proxy</span>
                <span class="inline-flex items-center gap-1 ${litellm.status === 'up' ? 'text-emerald-400' : 'text-rose-400'} font-mono text-xs">
                  <span class="w-1.5 h-1.5 rounded-full ${litellm.status === 'up' ? 'bg-emerald-400 animate-pulse' : 'bg-rose-400'}"></span>
                  ${litellm.status === 'up' ? 'Online' : 'Offline'}
                </span>
              </div>
              <div class="text-xl sm:text-2xl font-mono font-semibold text-main mt-2">
                ${litellm.latencyMs !== undefined ? `${litellm.latencyMs} ms` : '—'}
              </div>
              <div class="text-xs text-sec mt-1">Health check latency</div>
            </div>

            <!-- 2. Outbox Queue -->
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-subtle shadow-sm">
              <div class="flex items-center justify-between text-xs text-mut">
                <span>Outbox Queue</span>
                <span class="text-sec font-mono text-xs">Total: ${outbox.total}</span>
              </div>
              <div class="text-xl sm:text-2xl font-mono font-semibold text-main mt-2">
                <span class="${outbox.pending > 0 ? 'text-amber-400' : 'text-main'}">${outbox.pending}</span>
                <span class="text-xs text-mut font-normal">pending</span>
                /
                <span class="${outbox.failed > 0 ? 'text-rose-400' : 'text-main'}">${outbox.failed}</span>
                <span class="text-xs text-mut font-normal">failed</span>
              </div>
              <div class="text-xs text-sec mt-1">${outbox.done} processed successfully</div>
            </div>

            <!-- 3. Usage Sync Status -->
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-subtle shadow-sm">
              <div class="flex items-center justify-between text-xs text-mut">
                <span>Usage Ingestion</span>
                <span class="text-sec font-mono text-xs">Interval: 60s</span>
              </div>
              <div class="text-xs sm:text-sm font-mono font-semibold text-main mt-2 truncate" title="${lastSync}">
                ${lastSync}
              </div>
              <div class="text-xs text-sec mt-1">Last spend log cursor</div>
            </div>

            <!-- 4. Cost Mismatches -->
            <div class="surface-card rounded-2xl p-4 sm:p-5 border border-subtle shadow-sm">
              <div class="flex items-center justify-between text-xs text-mut">
                <span>Integrity (7d)</span>
                <span class="${mismatchCount === 0 ? 'text-emerald-400' : 'text-amber-400'} font-mono text-xs">${mismatchCount === 0 ? 'Verified' : 'Flagged'}</span>
              </div>
              <div class="text-xl sm:text-2xl font-mono font-semibold ${mismatchCount === 0 ? 'text-emerald-400' : 'text-amber-400'} mt-2">
                ${mismatchCount}
              </div>
              <div class="text-xs text-sec mt-1">Cost mismatch incidents</div>
            </div>
          </div>

          <!-- Server & Database Runtime Details -->
          <div class="surface-card rounded-2xl p-6 border border-subtle space-y-4 shadow-sm">
            <h3 class="text-sm font-semibold text-main">Environment & Database Pool</h3>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs font-mono">
              <div class="surface-card-solid p-3 rounded-xl border border-subtle">
                <div class="text-mut font-sans mb-1">Database Connection</div>
                <div class="text-main font-semibold text-sm capitalize">${dbInfo.status}</div>
                <div class="text-sec mt-1">Pool: ${dbInfo.totalCount} conn (${dbInfo.idleCount} idle, ${dbInfo.waitingCount} waiting)</div>
              </div>
              <div class="surface-card-solid p-3 rounded-xl border border-subtle">
                <div class="text-mut font-sans mb-1">Process Uptime</div>
                <div class="text-main font-semibold text-sm">${uptimeFmt}</div>
                <div class="text-sec mt-1">Node ${sys.nodeVersion || 'v20+'}</div>
              </div>
              <div class="surface-card-solid p-3 rounded-xl border border-subtle">
                <div class="text-mut font-sans mb-1">Zero Data Retention</div>
                <div class="text-emerald-400 font-semibold text-sm">Active</div>
                <div class="text-sec mt-1">No request/response bodies retained</div>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    function renderDevStructurePage() {
      const html = `
        <div>
          <h1 class="font-serif font-medium text-h2 text-main">Structure</h1>
          <p class="text-xs text-sec mt-1">Visual system architecture, proxy pipelines, and multi-model failover matrix.</p>
        </div>

        <!-- Architecture Flow Diagram Card -->
        <div class="surface-card rounded-2xl p-6 border border-white/5 space-y-6">
          <div class="flex flex-wrap items-center justify-between gap-2 border-b border-subtle pb-4">
            <div>
              <h3 class="text-base font-semibold text-main">OpenCSN Gateway Architecture Pipeline</h3>
              <p class="text-xs text-sec mt-0.5">End-to-end request lifecycle from client SDK to upstream model response.</p>
            </div>
            <span class="text-xs font-mono text-[#c87a51] px-2.5 py-1 rounded-lg bg-[#c87a51]/10 border border-[#c87a51]/20">Protocol v2.4</span>
          </div>

          <!-- Flow Chart Visual -->
          <div class="grid grid-cols-1 md:grid-cols-5 gap-3 text-center">
            <!-- 1. Client Layer -->
            <div class="surface-card-solid rounded-2xl p-4 border border-white/10 flex flex-col items-center justify-between gap-3">
              <div class="w-8 h-8 rounded-xl bg-sky-500/15 text-sky-400 flex items-center justify-center">${ico('globe', 'w-4 h-4')}</div>
              <div>
                <div class="font-semibold text-main text-xs">1. Client / Agent</div>
                <div class="text-xs text-sec mt-1">OpenAI SDK, Anthropic SDK, cURL, Cursor, Claude Code</div>
              </div>
              <span class="text-xs font-mono px-2 py-0.5 rounded bg-white/5 text-mut">HTTPS / SSE</span>
            </div>

            <!-- Arrow 1 -->
            <div class="hidden md:flex items-center justify-center text-[#c87a51] font-mono text-lg font-bold">→</div>

            <!-- 2. Edge & Security -->
            <div class="surface-card-solid rounded-2xl p-4 border border-white/10 flex flex-col items-center justify-between gap-3">
              <div class="w-8 h-8 rounded-xl bg-amber-500/15 text-amber-400 flex items-center justify-center">${ico('key-round', 'w-4 h-4')}</div>
              <div>
                <div class="font-semibold text-main text-xs">2. Edge & Auth</div>
                <div class="text-xs text-sec mt-1">Key validator, rate limiting, quota check, Zero Retention filter</div>
              </div>
              <span class="text-xs font-mono px-2 py-0.5 rounded bg-white/5 text-mut">&lt; 3ms overhead</span>
            </div>

            <!-- Arrow 2 -->
            <div class="hidden md:flex items-center justify-center text-[#c87a51] font-mono text-lg font-bold">→</div>

            <!-- 3. Dynamic Router -->
            <div class="surface-card-solid rounded-2xl p-4 border border-white/10 flex flex-col items-center justify-between gap-3">
              <div class="w-8 h-8 rounded-xl bg-purple-500/15 text-purple-400 flex items-center justify-center">${ico('arrow-left-right', 'w-4 h-4')}</div>
              <div>
                <div class="font-semibold text-main text-xs">3. Routing Matrix</div>
                <div class="text-xs text-sec mt-1">Schema translation, model mapping, auto-fallback engine</div>
              </div>
              <span class="text-xs font-mono px-2 py-0.5 rounded bg-white/5 text-mut">Failover Ready</span>
            </div>
          </div>

          <div class="flex justify-center text-[#c87a51] font-mono font-bold text-lg">↓</div>

          <!-- 4. Upstream Tiering Grid -->
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div class="surface-card-solid rounded-xl p-3.5 border border-emerald-500/20 bg-emerald-500/5">
              <div class="flex items-center justify-between font-semibold text-main mb-1">
                <span class="text-emerald-400">Primary Route</span>
                <span class="text-xs px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300">Tier 0</span>
              </div>
              <p class="text-xs text-sec">Direct target provider (e.g. OpenAI GPT-4o / Anthropic Claude 3.7). Zero markup proxying.</p>
            </div>

            <div class="surface-card-solid rounded-xl p-3.5 border border-amber-500/20 bg-amber-500/5">
              <div class="flex items-center justify-between font-semibold text-main mb-1">
                <span class="text-amber-400">Secondary Fallback</span>
                <span class="text-xs px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300">Tier 1</span>
              </div>
              <p class="text-xs text-sec">Triggers on HTTP 429 rate limit or 5xx outage within 300ms without breaking client stream.</p>
            </div>

            <div class="surface-card-solid rounded-xl p-3.5 border border-sky-500/20 bg-sky-500/5">
              <div class="flex items-center justify-between font-semibold text-main mb-1">
                <span class="text-sky-400">Emergency Mesh</span>
                <span class="text-xs px-1.5 py-0.2 rounded bg-sky-500/20 text-sky-300">Tier 2</span>
              </div>
              <p class="text-xs text-sec">High-availability mirror node to ensure 99.99% uptime SLA for automated coding workflows.</p>
            </div>
          </div>
        </div>

        <!-- System Components Breakdown -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
          <div class="surface-card rounded-2xl p-5 border border-white/5 space-y-3">
            <div class="flex items-center gap-2.5 font-semibold text-sm text-main">
              <span class="text-[#c87a51]">${ico('layers', 'w-4 h-4')}</span>
              <span>Payload Protocol Normalization</span>
            </div>
            <p class="text-sec text-xs leading-relaxed">
              OpenCSN accepts either the standard OpenAI specification (<code class="text-[#df956d] font-mono">/v1/chat/completions</code>) or Anthropic Messages specification (<code class="text-[#df956d] font-mono">/v1/messages</code>) and translates schemas on-the-fly, allowing any agent or developer tool to connect without rewrite.
            </p>
          </div>

          <div class="surface-card rounded-2xl p-5 border border-white/5 space-y-3">
            <div class="flex items-center gap-2.5 font-semibold text-sm text-main">
              <span class="text-[#c87a51]">${ico('zap', 'w-4 h-4')}</span>
              <span>Zero-Data-Retention (ZDR) Enforcer</span>
            </div>
            <p class="text-sec text-xs leading-relaxed">
              Every message payload streams directly in volatile memory buffers. OpenCSN retains only metadata (timestamp, model ID, input tokens, output tokens, and response latency) for billing deductions and logging. Never prompt bodies or completions.
            </p>
          </div>
        </div>
      `;
      return renderConsoleLayout('Structure', 'Dev Console › Structure', html);
    }

    function renderDevLogsPage() {
      const logs = (state.devLogs && state.devLogs.length > 0) ? state.devLogs : [];

      const html = `
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 class="font-serif font-medium text-h2 text-main">Dev Console Logs</h1>
            <p class="text-xs text-sec mt-1">Real-time HTTP proxy stream, latency diagnostics, and fallback events.</p>
          </div>
          <div class="flex items-center gap-2">
            <button onclick="showToast('Logs refreshed'); fetchDevData();" class="surface-card hover:border-white/20 text-xs px-3.5 py-2 rounded-xl text-sec hover:text-main flex items-center gap-1.5 transition">
              <span class="w-2 h-2 rounded-full bg-white"></span> <span>Live Streaming</span>
            </button>
            <button onclick="state.devLogs = []; showToast('Cleared dev console logs'); render();" class="surface-card hover:border-white/20 text-xs px-3 py-2 rounded-xl text-sec hover:text-main flex items-center gap-1.5 transition">
              ${ico('trash-2', 'w-3.5 h-3.5')} <span>Clear</span>
            </button>
          </div>
        </div>

        <!-- Terminal Log Viewer -->
        <div class="surface-card-solid rounded-2xl border border-white/10 shadow-2xl overflow-hidden text-xs font-mono">
          <!-- Terminal Titlebar -->
          <div class="px-4 py-3 bg-[#161310] border-b border-white/10 flex flex-wrap items-center justify-between gap-3">
            <div class="flex items-center gap-2">
              <span class="w-3 h-3 rounded-full bg-[#ff5f56]"></span>
              <span class="w-3 h-3 rounded-full bg-[#ffbd2e]"></span>
              <span class="w-3 h-3 rounded-full bg-[#27c93f]"></span>
              <span class="ml-2 text-xs text-sec font-medium">tail -f /var/log/opencsn/gateway.log</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-xs text-mut px-2 py-0.5 rounded bg-white/5">Buffer: 250</span>
              <span class="text-xs text-[#c87a51] px-2 py-0.5 rounded bg-[#c87a51]/10 border border-[#c87a51]/20">ZDR Active</span>
            </div>
          </div>

          <!-- Logs Body -->
          <div class="p-4 sm:p-5 bg-[#0c0a08]/95 overflow-x-auto space-y-2.5 max-h-[550px] overflow-y-auto leading-relaxed select-text">
            ${logs.length === 0 ? `
              <div class="py-16 text-center text-mut text-xs font-sans">
                Belum ada logs gateway yang terekam. Log request dan proxy akan tampil otomatis secara real-time saat ada pemanggilan API.
              </div>
            ` : logs.map(l => {
              const lvlColor = l.level === 'WARN' ? 'text-amber-400 bg-amber-500/10 border-amber-500/20' : l.level === 'ERROR' ? 'text-rose-400 bg-rose-500/10 border-rose-500/20' : 'text-neutral-300 bg-white/5 border-white/10';
              return `
                <div class="flex items-start gap-2 sm:gap-3 text-xs sm:text-xs hover:bg-white/[0.02] p-1.5 rounded font-mono">
                  <span class="text-mut shrink-0">${l.time || 'Just now'}</span>
                  <span class="px-1.5 py-0.2 rounded border font-semibold text-xs shrink-0 ${lvlColor}">[${l.level || 'INFO'}]</span>
                  <span class="text-sec font-bold shrink-0">${l.service || 'GATEWAY'}</span>
                  <span class="text-main font-semibold shrink-0">${l.event || 'EVENT'}</span>
                  <span class="text-sec truncate">${l.message || ''}</span>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `;
      return renderConsoleLayout('Dev Logs', 'Dev Console › Logs', html);
    }

    // ============================================================================
    // DEVICE MORE BOTTOM SHEET MODAL (matching Image 1)
    // ============================================================================
    function openDeviceMoreModal() {
      const root = document.getElementById('modal-root');
      if (!root) return;

      const isDev = !!(state.user && state.user.isDev);

      const sections = [
        {
          heading: 'OVERVIEW',
          items: [
            { route: '/dashboard', label: 'Dashboard', icon: 'layout-dashboard' }
          ]
        },
        {
          heading: 'MODELS',
          items: [
            { route: '/catalog', label: 'Models', icon: 'boxes' },
            { route: '/status', label: 'Status', icon: 'radio' },
            { route: '/leaderboard', label: 'Leaderboard', icon: 'trophy' }
          ]
        },
        {
          heading: 'USAGE',
          items: [
            { route: '/usage', label: 'Usage', icon: 'chart-column' },
            { route: '/activity', label: 'Activity', icon: 'activity' },
            { route: '/request-logs', label: 'Request logs', icon: 'scroll-text' }
          ]
        },
        {
          heading: 'DEVELOPER',
          items: [
            { route: '/api-keys', label: 'API keys', icon: 'key-round' },
            { route: '/integrations', label: 'Integrations', icon: 'plug' }
          ]
        },
        {
          heading: 'BILLING',
          items: [
            { route: '/plan', label: 'Subscription', icon: 'circle-dollar-sign' },
            { route: '/billing', label: 'Billing', icon: 'credit-card' },
            { route: '/referral', label: 'Referral', icon: 'gift' }
          ]
        }
      ];

      if (isDev) {
        sections.push({
          heading: 'DEV CONSOLE',
          items: [
            { route: '/management', label: 'Management', icon: 'sliders' },
            { route: '/structure', label: 'Structure', icon: 'layers' },
            { route: '/dev-logs', label: 'Logs', icon: 'terminal' }
          ]
        });
      }

      window.__moreSheetSections = sections;

      root.innerHTML = `
        <div id="device-more-overlay" onclick="if(event.target===this) closeModal();" class="fixed inset-0 z-50 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex flex-col justify-end select-none">
          <div class="surface-card-solid w-full rounded-t-3xl border-t border-strong max-h-[85vh] overflow-y-auto px-5 pt-3 pb-8 text-xs shadow-2xl">
            <!-- Grab handle -->
            <div class="w-12 h-1 bg-white/20 rounded-full mx-auto mb-4 cursor-pointer" onclick="closeModal()"></div>

            <!-- Sheet Header -->
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2 font-semibold text-base text-main">
                ${getOpenCSNLogo()}
                <span>OpenCSN</span>
              </div>
              <button onclick="closeModal()" class="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-sec hover:text-main transition">
                ${ico('x', 'w-4 h-4')}
              </button>
            </div>

            <!-- Search Pages Input -->
            <div class="relative mb-5">
              <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-sec pointer-events-none">
                ${ico('search', 'w-4 h-4')}
              </span>
              <input id="device-more-search" oninput="filterMoreSheet(this.value)" type="text" placeholder="Search pages" class="w-full bg-[#1b1714] border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-xs text-main placeholder:text-mut focus:outline-none focus:border-[#c87a51] transition" />
            </div>

            <!-- Sheet Grid of Sections -->
            <div id="device-more-grid" class="space-y-5">
              ${renderMoreSheetSections(sections)}
            </div>
          </div>
        </div>
      `;
      setTimeout(() => {
        const inp = document.getElementById('device-more-search');
        if (inp) inp.focus();
      }, 50);
    }

    function renderMoreSheetSections(sections) {
      return sections.map(sec => `
        <div>
          <div class="text-xs font-semibold tracking-wider text-mut uppercase px-1 mb-2">${sec.heading}</div>
          <div class="grid grid-cols-3 gap-2.5">
            ${sec.items.map(item => {
              const active = state.route === item.route;
              return `
                <button onclick="closeModal(); navigate('${item.route}');" class="flex flex-col items-center justify-center gap-2 py-3.5 px-2 rounded-2xl transition ${active ? 'bg-[#261c16] border border-[#c87a51]/50 text-[#df956d] shadow-sm' : 'bg-[#1a1613] border border-white/5 hover:border-white/20 text-sec hover:text-main hover:bg-white/5'}">
                  <span class="${active ? 'text-[#df956d]' : 'text-sec'}">${ico(item.icon, 'w-6 h-6')}</span>
                  <span class="text-xs sm:text-xs font-medium truncate w-full text-center ${active ? 'text-[#df956d]' : ''}">${item.label}</span>
                </button>
              `;
            }).join('')}
          </div>
        </div>
      `).join('');
    }

    function filterMoreSheet(query) {
      const container = document.getElementById('device-more-grid');
      if (!container || !window.__moreSheetSections) return;
      const q = (query || '').trim().toLowerCase();
      if (!q) {
        container.innerHTML = renderMoreSheetSections(window.__moreSheetSections);
        return;
      }
      const filtered = window.__moreSheetSections.map(sec => {
        const matched = sec.items.filter(it => it.label.toLowerCase().includes(q) || sec.heading.toLowerCase().includes(q));
        return { ...sec, items: matched };
      }).filter(sec => sec.items.length > 0);

      if (filtered.length === 0) {
        container.innerHTML = `
          <div class="text-center py-8 text-mut text-xs">
            <div class="mb-2 flex justify-center">${ico('search', 'w-8 h-8 opacity-40')}</div>
            <div>No pages found for "${query}"</div>
          </div>
        `;
      } else {
        container.innerHTML = renderMoreSheetSections(filtered);
      }
    }


    // ============================================================================
    // MODALS
    // ============================================================================
    function closeModal() {
      const root = document.getElementById('modal-root');
      if (root) root.innerHTML = '';
    }

    function setKeyStatusBadge(k) {
      return k.active
        ? `<span class="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs font-sans font-medium">${t('keys.badgeActive')}</span>`
        : `<span class="px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-400 text-xs font-sans font-medium">${t('keys.badgeBlocked')}</span>`;
    }

    function openCopySecretModal(name, secret) {
      state.justCreatedKey = secret;
      const root = document.getElementById('modal-root');
      root.innerHTML = `
        <div class="fixed inset-0 z-50 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div class="surface-card-solid rounded-3xl max-w-lg w-full p-6 sm:p-7 border border-strong space-y-4 shadow-2xl animate-in fade-in zoom-in-95 duration-150 text-xs">
            <div class="flex items-start justify-between">
              <h3 class="text-base sm:text-lg font-semibold text-main">${t('keys.copyTitle')}</h3>
              <button onclick="closeSecretModal()" class="text-sec hover:text-main p-1 transition">${ico('x', 'w-4 h-4')}</button>
            </div>
            <p class="text-xs text-sec leading-relaxed">
              ${t('keys.copyOnce', { name })}
            </p>
            <div class="rounded-xl px-4 py-3 border border-subtle flex items-center justify-between gap-3 shadow-inner bg-inset">
              <span class="font-mono text-xs sm:text-sm text-main truncate font-medium select-all">${secret}</span>
              <button onclick="copyToClipboard('${secret}', this, '${t('keys.copy')}')" class="surface-card hover:border-strong px-3 py-1.5 rounded-lg border border-subtle text-xs text-sec hover:text-main flex items-center gap-1.5 transition font-medium shrink-0 shadow-sm">
                ${ico('copy', 'w-3.5 h-3.5')} <span>${t('keys.copy')}</span>
              </button>
            </div>
            <p class="text-xs text-sec leading-relaxed">${t('keys.copyRotateHint')}</p>
            <div class="pt-1">
              <label class="flex items-center gap-2.5 text-xs text-sec cursor-pointer select-none">
                <input type="checkbox" id="saved-key-checkbox" onchange="toggleApiKeyModalDone(this.checked)" class="w-4 h-4 rounded accent-[#df956d] cursor-pointer" />
                <span>${t('keys.savedCheckbox')}</span>
              </label>
            </div>
            <div class="flex justify-end pt-2">
              <button id="api-key-done-btn" onclick="closeSecretModal()" disabled class="bg-[#df956d] text-[#14110f] font-semibold px-6 py-2 rounded-xl text-xs transition shadow-sm opacity-50 cursor-not-allowed">
                ${t('keys.done')}
              </button>
            </div>
          </div>
        </div>
      `;
    }

    function closeSecretModal() {
      state.justCreatedKey = null;
      closeModal();
    }

    function toggleApiKeyModalDone(checked) {
      const btn = document.getElementById('api-key-done-btn');
      if (!btn) return;
      if (checked) {
        btn.removeAttribute('disabled');
        btn.className = 'bg-[#df956d] hover:bg-[#c87a51] text-[#14110f] font-semibold px-6 py-2 rounded-xl text-xs transition shadow-sm cursor-pointer';
      } else {
        btn.setAttribute('disabled', 'true');
        btn.className = 'bg-[#df956d] text-[#14110f] font-semibold px-6 py-2 rounded-xl text-xs transition shadow-sm opacity-50 cursor-not-allowed';
      }
    }

    function openRotateKeyModal(id) {
      const k = (state.apiKeys || []).find(x => x.id === id);
      if (!k) return;
      const root = document.getElementById('modal-root');
      root.innerHTML = `
        <div class="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div class="status-surface-card rounded-3xl max-w-md w-full p-6 sm:p-7 border border-[rgba(255,255,255,0.08)] space-y-4 shadow-2xl animate-in fade-in zoom-in-95 duration-150 text-xs">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 border border-amber-500/20 flex items-center justify-center shrink-0 shadow-sm">
                ${ico('rotate-ccw', 'w-5 h-5')}
              </div>
              <div>
                <h3 class="text-base sm:text-lg font-bold text-[var(--text-primary)]">Rotate this key?</h3>
                <div class="text-xs text-[var(--text-muted)] font-mono truncate max-w-[260px]">${k.name} (${k.masked})</div>
              </div>
            </div>
            <p class="text-xs text-[var(--text-secondary)] leading-relaxed">
              Rotate this key? The current secret will stop working immediately and a new one will be issued.
            </p>
            <div class="flex justify-end gap-2.5 pt-3">
              <button onclick="closeModal()" id="rotate-cancel-btn" class="surface-card hover:border-strong px-4 py-2 rounded-xl text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] border border-subtle transition font-medium">Cancel</button>
              <button onclick="confirmRotateKey('${id}', this)" id="rotate-confirm-btn" class="bg-[#df956d] hover:bg-[#c87a51] text-[#14110f] font-semibold px-4 py-2 rounded-xl text-xs transition shadow-sm flex items-center gap-2">
                <span>Rotate key</span>
              </button>
            </div>
          </div>
        </div>
      `;
    }

    async function confirmRotateKey(id, btnEl) {
      if (state.rotatingKey) return;
      state.rotatingKey = true;

      const confirmBtn = btnEl || document.getElementById('rotate-confirm-btn');
      const cancelBtn = document.getElementById('rotate-cancel-btn');

      if (confirmBtn) {
        confirmBtn.setAttribute('disabled', 'true');
        confirmBtn.classList.add('opacity-75', 'cursor-not-allowed');
        confirmBtn.innerHTML = `${ico('rotate-cw', 'w-3.5 h-3.5 animate-spin')} <span>Rotating...</span>`;
      }
      if (cancelBtn) {
        cancelBtn.setAttribute('disabled', 'true');
        cancelBtn.classList.add('opacity-50', 'cursor-not-allowed');
      }

      try {
        const res = await apiFetch(`/api/keys/${encodeURIComponent(id)}/rotate`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          showToast(data.error || 'Rotation failed.');
          if (confirmBtn) {
            confirmBtn.removeAttribute('disabled');
            confirmBtn.classList.remove('opacity-75', 'cursor-not-allowed');
            confirmBtn.innerHTML = `<span>Rotate key</span>`;
          }
          if (cancelBtn) {
            cancelBtn.removeAttribute('disabled');
            cancelBtn.classList.remove('opacity-50', 'cursor-not-allowed');
          }
          return;
        }

        openNewRotatedKeyModal(data.key);
      } catch (e) {
        showToast('Rotation failed.');
        if (confirmBtn) {
          confirmBtn.removeAttribute('disabled');
          confirmBtn.classList.remove('opacity-75', 'cursor-not-allowed');
          confirmBtn.innerHTML = `<span>Rotate key</span>`;
        }
        if (cancelBtn) {
          cancelBtn.removeAttribute('disabled');
          cancelBtn.classList.remove('opacity-50', 'cursor-not-allowed');
        }
      } finally {
        state.rotatingKey = false;
      }
    }

    function openNewRotatedKeyModal(secret) {
      const root = document.getElementById('modal-root');
      root.innerHTML = `
        <div class="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4">
          <div class="status-surface-card rounded-3xl max-w-lg w-full p-6 sm:p-7 border border-[rgba(255,255,255,0.08)] space-y-5 shadow-2xl animate-in fade-in zoom-in-95 duration-150 text-xs">
            <div class="space-y-1">
              <h3 class="text-base sm:text-lg font-bold text-[var(--text-primary)]">New API key created</h3>
              <p class="text-xs text-[var(--text-secondary)] leading-relaxed">
                Your key was rotated. Store your new secret key below:
              </p>
            </div>

            <!-- Full secret in monospace box with Copy button -->
            <div class="rounded-xl px-4 py-3 border border-subtle flex items-center justify-between gap-3 shadow-inner bg-inset">
              <span class="font-mono text-xs sm:text-sm text-[var(--text-primary)] truncate font-medium select-all" id="rotated-secret-box">${secret}</span>
              <button onclick="copyRotatedKeySecret('${secret}', this)" class="surface-card hover:border-strong px-3 py-1.5 rounded-lg border border-subtle text-xs text-[var(--text-primary)] hover:text-white flex items-center gap-1.5 transition font-medium shrink-0 shadow-sm" id="rotated-key-copy-btn">
                <span class="copy-btn-icon">${ico('copy', 'w-3.5 h-3.5')}</span>
                <span class="copy-label-slot">Copy</span>
              </button>
            </div>

            <!-- Warning line -->
            <div class="flex items-start gap-2.5 p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-[var(--text-secondary)] leading-relaxed">
              <span class="text-amber-400 shrink-0 mt-0.5">${ico('shield-alert', 'w-4 h-4')}</span>
              <span>This is the only time the full key is shown. Store it somewhere safe. The old key no longer works.</span>
            </div>

            <!-- Explicit "I have saved it" button -->
            <div class="pt-2">
              <button onclick="closeRotatedKeyModal()" class="w-full bg-[#df956d] hover:bg-[#c87a51] text-[#14110f] font-semibold px-6 py-2.5 rounded-xl text-xs transition shadow-sm text-center">
                I have saved it
              </button>
            </div>
          </div>
        </div>
      `;
    }

    function copyRotatedKeySecret(text, btnEl) {
      if (!btnEl) return;
      try {
        navigator.clipboard.writeText(text);
      } catch (err) {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }

      const iconSpan = btnEl.querySelector('.copy-btn-icon');
      const labelSpan = btnEl.querySelector('.copy-label-slot');

      if (iconSpan && labelSpan) {
        iconSpan.innerHTML = ico('check', 'w-3.5 h-3.5 text-emerald-400');
        labelSpan.textContent = 'Copied';
        labelSpan.className = 'copy-label-slot text-emerald-400 font-medium';

        setTimeout(() => {
          if (iconSpan && labelSpan) {
            iconSpan.innerHTML = ico('copy', 'w-3.5 h-3.5');
            labelSpan.textContent = 'Copy';
            labelSpan.className = 'copy-label-slot';
          }
        }, 2000);
      }
    }

    async function closeRotatedKeyModal() {
      closeModal();
      await loadApiKeys();
      showToast('API key rotated successfully.');
    }

    function openRevokeKeyModal(id) {
      const k = (state.apiKeys || []).find(x => x.id === id);
      if (!k) return;
      const root = document.getElementById('modal-root');
      root.innerHTML = `
        <div class="fixed inset-0 z-50 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div class="surface-card-solid rounded-3xl max-w-md w-full p-6 sm:p-7 border border-strong space-y-4 shadow-2xl animate-in fade-in zoom-in-95 duration-150 text-xs">
            <h3 class="text-base sm:text-lg font-semibold text-main">${t('keys.revokeTitle')}</h3>
            <p class="text-xs text-sec leading-relaxed">
              ${t('keys.revokeBody', { name: k.name })}
            </p>
            <div class="flex justify-end gap-2.5 pt-3">
              <button onclick="closeModal()" class="surface-card hover:border-strong px-4 py-2 rounded-xl text-xs text-sec hover:text-main border border-subtle transition font-medium">${t('keys.cancel')}</button>
              <button onclick="confirmRevokeKey('${id}')" class="bg-rose-600 hover:bg-rose-700 text-white font-semibold px-4 py-2 rounded-xl text-xs transition shadow-sm">${t('keys.revokeConfirm')}</button>
            </div>
          </div>
        </div>
      `;
    }

    async function confirmRevokeKey(id) {
      const k = (state.apiKeys || []).find(x => x.id === id);
      const name = k ? k.name : t('keys.fallbackName');
      try {
        const res = await apiFetch(`/api/keys/${encodeURIComponent(id)}`, { method: 'DELETE' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          showToast(data.error || t('keys.revokeFailed'));
          return;
        }
        if (data.warning === 'PENDING_REMOTE_DELETE') {
          showToast(t('keys.pendingRemoteDelete'));
        } else {
          showToast(t('keys.revoked', { name }));
        }
        closeModal();
        await loadApiKeys();
      } catch (e) {
        showToast(t('keys.revokeFailed'));
      }
    }

    async function toggleKeyBlocked(id) {
      const k = (state.apiKeys || []).find(x => x.id === id);
      if (!k) return;
      try {
        const res = await apiFetch(`/api/keys/${encodeURIComponent(id)}/status`, {
          method: 'PUT',
          body: JSON.stringify({ active: !k.active })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          showToast(data.error || t('keys.statusFailed'));
          return;
        }
        await loadApiKeys();
      } catch (e) {
        showToast(t('keys.statusFailed'));
      }
    }

    function keyExpiresDisplayText(optionValue) {
      const labels = {
        never: t('keys.expiresNever'),
        '30d': t('keys.expires30'),
        '60d': t('keys.expires60'),
        '90d': t('keys.expires90'),
        '1y': t('keys.expires1y')
      };
      return labels[optionValue] || labels.never;
    }

    function expiresInDaysFromOption(optionValue) {
      const map = { never: null, '30d': 30, '60d': 60, '90d': 90, '1y': 365 };
      return Object.prototype.hasOwnProperty.call(map, optionValue) ? map[optionValue] : null;
    }

    function openCreateApiKeyModal() {
      const root = document.getElementById('modal-root');
      const defaultName = (state.apiKeys && state.apiKeys.length === 0) ? 'production-worker' : `production-worker-${(state.apiKeys || []).length + 1}`;
      state.createKeySelectedModels = [];

      root.innerHTML = `
        <div class="fixed inset-0 z-50 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div class="surface-card-solid rounded-3xl max-w-lg w-full p-6 sm:p-7 border border-subtle space-y-5 shadow-2xl animate-in fade-in zoom-in-95 duration-150 text-xs">
            <!-- Modal Header matching Image 3 -->
            <div class="flex items-start justify-between gap-4">
              <div class="flex items-start gap-3.5">
                <div class="w-11 h-11 rounded-2xl bg-[#faebe1] dark:bg-[#3a2216] border border-[#f0d0be] dark:border-[#c87a51]/30 flex items-center justify-center shrink-0 text-[#c87a51] dark:text-[#df956d] shadow-sm">
                  ${ico('key-round', 'w-5 h-5')}
                </div>
                <div>
                  <h3 class="text-base sm:text-lg font-bold text-main leading-tight">Create a new API key</h3>
                  <p class="text-xs text-sec mt-1 leading-relaxed">
                    Name it after where it will live: one key per app makes rotation painless.
                  </p>
                </div>
              </div>
              <button onclick="closeModal()" class="text-mut hover:text-main p-1 transition rounded-lg shrink-0">
                ${ico('x', 'w-4 h-4')}
              </button>
            </div>

            <!-- 1. Key name -->
            <div class="space-y-1.5">
              <div class="flex items-center gap-2.5">
                <div class="w-5 h-5 rounded-full bg-[#8c4620] dark:bg-[#a6562a] text-white flex items-center justify-center font-bold text-xs shrink-0">
                  1
                </div>
                <div class="font-semibold text-xs sm:text-sm text-main">Key name</div>
              </div>
              <p class="text-xs text-mut ml-7.5 pl-0.5">Pick a descriptive name so you can find it later.</p>
              <div class="ml-7.5 pl-0.5 pt-1">
                <input id="new-key-name-input" type="text" value="${defaultName}" placeholder="production-worker" class="w-full surface-card rounded-xl px-3.5 py-2.5 text-main border border-[#c87a51]/50 dark:border-[#c87a51]/60 focus:border-[#c87a51] focus:ring-1 focus:ring-[#c87a51]/30 focus:outline-none text-xs transition shadow-sm placeholder:text-mut/60" />
              </div>
            </div>

            <!-- 2. Limits & expiration -->
            <div class="space-y-1.5">
              <div class="flex items-center gap-2.5">
                <div class="w-5 h-5 rounded-full bg-[#8c4620] dark:bg-[#a6562a] text-white flex items-center justify-center font-bold text-xs shrink-0">
                  2
                </div>
                <div class="font-semibold text-xs sm:text-sm text-main">Limits & expiration</div>
              </div>
              <p class="text-xs text-mut ml-7.5 pl-0.5">Set a monthly spending cap and expiration policy.</p>
              
              <div class="ml-7.5 pl-0.5 pt-1">
                <div class="surface-card rounded-2xl border border-subtle p-3.5 sm:p-4 space-y-3 shadow-sm">
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label class="block text-xs font-medium text-sec mb-1.5 flex items-center gap-1.5">
                        <span>Max spend (USD, optional)</span>
                        <span title="Monthly spending limit" class="text-mut hover:text-sec cursor-pointer">${ico('circle-dot', 'w-3 h-3')}</span>
                      </label>
                      <div class="relative">
                        <span class="absolute left-3.5 top-2 text-xs text-mut font-mono">$</span>
                        <input id="new-key-spend-input" type="text" placeholder="e.g. 25" class="w-full surface-card-solid border border-subtle rounded-xl pl-8 pr-3 py-1.5 text-xs text-main placeholder:text-mut focus:outline-none focus:border-[#c87a51]/60 transition" />
                      </div>
                    </div>
                    <div>
                      <label class="block text-xs font-medium text-sec mb-1.5">Expires</label>
                      <div class="relative">
                        <select id="new-key-expires-input" class="w-full surface-card-solid border border-subtle rounded-xl px-3 py-1.5 text-xs text-main focus:outline-none focus:border-[#c87a51]/60 transition appearance-none cursor-pointer pr-8">
                          <option value="never">Never</option>
                          <option value="30d">30 days</option>
                          <option value="60d">60 days</option>
                          <option value="90d">90 days</option>
                          <option value="1y">1 year</option>
                        </select>
                        <span class="absolute right-2.5 top-2 text-mut pointer-events-none">${ico('chevron-down', 'w-3.5 h-3.5')}</span>
                      </div>
                    </div>
                  </div>
                  <div class="text-xs text-mut">Resets monthly on the 1st</div>
                </div>
              </div>
            </div>

            <!-- 3. Allowed models (optional) -->
            <div class="space-y-1.5">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2.5">
                  <div class="w-5 h-5 rounded-full bg-[#8c4620] dark:bg-[#a6562a] text-white flex items-center justify-center font-bold text-xs shrink-0">
                    3
                  </div>
                  <div class="font-semibold text-xs sm:text-sm text-main">
                    Allowed models <span class="text-xs text-mut font-normal">(optional)</span>
                  </div>
                </div>
                <button type="button" onclick="toggleAllowedModelsSelectAll()" id="allowed-models-select-btn" class="surface-card hover:border-strong border border-subtle px-2.5 py-1 rounded-lg text-xs font-medium text-sec hover:text-main transition shadow-sm">
                  Select all
                </button>
              </div>
              <p class="text-xs text-mut ml-7.5 pl-0.5">Leave all unchecked to allow every model.</p>
              
              <div class="ml-7.5 pl-0.5 pt-1 relative">
                <button type="button" onclick="toggleAllowedModelsDropdown()" class="w-full surface-card border border-subtle rounded-xl px-3.5 py-2 text-xs text-left text-main flex items-center justify-between transition shadow-sm hover:border-strong">
                  <span id="allowed-models-label" class="font-medium text-main">All models</span>
                  <span class="text-mut">${ico('chevron-down', 'w-3.5 h-3.5')}</span>
                </button>
                <div id="allowed-models-dropdown" class="hidden absolute top-full left-0 right-0 mt-1 surface-card-solid border border-subtle rounded-xl p-2.5 shadow-xl z-30 max-h-48 overflow-y-auto custom-scrollbar space-y-1.5 text-xs">
                  ${MODELS_DATA.map(m => `
                    <label class="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-pill cursor-pointer select-none">
                      <span class="font-mono text-xs text-main">${m.id}</span>
                      <input type="checkbox" value="${m.id}" class="accent-[#c87a51] rounded allowed-model-cb" onchange="updateAllowedModelsLabel()" />
                    </label>
                  `).join('')}
                </div>
              </div>
            </div>

            <!-- Footer Buttons matching Image 3 -->
            <div class="flex items-center justify-end gap-3 pt-3">
              <button onclick="closeModal()" class="surface-card hover:border-strong px-4 py-2 rounded-xl text-xs text-sec hover:text-main border border-subtle transition font-medium">
                Cancel
              </button>
              <button onclick="confirmCreateApiKey()" class="surface-card hover:border-strong hover:bg-pill border border-subtle text-main font-semibold px-4 py-2 rounded-xl text-xs transition shadow-sm flex items-center gap-2">
                ${ico('key-round', 'w-3.5 h-3.5 text-mut')} <span>Create key</span>
              </button>
            </div>
          </div>
        </div>
      `;
    }

    function toggleAllowedModelsDropdown() {
      const el = document.getElementById('allowed-models-dropdown');
      if (el) el.classList.toggle('hidden');
    }

    function toggleAllowedModelsSelectAll() {
      const cbs = document.querySelectorAll('.allowed-model-cb');
      const btn = document.getElementById('allowed-models-select-btn');
      const allChecked = Array.from(cbs).every(cb => cb.checked);
      cbs.forEach(cb => { cb.checked = !allChecked; });
      if (btn) btn.textContent = allChecked ? 'Select all' : 'Deselect all';
      updateAllowedModelsLabel();
    }

    function updateAllowedModelsLabel() {
      const cbs = document.querySelectorAll('.allowed-model-cb');
      const checked = Array.from(cbs).filter(cb => cb.checked);
      const lbl = document.getElementById('allowed-models-label');
      if (lbl) {
        if (checked.length === 0 || checked.length === cbs.length) {
          lbl.textContent = 'All models';
        } else {
          lbl.textContent = `${checked.length} models selected`;
        }
      }
    }

    async function confirmCreateApiKey() {
      const nameInput = document.getElementById('new-key-name-input');
      const name = (nameInput && nameInput.value.trim()) || 'production-worker';
      const spendInput = document.getElementById('new-key-spend-input');
      const spendRaw = (spendInput && spendInput.value.trim()) ? parseFloat(spendInput.value.trim()) : null;
      const expiresSelect = document.getElementById('new-key-expires-input');
      const expiresOption = (expiresSelect && expiresSelect.value) || 'never';
      const expiresInDays = expiresInDaysFromOption(expiresOption);
      const models = Array.from(document.querySelectorAll('.allowed-model-cb:checked')).map(cb => cb.value);

      try {
        const res = await apiFetch('/api/keys', {
          method: 'POST',
          body: JSON.stringify({
            name,
            ...(spendRaw !== null && !Number.isNaN(spendRaw) ? { spendLimit: spendRaw } : {}),
            ...(expiresInDays !== null ? { expiresInDays } : {}),
            models
          })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          showToast(data.error || t('keys.createFailed'));
          return;
        }
        closeModal();
        state.selectedQuickstartKey = data.item ? data.item.id : null;
        await loadApiKeys();
        openCopySecretModal(name, data.key);
        showToast(t('keys.created', { name }));
      } catch (e) {
        showToast(t('keys.createFailed'));
      }
    }

    function openBuyPlanModal(planName, desc) {
      const root = document.getElementById('modal-root');
      root.innerHTML = `
        <div class="fixed inset-0 z-50 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div class="surface-card-solid rounded-2xl max-w-sm w-full p-6 border border-strong space-y-4 shadow-2xl text-xs">
            <h3 class="text-base font-semibold text-main">Buy ${planName}?</h3>
            <p class="text-sec leading-relaxed">${desc}</p>
            <div class="flex justify-end gap-2 pt-2">
              <button onclick="closeModal()" class="surface-card px-4 py-2 rounded-xl text-sec">Cancel</button>
              <button onclick="closeModal(); navigate('/billing');" class="bg-[#c87a51] text-[#14110f] font-semibold px-4 py-2 rounded-xl">Buy plan</button>
            </div>
          </div>
        </div>
      `;
    }

    function openPlanTokensModal(planName, count) {
      const root = document.getElementById('modal-root');
      root.innerHTML = `
        <div class="fixed inset-0 z-50 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div class="surface-card-solid rounded-2xl max-w-lg w-full p-6 border border-strong space-y-4 shadow-2xl max-h-[80vh] flex flex-col text-xs">
            <div class="flex items-start justify-between">
              <h3 class="text-base font-semibold text-main">${planName}: models and tokens (${count} models)</h3>
              <button onclick="closeModal()" class="text-sec hover:text-main">${ico('x')}</button>
            </div>
            <div class="flex-1 overflow-y-auto divide-y divide-white/5 pr-1">
              ${MODELS_DATA.slice(0, count).map(m => `
                <div class="py-2.5 flex items-center justify-between">
                  <div>
                    <div class="font-semibold text-main">${m.name}</div>
                    <div class="font-mono text-xs text-mut">${m.id}</div>
                  </div>
                  <div class="font-mono text-sm font-semibold text-[#df956d]">≈ ${Math.round(48 / m.blended)}M tokens</div>
                </div>
              `).join('')}
            </div>
          </div>
        </div>
      `;
    }

    function openInvoiceDetailsModal(invId) {
      const inv = (state.invoices || []).find(i => i.id === invId) || {
        id: invId || '926e49d8',
        amount: 25.00,
        gateway: 'OxaPay Crypto',
        status: 'Pending',
        created: 'Just now',
        fullId: (invId || '926e49d8') + '-c812-4fe0-93bf-4819d20c581a'
      };

      const bonusAmt = (inv.amount * 0.20).toFixed(2);
      const totalCredited = (inv.amount * 1.20).toFixed(2);

      const root = document.getElementById('modal-root');
      root.innerHTML = `
        <div onclick="if(event.target===this) closeModal()" class="fixed inset-0 z-50 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div class="surface-card-solid rounded-2xl max-w-md w-full p-6 border border-strong space-y-5 shadow-2xl text-xs">
            <div class="flex items-start justify-between">
              <div>
                <h3 class="text-base font-semibold text-main">Invoice Details</h3>
                <div class="text-mut text-xs font-mono mt-0.5">ID: ${inv.id}</div>
              </div>
              <button onclick="closeModal()" class="text-sec hover:text-main p-1 rounded-lg surface-card">${ico('x', 'w-4 h-4')}</button>
            </div>

            <!-- Full UUID Copy Container -->
            <div class="surface-card rounded-xl p-3 flex items-center justify-between font-mono text-main border border-subtle">
              <span class="truncate text-xs text-sec">${inv.fullId || inv.id}</span>
              <button onclick="navigator.clipboard.writeText('${inv.fullId || inv.id}'); showToast('Copied invoice ID');" class="px-2.5 py-1 rounded-lg surface-card-solid text-sec hover:text-main border border-subtle flex items-center gap-1.5 transition ml-2 shrink-0">
                ${ico('copy', 'w-3 h-3')} <span>Copy</span>
              </button>
            </div>

            <!-- Breakdown Table -->
            <div class="space-y-2.5 py-1">
              <div class="flex justify-between items-center text-sec">
                <span>Created</span>
                <span class="font-mono text-main">${inv.created}</span>
              </div>
              <div class="flex justify-between items-center text-sec">
                <span>Payment Gateway</span>
                <span class="text-main font-medium">${inv.gateway || 'Crypto / QRIS'}</span>
              </div>
              <div class="flex justify-between items-center text-sec">
                <span>Top-up Amount</span>
                <span class="font-mono text-main font-semibold">$${inv.amount.toFixed(2)}</span>
              </div>
              <div class="flex justify-between items-center text-emerald-500 dark:text-emerald-400">
                <span>Bonus Credit (+20%)</span>
                <span class="font-mono font-semibold">+$${bonusAmt}</span>
              </div>
              <div class="border-t border-subtle pt-2 flex justify-between items-center font-semibold text-main">
                <span>Total Balance Credited</span>
                <span class="font-mono text-base text-[#df956d]">$${totalCredited}</span>
              </div>
              <div class="flex justify-between items-center text-sec pt-1">
                <span>Status</span>
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${inv.status === 'Completed' ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-500 dark:text-emerald-400' : 'bg-black/5 dark:bg-white/5 border-subtle text-mut'}">
                  ${inv.status}
                </span>
              </div>
            </div>

            <!-- QR or Payment Simulator if Pending -->
            ${inv.status === 'Pending' ? `
              <div class="rounded-xl bg-inset border border-subtle p-3.5 space-y-2 text-center">
                <div class="text-xs text-sec">Complete payment via ${inv.gateway}:</div>
                <div class="w-32 h-32 mx-auto bg-white p-2 rounded-xl border border-subtle flex items-center justify-center shadow-inner">
                  ${ico('qr-code', 'w-24 h-24 text-black')}
                </div>
                <div class="text-xs text-mut font-mono">Scan QR or transfer exactly $${inv.amount.toFixed(2)}</div>
                <button onclick="inv.status='Completed'; inv.credited='$' + totalCredited + ' credited'; showToast('Payment confirmed!'); closeModal(); render();" class="w-full mt-2 bg-[#df956d] hover:bg-[#c87a51] text-[#14110f] font-semibold py-2 rounded-xl text-xs transition shadow-sm">
                  Simulate Payment Success (Demo)
                </button>
              </div>
            ` : ''}

            <div class="flex justify-end pt-2">
              <button onclick="closeModal()" class="surface-card-solid hover:border-strong text-main font-semibold px-4 py-2 rounded-xl border border-subtle transition">Close</button>
            </div>
          </div>
        </div>
      `;
    }

    function openCommandPaletteModal() {
      const isDev = !!(state.user && state.user.isDev);
      const allPages = [
        { label: 'Dashboard', route: '/dashboard', cat: t('cmd.overview') },
        { label: 'Models Catalog', route: '/catalog', cat: t('cmd.models') },
        { label: 'Status', route: '/status', cat: t('cmd.models') },
        { label: 'Leaderboard', route: '/leaderboard', cat: t('cmd.models') },
        { label: 'Usage Analytics', route: '/usage', cat: t('cmd.usage') },
        { label: 'Activity Feed', route: '/activity', cat: t('cmd.usage') },
        { label: 'Request Logs', route: '/request-logs', cat: t('cmd.usage') },
        { label: 'API Keys', route: '/api-keys', cat: t('cmd.developer') },
        { label: 'Integrations', route: '/integrations', cat: t('cmd.developer') },
        { label: 'Subscription Plans', route: '/plan', cat: t('cmd.billing') },
        { label: 'Billing & Top-up', route: '/billing', cat: t('cmd.billing') },
        { label: 'Referral Program', route: '/referral', cat: t('cmd.billing') },
        { label: 'Announcements', route: '/announcements', cat: t('cmd.system') },
        { label: 'Dev Console: Management', route: '/management', cat: 'Dev Console' },
        { label: 'Dev Console: Structure', route: '/structure', cat: 'Dev Console' },
        { label: 'Dev Console: Logs', route: '/dev-logs', cat: 'Dev Console' },
        { label: 'Public Landing Page', route: '/', cat: t('cmd.public') },
        { label: 'Public Models', route: '/models', cat: t('cmd.public') },
        { label: 'Public Pricing', route: '/pricing', cat: t('cmd.public') },
        { label: 'Public Documentation', route: '/docs', cat: t('cmd.public') }
      ];
      const pages = isDev ? allPages : allPages.filter(p => p.cat !== 'Dev Console');
      window._cmdPalettePages = pages;
      window.filterCommandPalette = function(query) {
        const q = (query || '').toLowerCase().trim();
        const listEl = document.getElementById('cmd-palette-list');
        if (!listEl) return;
        const filtered = window._cmdPalettePages.filter(p => !q || p.label.toLowerCase().includes(q) || p.route.toLowerCase().includes(q) || p.cat.toLowerCase().includes(q));
        if (filtered.length === 0) {
          listEl.innerHTML = '<div class="p-6 text-center text-mut text-xs">' + t('cmd.empty', { query: q.replace(/"/g, '&quot;') }) + '</div>';
          return;
        }
        listEl.innerHTML = filtered.map(p => `
          <button onclick="closeModal(); navigate('${p.route}');" class="w-full px-3.5 py-2.5 rounded-xl flex items-center justify-between text-left hover:bg-[#c87a51]/15 text-main hover:text-[#df956d] transition group">
            <div class="flex items-center gap-2.5">
              <span class="text-xs font-mono px-2 py-0.5 rounded bg-white/5 border border-white/5 text-mut group-hover:text-[#df956d]">${p.cat}</span>
              <span class="font-medium text-xs">${p.label}</span>
            </div>
            <span class="font-mono text-xs text-mut group-hover:text-sec">${p.route}</span>
          </button>
        `).join('');
      };
      const root = document.getElementById('modal-root');
      root.innerHTML = `
        <div onclick="if(event.target===this) closeModal()" class="fixed inset-0 z-50 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-start justify-center pt-20 p-4">
          <div class="surface-card-solid rounded-2xl max-w-lg w-full border border-strong shadow-2xl overflow-hidden text-xs">
            <div class="p-3.5 border-b border-subtle flex items-center gap-3">
              <span class="text-mut shrink-0">${ico('search', 'w-4 h-4')}</span>
              <input id="cmd-palette-input" type="text" placeholder="${t('cmd.placeholder')}" oninput="window.filterCommandPalette(this.value)" class="bg-transparent text-main flex-1 focus:outline-none text-xs" />
              <kbd class="text-xs font-mono px-1.5 py-0.5 rounded bg-white/10 text-sec border border-white/5 shrink-0 cursor-pointer" onclick="closeModal()">ESC</kbd>
            </div>
            <div id="cmd-palette-list" class="p-2 max-h-80 overflow-y-auto space-y-0.5 custom-scrollbar">
              ${pages.map(p => `
                <button onclick="closeModal(); navigate('${p.route}');" class="w-full px-3.5 py-2.5 rounded-xl flex items-center justify-between text-left hover:bg-[#c87a51]/15 text-main hover:text-[#df956d] transition group">
                  <div class="flex items-center gap-2.5">
                    <span class="text-xs font-mono px-2 py-0.5 rounded bg-white/5 border border-white/5 text-mut group-hover:text-[#df956d]">${p.cat}</span>
                    <span class="font-medium text-xs">${p.label}</span>
                  </div>
                  <span class="font-mono text-xs text-mut group-hover:text-sec">${p.route}</span>
                </button>
              `).join('')}
            </div>
          </div>
        </div>
      `;
      setTimeout(() => {
        const inp = document.getElementById('cmd-palette-input');
        if (inp) inp.focus();
      }, 50);
    }

    // ============================================================================
    // MAIN ROUTER
    // ============================================================================
    function render() {
      const app = document.getElementById('app');
      if (!app) return;

      updateDocumentTitle();
      const activeEl = document.activeElement;
      const activeId = activeEl ? activeEl.id : null;
      const selStart = (activeEl && typeof activeEl.selectionStart === 'number') ? activeEl.selectionStart : null;
      const selEnd = (activeEl && typeof activeEl.selectionEnd === 'number') ? activeEl.selectionEnd : null;

      // Protected Console Routes (Require Login)
      const protectedConsoleRoutes = [
        '/dashboard', '/usage', '/activity', '/request-logs',
        '/api-keys', '/plan', '/billing', '/referral'
      ];
      if (protectedConsoleRoutes.includes(state.route)) {
        if (!state.user) {
          showToast(t('toast.loginRequired'));
          state.route = '/login';
          app.innerHTML = renderLoginPage();
          setTimeout(initTurnstile, 100);
          return;
        }
      }

      // Handle Dev Console Route Guard
      const isDev = !!(state.user && state.user.isDev);
      const devRoutes = ['/management', '/dev/management', '/structure', '/dev/structure', '/logs', '/dev-logs', '/dev/logs'];
      if (devRoutes.includes(state.route)) {
        if (!isDev) {
          showToast(t('toast.devOnly'));
          state.route = '/login';
          app.innerHTML = renderLoginPage();
          setTimeout(initTurnstile, 100);
          return;
        }
      }

      if (state.route === '/login' || state.route === '/register' || (state.route === '/forgot-password' && state.forgotStep !== 2)) {
        setTimeout(initTurnstile, 100);
      }

      // Handle Model AI Detail Page route like video (/models/:id)
      if (state.route.startsWith('/models/') && state.route !== '/models') {
        const modelId = state.route.replace('/models/', '');
        app.innerHTML = renderModelDetailPage(modelId);
        if (activeId) {
          const el = document.getElementById(activeId);
          if (el) {
            el.focus();
            if (selStart !== null && selEnd !== null) el.setSelectionRange(selStart, selEnd);
          }
        }
        return;
      }

      switch (state.route) {
        case '/': app.innerHTML = renderLandingPage(); break;
        case '/pricing': app.innerHTML = renderPricingPage(); break;
        case '/changelog': app.innerHTML = renderChangelogPage(); break;
        case '/privacy':
        case '/terms':
          window.location.href = state.route;
          return;
        case '/models': app.innerHTML = renderModelsPage(); break;
        case '/login': app.innerHTML = renderLoginPage(); break;
        case '/register': app.innerHTML = renderRegisterPage(); break;
        case '/forgot-password': app.innerHTML = renderForgotPasswordPage(); break;
        case '/docs': app.innerHTML = renderDocsPage(); break;
        case '/llms-full.txt': app.innerHTML = renderLlmsFullTxtPage(); break;
        case '/dashboard': app.innerHTML = renderDashboardPage(); break;
        case '/catalog': app.innerHTML = renderCatalogPage(); break;
        case '/status': app.innerHTML = renderStatusPage(); break;
        case '/leaderboard': app.innerHTML = renderLeaderboardPage(); break;
        case '/usage': app.innerHTML = renderUsagePage(); break;
        case '/activity': app.innerHTML = renderActivityPage(); break;
        case '/request-logs': app.innerHTML = renderRequestLogsPage(); break;
        case '/api-keys': app.innerHTML = renderApiKeysPage(); break;
        case '/integrations': app.innerHTML = renderIntegrationsPage(); break;
        case '/integrations/hermes': app.innerHTML = renderHermesWizardPage(); break;
        case '/plan': app.innerHTML = renderSubscriptionPage(); break;
        case '/billing': app.innerHTML = renderBillingPage(); break;
        case '/referral': app.innerHTML = renderReferralPage(); break;
        case '/announcements': app.innerHTML = renderAnnouncementsPage(); break;
        case '/management':
        case '/dev/management': app.innerHTML = renderDevManagementPage(); break;
        case '/structure':
        case '/dev/structure': app.innerHTML = renderDevStructurePage(); break;
        case '/logs':
        case '/dev-logs':
        case '/dev/logs': app.innerHTML = renderDevLogsPage(); break;
        default: app.innerHTML = renderNotFoundPage();
      }

      if (activeId) {
        const el = document.getElementById(activeId);
        if (el) {
          el.focus();
          if (selStart !== null && selEnd !== null) el.setSelectionRange(selStart, selEnd);
        }
      }
    }

    window.addEventListener('keydown', function (e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openCommandPaletteModal();
      } else if (e.key === 'Escape') {
        closeModal();
      }
    });

    // window.onload removed in favor of React mount
  
    // Marquee: pause while pressed/touched, resume on release
    (function () {
      const pause = (e) => {
        const m = e.target.closest && e.target.closest('.marquee');
        if (m) m.classList.add('is-paused');
      };
      const resume = () => {
        document.querySelectorAll('.marquee.is-paused').forEach(m => m.classList.remove('is-paused'));
      };
      document.addEventListener('pointerdown', pause);
      document.addEventListener('pointerup', resume);
      document.addEventListener('pointercancel', resume);
      document.addEventListener('dragstart', (e) => { if (e.target.closest && e.target.closest('.marquee')) e.preventDefault(); });
      window.addEventListener('blur', resume);
      document.addEventListener('contextmenu', (e) => { if (e.target.closest && e.target.closest('.marquee')) e.preventDefault(); });
    })();


// Expose state and interactive actions to window for all inline HTML event attributes
if (typeof window !== 'undefined') {
  window.state = state;
  window.render = render;
  window.navigate = navigate;
  window.historyBack = historyBack;
  window.historyForward = historyForward;
  window.setLang = setLang;
  window.t = t;
  window.fmtDate = fmtDate;
  window.fmtNumber = fmtNumber;
  window.fmtUsd = fmtUsd;
  window.toggleTheme = toggleTheme;
  window.showToast = showToast;
  window.copyText = copyText;
  window.copyFromAttr = copyFromAttr;
  window.copyToClipboard = copyToClipboard;
  window.copyHeroCodeSnippet = copyHeroCodeSnippet;
  window.switchHeroCodeTab = switchHeroCodeTab;
  window.togglePublicProvider = togglePublicProvider;
  window.resetPublicModelsFilters = resetPublicModelsFilters;
  window.toggleModelActive = toggleModelActive;
  window.openCommandPaletteModal = openCommandPaletteModal;
  window.closeModal = closeModal;
  window.openCreateApiKeyModal = openCreateApiKeyModal;
  window.confirmCreateApiKey = confirmCreateApiKey;
  window.toggleApiKeyModalDone = toggleApiKeyModalDone;
  window.toggleAllowedModelsDropdown = toggleAllowedModelsDropdown;
  window.toggleAllowedModelsSelectAll = toggleAllowedModelsSelectAll;
  window.updateAllowedModelsLabel = updateAllowedModelsLabel;
  window.closeSecretModal = closeSecretModal;
  window.toggleKeyBlocked = toggleKeyBlocked;
  window.loadApiKeys = loadApiKeys;
  window.openRotateKeyModal = openRotateKeyModal;
  window.confirmRotateKey = confirmRotateKey;
  window.openNewRotatedKeyModal = openNewRotatedKeyModal;
  window.copyRotatedKeySecret = copyRotatedKeySecret;
  window.closeRotatedKeyModal = closeRotatedKeyModal;
  window.openRevokeKeyModal = openRevokeKeyModal;
  window.confirmRevokeKey = confirmRevokeKey;
  window.toggleCatalogProviderCollapse = toggleCatalogProviderCollapse;
  window.copyModelId = copyModelId;
  window.copySnippetToClipboard = copySnippetToClipboard;
  window.openBuyPlanModal = openBuyPlanModal;
  window.openPlanTokensModal = openPlanTokensModal;
  window.handleBillingPayment = handleBillingPayment;
  window.openInvoiceDetailsModal = openInvoiceDetailsModal;
  window.setBillingAmount = setBillingAmount;
  window.handleRedeemCode = handleRedeemCode;
  window.loadBillingData = loadBillingData;
  window.copyReferralLink = copyReferralLink;
  window.copyReferralCode = copyReferralCode;
  window.dismissLowBalanceBanner = dismissLowBalanceBanner;
  window.loadDashboardData = loadDashboardData;
  window.loadUsageData = loadUsageData;
  window.loadActivityData = loadActivityData;
  window.loadRequestLogsData = loadRequestLogsData;
  window.loadLeaderboardData = loadLeaderboardData;
  window.loadStatusData = loadStatusData;
  window.openCustomizeReferralCodeModal = openCustomizeReferralCodeModal;
  window.saveCustomReferralCode = saveCustomReferralCode;
  window.setManagementTab = setManagementTab;
  window.openCreateAccountModal = openCreateAccountModal;
  window.submitCreateAccount = submitCreateAccount;
  window.toggleAccountStatus = toggleAccountStatus;
  window.addAccountBalance = addAccountBalance;
  window.openCreditModal = openCreditModal;
  window.submitCreditAdjustment = submitCreditAdjustment;
  window.deleteAccount = deleteAccount;
  window.approvePayment = approvePayment;
  window.refundPayment = refundPayment;
  window.togglePaymentGateway = togglePaymentGateway;
  window.setTermsTab = setTermsTab;
  window.saveAndPublishTerms = saveAndPublishTerms;
  window.setPrivacyTab = setPrivacyTab;
  window.togglePrivacyZdr = togglePrivacyZdr;
  window.saveAndPublishPrivacy = saveAndPublishPrivacy;
  window.submitNewAnnouncement = submitNewAnnouncement;
  window.toggleAnnouncementBanner = toggleAnnouncementBanner;
  window.toggleAnnouncementPin = toggleAnnouncementPin;
  window.toggleAnnouncementStatus = toggleAnnouncementStatus;
  window.deleteAnnouncement = deleteAnnouncement;
  window.selectMgmtModel = selectMgmtModel;
  window.stepModelPrice = stepModelPrice;
  window.updateModelPrice = updateModelPrice;
  window.applyModelDiscountPreset = applyModelDiscountPreset;
  window.updateModelDescription = updateModelDescription;
  window.saveModelAiChanges = saveModelAiChanges;
  window.restoreModelVideoDescription = restoreModelVideoDescription;
  window.updateModelField = updateModelField;
  window.refreshModels = refreshModels;
  window.importModelsFromLiteLLM = importModelsFromLiteLLM;
  window.loadModelHistory = loadModelHistory;
  window.openCreateModelModal = openCreateModelModal;
  window.submitCreateModel = submitCreateModel;
  window.openDeleteModelModal = openDeleteModelModal;
  window.confirmDeleteModel = confirmDeleteModel;
  window.openDeviceMoreModal = openDeviceMoreModal;
  window.filterMoreSheet = filterMoreSheet;
  window.handleIntegrationClick = handleIntegrationClick;
  window.regenerateHermesToken = regenerateHermesToken;
  window.renderLeaderboardPage = renderLeaderboardPage;
  window.renderStatusPage = renderStatusPage;
  window.renderUsagePage = renderUsagePage;
  window.submitLoginForm = submitLoginForm;
  window.handleLogout = handleLogout;
  window.handleGoogleAuth = handleGoogleAuth;
  window.initTurnstile = initTurnstile;
  window.updateTurnstileButtons = updateTurnstileButtons;
  window.sendForgotOtp = sendForgotOtp;
  window.submitResetPassword = submitResetPassword;
  window.backToForgotStep1 = backToForgotStep1;
  window.renderForgotPasswordPage = renderForgotPasswordPage;
  window.fetchDevData = fetchDevData;
  window.handleRunReconcile = handleRunReconcile;
  window.getExampleModelId = getExampleModelId;
  window.apiFetch = apiFetch;

  window.addEventListener('popstate', () => {
    const p = window.location.pathname || '/';
    state.route = p;
    render();
  });
}

export function initOpenCSN() {
  if (typeof window === 'undefined') return;

  document.documentElement.lang = state.lang;
  // Intercept Google OAuth callback redirect parameters directly
  if (window.location.search) {
    const params = new URLSearchParams(window.location.search);
    const authSuccess = params.get('auth');
    const authUserRaw = params.get('user');
    const authError = params.get('error');

    if (authSuccess === 'google_success' && authUserRaw) {
      try {
        const parsed = JSON.parse(decodeURIComponent(authUserRaw));
        if (parsed && parsed.email) {
          state.user = parsed;
          state.referralCode = parsed.referralCode || generateRandomReferralCode();
          try { localStorage.setItem('opencsn_user', JSON.stringify(parsed)); } catch (e) {}
          window.history.replaceState({}, '', '/dashboard');
          state.route = '/dashboard';
          state.history = ['/dashboard'];
          showToast(t('toast.welcome', { name: parsed.name || parsed.email }));
        }
      } catch (e) {
        console.error('Failed to parse Google user session:', e);
      }
    } else if (authError) {
      showToast(t('toast.authFailed', { message: decodeURIComponent(authError) }));
      window.history.replaceState({}, '', '/login');
      state.route = '/login';
    }
  }

  const p = window.location.pathname;
  if (p && p !== '/') {
    state.route = p;
    state.history = [p];
  }
  if (state.theme === 'light') {
    document.documentElement.classList.remove('dark');
    document.documentElement.classList.add('light');
  } else {
    document.documentElement.classList.add('dark');
    document.documentElement.classList.remove('light');
  }
  render();

  // Load public config
  apiFetch('/api/public-config')
    .then(r => (r.ok ? r.json() : null))
    .then(cfg => {
      if (cfg && cfg.apiBaseUrl) {
        state.apiBaseUrl = cfg.apiBaseUrl;
      }
    })
    .catch(() => {});

  // Load public status / leaderboard if accessed directly
  if (state.route === '/leaderboard') loadLeaderboardData();
  if (state.route === '/status') loadStatusData();

  // Load public catalog snapshot and initiate realtime SSE stream
  apiFetch('/api/catalog')
    .then(r => (r.ok ? r.json() : null))
    .then(data => {
      const list = data && Array.isArray(data.models) ? data.models : (Array.isArray(data) ? data : []);
      if (list.length > 0) {
        applyCatalogUpdate(list, data ? data.stale : false);
      }
      render();
    })
    .catch(() => {});

  startRealtimeStream();

  // Verify server session with GET /api/me (rely on server instead of trusting localStorage)
  apiFetch('/api/me')
    .then(r => r.ok ? r.json() : null)
    .then(data => {
      if (data && data.user) {
        state.user = data.user;
        try { localStorage.setItem('opencsn_user', JSON.stringify(data.user)); } catch (e) {}
        if (state.user.isDev) {
          fetchDevData();
        }
        loadBillingData().then(() => render());
        loadApiKeys();
        if (state.route === '/dashboard') loadDashboardData();
        else if (state.route === '/usage') loadUsageData();
        else if (state.route === '/activity') loadActivityData();
        else if (state.route === '/request-logs') loadRequestLogsData();
        else if (state.route === '/leaderboard') loadLeaderboardData();
        else if (state.route === '/status') loadStatusData();
      } else {
        state.user = null;
        try { localStorage.removeItem('opencsn_user'); } catch (e) {}
      }
      render();
    })
    .catch(() => {});

  if (state.user && state.user.isDev) {
    fetchDevData();
  }

  if (state.route === '/login' || state.route === '/register' || (state.route === '/forgot-password' && state.forgotStep !== 2)) {
    setTimeout(initTurnstile, 100);
  }
}

export { apiFetch, state, navigate, render, fetchDevData, loadBillingData, submitLoginForm, handleLogout, handleGoogleAuth, initTurnstile, openCreditModal, submitCreditAdjustment, sendForgotOtp, submitResetPassword, backToForgotStep1, renderForgotPasswordPage };
